import type { ServicePageConfig } from "../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../package-pricing";

export const transportPages: ServicePageConfig[] = [
  {
    // Transport förråd — config för /transport-forrad
    // Vinkel: Transport av saker till och från förråd/magasin (self storage).
    // Unik vinkel vs tömma-förråd: här ANVÄNDS förrådet aktivt — saker körs dit eller hämtas därifrån.
    // tömma-förråd = tömma förrådet helt, ofta inför uppsägning.
    slug: "transport-forrad",
    serviceCategory: "transport",

    meta: {
      title: "Transport Förråd – Smidig Transport Till & Från Förråd | Ungdomskraft",
      description:
        "Boka transport till och från förråd i Stockholm och Göteborg. Vi hämtar, bär och kör dina saker till förrådet – eller hem igen. Fast pris, försäkrat.",
      h1: "Förrådstransport",
    },

    hero: {
      heading: "Transport till och från förråd",
      intro:
        "Behöver du köra möbler, kartonger eller säsongsprylar till ditt förråd – eller hämta tillbaka saker därifrån? Vi hämtar dina grejer hemma hos dig, bär ut, lastar skåpbilen och kör till förrådsanläggningen. Där bär vi in allt och ställer det på plats i ditt förråd. Eller tvärtom – vi hämtar i förrådet och levererar hem. Du slipper hyra bil, bära själv eller göra flera vändor.",
      heroImage: "/hero-transport-forrad.png",
    },

    featureCards: {
      sinceText: "Förrådstransport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att transportera saker till och från förråd i Stockholms- och Göteborgsområdet. Våra ungdomar vet hur förrådsanläggningar fungerar och hanterar allt från tunga möbler till ömtåliga kartonger.",
      bookYourText: "Boka din förrådstransport",
      bookDescText:
        "Ange din adress och förrådsadressen, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Perfekta skåpbilar för förrådstransport",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska köra en enda kartong eller fylla ett helt förråd. Du slipper hyra bil, göra flera turer eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi kör till och från förråd",
      items: [
        {
          name: "Möbler",
          description: "Soffor, sängar, bord, garderober och stolar som ska till eller från förvaring",
          image: "/sofa.webp",
        },
        {
          name: "Flyttkartonger",
          description: "Kartonger med kläder, böcker, köksprylar och personliga ägodelar",
          image: "/box.webp",
        },
        {
          name: "Säsongsprylar",
          description: "Vinterdäck, skidor, cyklar, trädgårdsmöbler och julpynt",
          image: "/patio-furniture.webp",
        },
        {
          name: "Vitvaror",
          description: "Tvättmaskin, kylskåp, frys och andra vitvaror som ska mellanlagras",
          image: "/washer.webp",
        },
        {
          name: "Elektronik",
          description: "TV-apparater, datorer, skärmar och annan elektronik",
          image: "/tv.webp",
        },
        {
          name: "Konst & ömtåligt",
          description: "Tavlor, speglar, porslin och andra ömtåliga föremål",
          image: "/painting.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Transport till och från förråd – vi kör, bär och ställer in åt dig",
      mainText:
        "Att köra saker till förrådet borde vara enkelt – men ofta slutar det med att du hyr en skåpbil, gör tre turer och sliter ryggen i trappan. Med Ungdomskrafts förrådstransport slipper du allt det. Vi kommer hem till dig med skåpbil och ett team av starka ungdomar, bär ut det som ska till förrådet, lastar och kör till anläggningen. Där bär vi in allt och ställer det organiserat i ditt förråd. Behöver du hämta saker från förrådet istället? Samma sak fast åt andra hållet – vi hämtar i förrådet och levererar hem till din dörr. Vi arbetar med alla typer av förrådsanläggningar: Pelican Self Storage, 24Storage, Shurgard, Green Storage, Storage365, Stockholm Self Storage, Big Easy, Kopparcronan, Lidingö Förrådcenter och alla andra. Oavsett om du ska ställa in möbler inför en renovering, lagra säsongsprylar eller hämta tillbaka saker efter en flytt anpassar vi fordon och team efter mängden.",
      preparationHeading: "Förbered inför förrådstransport",
      preparationText:
        "Packa det du kan i kartonger och märk dem med innehåll – det gör det lättare att hitta saker senare. Demontera möbler som inte går genom dörrar hela. Ha nycklar, portkod eller bokningskod till förrådet redo. Kolla att det finns fri passage i trapphus och att skåpbilen kan parkera nära porten både hemma och vid förrådsanläggningen. Om du hämtar från förrådet – se till att du vet var i förrådet sakerna står.",
      additionalSections: [
        {
          heading: "Transport till förråd vid renovering och flytt",
          content:
            "Ska du renovera och behöver ett ställe att förvara möblerna under tiden? Eller flyttar du och den nya bostaden inte är redo än? Vi kör dina saker till förrådet, bär in och ställer allt organiserat. När du är redo att hämta tillbaka allt bokar du en ny frakt och vi levererar det hela hem till dig. Många av våra kunder använder förrådstransport som en flexibel mellanlagringslösning – du bestämmer hur länge sakerna ska stå och vi sköter transporten åt båda hållen.",
        },
        {
          heading: "Hämta saker från förråd",
          content:
            "Behöver du hämta tillbaka möbler, kartonger eller säsongsprylar från förrådet? Vi åker till anläggningen, bär ut det du vill ha, lastar skåpbilen och kör hem till dig. Vi bär in allt och ställer det där du vill ha det. Perfekt när vintern är över och trädgårdsmöblerna ska hem, när renoveringen är klar eller när du vill hämta tillbaka saker efter en flytt. Du slipper hyra bil och göra det själv.",
        },
        {
          heading: "Vi kör till alla förrådsanläggningar",
          listItems: [
            {
              label: "Pelican Self Storage",
              text: "Anläggningar i Stockholm och Göteborg – vi hanterar era portkoder och hissar.",
            },
            {
              label: "24Storage",
              text: "Moderna anläggningar med digital access – vi vet hur systemen fungerar.",
            },
            {
              label: "Shurgard",
              text: "Internationell kedja med anläggningar i Stockholmsområdet.",
            },
            {
              label: "Green Storage",
              text: "Klimatsmarta förråd – vi kör och bär in varsamt.",
            },
            {
              label: "Övriga",
              text: "Storage365, Stockholm Self Storage, Big Easy, Kopparcronan, Lidingö Förrådcenter och alla andra anläggningar.",
            },
          ],
          afterContent:
            "Vi kör till alla förrådsanläggningar i Stockholm och Göteborg – ange bara adressen vid bokning.",
        },
        {
          heading: "Varför boka förrådstransport med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje förrådstransport vi genomför. När du bokar förrådstransport hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de bär dina kartonger till hur de staplar allt snyggt och organiserat i ditt förråd.\n\nSedan starten 2014 har vi kört tusentals förrådstransporter i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker lastning och hur man hanterar allt från ömtåliga föremål till tunga möbler i trånga förrådsgångar. De tränas i att skydda kundens ägodelar, golv, väggar och dörrkarmar – och i att alltid lämna allt i gott skick.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att allt är rätt lastat och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje förrådstransport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje förrådstransport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din förrådstransport blir en del av något större.\n\nVi tar också hållbarhet på allvar. Saker som inte längre behövs sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer som Myrorna, Erikshjälpen och Stadsmissionen. Ingenting slängs i onödan.\n\nDet är smidig förrådstransport med mening.\n\nFörrådstransport ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport till förråd?",
        answer:
          "Priset beror på mängden saker, avstånd och om bärning i trappor krävs. Ett par kartonger och en liten möbel landar ofta på 595–995 kr. Större mängder med flera tunga möbler kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på förrådstransport?",
        answer:
          "Förrådstransport ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni hämta saker från mitt förråd?",
        answer:
          "Ja, vi hämtar saker från ditt förråd och levererar hem till din dörr. Vi bär ut ur förrådet, lastar skåpbilen, kör till din adress och bär in. Ange förrådsadressen vid bokning.",
      },
      {
        question: "Ingår skåpbil i förrådstransport?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Vilka förrådsanläggningar kör ni till?",
        answer:
          "Vi kör till alla förrådsanläggningar i Stockholm och Göteborg: Pelican Self Storage, 24Storage, Shurgard, Green Storage, Storage365, Stockholm Self Storage, Big Easy, Kopparcronan, Lidingö Förrådcenter och alla andra. Ange bara adressen vid bokning.",
      },
      {
        question: "Kan ni köra saker till förråd vid renovering?",
        answer:
          "Ja, vi kör möbler och kartonger till förrådet under renoveringen. När den är klar bokar du en ny frakt och vi hämtar tillbaka allt. Perfekt vid golvslipning, målning eller köksrenovering.",
      },
      {
        question: "Bär ni in sakerna i förrådet?",
        answer:
          "Ja, vi bär in alla saker och ställer dem organiserat i ditt förråd. Vi hanterar hissar, trånga gångar och tunga luckor på förrådsanläggningen. Du behöver inte göra något annat än att öppna förrådet.",
      },
      {
        question: "Kan ni transportera tunga möbler till förråd?",
        answer:
          "Ja, vi transporterar alla typer av möbler – soffor, sängar, garderober, bord och allt annat tungt. Vi har rätt teknik och utrustning för att bära och lasta säkert.",
      },
      {
        question: "Demonterar ni möbler innan transport till förråd?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att kunna transporteras. Spara gärna skruvar i märkta påsar.",
      },
      {
        question: "Kan ni köra säsongsprylar till och från förråd?",
        answer:
          "Ja, vi kör vinterdäck, skidor, trädgårdsmöbler, julpynt och andra säsongsprylar till och från förrådet. Perfekt för att rotera grejer mellan hemmet och förrådet utan att hyra bil.",
      },
      {
        question: "Hur snabbt kan ni köra till förrådet?",
        answer:
          "Vi erbjuder ofta förrådstransport med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Skyddar ni sakerna under transport till förråd?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar möbler och ömtåliga föremål under lastning, transport och inbärning. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Kan ni köra saker till förråd på helger?",
        answer:
          "Ja, vi erbjuder förrådstransport på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Behöver jag vara med vid förrådsanläggningen?",
        answer:
          "Du behöver vara med för att ge oss tillgång till förrådet – med nyckel, portkod eller digital bokningskod. Vill du slippa åka dit kan du ordna en fullmakt eller lämna nyckeln till oss i förväg.",
      },
      {
        question: "I vilka kommuner erbjuder ni transport till förråd?",
        answer:
          "Vi erbjuder transport till och från förråd i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Förrådstransport i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut dina saker, lastar skåpbilen och kör till förrådsanläggningen. Där bär vi in allt och ställer det organiserat i ditt förråd.",
      step3Title: "Allt inställt",
      step3Desc:
        "Dina saker står säkert och organiserat i förrådet. Eller hemma hos dig – om du hämtade från förrådet. Klart.",
      ctaHeading: "Boka förrådstransport idag",
      ctaText:
        "Ange din adress och förrådsadressen, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "storage-transport",
      meta: {
        title: "Storage Transport – Easy Transport To & From Storage | Ungdomskraft",
        description:
          "Book storage transport in Stockholm & Gothenburg. We pick up, carry and deliver your items to or from your storage unit. Fixed price, insured. Book today!",
        h1: "Storage transport",
      },
      hero: {
        heading: "Transport to and from storage",
        intro:
          "Need to take furniture, boxes or seasonal items to your storage unit — or pick things up from it? We come to your home with a van and crew, carry everything out, load up and drive to the storage facility. There we carry everything in and place it neatly in your unit. Or the other way round — we collect from your unit and deliver to your door. No need to rent a car, carry things yourself or make multiple trips.",
      },
      featureCards: {
        sinceText: "Storage transport since 2014",
        bookYourText: "Book your storage transport",
        bookDescText:
          "Enter your home address and storage address, describe what needs transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "Van perfect for storage transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're taking a single box or filling an entire unit. No need to rent a vehicle, make multiple trips or strain your back.",
      },
      itemsSection: {
        heading: "Common items we transport to and from storage",
        items: [
          {
            name: "Furniture",
            description: "Sofas, beds, tables, wardrobes and chairs going to or from storage",
            image: "/sofa.webp",
          },
          {
            name: "Moving boxes",
            description: "Boxes with clothes, books, kitchenware and personal belongings",
            image: "/box.webp",
          },
          {
            name: "Seasonal items",
            description: "Winter tyres, skis, bicycles, garden furniture and holiday decorations",
            image: "/patio-furniture.webp",
          },
          {
            name: "Appliances",
            description: "Washing machines, fridges, freezers and other appliances for interim storage",
            image: "/washer.webp",
          },
          {
            name: "Electronics",
            description: "TVs, computers, monitors and other electronics",
            image: "/tv.webp",
          },
          {
            name: "Art & fragile items",
            description: "Paintings, mirrors, china and other delicate objects",
            image: "/painting.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Storage transport — we drive, carry and place everything for you",
        mainText:
          "Taking things to your storage unit should be easy — but it often ends with renting a van, making three trips and straining your back on the stairs. With Ungdomskraft's storage transport you skip all of that. We come to your home with a van and a team of strong young workers, carry out what needs to go to storage, load up and drive to the facility. There we carry everything in and place it neatly in your unit. Need to collect items from storage instead? Same thing in reverse — we pick up from your unit and deliver to your door. We work with every type of storage facility: Pelican Self Storage, 24Storage, Shurgard, Green Storage, Storage365, Stockholm Self Storage, Big Easy, Kopparcronan, Lidingö Förrådcenter and all others. Whether you are storing furniture during a renovation, putting away seasonal items or collecting things after a move, we scale the vehicle and team to match the volume.",
        preparationHeading: "Preparing for storage transport",
        preparationText:
          "Pack what you can into boxes and label them with their contents — it makes it easier to find things later. Disassemble furniture that won't fit through doorways whole. Have keys, a door code or booking code for the storage facility ready. Check that there is clear access in stairwells and that the van can park close to the entrance both at home and at the facility. If you're collecting from storage, make sure you know where in the unit your items are.",
        additionalSections: [
          {
            heading: "Storage transport during renovation and moving",
            content:
              "Renovating and need somewhere to keep your furniture in the meantime? Or moving when the new home isn't ready yet? We drive your belongings to the storage facility, carry them in and place everything neatly. When you're ready to have everything back, book another transport and we deliver it all to your door. Many of our customers use storage transport as a flexible interim solution — you decide how long things stay and we handle the transport both ways.",
          },
          {
            heading: "Collecting items from storage",
            content:
              "Need to get furniture, boxes or seasonal items back from your storage unit? We drive to the facility, carry out what you want, load the van and deliver to your door. We carry everything in and place it where you want it. Perfect when winter is over and the garden furniture needs to come home, when a renovation is finished or when you want to retrieve things after a move. No need to rent a car and do it yourself.",
          },
          {
            heading: "We drive to every storage facility",
            listItems: [
              {
                label: "Pelican Self Storage",
                text: "Facilities in Stockholm and Gothenburg — we handle your door codes and lifts.",
              },
              {
                label: "24Storage",
                text: "Modern facilities with digital access — we know how the systems work.",
              },
              {
                label: "Shurgard",
                text: "International chain with facilities across the Stockholm area.",
              },
              {
                label: "Green Storage",
                text: "Climate-smart storage — we drive and carry in with care.",
              },
              {
                label: "Others",
                text: "Storage365, Stockholm Self Storage, Big Easy, Kopparcronan, Lidingö Förrådcenter and every other facility.",
              },
            ],
            afterContent:
              "We drive to every storage facility in Stockholm and Gothenburg — just enter the address when booking.",
          },
          {
            heading: "Why book storage transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every storage transport we carry out. When you book storage transport with us, you get a team of motivated, energetic young workers who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that mindset runs through everything — from how they carry your boxes to how they stack everything neatly and organised in your unit.\n\nSince our founding in 2014, we have carried out thousands of storage transports across the Stockholm and Gothenburg regions. Along the way, we have given thousands of young people their first real work experience — with everything that entails: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn lifting techniques, safe loading and how to handle everything from fragile items to heavy furniture in narrow storage corridors. They are trained to protect customers' belongings, floors, walls and door frames — and to always leave everything in good condition.\n\nThis is not just good for the young people — it is good for you as a customer. You notice the difference in commitment and energy. Our teams run where others walk, double-check that everything is properly loaded and always ask if there is anything else they can help with. We have built a culture where every storage transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every storage transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your storage transport becomes part of something bigger.\n\nWe also take sustainability seriously. Items that are no longer needed are sorted and taken to the right recycling station or donated to charities such as Myrorna, Erikshjälpen and Stadsmissionen. Nothing is dumped unnecessarily.\n\nIt is smooth storage transport with purpose.\n\nStorage transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does storage transport cost?",
          answer:
            "The price depends on the volume, distance and whether stair carrying is needed. A few boxes and a small piece of furniture often comes to SEK 595–995. Larger loads with several heavy items can come to SEK 1,495–2,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on storage transport?",
          answer:
            "Storage transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Can you collect items from my storage unit?",
          answer:
            "Yes, we collect items from your storage unit and deliver them to your door. We carry out of the unit, load the van, drive to your address and carry everything in. Just enter the storage address when booking.",
        },
        {
          question: "Is a van included in storage transport?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting — you don't need to rent a car, drive or refuel.",
        },
        {
          question: "Which storage facilities do you drive to?",
          answer:
            "We drive to every storage facility in Stockholm and Gothenburg: Pelican Self Storage, 24Storage, Shurgard, Green Storage, Storage365, Stockholm Self Storage, Big Easy, Kopparcronan, Lidingö Förrådcenter and all others. Just enter the address when booking.",
        },
        {
          question: "Can you transport items to storage during a renovation?",
          answer:
            "Yes, we drive furniture and boxes to your storage unit during the renovation. When it's done, book another transport and we bring everything back. Perfect for floor sanding, painting or kitchen renovations.",
        },
        {
          question: "Do you carry items into the storage unit?",
          answer:
            "Yes, we carry everything in and place it neatly in your unit. We handle lifts, narrow corridors and heavy shutters at the facility. You don't need to do anything other than open the unit.",
        },
        {
          question: "Can you transport heavy furniture to storage?",
          answer:
            "Yes, we transport all types of furniture — sofas, beds, wardrobes, tables and anything else that's heavy. We have the right technique and equipment to carry and load safely.",
        },
        {
          question: "Do you disassemble furniture before transport to storage?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart. Keep screws in labelled bags so reassembly is easy when you collect them.",
        },
        {
          question: "Can you transport seasonal items to and from storage?",
          answer:
            "Yes, we transport winter tyres, skis, garden furniture, holiday decorations and other seasonal items to and from your storage unit. Perfect for rotating things between home and storage without renting a car.",
        },
        {
          question: "How quickly can you do storage transport?",
          answer:
            "We often offer storage transport at short notice — sometimes the same week. Book online and you'll see available times straight away.",
        },
        {
          question: "Do you protect items during storage transport?",
          answer:
            "Yes, we bring furniture blankets, carrying straps and ratchet straps. We protect furniture and fragile items during loading, transport and carrying in. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question: "Can you do storage transport on weekends?",
          answer:
            "Yes, we offer storage transport on weekends and some evenings. Book online and you'll see all available slots.",
        },
        {
          question: "Do I need to be at the storage facility?",
          answer:
            "You need to be there to give us access to the unit — with a key, door code or digital booking code. If you'd rather not go, you can arrange authorisation or leave the key with us in advance.",
        },
        {
          question: "Which areas do you offer storage transport in?",
          answer:
            "We offer storage transport throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Storage transport in 3 easy steps",
        step2Text:
          "Our workers carry your items out, load the van and drive to the storage facility. There we carry everything in and place it neatly in your unit.",
        step3Title: "All stored",
        step3Desc:
          "Your items are safely and neatly placed in your storage unit. Or at home — if you were collecting from storage. Done.",
        ctaHeading: "Book storage transport today",
        ctaText:
          "Enter your home address and storage address, describe what needs transporting and pick a time. You see the price instantly.",
      },
    },
  },

  {
    // Transport Pelican — config för /transport-pelican
    // Vinkel: Specifikt för kunder som hyr förråd hos Pelican Self Storage.
    // Nischad sida — lätt att ranka på "transport pelican self storage".
    slug: "transport-pelican",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Pelican Self Storage – Smidig Transport Till & Från Pelican | Ungdomskraft",
      description:
        "Boka transport till och från Pelican Self Storage. Vi hämtar hemma, kör till Pelican och bär in – eller hämtar från ditt förråd. Fast pris. Boka idag!",
      h1: "Transport Pelican Self Storage",
    },

    hero: {
      heading: "Transport till och från Pelican",
      intro:
        "Hyr du förråd hos Pelican Self Storage och behöver hjälp att köra saker dit eller hämta hem? Vi kommer med skåpbil och personal, bär ut hemma hos dig, kör till din Pelican-anläggning och bär in allt i ditt förråd. Eller tvärtom – vi hämtar från ditt Pelican-förråd och levererar hem. Du slipper hyra bil, bära själv och göra flera vändor.",
      heroImage: "/hero-transport-forrad.png",
    },

    featureCards: {
      sinceText: "Pelican-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt hundratals Pelican Self Storage-kunder att transportera saker till och från sina förråd. Våra ungdomar känner till Pelicans anläggningar, vet hur portkodsystemen fungerar och hanterar allt från tunga möbler till ömtåliga kartonger.",
      bookYourText: "Boka din Pelican-transport",
      bookDescText:
        "Ange din hemadress och vilken Pelican-anläggning du hyr förråd hos, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar anpassade för Pelican-förråd",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska köra en kartong eller fylla ett helt Pelican-förråd. Du slipper hyra bil, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi kör till och från Pelican Self Storage",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, sängar, bord, garderober och stolar som ska till eller från ditt Pelican-förråd",
          image: "/sofa.webp",
        },
        {
          name: "Flyttkartonger",
          description:
            "Kartonger med kläder, böcker, köksprylar och personliga ägodelar",
          image: "/box.webp",
        },
        {
          name: "Säsongsprylar",
          description:
            "Vinterdäck, skidor, cyklar, trädgårdsmöbler och julpynt",
          image: "/patio-furniture.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Tvättmaskin, kylskåp, frys och andra vitvaror som ska mellanlagras hos Pelican",
          image: "/washer.webp",
        },
        {
          name: "Elektronik",
          description: "TV-apparater, datorer, skärmar och annan elektronik",
          image: "/tv.webp",
        },
        {
          name: "Konst & ömtåligt",
          description:
            "Tavlor, speglar, porslin och andra ömtåliga föremål",
          image: "/painting.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport till och från Pelican Self Storage – vi kör, bär och ställer in",
      mainText:
        "Pelican Self Storage är en av Sveriges största förrådskedjor med moderna anläggningar i Stockholm och Göteborg. Att hyra förråd hos Pelican är enkelt – men att fysiskt få dit sina saker kan vara en utmaning. Tunga möbler i trappor, en skåpbil som ska hyras, flera vändor i rusningstrafik och sedan ska allt bäras genom Pelicans gångar och in i ditt förråd. Med Ungdomskraft slipper du allt det. Vi kommer hem till dig med skåpbil och ett team av starka ungdomar, bär ut det som ska till förrådet, lastar och kör till din Pelican-anläggning. Där hanterar vi portkoder, hissar och trånga gångar – och ställer in allt organiserat i ditt förråd. Behöver du hämta saker från ditt Pelican-förråd? Vi hämtar, lastar och levererar hem till din dörr. Vi kör till alla Pelicans anläggningar: Bromma, Hägersten, Farsta, Kista, Sollentuna, Haninge, Mörby, Solna, Högsbo och alla andra. Oavsett om du ska ställa in möbler inför en renovering, lagra säsongsprylar eller hämta tillbaka saker efter en flytt anpassar vi fordon och team efter mängden.",
      preparationHeading: "Förbered inför transport till Pelican Self Storage",
      preparationText:
        "Packa det du kan i kartonger och märk dem med innehåll – det gör det lättare att hitta saker i ditt Pelican-förråd senare. Demontera möbler som inte går genom dörrar hela. Ha din portkod eller bokningskod till Pelican-anläggningen redo. Se till att du vet vilket förråd som är ditt och på vilken våning det ligger. Kolla att det finns fri passage i trapphus hemma och att skåpbilen kan parkera nära porten. Om du hämtar från Pelican – se till att du vet var i förrådet sakerna står.",
      additionalSections: [
        {
          heading: "Pelican Self Storage-anläggningar vi kör till",
          listItems: [
            {
              label: "Pelican Bromma",
              text: "Nära Bromma Blocks – en av Stockholms mest populära Pelican-anläggningar.",
            },
            {
              label: "Pelican Hägersten",
              text: "Lättillgängligt vid Västberga industriområde med stora förråd.",
            },
            {
              label: "Pelican Farsta",
              text: "Söder om Stockholm med bra tillgänglighet från Nynäsvägen.",
            },
            {
              label: "Pelican Kista",
              text: "Norra Stockholm – perfekt för dig i Kista, Järfälla eller Sollentuna.",
            },
            {
              label: "Pelican Sollentuna",
              text: "Norra förorterna – nära E4:an med enkelt stopp för lastning.",
            },
            {
              label: "Pelican Solna",
              text: "Centralt i Solna med snabb access från Solnavägen.",
            },
            {
              label: "Pelican Högsbo",
              text: "Göteborg – centralt i Högsbo med bra tillgänglighet från Dag Hammarskjöldsleden.",
            },
            {
              label: "Pelican Mölndal",
              text: "Göteborg – nära Mölndals centrum med enkel access från E6:an.",
            },
            {
              label: "Pelican Backaplan",
              text: "Göteborg – Hisingen-sidan med bra parkering och lastzon.",
            },
          ],
          afterContent:
            "Vi kör till alla Pelican Self Storage-anläggningar i Stockholm och Göteborg. Ange bara adressen vid bokning – vi fixar resten. Hittar du inte din Pelican-anläggning i listan? Kontakta oss så hjälper vi dig.",
        },
        {
          heading: "Transport till Pelican vid renovering eller flytt",
          content:
            "Ska du renovera och behöver mellanlagra möblerna? Eller flyttar du och den nya bostaden inte är redo än? Vi kör dina saker till ditt Pelican-förråd, bär in och ställer allt organiserat. När du är redo att hämta tillbaka allt bokar du en ny transport och vi levererar det hela hem till dig. Många Pelican-kunder använder oss som en smidig transportlösning åt båda hållen – du fokuserar på renoveringen eller flytten, vi sköter logistiken. Pelicans flexibla hyrperioder gör det enkelt att anpassa förrådet efter dina behov, och med Ungdomskraft behöver du inte oroa dig för transporten.",
        },
        {
          heading: "Hämta saker från ditt Pelican-förråd",
          content:
            "Behöver du hämta tillbaka möbler, kartonger eller säsongsprylar från ditt Pelican-förråd? Vi åker till anläggningen, hanterar portkod och hiss, bär ut det du vill ha, lastar skåpbilen och kör hem till dig. Vi bär in allt och ställer det där du vill ha det. Perfekt när vintern är över och trädgårdsmöblerna ska hem, när renoveringen är klar eller när du ska hämta saker inför en flytt. Du slipper hyra bil och göra det själv – vi gör hela jobbet åt dig.",
        },
        {
          heading:
            "Varför boka transport till Pelican Self Storage med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje Pelican-transport vi genomför. När du bokar transport till eller från ditt Pelican-förråd hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de bär dina kartonger till hur de staplar allt snyggt och organiserat i ditt Pelican-förråd.\n\nSedan starten 2014 har vi kört tusentals transporter till och från förråd i Stockholms- och Göteborgsområdet – varav en stor del till just Pelican Self Storage. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker lastning och hur man hanterar allt från ömtåliga föremål till tunga möbler i trånga förrådsgångar.\n\nVåra ungdomar känner till Pelicans anläggningar. De vet hur portkodsystemen fungerar, vilka hissar som finns, hur gångarna är uppbyggda och var man bäst parkerar skåpbilen. Det gör att transporten går snabbare och smidigare – du slipper stå och förklara och kan lita på att teamet hanterar allt självständigt.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att allt är rätt lastat och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje transport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje transport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din transport till Pelican blir en del av något större.\n\nVi tar också hållbarhet på allvar. Saker som inte längre behövs sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer som Myrorna, Erikshjälpen och Stadsmissionen. Ingenting slängs i onödan.\n\nDet är smidig Pelican-transport med mening.\n\nTransport till Pelican Self Storage ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport till Pelican Self Storage?",
        answer:
          "Priset beror på mängden saker, avstånd mellan hemadress och Pelican-anläggningen och om bärning i trappor krävs. Ett par kartonger och en liten möbel landar ofta på 595–995 kr. Flera tunga möbler och kartonger kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport till Pelican Self Storage?",
        answer:
          "Transport till Pelican Self Storage ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni hämta saker från mitt Pelican-förråd?",
        answer:
          "Ja, vi åker till din Pelican-anläggning, hanterar portkod och hiss, bär ut sakerna ur ditt förråd, lastar skåpbilen och kör hem till dig. Vi bär in allt och ställer det där du vill ha det.",
      },
      {
        question: "Ingår skåpbil i Pelican-transporten?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras till eller från ditt Pelican-förråd – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Vilka Pelican-anläggningar kör ni till?",
        answer:
          "Vi kör till alla Pelican Self Storage-anläggningar i Stockholm och Göteborg: Bromma, Hägersten, Farsta, Kista, Sollentuna, Haninge, Mörby, Solna, Högsbo och alla andra. Ange bara adressen vid bokning.",
      },
      {
        question:
          "Kan ni köra saker till mitt Pelican-förråd vid renovering?",
        answer:
          "Ja, vi kör möbler och kartonger till ditt Pelican-förråd under renoveringen. När den är klar bokar du en ny transport och vi hämtar tillbaka allt. Perfekt vid golvslipning, målning eller köksrenovering.",
      },
      {
        question: "Bär ni in sakerna i mitt Pelican-förråd?",
        answer:
          "Ja, vi bär in alla saker och ställer dem organiserat i ditt Pelican-förråd. Vi hanterar portkoder, hissar, trånga gångar och tunga luckor på anläggningen. Du behöver inte göra något annat än att visa oss rätt förråd.",
      },
      {
        question:
          "Kan ni transportera tunga möbler till Pelican Self Storage?",
        answer:
          "Ja, vi transporterar alla typer av möbler – soffor, sängar, garderober, bord och allt annat tungt. Vi har rätt teknik och utrustning för att bära och lasta säkert.",
      },
      {
        question:
          "Demonterar ni möbler innan transport till Pelican-förrådet?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att kunna transporteras. Spara gärna skruvar i märkta påsar.",
      },
      {
        question:
          "Kan ni köra säsongsprylar till och från Pelican Self Storage?",
        answer:
          "Ja, vi kör vinterdäck, skidor, trädgårdsmöbler, julpynt och andra säsongsprylar till och från ditt Pelican-förråd. Perfekt för att rotera grejer mellan hemmet och förrådet utan att hyra bil.",
      },
      {
        question:
          "Hur snabbt kan ni köra till Pelican Self Storage?",
        answer:
          "Vi erbjuder ofta transport till Pelican med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question:
          "Skyddar ni sakerna under transport till Pelican-förrådet?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar möbler och ömtåliga föremål under lastning, transport och inbärning i ditt Pelican-förråd. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question:
          "Kan ni köra till Pelican Self Storage på helger?",
        answer:
          "Ja, vi erbjuder transport till och från Pelican på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question:
          "Behöver jag vara med vid Pelican-anläggningen?",
        answer:
          "Du behöver vara med för att ge oss tillgång till ditt Pelican-förråd – med portkod eller digital bokningskod. Vill du slippa åka dit kan du ordna en fullmakt eller lämna koden till oss i förväg.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni transport till Pelican Self Storage?",
        answer:
          "Vi erbjuder transport till och från Pelican Self Storage i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transport till Pelican i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut dina saker, lastar skåpbilen och kör till din Pelican-anläggning. Där bär vi in allt och ställer det organiserat i ditt förråd.",
      step3Title: "Allt inställt hos Pelican",
      step3Desc:
        "Dina saker står säkert och organiserat i ditt Pelican-förråd. Eller hemma hos dig – om du hämtade därifrån. Klart.",
      ctaHeading: "Boka transport till Pelican idag",
      ctaText:
        "Ange din hemadress och vilken Pelican-anläggning du hyr förråd hos, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "transport-pelican",
      meta: {
        title:
          "Transport Pelican Self Storage – Easy Transport To & From Pelican | Ungdomskraft",
        description:
          "Book Pelican Self Storage transport – we pick up at home, drive to Pelican and carry everything in, or collect from your unit. Fixed price. Book today!",
        h1: "Transport Pelican Self Storage",
      },
      hero: {
        heading: "Transport to and from Pelican",
        intro:
          "Do you rent a storage unit at Pelican Self Storage and need help getting things there or back home? We come with a van and crew, carry everything out at your home, drive to your Pelican facility and place it all neatly in your unit. Or the other way round — we collect from your Pelican unit and deliver to your door. No need to rent a car, carry things yourself or make multiple trips.",
      },
      featureCards: {
        sinceText: "Pelican transport since 2014",
        bookYourText: "Book your Pelican transport",
        bookDescText:
          "Enter your home address and which Pelican facility you use, describe what needs transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans sized for Pelican units",
        vehicleDescText:
          "We arrive with the right size van — whether you're taking a single box or filling an entire Pelican unit. No need to rent a vehicle, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Items we transport to and from Pelican Self Storage",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, beds, tables, wardrobes and chairs going to or from your Pelican unit",
            image: "/sofa.webp",
          },
          {
            name: "Moving boxes",
            description:
              "Boxes with clothes, books, kitchenware and personal belongings",
            image: "/box.webp",
          },
          {
            name: "Seasonal items",
            description:
              "Winter tyres, skis, bicycles, garden furniture and holiday decorations",
            image: "/patio-furniture.webp",
          },
          {
            name: "Appliances",
            description:
              "Washing machines, fridges, freezers and other appliances for interim storage at Pelican",
            image: "/washer.webp",
          },
          {
            name: "Electronics",
            description: "TVs, computers, monitors and other electronics",
            image: "/tv.webp",
          },
          {
            name: "Art & fragile items",
            description:
              "Paintings, mirrors, china and other delicate objects",
            image: "/painting.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Transport to and from Pelican Self Storage — we drive, carry and place everything",
        mainText:
          "Pelican Self Storage is one of Sweden's largest storage chains with modern facilities across Stockholm and Gothenburg. Renting a unit at Pelican is easy — but physically getting your belongings there can be a challenge. Heavy furniture down the stairs, a van to rent, multiple trips through rush-hour traffic, and then everything needs carrying through Pelican's corridors into your unit. With Ungdomskraft you skip all of that. We come to your home with a van and a team of strong young workers, carry out what needs storing, load up and drive to your Pelican facility. There we handle door codes, lifts and narrow corridors — and place everything neatly in your unit. Need to collect items from your Pelican unit? We pick up, load and deliver to your door. We drive to every Pelican facility: Bromma, Hägersten, Farsta, Kista, Sollentuna, Haninge, Mörby, Solna, Högsbo and all others. Whether you are storing furniture during a renovation, putting away seasonal items or collecting things after a move, we scale the vehicle and team to match the volume.",
        preparationHeading:
          "Preparing for transport to Pelican Self Storage",
        preparationText:
          "Pack what you can into boxes and label them with their contents — it makes things easier to find in your Pelican unit later. Disassemble furniture that won't fit through doorways whole. Have your door code or booking code for the Pelican facility ready. Make sure you know which unit is yours and on which floor it is. Check that there is clear access in stairwells at home and that the van can park close to the entrance. If you are collecting from Pelican, make sure you know where in the unit your items are.",
        additionalSections: [
          {
            heading: "Pelican Self Storage facilities we drive to",
            listItems: [
              {
                label: "Pelican Bromma",
                text: "Near Bromma Blocks — one of Stockholm's most popular Pelican facilities.",
              },
              {
                label: "Pelican Hägersten",
                text: "Easy access at Västberga industrial area with large units.",
              },
              {
                label: "Pelican Farsta",
                text: "South of Stockholm with good access from Nynäsvägen.",
              },
              {
                label: "Pelican Kista",
                text: "Northern Stockholm — ideal for Kista, Järfälla or Sollentuna residents.",
              },
              {
                label: "Pelican Sollentuna",
                text: "Northern suburbs — close to the E4 motorway for easy loading.",
              },
              {
                label: "Pelican Solna",
                text: "Central Solna with quick access from Solnavägen.",
              },
              {
                label: "Pelican Högsbo",
                text: "Gothenburg — central Högsbo with good access from Dag Hammarskjöldsleden.",
              },
              {
                label: "Pelican Mölndal",
                text: "Gothenburg — near Mölndal centre with easy access from the E6.",
              },
              {
                label: "Pelican Backaplan",
                text: "Gothenburg — Hisingen side with good parking and loading zone.",
              },
            ],
            afterContent:
              "We drive to every Pelican Self Storage facility in Stockholm and Gothenburg. Just enter the address when booking — we handle the rest. Can't find your Pelican facility in the list? Contact us and we'll help you.",
          },
          {
            heading:
              "Transport to Pelican during renovation or moving",
            content:
              "Renovating and need somewhere to keep your furniture in the meantime? Or moving when the new home isn't ready yet? We drive your belongings to your Pelican unit, carry them in and place everything neatly. When you're ready to have everything back, book another transport and we deliver it all to your door. Many Pelican customers use us as a convenient two-way transport solution — you focus on the renovation or move, we handle the logistics. Pelican's flexible rental periods make it easy to adapt your unit to your needs, and with Ungdomskraft you don't have to worry about the transport.",
          },
          {
            heading: "Collecting items from your Pelican unit",
            content:
              "Need to get furniture, boxes or seasonal items back from your Pelican unit? We drive to the facility, handle door codes and lifts, carry out what you want, load the van and deliver to your door. We carry everything in and place it where you want it. Perfect when winter is over and the garden furniture needs to come home, when a renovation is finished or when you need things for a move. No need to rent a car and do it yourself — we do the whole job for you.",
          },
          {
            heading:
              "Why book Pelican transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every Pelican transport we carry out. When you book transport to or from your Pelican unit with us, you get a team of motivated, energetic young workers who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that mindset runs through everything — from how they carry your boxes to how they stack everything neatly in your Pelican unit.\n\nSince our founding in 2014, we have carried out thousands of storage transports across the Stockholm and Gothenburg regions — many of them to Pelican Self Storage. Along the way, we have given thousands of young people their first real work experience: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn lifting techniques, safe loading and how to handle everything from fragile items to heavy furniture in narrow storage corridors.\n\nOur young workers know Pelican's facilities. They understand how the door-code systems work, which lifts are available, how the corridors are laid out and where best to park the van. That makes the transport faster and smoother — you don't have to stand around explaining, you can trust the team to handle everything independently.\n\nYou notice the difference in commitment and energy. Our teams run where others walk, double-check that everything is properly loaded and always ask if there is anything else they can help with. We have built a culture where every transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your Pelican transport becomes part of something bigger.\n\nWe also take sustainability seriously. Items that are no longer needed are sorted and taken to the right recycling station or donated to charities such as Myrorna, Erikshjälpen and Stadsmissionen. Nothing is dumped unnecessarily.\n\nIt is smooth Pelican transport with purpose.\n\nTransport to Pelican Self Storage does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question:
            "How much does transport to Pelican Self Storage cost?",
          answer:
            "The price depends on the volume, distance between your home and the Pelican facility, and whether stair carrying is needed. A few boxes and a small piece of furniture often comes to SEK 595–995. Larger loads with several heavy items can come to SEK 1,495–2,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on transport to Pelican Self Storage?",
          answer:
            "Transport to Pelican Self Storage does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Can you collect items from my Pelican unit?",
          answer:
            "Yes, we drive to your Pelican facility, handle door codes and lifts, carry your items out of the unit, load the van and deliver to your door. We carry everything in and place it where you want it.",
        },
        {
          question:
            "Is a van included in the Pelican transport?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting to or from your Pelican unit — you don't need to rent a car, drive or refuel.",
        },
        {
          question:
            "Which Pelican Self Storage facilities do you drive to?",
          answer:
            "We drive to every Pelican Self Storage facility in Stockholm and Gothenburg: Bromma, Hägersten, Farsta, Kista, Sollentuna, Haninge, Mörby, Solna, Högsbo and all others. Just enter the address when booking.",
        },
        {
          question:
            "Can you transport items to Pelican during a renovation?",
          answer:
            "Yes, we drive furniture and boxes to your Pelican unit during the renovation. When it's done, book another transport and we bring everything back. Perfect for floor sanding, painting or kitchen renovations.",
        },
        {
          question:
            "Do you carry items into the Pelican storage unit?",
          answer:
            "Yes, we carry everything in and place it neatly in your Pelican unit. We handle door codes, lifts, narrow corridors and heavy shutters. You don't need to do anything other than show us the right unit.",
        },
        {
          question:
            "Do you protect items during transport to Pelican?",
          answer:
            "Yes, we bring furniture blankets, carrying straps and ratchet straps. We protect furniture and fragile items during loading, transport and carrying in. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question:
            "Which areas do you offer Pelican transport in?",
          answer:
            "We offer transport to and from Pelican Self Storage throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Pelican transport in 3 easy steps",
        step2Text:
          "Our workers carry your items out, load the van and drive to your Pelican facility. There we carry everything in and place it neatly in your unit.",
        step3Title: "All stored at Pelican",
        step3Desc:
          "Your items are safely and neatly placed in your Pelican unit. Or at home — if you were collecting from storage. Done.",
        ctaHeading: "Book Pelican transport today",
        ctaText:
          "Enter your home address and which Pelican facility you use, describe what needs transporting and pick a time. You see the price instantly.",
      },
    },
  },

  {
    // Transport Shurgard — config för /transport-shurgard
    // Vinkel: Specifikt för kunder som hyr förråd hos Shurgard Self-Storage.
    // Nischad sida — lätt att ranka på "transport shurgard".
    slug: "transport-shurgard",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Shurgard – Smidig Transport Till & Från Shurgard | Ungdomskraft",
      description:
        "Boka transport till och från Shurgard. Vi hämtar hemma, kör till Shurgard och bär in – eller hämtar från ditt förråd. Fast pris, försäkrat. Boka idag!",
      h1: "Transport Shurgard",
    },

    hero: {
      heading: "Transport till och från Shurgard",
      intro:
        "Hyr du förråd hos Shurgard och behöver hjälp att köra saker dit eller hämta hem? Vi kommer med skåpbil och personal, bär ut hemma hos dig, kör till din Shurgard-anläggning och bär in allt i ditt förråd. Eller tvärtom – vi hämtar från ditt Shurgard-förråd och levererar hem. Du slipper hyra bil, bära själv och göra flera vändor.",
      heroImage: "/hero-transport-forrad.png",
    },

    featureCards: {
      sinceText: "Shurgard-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt hundratals Shurgard-kunder att transportera saker till och från sina förråd i Stockholmsområdet. Våra ungdomar känner till Shurgards anläggningar, vet hur access-systemen fungerar och hanterar allt från tunga möbler till ömtåliga kartonger.",
      bookYourText: "Boka din Shurgard-transport",
      bookDescText:
        "Ange din hemadress och vilken Shurgard-anläggning du hyr förråd hos, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar anpassade för Shurgard-förråd",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska köra en kartong eller fylla ett helt Shurgard-förråd. Du slipper hyra bil, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi kör till och från Shurgard",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, sängar, bord, garderober och stolar som ska till eller från ditt Shurgard-förråd",
          image: "/sofa.webp",
        },
        {
          name: "Flyttkartonger",
          description:
            "Kartonger med kläder, böcker, köksprylar och personliga ägodelar",
          image: "/box.webp",
        },
        {
          name: "Säsongsprylar",
          description:
            "Vinterdäck, skidor, cyklar, trädgårdsmöbler och julpynt",
          image: "/patio-furniture.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Tvättmaskin, kylskåp, frys och andra vitvaror som ska mellanlagras hos Shurgard",
          image: "/washer.webp",
        },
        {
          name: "Elektronik",
          description: "TV-apparater, datorer, skärmar och annan elektronik",
          image: "/tv.webp",
        },
        {
          name: "Konst & ömtåligt",
          description:
            "Tavlor, speglar, porslin och andra ömtåliga föremål",
          image: "/painting.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport till och från Shurgard – vi kör, bär och ställer in",
      mainText:
        "Shurgard Self-Storage är en internationell förrådskedja med moderna, klimatstyrda anläggningar i Stockholmsområdet. Att hyra förråd hos Shurgard är smidigt – men att fysiskt få dit sina saker kan vara en utmaning. Tunga möbler i trappor, en skåpbil som ska hyras, flera vändor i rusningstrafik och sedan ska allt bäras genom Shurgards gångar och in i ditt förråd. Med Ungdomskraft slipper du allt det. Vi kommer hem till dig med skåpbil och ett team av starka ungdomar, bär ut det som ska till förrådet, lastar och kör till din Shurgard-anläggning. Där hanterar vi access-kort, hissar och trånga gångar – och ställer in allt organiserat i ditt förråd. Behöver du hämta saker från ditt Shurgard-förråd? Vi hämtar, lastar och levererar hem till din dörr. Vi kör till alla Shurgards anläggningar i Stockholm och Göteborg: Bromma, Häggvik, Kungens Kurva, Nacka, Lunda, Solna, Upplands Väsby, Högsbo och Kungsbacka. Oavsett om du ska ställa in möbler inför en renovering, lagra säsongsprylar eller hämta tillbaka saker efter en flytt anpassar vi fordon och team efter mängden.",
      preparationHeading: "Förbered inför transport till Shurgard",
      preparationText:
        "Packa det du kan i kartonger och märk dem med innehåll – det gör det lättare att hitta saker i ditt Shurgard-förråd senare. Demontera möbler som inte går genom dörrar hela. Ha ditt access-kort eller din pinkod till Shurgard-anläggningen redo. Se till att du vet vilket förråd som är ditt och på vilken våning det ligger. Kolla att det finns fri passage i trapphus hemma och att skåpbilen kan parkera nära porten. Om du hämtar från Shurgard – se till att du vet var i förrådet sakerna står.",
      additionalSections: [
        {
          heading: "Shurgard-anläggningar vi kör till",
          listItems: [
            {
              label: "Shurgard Bromma",
              text: "Centralt i Bromma med bra tillgänglighet från Drottningholmsvägen.",
            },
            {
              label: "Shurgard Häggvik",
              text: "Norra Stockholm vid Häggvik – nära E4:an med enkelt stopp för lastning.",
            },
            {
              label: "Shurgard Kungens Kurva",
              text: "Söder om Stockholm vid Kungens Kurva – nära IKEA och Heron City.",
            },
            {
              label: "Shurgard Nacka",
              text: "Öster om Stockholm med snabb access från Värmdöleden.",
            },
            {
              label: "Shurgard Lunda",
              text: "Nordvästra Stockholm i Spångaområdet – nära E18.",
            },
            {
              label: "Shurgard Solna",
              text: "Centralt i Solna med bra tillgänglighet från hela norra innerstaden.",
            },
            {
              label: "Shurgard Upplands Väsby",
              text: "Norr om Stockholm – perfekt för dig i Upplands Väsby, Sigtuna eller Vallentuna.",
            },
            {
              label: "Shurgard Högsbo",
              text: "Göteborg – centralt i Högsbo med bra tillgänglighet från Dag Hammarskjöldsleden.",
            },
            {
              label: "Shurgard Kungsbacka",
              text: "Söder om Göteborg – nära E6:an med enkelt stopp för lastning.",
            },
          ],
          afterContent:
            "Vi kör till alla Shurgard-anläggningar i Stockholm och Göteborg. Ange bara adressen vid bokning – vi fixar resten. Hittar du inte din Shurgard-anläggning i listan? Kontakta oss så hjälper vi dig.",
        },
        {
          heading: "Transport till Shurgard vid renovering eller flytt",
          content:
            "Ska du renovera och behöver mellanlagra möblerna? Eller flyttar du och den nya bostaden inte är redo än? Vi kör dina saker till ditt Shurgard-förråd, bär in och ställer allt organiserat. När du är redo att hämta tillbaka allt bokar du en ny transport och vi levererar det hela hem till dig. Många Shurgard-kunder använder oss som en smidig transportlösning åt båda hållen – du fokuserar på renoveringen eller flytten, vi sköter logistiken. Shurgards klimatstyrda förråd är perfekta för mellanlagring av ömtåliga möbler och elektronik, och med Ungdomskraft behöver du inte oroa dig för transporten.",
        },
        {
          heading: "Hämta saker från ditt Shurgard-förråd",
          content:
            "Behöver du hämta tillbaka möbler, kartonger eller säsongsprylar från ditt Shurgard-förråd? Vi åker till anläggningen, hanterar access-kort och hiss, bär ut det du vill ha, lastar skåpbilen och kör hem till dig. Vi bär in allt och ställer det där du vill ha det. Perfekt när vintern är över och trädgårdsmöblerna ska hem, när renoveringen är klar eller när du ska hämta saker inför en flytt. Du slipper hyra bil och göra det själv – vi gör hela jobbet åt dig.",
        },
        {
          heading:
            "Varför boka transport till Shurgard med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje Shurgard-transport vi genomför. När du bokar transport till eller från ditt Shurgard-förråd hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de bär dina kartonger till hur de staplar allt snyggt och organiserat i ditt Shurgard-förråd.\n\nSedan starten 2014 har vi kört tusentals transporter till och från förråd i Stockholmsområdet – varav en stor del till just Shurgard Self-Storage. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker lastning och hur man hanterar allt från ömtåliga föremål till tunga möbler i trånga förrådsgångar.\n\nVåra ungdomar känner till Shurgards anläggningar. De vet hur access-systemen fungerar, vilka hissar som finns, hur de klimatstyrda utrymmena är uppbyggda och var man bäst parkerar skåpbilen. Shurgards moderna anläggningar har ofta breda gångar och bra belysning, men det krävs ändå erfarenhet för att lasta och bära effektivt – och den erfarenheten har vårt team.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att allt är rätt lastat och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje transport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje transport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din transport till Shurgard blir en del av något större.\n\nVi tar också hållbarhet på allvar. Saker som inte längre behövs sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer som Myrorna, Erikshjälpen och Stadsmissionen. Ingenting slängs i onödan.\n\nDet är smidig Shurgard-transport med mening.\n\nTransport till Shurgard ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport till Shurgard?",
        answer:
          "Priset beror på mängden saker, avstånd mellan hemadress och Shurgard-anläggningen och om bärning i trappor krävs. Ett par kartonger och en liten möbel landar ofta på 595–995 kr. Flera tunga möbler och kartonger kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport till Shurgard?",
        answer:
          "Transport till Shurgard ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni hämta saker från mitt Shurgard-förråd?",
        answer:
          "Ja, vi åker till din Shurgard-anläggning, hanterar access-kort och hiss, bär ut sakerna ur ditt förråd, lastar skåpbilen och kör hem till dig. Vi bär in allt och ställer det där du vill ha det.",
      },
      {
        question: "Ingår skåpbil i Shurgard-transporten?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras till eller från ditt Shurgard-förråd – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Vilka Shurgard-anläggningar kör ni till?",
        answer:
          "Vi kör till alla Shurgard-anläggningar i Stockholm och Göteborg: Bromma, Häggvik, Kungens Kurva, Nacka, Lunda, Solna, Upplands Väsby, Högsbo och Kungsbacka. Ange bara adressen vid bokning.",
      },
      {
        question:
          "Kan ni köra saker till mitt Shurgard-förråd vid renovering?",
        answer:
          "Ja, vi kör möbler och kartonger till ditt Shurgard-förråd under renoveringen. När den är klar bokar du en ny transport och vi hämtar tillbaka allt. Shurgards klimatstyrda förråd är perfekta för mellanlagring av ömtåliga möbler.",
      },
      {
        question: "Bär ni in sakerna i mitt Shurgard-förråd?",
        answer:
          "Ja, vi bär in alla saker och ställer dem organiserat i ditt Shurgard-förråd. Vi hanterar access-kort, hissar, trånga gångar och tunga luckor på anläggningen. Du behöver inte göra något annat än att visa oss rätt förråd.",
      },
      {
        question:
          "Kan ni transportera tunga möbler till Shurgard?",
        answer:
          "Ja, vi transporterar alla typer av möbler – soffor, sängar, garderober, bord och allt annat tungt. Vi har rätt teknik och utrustning för att bära och lasta säkert.",
      },
      {
        question:
          "Demonterar ni möbler innan transport till Shurgard-förrådet?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att kunna transporteras. Spara gärna skruvar i märkta påsar.",
      },
      {
        question:
          "Kan ni köra säsongsprylar till och från Shurgard?",
        answer:
          "Ja, vi kör vinterdäck, skidor, trädgårdsmöbler, julpynt och andra säsongsprylar till och från ditt Shurgard-förråd. Perfekt för att rotera grejer mellan hemmet och förrådet utan att hyra bil.",
      },
      {
        question:
          "Hur snabbt kan ni köra till Shurgard?",
        answer:
          "Vi erbjuder ofta transport till Shurgard med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question:
          "Skyddar ni sakerna under transport till Shurgard-förrådet?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar möbler och ömtåliga föremål under lastning, transport och inbärning i ditt Shurgard-förråd. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question:
          "Kan ni köra till Shurgard på helger?",
        answer:
          "Ja, vi erbjuder transport till och från Shurgard på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question:
          "Behöver jag vara med vid Shurgard-anläggningen?",
        answer:
          "Du behöver vara med för att ge oss tillgång till ditt Shurgard-förråd – med access-kort eller pinkod. Vill du slippa åka dit kan du ordna en fullmakt eller lämna kortet till oss i förväg.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni transport till Shurgard?",
        answer:
          "Vi erbjuder transport till och från Shurgard i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transport till Shurgard i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut dina saker, lastar skåpbilen och kör till din Shurgard-anläggning. Där bär vi in allt och ställer det organiserat i ditt förråd.",
      step3Title: "Allt inställt hos Shurgard",
      step3Desc:
        "Dina saker står säkert och organiserat i ditt Shurgard-förråd. Eller hemma hos dig – om du hämtade därifrån. Klart.",
      ctaHeading: "Boka transport till Shurgard idag",
      ctaText:
        "Ange din hemadress och vilken Shurgard-anläggning du hyr förråd hos, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "transport-shurgard",
      meta: {
        title:
          "Transport Shurgard – Easy Transport To & From Shurgard | Ungdomskraft",
        description:
          "Book Shurgard transport – we pick up at home, drive to Shurgard and carry everything in, or collect from your unit. Fixed price, insured. Book today!",
        h1: "Transport Shurgard",
      },
      hero: {
        heading: "Transport to and from Shurgard",
        intro:
          "Do you rent a storage unit at Shurgard and need help getting things there or back home? We come with a van and crew, carry everything out at your home, drive to your Shurgard facility and place it all neatly in your unit. Or the other way round — we collect from your Shurgard unit and deliver to your door. No need to rent a car, carry things yourself or make multiple trips.",
      },
      featureCards: {
        sinceText: "Shurgard transport since 2014",
        bookYourText: "Book your Shurgard transport",
        bookDescText:
          "Enter your home address and which Shurgard facility you use, describe what needs transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans sized for Shurgard units",
        vehicleDescText:
          "We arrive with the right size van — whether you're taking a single box or filling an entire Shurgard unit. No need to rent a vehicle, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Items we transport to and from Shurgard",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, beds, tables, wardrobes and chairs going to or from your Shurgard unit",
            image: "/sofa.webp",
          },
          {
            name: "Moving boxes",
            description:
              "Boxes with clothes, books, kitchenware and personal belongings",
            image: "/box.webp",
          },
          {
            name: "Seasonal items",
            description:
              "Winter tyres, skis, bicycles, garden furniture and holiday decorations",
            image: "/patio-furniture.webp",
          },
          {
            name: "Appliances",
            description:
              "Washing machines, fridges, freezers and other appliances for interim storage at Shurgard",
            image: "/washer.webp",
          },
          {
            name: "Electronics",
            description: "TVs, computers, monitors and other electronics",
            image: "/tv.webp",
          },
          {
            name: "Art & fragile items",
            description:
              "Paintings, mirrors, china and other delicate objects",
            image: "/painting.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Transport to and from Shurgard — we drive, carry and place everything",
        mainText:
          "Shurgard Self-Storage is an international storage chain with modern, climate-controlled facilities across the Stockholm area. Renting a unit at Shurgard is easy — but physically getting your belongings there can be a challenge. Heavy furniture down the stairs, a van to rent, multiple trips through rush-hour traffic, and then everything needs carrying through Shurgard's corridors into your unit. With Ungdomskraft you skip all of that. We come to your home with a van and a team of strong young workers, carry out what needs storing, load up and drive to your Shurgard facility. There we handle access cards, lifts and corridors — and place everything neatly in your unit. Need to collect items from your Shurgard unit? We pick up, load and deliver to your door. We drive to every Shurgard facility in Stockholm and Gothenburg: Bromma, Häggvik, Kungens Kurva, Nacka, Lunda, Solna, Upplands Väsby, Högsbo and Kungsbacka. Whether you are storing furniture during a renovation, putting away seasonal items or collecting things after a move, we scale the vehicle and team to match the volume.",
        preparationHeading:
          "Preparing for transport to Shurgard",
        preparationText:
          "Pack what you can into boxes and label them with their contents — it makes things easier to find in your Shurgard unit later. Disassemble furniture that won't fit through doorways whole. Have your access card or PIN code for the Shurgard facility ready. Make sure you know which unit is yours and on which floor it is. Check that there is clear access in stairwells at home and that the van can park close to the entrance. If you are collecting from Shurgard, make sure you know where in the unit your items are.",
        additionalSections: [
          {
            heading: "Shurgard facilities we drive to",
            listItems: [
              {
                label: "Shurgard Bromma",
                text: "Central Bromma with good access from Drottningholmsvägen.",
              },
              {
                label: "Shurgard Häggvik",
                text: "Northern Stockholm at Häggvik — close to the E4 motorway for easy loading.",
              },
              {
                label: "Shurgard Kungens Kurva",
                text: "South of Stockholm at Kungens Kurva — near IKEA and Heron City.",
              },
              {
                label: "Shurgard Nacka",
                text: "East of Stockholm with quick access from Värmdöleden.",
              },
              {
                label: "Shurgard Lunda",
                text: "North-west Stockholm in the Spånga area — close to the E18.",
              },
              {
                label: "Shurgard Solna",
                text: "Central Solna with good access from across the northern inner city.",
              },
              {
                label: "Shurgard Upplands Väsby",
                text: "North of Stockholm — ideal for Upplands Väsby, Sigtuna or Vallentuna residents.",
              },
              {
                label: "Shurgard Högsbo",
                text: "Gothenburg — central Högsbo with good access from Dag Hammarskjöldsleden.",
              },
              {
                label: "Shurgard Kungsbacka",
                text: "South of Gothenburg — close to the E6 motorway for easy loading.",
              },
            ],
            afterContent:
              "We drive to every Shurgard facility in Stockholm and Gothenburg. Just enter the address when booking — we handle the rest. Can't find your Shurgard facility in the list? Contact us and we'll help you.",
          },
          {
            heading:
              "Transport to Shurgard during renovation or moving",
            content:
              "Renovating and need somewhere to keep your furniture in the meantime? Or moving when the new home isn't ready yet? We drive your belongings to your Shurgard unit, carry them in and place everything neatly. When you're ready to have everything back, book another transport and we deliver it all to your door. Many Shurgard customers use us as a convenient two-way transport solution — you focus on the renovation or move, we handle the logistics. Shurgard's climate-controlled units are ideal for interim storage of delicate furniture and electronics, and with Ungdomskraft you don't have to worry about the transport.",
          },
          {
            heading: "Collecting items from your Shurgard unit",
            content:
              "Need to get furniture, boxes or seasonal items back from your Shurgard unit? We drive to the facility, handle access cards and lifts, carry out what you want, load the van and deliver to your door. We carry everything in and place it where you want it. Perfect when winter is over and the garden furniture needs to come home, when a renovation is finished or when you need things for a move. No need to rent a car and do it yourself — we do the whole job for you.",
          },
          {
            heading:
              "Why book Shurgard transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every Shurgard transport we carry out. When you book transport to or from your Shurgard unit with us, you get a team of motivated, energetic young workers who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that mindset runs through everything — from how they carry your boxes to how they stack everything neatly in your Shurgard unit.\n\nSince our founding in 2014, we have carried out thousands of storage transports across the Stockholm region — many of them to Shurgard Self-Storage. Along the way, we have given thousands of young people their first real work experience: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn lifting techniques, safe loading and how to handle everything from fragile items to heavy furniture in narrow storage corridors.\n\nOur young workers know Shurgard's facilities. They understand how the access-card systems work, which lifts are available, how the climate-controlled spaces are laid out and where best to park the van. Shurgard's modern facilities often have wide corridors and good lighting, but experience is still needed to load and carry efficiently — and our team has that experience.\n\nYou notice the difference in commitment and energy. Our teams run where others walk, double-check that everything is properly loaded and always ask if there is anything else they can help with. We have built a culture where every transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your Shurgard transport becomes part of something bigger.\n\nWe also take sustainability seriously. Items that are no longer needed are sorted and taken to the right recycling station or donated to charities such as Myrorna, Erikshjälpen and Stadsmissionen. Nothing is dumped unnecessarily.\n\nIt is smooth Shurgard transport with purpose.\n\nTransport to Shurgard does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question:
            "How much does transport to Shurgard cost?",
          answer:
            "The price depends on the volume, distance between your home and the Shurgard facility, and whether stair carrying is needed. A few boxes and a small piece of furniture often comes to SEK 595–995. Larger loads with several heavy items can come to SEK 1,495–2,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on transport to Shurgard?",
          answer:
            "Transport to Shurgard does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Can you collect items from my Shurgard unit?",
          answer:
            "Yes, we drive to your Shurgard facility, handle access cards and lifts, carry your items out of the unit, load the van and deliver to your door. We carry everything in and place it where you want it.",
        },
        {
          question:
            "Is a van included in the Shurgard transport?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting to or from your Shurgard unit — you don't need to rent a car, drive or refuel.",
        },
        {
          question:
            "Which Shurgard facilities do you drive to?",
          answer:
            "We drive to every Shurgard facility in Stockholm and Gothenburg: Bromma, Häggvik, Kungens Kurva, Nacka, Lunda, Solna, Upplands Väsby, Högsbo and Kungsbacka. Just enter the address when booking.",
        },
        {
          question:
            "Can you transport items to Shurgard during a renovation?",
          answer:
            "Yes, we drive furniture and boxes to your Shurgard unit during the renovation. When it's done, book another transport and we bring everything back. Shurgard's climate-controlled units are ideal for protecting delicate furniture.",
        },
        {
          question:
            "Do you carry items into the Shurgard unit?",
          answer:
            "Yes, we carry everything in and place it neatly in your Shurgard unit. We handle access cards, lifts, corridors and heavy shutters. You don't need to do anything other than show us the right unit.",
        },
        {
          question:
            "Do you protect items during transport to Shurgard?",
          answer:
            "Yes, we bring furniture blankets, carrying straps and ratchet straps. We protect furniture and fragile items during loading, transport and carrying in. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question:
            "Which areas do you offer Shurgard transport in?",
          answer:
            "We offer transport to and from Shurgard throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Shurgard transport in 3 easy steps",
        step2Text:
          "Our workers carry your items out, load the van and drive to your Shurgard facility. There we carry everything in and place it neatly in your unit.",
        step3Title: "All stored at Shurgard",
        step3Desc:
          "Your items are safely and neatly placed in your Shurgard unit. Or at home — if you were collecting from storage. Done.",
        ctaHeading: "Book Shurgard transport today",
        ctaText:
          "Enter your home address and which Shurgard facility you use, describe what needs transporting and pick a time. You see the price instantly.",
      },
    },
  },

  {
    // Transport Green Storage — config för /transport-green-storage
    // Vinkel: Specifikt för kunder som hyr förråd hos Green Storage.
    // Nischad sida — lätt att ranka på "transport green storage".
    slug: "transport-green-storage",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Green Storage – Smidig Transport Till & Från Green Storage | Ungdomskraft",
      description:
        "Boka transport till och från Green Storage. Vi hämtar hemma, kör och bär in allt i ditt förråd – eller hämtar därifrån. Fast pris, försäkrat. Boka idag!",
      h1: "Transport Green Storage",
    },

    hero: {
      heading: "Transport till och från Green Storage",
      intro:
        "Hyr du förråd hos Green Storage och behöver hjälp att köra saker dit eller hämta hem? Vi kommer med skåpbil och personal, bär ut hemma hos dig, kör till din Green Storage-anläggning och bär in allt i ditt förråd. Eller tvärtom – vi hämtar från ditt Green Storage-förråd och levererar hem. Du slipper hyra bil, bära själv och göra flera vändor.",
      heroImage: "/hero-transport-forrad.png",
    },

    featureCards: {
      sinceText: "Green Storage-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt hundratals Green Storage-kunder att transportera saker till och från sina förråd. Våra ungdomar känner till Green Storages anläggningar, vet hur de digitala access-systemen fungerar och hanterar allt från tunga möbler till ömtåliga kartonger – med samma hållbarhetstänk som Green Storage själva.",
      bookYourText: "Boka din Green Storage-transport",
      bookDescText:
        "Ange din hemadress och vilken Green Storage-anläggning du hyr förråd hos, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar anpassade för Green Storage-förråd",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska köra en kartong eller fylla ett helt Green Storage-förråd. Du slipper hyra bil, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi kör till och från Green Storage",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, sängar, bord, garderober och stolar som ska till eller från ditt Green Storage-förråd",
          image: "/sofa.webp",
        },
        {
          name: "Flyttkartonger",
          description:
            "Kartonger med kläder, böcker, köksprylar och personliga ägodelar",
          image: "/box.webp",
        },
        {
          name: "Säsongsprylar",
          description:
            "Vinterdäck, skidor, cyklar, trädgårdsmöbler och julpynt",
          image: "/patio-furniture.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Tvättmaskin, kylskåp, frys och andra vitvaror som ska mellanlagras hos Green Storage",
          image: "/washer.webp",
        },
        {
          name: "Elektronik",
          description: "TV-apparater, datorer, skärmar och annan elektronik",
          image: "/tv.webp",
        },
        {
          name: "Konst & ömtåligt",
          description:
            "Tavlor, speglar, porslin och andra ömtåliga föremål",
          image: "/painting.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport till och från Green Storage – vi kör, bär och ställer in",
      mainText:
        "Green Storage är Sveriges klimatsmartaste förrådskedja med hållbara, moderna anläggningar i Stockholmsområdet. Att hyra förråd hos Green Storage är enkelt – men att fysiskt få dit sina saker kan vara en utmaning. Tunga möbler i trappor, en skåpbil som ska hyras, flera vändor i rusningstrafik och sedan ska allt bäras genom gångarna och in i ditt förråd. Med Ungdomskraft slipper du allt det. Vi kommer hem till dig med skåpbil och ett team av starka ungdomar, bär ut det som ska till förrådet, lastar och kör till din Green Storage-anläggning. Där hanterar vi digitala access-system, hissar och trånga gångar – och ställer in allt organiserat i ditt förråd. Behöver du hämta saker från ditt Green Storage-förråd? Vi hämtar, lastar och levererar hem till din dörr. Vi kör till alla Green Storages anläggningar i Stockholm och Göteborg: Årsta, Bromma, Hägersten, Solna, Sundbyberg, Högsbo, Sisjön och alla andra. Oavsett om du ska ställa in möbler inför en renovering, lagra säsongsprylar eller hämta tillbaka saker efter en flytt anpassar vi fordon och team efter mängden.",
      preparationHeading: "Förbered inför transport till Green Storage",
      preparationText:
        "Packa det du kan i kartonger och märk dem med innehåll – det gör det lättare att hitta saker i ditt Green Storage-förråd senare. Demontera möbler som inte går genom dörrar hela. Ha din digitala access-kod eller app-bokning till Green Storage-anläggningen redo. Se till att du vet vilket förråd som är ditt och på vilken våning det ligger. Kolla att det finns fri passage i trapphus hemma och att skåpbilen kan parkera nära porten. Om du hämtar från Green Storage – se till att du vet var i förrådet sakerna står.",
      additionalSections: [
        {
          heading: "Green Storage-anläggningar vi kör till",
          listItems: [
            {
              label: "Green Storage Årsta",
              text: "Söder om city med bra tillgänglighet från Årstalänken och Nynäsvägen.",
            },
            {
              label: "Green Storage Bromma",
              text: "Västra Stockholm med snabb access från Drottningholmsvägen.",
            },
            {
              label: "Green Storage Hägersten",
              text: "Sydvästra Stockholm vid Västberga – enkelt att nå från Essingeleden.",
            },
            {
              label: "Green Storage Solna",
              text: "Norra innerstaden med centralt läge nära Solnavägen.",
            },
            {
              label: "Green Storage Sundbyberg",
              text: "Lättillgängligt i Sundbyberg – perfekt för dig i nordvästra Stockholm.",
            },
            {
              label: "Green Storage Högsbo",
              text: "Göteborg – centralt i Högsbo med bra tillgänglighet från Dag Hammarskjöldsleden.",
            },
            {
              label: "Green Storage Sisjön",
              text: "Göteborg – nära Sisjöns handelsområde med enkelt stopp för lastning.",
            },
          ],
          afterContent:
            "Vi kör till alla Green Storage-anläggningar i Stockholm och Göteborg. Ange bara adressen vid bokning – vi fixar resten. Hittar du inte din Green Storage-anläggning i listan? Kontakta oss så hjälper vi dig.",
        },
        {
          heading: "Transport till Green Storage vid renovering eller flytt",
          content:
            "Ska du renovera och behöver mellanlagra möblerna? Eller flyttar du och den nya bostaden inte är redo än? Vi kör dina saker till ditt Green Storage-förråd, bär in och ställer allt organiserat. När du är redo att hämta tillbaka allt bokar du en ny transport och vi levererar det hela hem till dig. Många Green Storage-kunder använder oss som en smidig transportlösning åt båda hållen – du fokuserar på renoveringen eller flytten, vi sköter logistiken. Green Storages flexibla hyresperioder och klimatsmarta lokaler gör dem till ett utmärkt val för mellanlagring, och med Ungdomskraft behöver du inte oroa dig för transporten.",
        },
        {
          heading: "Hämta saker från ditt Green Storage-förråd",
          content:
            "Behöver du hämta tillbaka möbler, kartonger eller säsongsprylar från ditt Green Storage-förråd? Vi åker till anläggningen, hanterar det digitala access-systemet och hissen, bär ut det du vill ha, lastar skåpbilen och kör hem till dig. Vi bär in allt och ställer det där du vill ha det. Perfekt när vintern är över och trädgårdsmöblerna ska hem, när renoveringen är klar eller när du ska hämta saker inför en flytt. Du slipper hyra bil och göra det själv – vi gör hela jobbet åt dig.",
        },
        {
          heading:
            "Varför boka transport till Green Storage med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje Green Storage-transport vi genomför. När du bokar transport till eller från ditt Green Storage-förråd hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de bär dina kartonger till hur de staplar allt snyggt och organiserat i ditt förråd.\n\nSedan starten 2014 har vi kört tusentals transporter till och från förråd i Stockholmsområdet – varav många till just Green Storage. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker lastning och hur man hanterar allt från ömtåliga föremål till tunga möbler i trånga förrådsgångar.\n\nVåra ungdomar känner till Green Storages anläggningar. De vet hur de digitala access-systemen fungerar, vilka hissar som finns, hur gångarna är uppbyggda och var man bäst parkerar skåpbilen. Green Storage satsar på hållbarhet med solceller, LED-belysning och klimatsmarta lösningar – och vi delar deras värderingar. Saker som inte längre behövs sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att allt är rätt lastat och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje transport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje transport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din transport till Green Storage blir en del av något större.\n\nVi tar hållbarhet på samma allvar som Green Storage. Saker som inte längre behövs sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer som Myrorna, Erikshjälpen och Stadsmissionen. Ingenting slängs i onödan. Att boka Ungdomskraft för din Green Storage-transport är ett dubbelt hållbart val – klimatsmart förvaring och meningsfull transport.\n\nDet är klimatsmart förrådshjälp med mening.\n\nTransport till Green Storage ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport till Green Storage?",
        answer:
          "Priset beror på mängden saker, avstånd mellan hemadress och Green Storage-anläggningen och om bärning i trappor krävs. Ett par kartonger och en liten möbel landar ofta på 595–995 kr. Flera tunga möbler och kartonger kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport till Green Storage?",
        answer:
          "Transport till Green Storage ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni hämta saker från mitt Green Storage-förråd?",
        answer:
          "Ja, vi åker till din Green Storage-anläggning, hanterar det digitala access-systemet och hissen, bär ut sakerna ur ditt förråd, lastar skåpbilen och kör hem till dig. Vi bär in allt och ställer det där du vill ha det.",
      },
      {
        question: "Ingår skåpbil i Green Storage-transporten?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras till eller från ditt Green Storage-förråd – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Vilka Green Storage-anläggningar kör ni till?",
        answer:
          "Vi kör till alla Green Storage-anläggningar i Stockholm och Göteborg: Årsta, Bromma, Hägersten, Solna, Sundbyberg, Högsbo, Sisjön och alla andra. Ange bara adressen vid bokning.",
      },
      {
        question:
          "Kan ni köra saker till mitt Green Storage-förråd vid renovering?",
        answer:
          "Ja, vi kör möbler och kartonger till ditt Green Storage-förråd under renoveringen. När den är klar bokar du en ny transport och vi hämtar tillbaka allt. Green Storages klimatsmarta lokaler är perfekta för mellanlagring.",
      },
      {
        question: "Bär ni in sakerna i mitt Green Storage-förråd?",
        answer:
          "Ja, vi bär in alla saker och ställer dem organiserat i ditt Green Storage-förråd. Vi hanterar digitala access-system, hissar, trånga gångar och tunga luckor på anläggningen. Du behöver inte göra något annat än att visa oss rätt förråd.",
      },
      {
        question:
          "Kan ni transportera tunga möbler till Green Storage?",
        answer:
          "Ja, vi transporterar alla typer av möbler – soffor, sängar, garderober, bord och allt annat tungt. Vi har rätt teknik och utrustning för att bära och lasta säkert.",
      },
      {
        question:
          "Demonterar ni möbler innan transport till Green Storage-förrådet?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att kunna transporteras. Spara gärna skruvar i märkta påsar.",
      },
      {
        question:
          "Kan ni köra säsongsprylar till och från Green Storage?",
        answer:
          "Ja, vi kör vinterdäck, skidor, trädgårdsmöbler, julpynt och andra säsongsprylar till och från ditt Green Storage-förråd. Perfekt för att rotera grejer mellan hemmet och förrådet utan att hyra bil.",
      },
      {
        question:
          "Hur snabbt kan ni köra till Green Storage?",
        answer:
          "Vi erbjuder ofta transport till Green Storage med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question:
          "Skyddar ni sakerna under transport till Green Storage?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar möbler och ömtåliga föremål under lastning, transport och inbärning i ditt Green Storage-förråd. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question:
          "Kan ni köra till Green Storage på helger?",
        answer:
          "Ja, vi erbjuder transport till och från Green Storage på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question:
          "Behöver jag vara med vid Green Storage-anläggningen?",
        answer:
          "Du behöver vara med för att ge oss tillgång till ditt Green Storage-förråd – med digital access-kod eller app. Vill du slippa åka dit kan du ordna en fullmakt eller dela koden med oss i förväg.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni transport till Green Storage?",
        answer:
          "Vi erbjuder transport till och från Green Storage i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transport till Green Storage i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut dina saker, lastar skåpbilen och kör till din Green Storage-anläggning. Där bär vi in allt och ställer det organiserat i ditt förråd.",
      step3Title: "Allt inställt hos Green Storage",
      step3Desc:
        "Dina saker står säkert och organiserat i ditt Green Storage-förråd. Eller hemma hos dig – om du hämtade därifrån. Klart.",
      ctaHeading: "Boka transport till Green Storage idag",
      ctaText:
        "Ange din hemadress och vilken Green Storage-anläggning du hyr förråd hos, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "transport-green-storage",
      meta: {
        title:
          "Transport Green Storage – Easy Transport To & From Green Storage | Ungdomskraft",
        description:
          "Book Green Storage transport – we pick up at home, deliver to your unit and carry everything in, or collect from storage. Fixed price. Book today!",
        h1: "Transport Green Storage",
      },
      hero: {
        heading: "Transport to and from Green Storage",
        intro:
          "Do you rent a storage unit at Green Storage and need help getting things there or back home? We come with a van and crew, carry everything out at your home, drive to your Green Storage facility and place it all neatly in your unit. Or the other way round — we collect from your Green Storage unit and deliver to your door. No need to rent a car, carry things yourself or make multiple trips.",
      },
      featureCards: {
        sinceText: "Green Storage transport since 2014",
        bookYourText: "Book your Green Storage transport",
        bookDescText:
          "Enter your home address and which Green Storage facility you use, describe what needs transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans sized for Green Storage units",
        vehicleDescText:
          "We arrive with the right size van — whether you're taking a single box or filling an entire Green Storage unit. No need to rent a vehicle, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Items we transport to and from Green Storage",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, beds, tables, wardrobes and chairs going to or from your Green Storage unit",
            image: "/sofa.webp",
          },
          {
            name: "Moving boxes",
            description:
              "Boxes with clothes, books, kitchenware and personal belongings",
            image: "/box.webp",
          },
          {
            name: "Seasonal items",
            description:
              "Winter tyres, skis, bicycles, garden furniture and holiday decorations",
            image: "/patio-furniture.webp",
          },
          {
            name: "Appliances",
            description:
              "Washing machines, fridges, freezers and other appliances for interim storage at Green Storage",
            image: "/washer.webp",
          },
          {
            name: "Electronics",
            description: "TVs, computers, monitors and other electronics",
            image: "/tv.webp",
          },
          {
            name: "Art & fragile items",
            description:
              "Paintings, mirrors, china and other delicate objects",
            image: "/painting.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Transport to and from Green Storage — we drive, carry and place everything",
        mainText:
          "Green Storage is Sweden's most climate-smart storage chain with sustainable, modern facilities across the Stockholm area. Renting a unit at Green Storage is easy — but physically getting your belongings there can be a challenge. Heavy furniture down the stairs, a van to rent, multiple trips through rush-hour traffic, and then everything needs carrying through the corridors into your unit. With Ungdomskraft you skip all of that. We come to your home with a van and a team of strong young workers, carry out what needs storing, load up and drive to your Green Storage facility. There we handle digital access systems, lifts and corridors — and place everything neatly in your unit. Need to collect items from your Green Storage unit? We pick up, load and deliver to your door. We drive to every Green Storage facility in Stockholm and Gothenburg: Årsta, Bromma, Hägersten, Solna, Sundbyberg, Högsbo, Sisjön and all others. Whether you are storing furniture during a renovation, putting away seasonal items or collecting things after a move, we scale the vehicle and team to match the volume.",
        preparationHeading:
          "Preparing for transport to Green Storage",
        preparationText:
          "Pack what you can into boxes and label them with their contents — it makes things easier to find in your Green Storage unit later. Disassemble furniture that won't fit through doorways whole. Have your digital access code or app booking for the Green Storage facility ready. Make sure you know which unit is yours and on which floor it is. Check that there is clear access in stairwells at home and that the van can park close to the entrance. If you are collecting from Green Storage, make sure you know where in the unit your items are.",
        additionalSections: [
          {
            heading: "Green Storage facilities we drive to",
            listItems: [
              {
                label: "Green Storage Årsta",
                text: "South of the city centre with good access from Årstalänken and Nynäsvägen.",
              },
              {
                label: "Green Storage Bromma",
                text: "Western Stockholm with quick access from Drottningholmsvägen.",
              },
              {
                label: "Green Storage Hägersten",
                text: "South-west Stockholm at Västberga — easy to reach from Essingeleden.",
              },
              {
                label: "Green Storage Solna",
                text: "Northern inner city with a central location near Solnavägen.",
              },
              {
                label: "Green Storage Sundbyberg",
                text: "Easy access in Sundbyberg — ideal for north-west Stockholm residents.",
              },
              {
                label: "Green Storage Högsbo",
                text: "Gothenburg — central Högsbo with good access from Dag Hammarskjöldsleden.",
              },
              {
                label: "Green Storage Sisjön",
                text: "Gothenburg — near Sisjön retail area with easy loading access.",
              },
            ],
            afterContent:
              "We drive to every Green Storage facility in Stockholm and Gothenburg. Just enter the address when booking — we handle the rest. Can't find your Green Storage facility in the list? Contact us and we'll help you.",
          },
          {
            heading:
              "Transport to Green Storage during renovation or moving",
            content:
              "Renovating and need somewhere to keep your furniture in the meantime? Or moving when the new home isn't ready yet? We drive your belongings to your Green Storage unit, carry them in and place everything neatly. When you're ready to have everything back, book another transport and we deliver it all to your door. Many Green Storage customers use us as a convenient two-way transport solution — you focus on the renovation or move, we handle the logistics. Green Storage's flexible rental periods and climate-smart facilities make them an excellent choice for interim storage, and with Ungdomskraft you don't have to worry about the transport.",
          },
          {
            heading: "Collecting items from your Green Storage unit",
            content:
              "Need to get furniture, boxes or seasonal items back from your Green Storage unit? We drive to the facility, handle the digital access system and lift, carry out what you want, load the van and deliver to your door. We carry everything in and place it where you want it. Perfect when winter is over and the garden furniture needs to come home, when a renovation is finished or when you need things for a move. No need to rent a car and do it yourself — we do the whole job for you.",
          },
          {
            heading:
              "Why book Green Storage transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every Green Storage transport we carry out. When you book transport to or from your Green Storage unit with us, you get a team of motivated, energetic young workers who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that mindset runs through everything — from how they carry your boxes to how they stack everything neatly in your unit.\n\nSince our founding in 2014, we have carried out thousands of storage transports across the Stockholm region — many of them to Green Storage. Along the way, we have given thousands of young people their first real work experience: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn lifting techniques, safe loading and how to handle everything from fragile items to heavy furniture in narrow storage corridors.\n\nOur young workers know Green Storage's facilities. They understand how the digital access systems work, which lifts are available, how the spaces are laid out and where best to park the van. Green Storage invests in sustainability with solar panels, LED lighting and climate-smart solutions — and we share their values. Items that are no longer needed are sorted and taken to the right recycling station or donated to charities.\n\nYou notice the difference in commitment and energy. Our teams run where others walk, double-check that everything is properly loaded and always ask if there is anything else they can help with. We have built a culture where every transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your Green Storage transport becomes part of something bigger.\n\nWe take sustainability as seriously as Green Storage. Items that are no longer needed are sorted and taken to the right recycling station or donated to charities such as Myrorna, Erikshjälpen and Stadsmissionen. Nothing is dumped unnecessarily. Booking Ungdomskraft for your Green Storage transport is a doubly sustainable choice — climate-smart storage and meaningful transport.\n\nIt is climate-smart storage help with purpose.\n\nTransport to Green Storage does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question:
            "How much does transport to Green Storage cost?",
          answer:
            "The price depends on the volume, distance between your home and the Green Storage facility, and whether stair carrying is needed. A few boxes and a small piece of furniture often comes to SEK 595–995. Larger loads with several heavy items can come to SEK 1,495–2,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on transport to Green Storage?",
          answer:
            "Transport to Green Storage does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Can you collect items from my Green Storage unit?",
          answer:
            "Yes, we drive to your Green Storage facility, handle the digital access system and lift, carry your items out of the unit, load the van and deliver to your door. We carry everything in and place it where you want it.",
        },
        {
          question:
            "Is a van included in the Green Storage transport?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting to or from your Green Storage unit — you don't need to rent a car, drive or refuel.",
        },
        {
          question:
            "Which Green Storage facilities do you drive to?",
          answer:
            "We drive to every Green Storage facility in Stockholm and Gothenburg: Årsta, Bromma, Hägersten, Solna, Sundbyberg, Högsbo, Sisjön and all others. Just enter the address when booking.",
        },
        {
          question:
            "Can you transport items to Green Storage during a renovation?",
          answer:
            "Yes, we drive furniture and boxes to your Green Storage unit during the renovation. When it's done, book another transport and we bring everything back. Green Storage's climate-smart facilities are ideal for interim storage.",
        },
        {
          question:
            "Do you carry items into the Green Storage unit?",
          answer:
            "Yes, we carry everything in and place it neatly in your Green Storage unit. We handle digital access systems, lifts, corridors and heavy shutters. You don't need to do anything other than show us the right unit.",
        },
        {
          question:
            "Do you protect items during transport to Green Storage?",
          answer:
            "Yes, we bring furniture blankets, carrying straps and ratchet straps. We protect furniture and fragile items during loading, transport and carrying in. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question:
            "Which areas do you offer Green Storage transport in?",
          answer:
            "We offer transport to and from Green Storage throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Green Storage transport in 3 easy steps",
        step2Text:
          "Our workers carry your items out, load the van and drive to your Green Storage facility. There we carry everything in and place it neatly in your unit.",
        step3Title: "All stored at Green Storage",
        step3Desc:
          "Your items are safely and neatly placed in your Green Storage unit. Or at home — if you were collecting from storage. Done.",
        ctaHeading: "Book Green Storage transport today",
        ctaText:
          "Enter your home address and which Green Storage facility you use, describe what needs transporting and pick a time. You see the price instantly.",
      },
    },
  },

  {
    // Transport Storage365 — config för /transport-storage365
    // Vinkel: Specifikt för kunder som hyr förråd hos Storage365.
    // Nischad sida — lätt att ranka på "transport storage365".
    slug: "transport-storage365",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Storage365 – Smidig Transport Till & Från Storage365 | Ungdomskraft",
      description:
        "Boka transport till och från Storage365. Vi hämtar hemma, kör och bär in allt i ditt förråd – eller hämtar från förrådet. Fast pris. Boka idag!",
      h1: "Transport Storage365",
    },

    hero: {
      heading: "Transport till och från Storage365",
      intro:
        "Hyr du förråd hos Storage365 och behöver hjälp att köra saker dit eller hämta hem? Vi kommer med skåpbil och personal, bär ut hemma hos dig, kör till din Storage365-anläggning och bär in allt i ditt förråd. Eller tvärtom – vi hämtar från ditt Storage365-förråd och levererar hem. Du slipper hyra bil, bära själv och göra flera vändor.",
      heroImage: "/hero-transport-forrad.png",
    },

    featureCards: {
      sinceText: "Storage365-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt hundratals Storage365-kunder att transportera saker till och från sina förråd. Våra ungdomar känner till Storage365s anläggningar, vet hur de digitala access-systemen fungerar och hanterar allt från tunga möbler till ömtåliga kartonger.",
      bookYourText: "Boka din Storage365-transport",
      bookDescText:
        "Ange din hemadress och vilken Storage365-anläggning du hyr förråd hos, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar anpassade för Storage365-förråd",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska köra en kartong eller fylla ett helt Storage365-förråd. Du slipper hyra bil, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi kör till och från Storage365",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, sängar, bord, garderober och stolar som ska till eller från ditt Storage365-förråd",
          image: "/sofa.webp",
        },
        {
          name: "Flyttkartonger",
          description:
            "Kartonger med kläder, böcker, köksprylar och personliga ägodelar",
          image: "/box.webp",
        },
        {
          name: "Säsongsprylar",
          description:
            "Vinterdäck, skidor, cyklar, trädgårdsmöbler och julpynt",
          image: "/patio-furniture.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Tvättmaskin, kylskåp, frys och andra vitvaror som ska mellanlagras hos Storage365",
          image: "/washer.webp",
        },
        {
          name: "Elektronik",
          description: "TV-apparater, datorer, skärmar och annan elektronik",
          image: "/tv.webp",
        },
        {
          name: "Konst & ömtåligt",
          description:
            "Tavlor, speglar, porslin och andra ömtåliga föremål",
          image: "/painting.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport till och från Storage365 – vi kör, bär och ställer in",
      mainText:
        "Storage365 erbjuder moderna, flexibla förråd med dygnet-runt-access i Stockholmsområdet. Att hyra förråd hos Storage365 är enkelt – men att fysiskt få dit sina saker kan vara en utmaning. Tunga möbler i trappor, en skåpbil som ska hyras, flera vändor i rusningstrafik och sedan ska allt bäras genom gångarna och in i ditt förråd. Med Ungdomskraft slipper du allt det. Vi kommer hem till dig med skåpbil och ett team av starka ungdomar, bär ut det som ska till förrådet, lastar och kör till din Storage365-anläggning. Där hanterar vi digitala access-system, hissar och trånga gångar – och ställer in allt organiserat i ditt förråd. Behöver du hämta saker från ditt Storage365-förråd? Vi hämtar, lastar och levererar hem till din dörr. Vi kör till alla Storage365-anläggningar i Stockholm och Göteborg: Älvsjö, Solna, Bromma, Järfälla, Högsbo och alla andra. Oavsett om du ska ställa in möbler inför en renovering, lagra säsongsprylar eller hämta tillbaka saker efter en flytt anpassar vi fordon och team efter mängden.",
      preparationHeading: "Förbered inför transport till Storage365",
      preparationText:
        "Packa det du kan i kartonger och märk dem med innehåll – det gör det lättare att hitta saker i ditt Storage365-förråd senare. Demontera möbler som inte går genom dörrar hela. Ha din digitala access-kod till Storage365-anläggningen redo. Se till att du vet vilket förråd som är ditt och på vilken våning det ligger. Kolla att det finns fri passage i trapphus hemma och att skåpbilen kan parkera nära porten. Om du hämtar från Storage365 – se till att du vet var i förrådet sakerna står.",
      additionalSections: [
        {
          heading: "Storage365-anläggningar vi kör till",
          listItems: [
            {
              label: "Storage365 Älvsjö",
              text: "Söder om Stockholm med bra tillgänglighet från E4/E20 och Huddingevägen.",
            },
            {
              label: "Storage365 Solna",
              text: "Centralt i Solna med snabb access från Solnavägen och E4:an.",
            },
            {
              label: "Storage365 Bromma",
              text: "Västra Stockholm med bra tillgänglighet från Drottningholmsvägen.",
            },
            {
              label: "Storage365 Järfälla",
              text: "Nordvästra Stockholm – perfekt för dig i Järfälla, Barkarby eller Jakobsberg.",
            },
            {
              label: "Storage365 Högsbo",
              text: "Göteborg – centralt i Högsbo med bra tillgänglighet från Dag Hammarskjöldsleden.",
            },
            {
              label: "Storage365 Mölndal",
              text: "Göteborg – nära Mölndals centrum med enkel access från E6:an.",
            },
          ],
          afterContent:
            "Vi kör till alla Storage365-anläggningar i Stockholm och Göteborg. Ange bara adressen vid bokning – vi fixar resten. Hittar du inte din Storage365-anläggning i listan? Kontakta oss så hjälper vi dig.",
        },
        {
          heading: "Transport till Storage365 vid renovering eller flytt",
          content:
            "Ska du renovera och behöver mellanlagra möblerna? Eller flyttar du och den nya bostaden inte är redo än? Vi kör dina saker till ditt Storage365-förråd, bär in och ställer allt organiserat. När du är redo att hämta tillbaka allt bokar du en ny transport och vi levererar det hela hem till dig. Många Storage365-kunder använder oss som en smidig transportlösning åt båda hållen – du fokuserar på renoveringen eller flytten, vi sköter logistiken. Storage365s flexibla hyresperioder och dygnet-runt-access gör det enkelt att anpassa förrådet efter dina behov, och med Ungdomskraft behöver du inte oroa dig för transporten.",
        },
        {
          heading: "Hämta saker från ditt Storage365-förråd",
          content:
            "Behöver du hämta tillbaka möbler, kartonger eller säsongsprylar från ditt Storage365-förråd? Vi åker till anläggningen, hanterar det digitala access-systemet och hissen, bär ut det du vill ha, lastar skåpbilen och kör hem till dig. Vi bär in allt och ställer det där du vill ha det. Perfekt när vintern är över och trädgårdsmöblerna ska hem, när renoveringen är klar eller när du ska hämta saker inför en flytt. Du slipper hyra bil och göra det själv – vi gör hela jobbet åt dig.",
        },
        {
          heading:
            "Varför boka transport till Storage365 med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje Storage365-transport vi genomför. När du bokar transport till eller från ditt Storage365-förråd hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de bär dina kartonger till hur de staplar allt snyggt och organiserat i ditt Storage365-förråd.\n\nSedan starten 2014 har vi kört tusentals transporter till och från förråd i Stockholms- och Göteborgsområdet – varav många till just Storage365. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker lastning och hur man hanterar allt från ömtåliga föremål till tunga möbler i trånga förrådsgångar.\n\nVåra ungdomar känner till Storage365s anläggningar. De vet hur de digitala access-systemen fungerar, vilka hissar som finns, hur gångarna är uppbyggda och var man bäst parkerar skåpbilen. Storage365s moderna anläggningar med dygnet-runt-access gör att vi kan planera transporten flexibelt – och vi utnyttjar den möjligheten för att ge dig bästa möjliga service.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att allt är rätt lastat och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje transport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje transport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din transport till Storage365 blir en del av något större.\n\nVi tar också hållbarhet på allvar. Saker som inte längre behövs sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer som Myrorna, Erikshjälpen och Stadsmissionen. Ingenting slängs i onödan.\n\nDet är smidig Storage365-transport med mening.\n\nTransport till Storage365 ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport till Storage365?",
        answer:
          "Priset beror på mängden saker, avstånd mellan hemadress och Storage365-anläggningen och om bärning i trappor krävs. Ett par kartonger och en liten möbel landar ofta på 595–995 kr. Flera tunga möbler och kartonger kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport till Storage365?",
        answer:
          "Transport till Storage365 ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni hämta saker från mitt Storage365-förråd?",
        answer:
          "Ja, vi åker till din Storage365-anläggning, hanterar det digitala access-systemet och hissen, bär ut sakerna ur ditt förråd, lastar skåpbilen och kör hem till dig. Vi bär in allt och ställer det där du vill ha det.",
      },
      {
        question: "Ingår skåpbil i Storage365-transporten?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras till eller från ditt Storage365-förråd – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Vilka Storage365-anläggningar kör ni till?",
        answer:
          "Vi kör till alla Storage365-anläggningar i Stockholm och Göteborg: Älvsjö, Solna, Bromma, Järfälla, Högsbo, Mölndal och alla andra. Ange bara adressen vid bokning.",
      },
      {
        question:
          "Kan ni köra saker till mitt Storage365-förråd vid renovering?",
        answer:
          "Ja, vi kör möbler och kartonger till ditt Storage365-förråd under renoveringen. När den är klar bokar du en ny transport och vi hämtar tillbaka allt. Perfekt vid golvslipning, målning eller köksrenovering.",
      },
      {
        question: "Bär ni in sakerna i mitt Storage365-förråd?",
        answer:
          "Ja, vi bär in alla saker och ställer dem organiserat i ditt Storage365-förråd. Vi hanterar digitala access-system, hissar, trånga gångar och tunga luckor på anläggningen. Du behöver inte göra något annat än att visa oss rätt förråd.",
      },
      {
        question:
          "Kan ni transportera tunga möbler till Storage365?",
        answer:
          "Ja, vi transporterar alla typer av möbler – soffor, sängar, garderober, bord och allt annat tungt. Vi har rätt teknik och utrustning för att bära och lasta säkert.",
      },
      {
        question:
          "Demonterar ni möbler innan transport till Storage365-förrådet?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att kunna transporteras. Spara gärna skruvar i märkta påsar.",
      },
      {
        question:
          "Kan ni köra säsongsprylar till och från Storage365?",
        answer:
          "Ja, vi kör vinterdäck, skidor, trädgårdsmöbler, julpynt och andra säsongsprylar till och från ditt Storage365-förråd. Perfekt för att rotera grejer mellan hemmet och förrådet utan att hyra bil.",
      },
      {
        question:
          "Hur snabbt kan ni köra till Storage365?",
        answer:
          "Vi erbjuder ofta transport till Storage365 med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question:
          "Skyddar ni sakerna under transport till Storage365?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar möbler och ömtåliga föremål under lastning, transport och inbärning i ditt Storage365-förråd. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question:
          "Kan ni köra till Storage365 på helger?",
        answer:
          "Ja, vi erbjuder transport till och från Storage365 på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question:
          "Behöver jag vara med vid Storage365-anläggningen?",
        answer:
          "Du behöver vara med för att ge oss tillgång till ditt Storage365-förråd – med digital access-kod. Vill du slippa åka dit kan du ordna en fullmakt eller dela koden med oss i förväg.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni transport till Storage365?",
        answer:
          "Vi erbjuder transport till och från Storage365 i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transport till Storage365 i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut dina saker, lastar skåpbilen och kör till din Storage365-anläggning. Där bär vi in allt och ställer det organiserat i ditt förråd.",
      step3Title: "Allt inställt hos Storage365",
      step3Desc:
        "Dina saker står säkert och organiserat i ditt Storage365-förråd. Eller hemma hos dig – om du hämtade därifrån. Klart.",
      ctaHeading: "Boka transport till Storage365 idag",
      ctaText:
        "Ange din hemadress och vilken Storage365-anläggning du hyr förråd hos, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "transport-storage365",
      meta: {
        title:
          "Transport Storage365 – Easy Transport To & From Storage365 | Ungdomskraft",
        description:
          "Book Storage365 transport – we pick up at home, deliver to your unit and carry everything in, or collect from storage. Fixed price. Book today!",
        h1: "Transport Storage365",
      },
      hero: {
        heading: "Transport to and from Storage365",
        intro:
          "Do you rent a storage unit at Storage365 and need help getting things there or back home? We come with a van and crew, carry everything out at your home, drive to your Storage365 facility and place it all neatly in your unit. Or the other way round — we collect from your Storage365 unit and deliver to your door. No need to rent a car, carry things yourself or make multiple trips.",
      },
      featureCards: {
        sinceText: "Storage365 transport since 2014",
        bookYourText: "Book your Storage365 transport",
        bookDescText:
          "Enter your home address and which Storage365 facility you use, describe what needs transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans sized for Storage365 units",
        vehicleDescText:
          "We arrive with the right size van — whether you're taking a single box or filling an entire Storage365 unit. No need to rent a vehicle, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Items we transport to and from Storage365",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, beds, tables, wardrobes and chairs going to or from your Storage365 unit",
            image: "/sofa.webp",
          },
          {
            name: "Moving boxes",
            description:
              "Boxes with clothes, books, kitchenware and personal belongings",
            image: "/box.webp",
          },
          {
            name: "Seasonal items",
            description:
              "Winter tyres, skis, bicycles, garden furniture and holiday decorations",
            image: "/patio-furniture.webp",
          },
          {
            name: "Appliances",
            description:
              "Washing machines, fridges, freezers and other appliances for interim storage at Storage365",
            image: "/washer.webp",
          },
          {
            name: "Electronics",
            description: "TVs, computers, monitors and other electronics",
            image: "/tv.webp",
          },
          {
            name: "Art & fragile items",
            description:
              "Paintings, mirrors, china and other delicate objects",
            image: "/painting.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Transport to and from Storage365 — we drive, carry and place everything",
        mainText:
          "Storage365 offers modern, flexible storage units with round-the-clock access across the Stockholm and Gothenburg areas. Renting a unit at Storage365 is easy — but physically getting your belongings there can be a challenge. Heavy furniture down the stairs, a van to rent, multiple trips through rush-hour traffic, and then everything needs carrying through the corridors into your unit. With Ungdomskraft you skip all of that. We come to your home with a van and a team of strong young workers, carry out what needs storing, load up and drive to your Storage365 facility. There we handle digital access systems, lifts and corridors — and place everything neatly in your unit. Need to collect items from your Storage365 unit? We pick up, load and deliver to your door. We drive to every Storage365 facility in Stockholm and Gothenburg: Älvsjö, Solna, Bromma, Järfälla, Högsbo and all others. Whether you are storing furniture during a renovation, putting away seasonal items or collecting things after a move, we scale the vehicle and team to match the volume.",
        preparationHeading:
          "Preparing for transport to Storage365",
        preparationText:
          "Pack what you can into boxes and label them with their contents — it makes things easier to find in your Storage365 unit later. Disassemble furniture that won't fit through doorways whole. Have your digital access code for the Storage365 facility ready. Make sure you know which unit is yours and on which floor it is. Check that there is clear access in stairwells at home and that the van can park close to the entrance. If you are collecting from Storage365, make sure you know where in the unit your items are.",
        additionalSections: [
          {
            heading: "Storage365 facilities we drive to",
            listItems: [
              {
                label: "Storage365 Älvsjö",
                text: "South of Stockholm with good access from the E4/E20 and Huddingevägen.",
              },
              {
                label: "Storage365 Solna",
                text: "Central Solna with quick access from Solnavägen and the E4.",
              },
              {
                label: "Storage365 Bromma",
                text: "Western Stockholm with good access from Drottningholmsvägen.",
              },
              {
                label: "Storage365 Järfälla",
                text: "North-west Stockholm — ideal for Järfälla, Barkarby or Jakobsberg residents.",
              },
              {
                label: "Storage365 Högsbo",
                text: "Gothenburg — central Högsbo with good access from Dag Hammarskjöldsleden.",
              },
              {
                label: "Storage365 Mölndal",
                text: "Gothenburg — near Mölndal centre with easy access from the E6.",
              },
            ],
            afterContent:
              "We drive to every Storage365 facility in Stockholm and Gothenburg. Just enter the address when booking — we handle the rest. Can't find your Storage365 facility in the list? Contact us and we'll help you.",
          },
          {
            heading:
              "Transport to Storage365 during renovation or moving",
            content:
              "Renovating and need somewhere to keep your furniture in the meantime? Or moving when the new home isn't ready yet? We drive your belongings to your Storage365 unit, carry them in and place everything neatly. When you're ready to have everything back, book another transport and we deliver it all to your door. Many Storage365 customers use us as a convenient two-way transport solution — you focus on the renovation or move, we handle the logistics. Storage365's flexible rental periods and round-the-clock access make it easy to adapt your unit to your needs, and with Ungdomskraft you don't have to worry about the transport.",
          },
          {
            heading: "Collecting items from your Storage365 unit",
            content:
              "Need to get furniture, boxes or seasonal items back from your Storage365 unit? We drive to the facility, handle the digital access system and lift, carry out what you want, load the van and deliver to your door. We carry everything in and place it where you want it. Perfect when winter is over and the garden furniture needs to come home, when a renovation is finished or when you need things for a move. No need to rent a car and do it yourself — we do the whole job for you.",
          },
          {
            heading:
              "Why book Storage365 transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every Storage365 transport we carry out. When you book transport to or from your Storage365 unit with us, you get a team of motivated, energetic young workers who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that mindset runs through everything — from how they carry your boxes to how they stack everything neatly in your unit.\n\nSince our founding in 2014, we have carried out thousands of storage transports across the Stockholm and Gothenburg regions — many of them to Storage365. Along the way, we have given thousands of young people their first real work experience: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn lifting techniques, safe loading and how to handle everything from fragile items to heavy furniture in narrow storage corridors.\n\nOur young workers know Storage365's facilities. They understand how the digital access systems work, which lifts are available, how the spaces are laid out and where best to park the van. Storage365's modern facilities with round-the-clock access mean we can plan transport flexibly — and we take full advantage of that to give you the best possible service.\n\nYou notice the difference in commitment and energy. Our teams run where others walk, double-check that everything is properly loaded and always ask if there is anything else they can help with. We have built a culture where every transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your Storage365 transport becomes part of something bigger.\n\nWe also take sustainability seriously. Items that are no longer needed are sorted and taken to the right recycling station or donated to charities such as Myrorna, Erikshjälpen and Stadsmissionen. Nothing is dumped unnecessarily.\n\nIt is smooth Storage365 transport with purpose.\n\nTransport to Storage365 does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question:
            "How much does transport to Storage365 cost?",
          answer:
            "The price depends on the volume, distance between your home and the Storage365 facility, and whether stair carrying is needed. A few boxes and a small piece of furniture often comes to SEK 595–995. Larger loads with several heavy items can come to SEK 1,495–2,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on transport to Storage365?",
          answer:
            "Transport to Storage365 does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Can you collect items from my Storage365 unit?",
          answer:
            "Yes, we drive to your Storage365 facility, handle the digital access system and lift, carry your items out of the unit, load the van and deliver to your door. We carry everything in and place it where you want it.",
        },
        {
          question:
            "Is a van included in the Storage365 transport?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting to or from your Storage365 unit — you don't need to rent a car, drive or refuel.",
        },
        {
          question:
            "Which Storage365 facilities do you drive to?",
          answer:
            "We drive to every Storage365 facility in Stockholm and Gothenburg: Älvsjö, Solna, Bromma, Järfälla, Högsbo, Mölndal and all others. Just enter the address when booking.",
        },
        {
          question:
            "Can you transport items to Storage365 during a renovation?",
          answer:
            "Yes, we drive furniture and boxes to your Storage365 unit during the renovation. When it's done, book another transport and we bring everything back. Perfect for floor sanding, painting or kitchen renovations.",
        },
        {
          question:
            "Do you carry items into the Storage365 unit?",
          answer:
            "Yes, we carry everything in and place it neatly in your Storage365 unit. We handle digital access systems, lifts, corridors and heavy shutters. You don't need to do anything other than show us the right unit.",
        },
        {
          question:
            "Do you protect items during transport to Storage365?",
          answer:
            "Yes, we bring furniture blankets, carrying straps and ratchet straps. We protect furniture and fragile items during loading, transport and carrying in. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question:
            "Which areas do you offer Storage365 transport in?",
          answer:
            "We offer transport to and from Storage365 throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Storage365 transport in 3 easy steps",
        step2Text:
          "Our workers carry your items out, load the van and drive to your Storage365 facility. There we carry everything in and place it neatly in your unit.",
        step3Title: "All stored at Storage365",
        step3Desc:
          "Your items are safely and neatly placed in your Storage365 unit. Or at home — if you were collecting from storage. Done.",
        ctaHeading: "Book Storage365 transport today",
        ctaText:
          "Enter your home address and which Storage365 facility you use, describe what needs transporting and pick a time. You see the price instantly.",
      },
    },
  },

  {
    // Transport Möbler — config för /transport-mobler
    // Vinkel: Ren möbeltransport från A till B – köpt på Blocket, sålt privat,
    // hämta från butik, skicka till familj. INTE flytt (hela hemmet) och INTE
    // bortforsling (slänga). Ren transport av en eller flera möbler.
    slug: "transport-mobler",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Möbler – Smidig Möbeltransport med Skåpbil | Ungdomskraft",
      description:
        "Boka möbeltransport med skåpbil och personal. Vi hämtar, bär, lastar och kör dina möbler från A till B – köpt på Blocket, från butik eller till en ny adress. Prisvärt och med unga som gör skillnad.",
      h1: "Möbeltransport",
    },

    hero: {
      heading: "Transport av möbler",
      intro:
        "Köpt en soffa på Blocket? Sålt ett bord på Facebook Marketplace? Ska skicka en bokhylla till familjen? Vi hämtar möbeln på upphämtningsadressen, bär ut, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer på plats. Du slipper hyra bil, ringa kompisar eller slita ryggen i trappan.",
      heroImage: "/hero-flytta-mobler.png",
    },

    featureCards: {
      sinceText: "Möbeltransport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft kört tusentals möbeltransporter i Stockholms- och Göteborgsområdet. Våra ungdomar vet hur man bär soffor genom trånga trapphus, lastar garderober säkert och levererar utan skador. Varje transport ger en ung människa arbetslivserfarenhet.",
      bookYourText: "Boka din möbeltransport",
      bookDescText:
        "Ange upphämtningsadress och leveransadress, beskriv möblerna och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Perfekta skåpbilar för möbeltransport",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska köra en fåtölj eller ett helt vardagsrum. Möbelfiltar, bärremmar och spännband ingår alltid.",
    },

    itemsSection: {
      heading: "Möbler vi transporterar",
      items: [
        {
          name: "Soffor",
          description:
            "Bäddsoffor, hörnsoffor, divaner och fåtöljer – vi bär och kör säkert",
          image: "/sofa.webp",
        },
        {
          name: "Sängar",
          description:
            "Ramsängar, kontinentalsängar, madrasser och sänggavlar",
          image: "/bed.webp",
        },
        {
          name: "Bord",
          description:
            "Matbord, skrivbord, soffbord och konferensbord – demonterade eller hela",
          image: "/table.webp",
        },
        {
          name: "Garderober & förvaring",
          description:
            "Garderober, byråer, bokhyllor och skåp – vi demonterar vid behov",
          image: "/wardrobe.webp",
        },
        {
          name: "Fåtöljer & stolar",
          description:
            "Fåtöljer, kontorsstolar, matstolar och barstolar",
          image: "/armchair.webp",
        },
        {
          name: "Trädgårdsmöbler",
          description:
            "Loungemöbler, parasoll, utebord och grillmöbler",
          image: "/patio-furniture.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Möbeltransport – vi hämtar, kör och bär in dina möbler",
      mainText:
        "Att transportera möbler själv är ofta krångligare än man tror. En soffa som inte går genom dörren, en garderob som väger för mycket, en skåpbil som ska hyras och en trappa som aldrig tar slut. Med Ungdomskrafts möbeltransport slipper du allt det. Vi kommer till upphämtningsadressen med skåpbil och ett team av starka ungdomar, bär ut möbeln, skyddar den med möbelfiltar, lastar och kör till leveransadressen. Där bär vi in och ställer möbeln exakt där du vill ha den. Vi kör möbeltransporter för alla situationer: Blocket-köp, Facebook Marketplace-fynd, möbler från butik som inte erbjuder hemleverans, möbler du skickar till familj eller vänner, och möbler som ska till en ny adress vid separation eller flytt. Oavsett om det gäller en enda fåtölj eller fem tunga möbler anpassar vi skåpbil och team efter uppdraget.",
      preparationHeading: "Förbered inför möbeltransport",
      preparationText:
        "Mät möblerna och dörröppningarna – framför allt soffor, sängar och garderober som kan vara svåra att få genom trånga passager. Demontera det som behöver demonteras innan vi kommer – eller meddela oss så gör vi det på plats. Töm lådor och hyllor så att möblerna blir lättare att bära. Se till att det finns fri passage i trapphus och att skåpbilen kan parkera nära porten både på hämtadressen och leveransadressen. Om du köpt möbeln av en privatperson – bekräfta tid och adress med säljaren innan bokning.",
      additionalSections: [
        {
          heading: "Möbeltransport vid Blocket- och Marketplace-köp",
          content:
            "Köpt en soffa på Blocket eller ett matbord på Facebook Marketplace? Det sista steget – att hämta möbeln – stoppar många. Säljaren bor kanske tre trappor upp utan hiss, möbeln är för stor för din bil och du har ingen kompis med skåpbil. Vi löser det. Boka din möbeltransport med Ungdomskraft, ange säljarens adress som upphämtning och din adress som leverans. Vi hämtar, bär, lastar, kör och bär in – du behöver bara vara hemma och ta emot. Perfekt även för Tradera-vinster, auktionsfynd och allt annat du köpt som inte får plats i bilen.",
        },
        {
          heading: "Transport av enstaka möbler och stora möbler",
          content:
            "Ibland behöver du bara köra en enda möbel – en soffa till stugan, ett skrivbord till kontoret eller en garderob till barnen. Och ibland är den möbeln för tung, för stor eller för ömtålig för att köra själv. Vi hanterar möbeltransporter av alla storlekar: från en enda fåtölj till ett helt vardagsrums möbler. Vi demonterar garderober och sängar som inte går genom dörren hela, skyddar allt med möbelfiltar och bärremmar, och bär försiktigt genom trapphus och trånga passager. Du slipper oroa dig – vi har gjort det här tusentals gånger.",
        },
        {
          heading: "Möbeltransport från butik",
          content:
            "Köpt möbler i butik men saknar hemleverans? Eller vill du slippa vänta veckor på butikens leverans? Vi hämtar möblerna direkt i butiken och kör hem till dig samma dag. Fungerar med alla möbelbutiker – IKEA, Mio, EM, Furniturebox, Svenssons i Lammhult och alla andra. Vi bär in och monterar vid behov. Du slipper hyra släp, lasta själv och köra.",
        },
        {
          heading: "Varför boka möbeltransport med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje möbeltransport vi genomför. När du bokar möbeltransport hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de skyddar din soffa med möbelfiltar till hur de försiktigt manövrerar en garderob genom trapphuset.\n\nSedan starten 2014 har vi kört tusentals möbeltransporter i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker lastning och hur man hanterar allt från ömtåliga antika möbler till tunga kontinentalsängar i trånga trapphus. De tränas i att skydda kundens ägodelar, golv, väggar och dörrkarmar – och i att alltid lämna allt i gott skick.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att allt är rätt lastat och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje möbeltransport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje möbeltransport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din möbeltransport blir en del av något större.\n\nVi tar också hållbarhet på allvar. Möbler som inte längre behövs – till exempel om du byter ut en gammal soffa i samband med att den nya levereras – sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer som Myrorna, Erikshjälpen och Stadsmissionen. Ingenting slängs i onödan.\n\nDet är smidig möbeltransport med mening.\n\nMöbeltransport ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar möbeltransport?",
        answer:
          "Priset beror på antal möbler, storlek, avstånd och om bärning i trappor krävs. En enda soffa eller fåtölj landar ofta på 595–995 kr. Flera tunga möbler kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på möbeltransport?",
        answer:
          "Möbeltransport ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni transportera en enda möbel?",
        answer:
          "Ja, vi kör gärna enstaka möbler – en soffa, en säng, en garderob eller ett bord. Du behöver inte fylla hela skåpbilen. Perfekt vid Blocket-köp eller när du ska skicka en möbel till någon.",
      },
      {
        question: "Ingår skåpbil i möbeltransporten?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån möblerna – du behöver inte hyra bil, köra eller tanka. Möbelfiltar och bärremmar ingår också.",
      },
      {
        question: "Kan ni hämta möbler jag köpt på Blocket?",
        answer:
          "Ja, ange säljarens adress som upphämtning och din adress som leverans. Vi hämtar, bär ut, lastar, kör och bär in hemma hos dig. Fungerar även med Facebook Marketplace, Tradera och auktioner.",
      },
      {
        question: "Demonterar ni möbler innan transport?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att kunna transporteras. Spara gärna skruvar i märkta påsar.",
      },
      {
        question: "Bär ni möblerna i trappor?",
        answer:
          "Ja, vi bär möbler i trappor utan extra kostnad upp till vanliga våningsplan. Vi hanterar trånga trapphus, hörn och smala dörröppningar – det har vi gjort tusentals gånger.",
      },
      {
        question: "Skyddar ni möblerna under transport?",
        answer:
          "Ja, vi skyddar alla möbler med möbelfiltar, bärremmar och spännband. Vi skyddar även golv, väggar och dörrkarmar vid bärning. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Kan ni transportera stora soffor och garderober?",
        answer:
          "Ja, vi transporterar alla typer av möbler oavsett storlek – hörnsoffor, kontinentalsängar, garderober, matbord och allt annat. Vi demonterar det som behöver demonteras och har rätt teknik för att bära säkert.",
      },
      {
        question: "Kan ni hämta möbler från butik?",
        answer:
          "Ja, vi hämtar möbler direkt i butik – IKEA, Mio, EM, Furniturebox och alla andra. Vi lastar, kör hem till dig, bär in och monterar vid behov. Snabbare än butikens egen leverans.",
      },
      {
        question: "Hur snabbt kan ni köra möbeltransport?",
        answer:
          "Vi erbjuder ofta möbeltransport med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni köra möbeltransport på helger?",
        answer:
          "Ja, vi erbjuder möbeltransport på helger och vissa kvällar. Perfekt vid Blocket-affärer där säljaren bara kan på helgen. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Kan ni transportera möbler till stugan?",
        answer:
          "Ja, vi kör möbler till fritidshus och stugor i Stockholms- och Göteborgsområdet. Ange stugans adress som leveransadress vid bokning.",
      },
      {
        question: "Vad är skillnaden mellan möbeltransport och flytt?",
        answer:
          "Möbeltransport är transport av enstaka eller flera möbler från A till B – perfekt vid köp, försäljning eller när du ska skicka möbler. Flytt innebär att vi tömmer och flyttar hela hemmet. Behöver du flytta hela bostaden? Kolla vår flytthjälp istället.",
      },
      {
        question: "I vilka kommuner erbjuder ni möbeltransport?",
        answer:
          "Vi erbjuder möbeltransport i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Möbeltransport i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut möblerna, skyddar dem med möbelfiltar, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer på plats.",
      step3Title: "Möblerna på plats",
      step3Desc:
        "Dina möbler står där du vill ha dem – skyddade, hela och levererade. Klart.",
      ctaHeading: "Boka möbeltransport idag",
      ctaText:
        "Ange upphämtningsadress och leveransadress, beskriv möblerna och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "furniture-transport",
      meta: {
        title:
          "Furniture Transport – Easy Furniture Delivery by Van | Ungdomskraft",
        description:
          "Book furniture transport in Stockholm & Gothenburg. We pick up, carry and deliver your furniture – Blocket purchases, store pickups or moving. Fixed price, insured.",
        h1: "Furniture transport",
      },
      hero: {
        heading: "Transport of furniture",
        intro:
          "Bought a sofa on Blocket? Sold a table on Facebook Marketplace? Sending a bookshelf to family? We collect the furniture at the pickup address, carry it out, load the van and drive to the delivery address. There we carry it in and place it exactly where you want it. No need to rent a car, call friends or strain your back on the stairs.",
      },
      featureCards: {
        sinceText: "Furniture transport since 2014",
        bookYourText: "Book your furniture transport",
        bookDescText:
          "Enter the pickup address and delivery address, describe the furniture and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for furniture transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're moving an armchair or an entire living room. Furniture blankets, carrying straps and ratchet straps are always included.",
      },
      itemsSection: {
        heading: "Furniture we transport",
        items: [
          {
            name: "Sofas",
            description:
              "Sofa beds, corner sofas, chaise longues and armchairs — carried and driven safely",
            image: "/sofa.webp",
          },
          {
            name: "Beds",
            description:
              "Bed frames, divan beds, mattresses and headboards",
            image: "/bed.webp",
          },
          {
            name: "Tables",
            description:
              "Dining tables, desks, coffee tables and conference tables — disassembled or whole",
            image: "/table.webp",
          },
          {
            name: "Wardrobes & storage",
            description:
              "Wardrobes, dressers, bookcases and cabinets — we disassemble if needed",
            image: "/wardrobe.webp",
          },
          {
            name: "Armchairs & chairs",
            description:
              "Armchairs, office chairs, dining chairs and bar stools",
            image: "/armchair.webp",
          },
          {
            name: "Garden furniture",
            description:
              "Lounge sets, parasols, outdoor tables and barbecue furniture",
            image: "/patio-furniture.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Furniture transport — we pick up, drive and carry in your furniture",
        mainText:
          "Transporting furniture yourself is often trickier than you think. A sofa that won't fit through the door, a wardrobe that weighs too much, a van to rent and a staircase that never ends. With Ungdomskraft's furniture transport you skip all of that. We come to the pickup address with a van and a team of strong young workers, carry the furniture out, protect it with blankets, load up and drive to the delivery address. There we carry it in and place it exactly where you want it. We handle furniture transport for every situation: Blocket purchases, Facebook Marketplace finds, store pickups with no home delivery, furniture you're sending to family or friends, and furniture going to a new address after a separation or move. Whether it's a single armchair or five heavy pieces, we scale the van and team to match.",
        preparationHeading: "Preparing for furniture transport",
        preparationText:
          "Measure the furniture and doorways — especially sofas, beds and wardrobes that can be hard to fit through narrow passages. Disassemble anything that needs it before we arrive, or let us know and we'll do it on-site. Empty drawers and shelves so the furniture is lighter to carry. Make sure there is clear access in stairwells and that the van can park close to the entrance at both the pickup and delivery address. If you bought the furniture from a private seller, confirm the time and address with them before booking.",
        additionalSections: [
          {
            heading: "Furniture transport for Blocket and Marketplace purchases",
            content:
              "Bought a sofa on Blocket or a dining table on Facebook Marketplace? The last step — actually collecting the furniture — stops many people. The seller might live three floors up with no lift, the furniture is too big for your car and you don't have a friend with a van. We solve it. Book your furniture transport with Ungdomskraft, enter the seller's address as pickup and your address as delivery. We collect, carry out, load, drive and carry in at your home — you just need to be there to receive it. Also perfect for Tradera wins, auction finds and anything else you've bought that won't fit in the car.",
          },
          {
            heading: "Transporting single items and oversized furniture",
            content:
              "Sometimes you only need to move one piece of furniture — a sofa to the summer house, a desk to the office or a wardrobe to the kids. And sometimes that piece is too heavy, too large or too fragile to drive yourself. We handle furniture transports of every size: from a single armchair to an entire living room's worth of furniture. We disassemble wardrobes and beds that won't fit through doorways whole, protect everything with blankets and straps, and carry carefully through stairwells and tight passages. No need to worry — we've done this thousands of times.",
          },
          {
            heading: "Furniture transport from stores",
            content:
              "Bought furniture in-store but there's no home delivery? Or don't want to wait weeks for the store's delivery service? We collect the furniture directly from the store and drive it home to you the same day. Works with any furniture store — IKEA, Mio, EM, Furniturebox, Svenssons i Lammhult and all others. We carry in and assemble if needed. Skip the trailer rental, the loading and the driving.",
          },
          {
            heading: "Why book furniture transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every furniture transport we carry out. When you book furniture transport with us, you get a team of motivated, energetic young workers who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that mindset runs through everything — from how they protect your sofa with blankets to how they carefully manoeuvre a wardrobe through the stairwell.\n\nSince our founding in 2014, we have carried out thousands of furniture transports across the Stockholm and Gothenburg regions. Along the way, we have given thousands of young people their first real work experience: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn lifting techniques, safe loading and how to handle everything from delicate antique furniture to heavy divan beds in narrow stairwells. They are trained to protect customers' belongings, floors, walls and door frames — and to always leave everything in good condition.\n\nYou notice the difference in commitment and energy. Our teams run where others walk, double-check that everything is properly loaded and always ask if there is anything else they can help with. We have built a culture where every furniture transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every furniture transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your furniture transport becomes part of something bigger.\n\nWe also take sustainability seriously. Furniture that is no longer needed — for example if you're replacing an old sofa when the new one arrives — is sorted and taken to the right recycling station or donated to charities such as Myrorna, Erikshjälpen and Stadsmissionen. Nothing is dumped unnecessarily.\n\nIt is smooth furniture transport with purpose.\n\nFurniture transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does furniture transport cost?",
          answer:
            "The price depends on the number of items, size, distance and whether stair carrying is needed. A single sofa or armchair often comes to SEK 595–995. Several heavy pieces can come to SEK 1,495–2,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on furniture transport?",
          answer:
            "Furniture transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Can you transport a single piece of furniture?",
          answer:
            "Yes, we happily transport single items — a sofa, a bed, a wardrobe or a table. You don't need to fill the whole van. Perfect for Blocket purchases or when you're sending a piece to someone.",
        },
        {
          question: "Can you collect furniture I bought on Blocket?",
          answer:
            "Yes, enter the seller's address as pickup and your address as delivery. We collect, carry out, load, drive and carry in at your home. Also works with Facebook Marketplace, Tradera and auctions.",
        },
        {
          question: "Do you disassemble furniture before transport?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart. Keep screws in labelled bags so reassembly is easy.",
        },
        {
          question: "Do you carry furniture up stairs?",
          answer:
            "Yes, we carry furniture up and down stairs. We handle tight stairwells, corners and narrow doorways — we've done it thousands of times.",
        },
        {
          question: "Do you protect furniture during transport?",
          answer:
            "Yes, we protect all furniture with blankets, carrying straps and ratchet straps. We also protect floors, walls and door frames when carrying. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question: "Can you collect furniture from a store?",
          answer:
            "Yes, we collect furniture directly from any store — IKEA, Mio, EM, Furniturebox and all others. We load, drive to your home, carry in and assemble if needed. Faster than the store's own delivery.",
        },
        {
          question: "Which areas do you offer furniture transport in?",
          answer:
            "We offer furniture transport throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Furniture transport in 3 easy steps",
        step2Text:
          "Our workers carry the furniture out, protect it with blankets, load the van and drive to the delivery address. There we carry in and place everything where you want it.",
        step3Title: "Furniture in place",
        step3Desc:
          "Your furniture is where you want it — protected, intact and delivered. Done.",
        ctaHeading: "Book furniture transport today",
        ctaText:
          "Enter the pickup address and delivery address, describe the furniture and pick a time. You see the price instantly.",
      },
    },
  },

  {
    // Transport Soffa — config för /transport-soffa
    // Vinkel: Nischad sida specifikt för transport av soffa. Enkel att ranka.
    // Unik vinkel vs transport-mobler: fokus på just soffor – typiskt Blocket-köp,
    // hörnsoffor i trånga trapphus, bäddsoffor, tunga skinnsoffor, etc.
    slug: "transport-soffa",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Soffa – Smidig Transport av soffa med Skåpbil | Ungdomskraft",
      description:
        "Boka transport av soffa med skåpbil och personal. Vi hämtar, bär ut, lastar och kör din soffa från A till B – köpt på Blocket, från butik eller till en ny adress. Prisvärt och med unga som gör skillnad.",
      h1: "Transport av soffa",
    },

    hero: {
      heading: "Transport av soffa",
      intro:
        "Köpt en soffa på Blocket? Ska du skicka den gamla bäddsoffan till stugan? Behöver du få hem en hörnsoffa från butiken? Vi hämtar soffan på upphämtningsadressen, bär ut genom trapphus och trånga passager, skyddar med möbelfiltar, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer soffan på plats. Du slipper hyra bil, ringa kompisar eller slita ryggen.",
      heroImage: "/hero-flytta-mobler.png",
    },

    featureCards: {
      sinceText: "Transport av soffa sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft kört tusentals soffatransporter i Stockholms- och Göteborgsområdet. Våra ungdomar vet exakt hur man manövrerar hörnsoffor genom trånga trapphus, bär tunga skinnsoffor och lastar bäddsoffor säkert i skåpbilen.",
      bookYourText: "Boka din transport av soffa",
      bookDescText:
        "Ange upphämtningsadress och leveransadress, beskriv soffan och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för transport av soffa",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska köra en liten tvåsits eller en stor hörnsoffa. Möbelfiltar, bärremmar och spännband ingår alltid.",
    },

    itemsSection: {
      heading: "Soffor vi transporterar",
      items: [
        {
          name: "Hörnsoffor",
          description:
            "L-formade och U-formade hörnsoffor – vi delar och bär ut sektion för sektion",
          image: "/sofa.webp",
        },
        {
          name: "Bäddsoffor",
          description:
            "Tunga bäddsoffor med utdragbar mekanism – vi hanterar vikten och storleken",
          image: "/sofa.webp",
        },
        {
          name: "Skinnsoffor",
          description:
            "Tunga skinnsoffor som kräver extra omsorg – vi skyddar med möbelfiltar",
          image: "/sofa.webp",
        },
        {
          name: "Divaner & schäslonger",
          description:
            "Divaner, dagbäddar och schäslonger – ofta lättare men ömtåliga",
          image: "/armchair.webp",
        },
        {
          name: "Tvåsitssoffor",
          description:
            "Kompakta soffor som ofta går genom dörrar hela – snabb transport",
          image: "/sofa.webp",
        },
        {
          name: "Fåtöljer",
          description:
            "Fåtöljer, öronlappsmodeller och recliners – bärs varsamt",
          image: "/armchair.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport av soffa – vi hämtar, kör och bär in din soffa",
      mainText:
        "Att transportera en soffa själv är nästan alltid krångligare än man tror. Soffan väger för mycket, är för stor för bilen, går inte genom dörren eller fastnar i trappan. Med Ungdomskrafts transport av soffa slipper du allt det. Vi kommer till upphämtningsadressen med skåpbil och ett team av starka ungdomar som vet exakt hur man manövrerar soffor genom trånga trapphus och smala dörröppningar. Vi skyddar soffan med möbelfiltar, bär ut, lastar och kör till leveransadressen. Där bär vi in och ställer soffan exakt där du vill ha den. Vi kör soffatransporter för alla situationer: Blocket-köp, Facebook Marketplace-fynd, soffor från butik, soffor du skickar till familj eller vänner, och soffor som ska till en ny adress. Hörnsoffor, bäddsoffor, skinnsoffor, divaner – vi hanterar alla typer och storlekar.",
      preparationHeading: "Förbered inför transport av soffa",
      preparationText:
        "Mät soffan och dörröppningarna – framför allt bredd, höjd och djup. Kolla om soffan går isär i sektioner (många hörnsoffor gör det) – det gör transporten enklare. Ta bort kuddar och lösa delar och lägg dem i en sopsäck eller kartong. Om ben kan skruvas av, gör det gärna – det minskar höjden. Se till att det finns fri passage i trapphus och korridorer. Kolla att skåpbilen kan parkera nära porten både på hämtadressen och leveransadressen. Om du köpt soffan av en privatperson – bekräfta tid och adress med säljaren innan bokning.",
      additionalSections: [
        {
          heading: "Transport av soffa vid Blocket- och Marketplace-köp",
          content:
            "Köpt en soffa på Blocket eller Facebook Marketplace? Det sista steget – att hämta soffan – stoppar många. Säljaren bor kanske tre trappor upp utan hiss, soffan är för stor för bilen och du har ingen kompis med skåpbil. Vi löser det. Boka din transport av soffa med Ungdomskraft, ange säljarens adress som upphämtning och din adress som leverans. Vi bär ut soffan hos säljaren, skyddar med möbelfiltar, lastar skåpbilen, kör till din adress och bär in. Du behöver bara vara hemma och visa var soffan ska stå. Fungerar även med Tradera-köp, auktionsfynd och alla andra situationer där du köpt en soffa som inte får plats i bilen.",
        },
        {
          heading: "Hörnsoffor och stora soffor i trånga trapphus",
          content:
            "Hörnsoffor är de mest utmanande möblerna att transportera – de är stora, tunga och ofta svåra att manövrera i trånga trapphus. Men det är precis det vi gör varje dag. Våra ungdomar har bärit tusentals hörnsoffor genom Stockholms och Göteborgs trapphus och vet exakt hur man vinklar, lyfter och manövrerar för att komma igenom utan att skada soffan, väggarna eller dörrkarmen. Många hörnsoffor kan delas i sektioner – vi tar isär och sätter ihop på plats. L-soffor, U-soffor, recamiere-modeller och alla andra former – vi fixar det.",
        },
        {
          heading: "Transport av soffa från butik",
          content:
            "Köpt en soffa i butik men saknar hemleverans? Eller vill du slippa vänta veckor på butikens leverans? Vi hämtar soffan direkt i butiken och kör hem till dig samma dag. Fungerar med alla möbelbutiker – IKEA, Mio, EM, Furniturebox, Svenssons i Lammhult, Sofacompany och alla andra. Vi bär in och ställer på plats. Du slipper hyra släp, lasta själv och köra.",
        },
        {
          heading: "Varför boka transport av soffa med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje transport av soffa vi genomför. När du bokar transport av soffa hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de skyddar din soffa med möbelfiltar till hur de försiktigt manövrerar den genom trapphuset.\n\nSedan starten 2014 har vi kört tusentals soffatransporter i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker lastning och hur man hanterar allt från lätta tygsoffor till tunga skinnsoffor och bäddsoffor i trånga trapphus. De tränas i att skydda kundens ägodelar, golv, väggar och dörrkarmar – och i att alltid lämna allt i gott skick.\n\nSoffor är en av de vanligaste sakerna vi transporterar – och en av de svåraste. Storleken, vikten och formen gör att det krävs erfarenhet och teknik för att bära säkert genom trånga passager. Våra team har den erfarenheten. De vet hur man vinklar en hörnsoffa i en trappa, hur man lyfter en bäddsoffa utan att skada mekanismen och hur man skyddar en skinnsoffa från repor.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att allt är rätt lastat och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje transport av soffa ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje transport av soffa du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din transport av soffa blir en del av något större.\n\nVi tar också hållbarhet på allvar. Soffor som inte längre behövs – till exempel om du byter ut den gamla när den nya levereras – sorterar vi och kör till rätt återvinningsstation eller skänker till välgörenhetsorganisationer som Myrorna, Erikshjälpen och Stadsmissionen. Ingenting slängs i onödan.\n\nDet är smidig transport av soffa med mening.\n\nTransport av soffa ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport av soffa?",
        answer:
          "Priset beror på soffans storlek, avstånd och om bärning i trappor krävs. En tvåsitssoffa landar ofta på 595–995 kr. En stor hörnsoffa eller bäddsoffa kan landa på 995–1 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport av soffa?",
        answer:
          "Transport av soffa ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni transportera en hörnsoffa?",
        answer:
          "Ja, vi transporterar alla typer av hörnsoffor – L-soffor, U-soffor och alla andra former. Många hörnsoffor kan delas i sektioner, vilket vi gör på plats. Vi vet hur man manövrerar stora soffor genom trånga trapphus.",
      },
      {
        question: "Ingår skåpbil i transporten av soffan?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån soffan – du behöver inte hyra bil, köra eller tanka. Möbelfiltar och bärremmar ingår också.",
      },
      {
        question: "Kan ni hämta en soffa jag köpt på Blocket?",
        answer:
          "Ja, ange säljarens adress som upphämtning och din adress som leverans. Vi hämtar, bär ut, lastar, kör och bär in hemma hos dig. Fungerar även med Facebook Marketplace, Tradera och auktioner.",
      },
      {
        question: "Bär ni soffan i trappor?",
        answer:
          "Ja, vi bär soffor i trappor – även i trånga trapphus utan hiss. Vi har bärit tusentals soffor och vet exakt hur man manövrerar genom hörn, smala passager och trånga dörröppningar.",
      },
      {
        question: "Skyddar ni soffan under transporten?",
        answer:
          "Ja, vi skyddar alla soffor med möbelfiltar, bärremmar och spännband. Vi skyddar även golv, väggar och dörrkarmar vid bärning. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Kan ni transportera en bäddsoffa?",
        answer:
          "Ja, vi transporterar bäddsoffor – de är tyngre än vanliga soffor men vi har rätt teknik och utrustning. Vi skyddar mekanismen under transport så att den fungerar perfekt vid leverans.",
      },
      {
        question: "Kan ni hämta en soffa från butik?",
        answer:
          "Ja, vi hämtar soffor direkt i butik – IKEA, Mio, EM, Sofacompany, Furniturebox och alla andra. Vi lastar, kör hem till dig och bär in. Snabbare än butikens egen leverans.",
      },
      {
        question: "Kan ni köra transport av soffa på helger?",
        answer:
          "Ja, vi erbjuder transport av soffa på helger och vissa kvällar. Perfekt vid Blocket-affärer där säljaren bara kan på helgen. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Hur snabbt kan ni köra transport av soffa?",
        answer:
          "Vi erbjuder ofta transport av soffa med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni transportera en soffa till stugan?",
        answer:
          "Ja, vi kör soffor till fritidshus och stugor i Stockholms- och Göteborgsområdet. Ange stugans adress som leveransadress vid bokning.",
      },
      {
        question: "Tar ni bort den gamla soffan också?",
        answer:
          "Ja, vi kan ta med oss den gamla soffan vid leverans av den nya. Vi kör den till återvinning eller skänker den till välgörenhet om den är i bra skick. Meddela oss vid bokning.",
      },
      {
        question: "Vad kostar det att transportera en soffa långt?",
        answer:
          "Priset beror på avståndet – du ser priset direkt vid bokning. Kilometer och tid räknas in. Korta transporter inom stan landar ofta på 595–995 kr, längre sträckor kan bli mer.",
      },
      {
        question: "I vilka kommuner erbjuder ni transport av soffa?",
        answer:
          "Vi erbjuder transport av soffa i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transport av soffa i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut soffan, skyddar den med möbelfiltar, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer soffan på plats.",
      step3Title: "Soffan på plats",
      step3Desc:
        "Din soffa står där du vill ha den – skyddad, hel och levererad. Klart.",
      ctaHeading: "Boka transport av soffa idag",
      ctaText:
        "Ange upphämtningsadress och leveransadress, beskriv soffan och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "sofa-transport",
      meta: {
        title:
          "Sofa Transport – Easy Sofa Delivery by Van | Ungdomskraft",
        description:
          "Book sofa transport with van and crew. We pick up, carry and deliver your sofa safely – Blocket purchases, store pickups or moving. Fixed price, insured. Book today!",
        h1: "Sofa transport",
      },
      hero: {
        heading: "Transport of sofa",
        intro:
          "Bought a sofa on Blocket? Sending the old sofa bed to the summer house? Need to get a corner sofa home from the store? We collect the sofa at the pickup address, carry it out through stairwells and tight passages, protect it with blankets, load the van and drive to the delivery address. There we carry it in and place it exactly where you want it. No need to rent a car, call friends or strain your back.",
      },
      featureCards: {
        sinceText: "Sofa transport since 2014",
        bookYourText: "Book your sofa transport",
        bookDescText:
          "Enter the pickup address and delivery address, describe the sofa and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for sofa transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're moving a small two-seater or a large corner sofa. Furniture blankets, carrying straps and ratchet straps are always included.",
      },
      itemsSection: {
        heading: "Sofas we transport",
        items: [
          {
            name: "Corner sofas",
            description:
              "L-shaped and U-shaped corner sofas — we split and carry section by section",
            image: "/sofa.webp",
          },
          {
            name: "Sofa beds",
            description:
              "Heavy sofa beds with pull-out mechanisms — we handle the weight and size",
            image: "/sofa.webp",
          },
          {
            name: "Leather sofas",
            description:
              "Heavy leather sofas requiring extra care — we protect with furniture blankets",
            image: "/sofa.webp",
          },
          {
            name: "Chaise longues & daybeds",
            description:
              "Chaise longues, daybeds and loungers — often lighter but delicate",
            image: "/armchair.webp",
          },
          {
            name: "Two-seater sofas",
            description:
              "Compact sofas that often fit through doors whole — quick transport",
            image: "/sofa.webp",
          },
          {
            name: "Armchairs",
            description:
              "Armchairs, wingbacks and recliners — carried with care",
            image: "/armchair.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Sofa transport — we pick up, drive and carry in your sofa",
        mainText:
          "Transporting a sofa yourself is almost always trickier than you think. The sofa weighs too much, is too big for the car, won't fit through the door or gets stuck on the stairs. With Ungdomskraft's sofa transport you skip all of that. We come to the pickup address with a van and a team of strong young workers who know exactly how to manoeuvre sofas through tight stairwells and narrow doorways. We protect the sofa with blankets, carry it out, load up and drive to the delivery address. There we carry it in and place it exactly where you want it. We handle sofa transport for every situation: Blocket purchases, Facebook Marketplace finds, store pickups, sofas you're sending to family or friends, and sofas going to a new address. Corner sofas, sofa beds, leather sofas, chaise longues — we handle every type and size.",
        preparationHeading: "Preparing for sofa transport",
        preparationText:
          "Measure the sofa and doorways — especially width, height and depth. Check whether the sofa comes apart in sections (many corner sofas do) — that makes transport easier. Remove cushions and loose parts and put them in a bag or box. If legs can be unscrewed, do so — it reduces the height. Make sure there is clear access in stairwells and corridors. Check that the van can park close to the entrance at both the pickup and delivery address. If you bought the sofa from a private seller, confirm the time and address with them before booking.",
        additionalSections: [
          {
            heading: "Sofa transport for Blocket and Marketplace purchases",
            content:
              "Bought a sofa on Blocket or Facebook Marketplace? The last step — actually collecting the sofa — stops many people. The seller might live three floors up with no lift, the sofa is too big for your car and you don't have a friend with a van. We solve it. Book your sofa transport with Ungdomskraft, enter the seller's address as pickup and your address as delivery. We carry the sofa out at the seller's, protect it with blankets, load the van, drive to your address and carry it in. You just need to be home and show us where the sofa goes. Also works with Tradera purchases, auction finds and any other situation where you've bought a sofa that won't fit in the car.",
          },
          {
            heading: "Corner sofas and large sofas in tight stairwells",
            content:
              "Corner sofas are among the most challenging pieces of furniture to transport — they are large, heavy and often hard to manoeuvre in tight stairwells. But that is exactly what we do every day. Our young workers have carried thousands of corner sofas through Stockholm's and Gothenburg's stairwells and know exactly how to angle, lift and manoeuvre to get through without damaging the sofa, walls or door frames. Many corner sofas can be split into sections — we take them apart and reassemble on-site. L-sofas, U-sofas, recamier models and every other shape — we handle it.",
          },
          {
            heading: "Sofa transport from stores",
            content:
              "Bought a sofa in-store but there's no home delivery? Or don't want to wait weeks for the store's delivery service? We collect the sofa directly from the store and drive it home to you the same day. Works with any furniture store — IKEA, Mio, EM, Sofacompany, Furniturebox, Svenssons i Lammhult and all others. We carry in and place it where you want it. Skip the trailer rental, the loading and the driving.",
          },
          {
            heading: "Why book sofa transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every sofa transport we carry out. When you book sofa transport with us, you get a team of motivated, energetic young workers who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that mindset runs through everything — from how they protect your sofa with blankets to how they carefully manoeuvre it through the stairwell.\n\nSince our founding in 2014, we have carried out thousands of sofa transports across the Stockholm and Gothenburg regions. Along the way, we have given thousands of young people their first real work experience: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn lifting techniques, safe loading and how to handle everything from light fabric sofas to heavy leather sofas and sofa beds in narrow stairwells.\n\nSofas are one of the most common things we transport — and one of the hardest. The size, weight and shape mean experience and technique are needed to carry safely through tight passages. Our teams have that experience. They know how to angle a corner sofa on a staircase, how to lift a sofa bed without damaging the mechanism and how to protect a leather sofa from scratches.\n\nYou notice the difference in commitment and energy. Our teams run where others walk, double-check that everything is properly loaded and always ask if there is anything else they can help with. We have built a culture where every sofa transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nBut it is not just about quality — it is about meaning. Every sofa transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your sofa transport becomes part of something bigger.\n\nWe also take sustainability seriously. Sofas that are no longer needed — for example if you're replacing the old one when the new one arrives — are sorted and taken to the right recycling station or donated to charities such as Myrorna, Erikshjälpen and Stadsmissionen. Nothing is dumped unnecessarily.\n\nIt is smooth sofa transport with purpose.\n\nSofa transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does sofa transport cost?",
          answer:
            "The price depends on the sofa's size, distance and whether stair carrying is needed. A two-seater often comes to SEK 595–995. A large corner sofa or sofa bed can come to SEK 995–1,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on sofa transport?",
          answer:
            "Sofa transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Can you transport a corner sofa?",
          answer:
            "Yes, we transport all types of corner sofas — L-sofas, U-sofas and every other shape. Many corner sofas can be split into sections, which we do on-site. We know how to manoeuvre large sofas through tight stairwells.",
        },
        {
          question: "Can you collect a sofa I bought on Blocket?",
          answer:
            "Yes, enter the seller's address as pickup and your address as delivery. We collect, carry out, load, drive and carry in at your home. Also works with Facebook Marketplace, Tradera and auctions.",
        },
        {
          question: "Do you carry sofas up stairs?",
          answer:
            "Yes, we carry sofas up and down stairs — even in tight stairwells with no lift. We've carried thousands of sofas and know exactly how to manoeuvre through corners, narrow passages and tight doorways.",
        },
        {
          question: "Do you protect the sofa during transport?",
          answer:
            "Yes, we protect every sofa with furniture blankets, carrying straps and ratchet straps. We also protect floors, walls and door frames when carrying. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question: "Can you collect a sofa from a store?",
          answer:
            "Yes, we collect sofas directly from any store — IKEA, Mio, EM, Sofacompany, Furniturebox and all others. We load, drive to your home and carry in. Faster than the store's own delivery.",
        },
        {
          question: "Can you take away the old sofa too?",
          answer:
            "Yes, we can take the old sofa when delivering the new one. We take it to recycling or donate it to charity if it's in good condition. Just let us know when booking.",
        },
        {
          question: "Which areas do you offer sofa transport in?",
          answer:
            "We offer sofa transport throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Sofa transport in 3 easy steps",
        step2Text:
          "Our workers carry the sofa out, protect it with blankets, load the van and drive to the delivery address. There we carry in and place the sofa where you want it.",
        step3Title: "Sofa in place",
        step3Desc:
          "Your sofa is where you want it — protected, intact and delivered. Done.",
        ctaHeading: "Book sofa transport today",
        ctaText:
          "Enter the pickup address and delivery address, describe the sofa and pick a time. You see the price instantly.",
      },
    },
  },

  {
    // Transport Säng — config för /transport-sang
    // Vinkel: Nischad sida specifikt för transport av säng/madrass.
    // Unik vinkel vs transport-mobler: fokus på just sängar – köpta på Blocket,
    // nya sängar från butik, stora kontinentalsängar, madrasser, sängstommar etc.
    slug: "transport-sang",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Säng – Smidig Sängtransport med Skåpbil | Ungdomskraft",
      description:
        "Boka transport av säng med skåpbil och personal. Vi hämtar, bär ut, lastar och kör din säng från A till B – ny, begagnad eller köpt på Blocket. Prisvärt och med unga som gör skillnad.",
      h1: "Transport av säng",
    },

    hero: {
      heading: "Transport av säng",
      intro:
        "Köpt en ny kontinentalsäng? Hittade du drömmadrassen på Blocket? Ska den gamla sängen till stugan eller till en ny ägare? Vi hämtar sängen på upphämtningsadressen, bär ut genom trapphus och trånga passager, skyddar med möbelfiltar, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer sängen på plats. Du slipper hyra bil, ringa kompisar eller slita ryggen.",
      heroImage: "/hero-transport-sang.png",
    },

    featureCards: {
      sinceText: "Sängtransport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft kört tusentals sängtransporter i Stockholms- och Göteborgsområdet. Våra ungdomar vet exakt hur man bär kontinentalsängar i trånga trapphus, vrider sängstommar runt hörn och lastar madrasser säkert i skåpbilen.",
      bookYourText: "Boka din sängtransport",
      bookDescText:
        "Ange upphämtningsadress och leveransadress, beskriv sängen och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för sängtransport",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska köra en madrass eller en hel kontinentalsäng med bäddmadrass och gavlar. Möbelfiltar, bärremmar och spännband ingår alltid.",
    },

    itemsSection: {
      heading: "Sängar vi transporterar",
      items: [
        {
          name: "Kontinentalsängar",
          description:
            "Tunga kontinentalsängar med undre och övre madrass, bäddmadrass och gavlar – vi tar allt i rätt ordning",
          image: "/bed.webp",
        },
        {
          name: "Madrasser",
          description:
            "Skummadrasser, hybridmadrasser och resårmadrasser – vi bär varsamt och skyddar under transport",
          image: "/bed.webp",
        },
        {
          name: "Sängstommar",
          description:
            "IKEA-stommar, ribbottnar, ställbara sängbottnar och träramar – vi demonterar om det behövs",
          image: "/bed.webp",
        },
        {
          name: "Våningssängar",
          description:
            "Våningssängar och loftsängar – vi demonterar, transporterar och monterar på leveransadressen",
          image: "/bed.webp",
        },
        {
          name: "Sänggavlar",
          description:
            "Stoppade gavlar, trägavlar och metallavlar – vi skyddar ytorna under transport",
          image: "/bed.webp",
        },
        {
          name: "Barnsängar",
          description:
            "Spjälsängar, juniorsängar och barnsängar – vi demonterar och monterar ihop hos mottagaren",
          image: "/bed.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport av säng – vi hämtar, bär och kör din säng från A till B",
      mainText:
        "Att transportera en säng är en av de saker som låter enkelt men sällan är det. Kontinentalsängar väger 80–120 kilo, madrasser är stora och ohanterliga, sängstommar behöver demonteras och allt ska genom trapphus, runt hörn och in i en bil som faktiskt rymmer. Med Ungdomskrafts sängtransport slipper du allt det. Vi kommer hem till dig med skåpbil och ett team av starka ungdomar, demonterar sängen om det behövs, bär ut genom trapphus, skyddar med möbelfiltar, lastar och kör till leveransadressen. Där bär vi in och ställer sängen på plats – eller monterar ihop den om du vill. Oavsett om du köpt en säng på Blocket, beställt en ny från butiken, ska flytta sängen till stugan eller skicka den till en ny ägare tar vi hand om hela transporten. Du behöver inte hyra bil, göra flera turer eller be kompisar om hjälp.",
      preparationHeading: "Förbered inför sängtransport",
      preparationText:
        "Ta av allt sänglinne, kuddar och överkast. Om sängen har gavlar – skruva loss dem om du kan, annars gör vi det. Demontera sängstommar (skruvar i en märkt påse) om du vill spara tid. Se till att det finns fri passage i trapphus och hiss – mät gärna dörröppningar om du vet att sängen är stor. Ha portkod och hissnycklar redo på båda adresserna. Om sängen är en kontinentalsäng med separata delar – låt dem ligga på plats, vi bär ut del för del.",
      additionalSections: [
        {
          heading: "Transport av säng köpt på Blocket eller Facebook Marketplace",
          content:
            "Hittat en drömmadrass på Blocket eller en kontinentalsäng på Facebook Marketplace? Vi hämtar sängen hos säljaren, bär ut, skyddar, lastar och kör hem till dig. Du behöver inte hyra bil eller be säljaren vänta – vi sköter allt och du kan vara med vid upphämtningen eller låta oss hantera det helt själva. Många av våra sängtransporter är just Blocket-köp, och vi vet att det kan vara stressigt att koordinera med en säljare. Därför är vi alltid i tid och sköter bärningen snabbt och smidigt.",
        },
        {
          heading: "Kontinentalsäng – så hanterar vi den tunga transporten",
          content:
            "En kontinentalsäng är ett av de tyngsta och mest ohanterliga föremålen att transportera. Undre madrassen, övre madrassen, bäddmadrassen, gavlar och ibland sängben – allt ska ut, ner och in i en skåpbil. Vi har kört hundratals kontinentalsängar och vet exakt hur det ska gå till. Vi bär ut del för del, skyddar varje del med möbelfiltar så att tyget inte repas i trapphuset, lastar smart i skåpbilen så att inget glider och levererar allt till rätt rum på leveransadressen. Behöver du hjälp att ställa ihop sängen på plats? Beskriv det vid bokning så fixar vi det också.",
        },
        {
          heading: "Sängtransport från butik och e-handel",
          content:
            "Beställt en ny säng från IKEA, Mio, JYSK, Sängjätten eller en webbutik men leveransen passar inte? Vi hämtar paketen i butiken eller tar emot dem vid din dörr och bär in till sovrummet. Vi monterar också ihop sängstommar, ställbara sängar och gavlar om du vill. Det fungerar även om du köpt en säng i en fysisk butik och inte får hem den – ange butikens adress som upphämtningsplats vid bokning.",
        },
        {
          heading: "Varför boka sängtransport med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje sängtransport vi genomför. När du bokar sängtransport hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de skyddar madrassen till hur de navigerar runt hörn i trånga trapphus.\n\nSedan starten 2014 har vi kört tusentals sängtransporter i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker hantering av tunga madrasser och ohanterliga sängstommar, hur man manövrerar i trånga trapphus och alltid skyddar kundens ägodelar, golv, väggar och dörrkarmar. De tränas i att lämna allt i gott skick.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team dubbelkollar att allt är rätt lastat, skyddar varje del av sängen med möbelfiltar och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje sängtransport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje sängtransport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport eller serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din sängtransport blir en del av något större.\n\nVi tar också hållbarhet på allvar. Gamla sängar som inte längre behövs hjälper vi att skänka vidare om de är i gott skick, eller köra till rätt återvinningsstation. Madrasser som inte kan återbrukas lämnas till deponi – vi slänger aldrig något på fel ställe.\n\nDet är smidig sängtransport med mening.\n\nSängtransport ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport av säng?",
        answer:
          "Priset beror på typ av säng, avstånd och om bärning i trappor krävs. En enkel madrass landar ofta på 595–995 kr. En hel kontinentalsäng med gavlar kan landa på 995–1 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på sängtransport?",
        answer:
          "Sängtransport ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni transportera en kontinentalsäng?",
        answer:
          "Ja, vi transporterar alla typer av kontinentalsängar – med undre och övre madrass, bäddmadrass och gavlar. Vi bär ut del för del, skyddar med möbelfiltar och lastar säkert i skåpbilen.",
      },
      {
        question: "Ingår skåpbil i sängtransport?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni hämta en säng jag köpt på Blocket?",
        answer:
          "Ja, vi hämtar sängen hos säljaren, bär ut, lastar och kör hem till dig. Vi sköter allt – du behöver bara ange säljarens adress vid bokning. Vi kan även hämta sängar köpta på Facebook Marketplace.",
      },
      {
        question: "Demonterar ni sängar innan transport?",
        answer:
          "Ja, vi demonterar sängstommar, gavlar, våningssängar och spjälsängar som behöver tas isär. Vi monterar även ihop dem på leveransadressen om du vill – beskriv det vid bokning.",
      },
      {
        question: "Skyddar ni sängen under transport?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar madrasser, gavlar och stommar under bärning, lastning och transport. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Kan ni bära sängen uppför trappor?",
        answer:
          "Ja, vi bär sängar i alla typer av trapphus – smala, svängda och utan hiss. Vi vet hur man vrider madrasser och sängstommar runt hörn och genom trånga passager.",
      },
      {
        question: "Kan ni transportera en säng från IKEA eller annan butik?",
        answer:
          "Ja, vi hämtar sängar i butik (IKEA, Mio, JYSK, Sängjätten m.fl.) och kör hem till dig. Vi bär in och monterar ihop om du vill. Ange butikens adress som upphämtningsplats vid bokning.",
      },
      {
        question: "Hur snabbt kan ni köra min säng?",
        answer:
          "Vi erbjuder ofta sängtransport med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni transportera en madrass separat?",
        answer:
          "Ja, vi transporterar enskilda madrasser – skum, hybrid och resår. Vi skyddar madrassen med filtarna och ser till att den inte böjs eller skadas under transporten.",
      },
      {
        question: "Kan ni transportera en ställbar säng?",
        answer:
          "Ja, vi transporterar ställbara sängar och elsängar. Vi hanterar motorn och mekanismen varsamt och ser till att inga kablar eller delar skadas under transporten.",
      },
      {
        question: "Kan ni köra sängtransport på helger?",
        answer:
          "Ja, vi erbjuder sängtransport på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Behöver jag vara hemma vid leveransen av sängen?",
        answer:
          "Ja, vi rekommenderar att du eller någon annan vuxen är hemma så att vi kan ställa sängen på rätt plats. Om du inte kan vara hemma – kontakta oss så löser vi det med portkod och instruktioner.",
      },
      {
        question: "I vilka kommuner erbjuder ni transport av säng?",
        answer:
          "Vi erbjuder transport av säng i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Sängtransport i 3 enkla steg",
      step2Text:
        "Våra ungdomar demonterar vid behov, bär ut sängen, skyddar med möbelfiltar, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer sängen på plats.",
      step3Title: "Sängen på plats",
      step3Desc:
        "Din säng står där du vill ha den – skyddad, hel och levererad. Klart.",
      ctaHeading: "Boka sängtransport idag",
      ctaText:
        "Ange upphämtningsadress och leveransadress, beskriv sängen och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "bed-transport",
      meta: {
        title: "Bed Transport – Easy Bed Delivery with Van & Crew | Ungdomskraft",
        description:
          "Book bed transport with van and crew. We pick up, carry and deliver your bed safely – new, used or from Blocket. Fixed price, insured. Book today!",
        h1: "Bed transport",
      },
      hero: {
        heading: "Bed transport",
        intro:
          "Bought a new bed? Found the perfect mattress on Blocket? Need to move a bed to a new address? We pick up the bed at the collection address, carry it out through stairwells and tight passages, protect it with furniture blankets, load the van and drive to the delivery address. There we carry in and place the bed where you want it. No need to rent a car, call friends or strain your back.",
      },
      featureCards: {
        sinceText: "Bed transport since 2014",
        bookYourText: "Book your bed transport",
        bookDescText:
          "Enter the pickup address and delivery address, describe the bed and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for bed transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're moving a mattress or a full continental bed with headboard. Furniture blankets, straps and tie-downs always included.",
      },
      itemsSection: {
        heading: "Beds we transport",
        items: [
          {
            name: "Continental beds",
            description:
              "Heavy continental beds with base, top mattress, mattress pad and headboard — we handle each piece in order",
            image: "/bed.webp",
          },
          {
            name: "Mattresses",
            description:
              "Foam, hybrid and spring mattresses — we carry carefully and protect during transport",
            image: "/bed.webp",
          },
          {
            name: "Bed frames",
            description:
              "IKEA frames, slatted bases, adjustable bases and wooden frames — we disassemble if needed",
            image: "/bed.webp",
          },
          {
            name: "Bunk beds",
            description:
              "Bunk beds and loft beds — we disassemble, transport and reassemble at the delivery address",
            image: "/bed.webp",
          },
          {
            name: "Headboards",
            description:
              "Upholstered, wooden and metal headboards — we protect surfaces during transport",
            image: "/bed.webp",
          },
          {
            name: "Children's beds",
            description:
              "Cots, junior beds and children's beds — we disassemble and reassemble at the recipient's address",
            image: "/bed.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Bed transport — we pick up, carry and deliver your bed from A to B",
        mainText:
          "Transporting a bed is one of those things that sounds simple but rarely is. Continental beds weigh 80–120 kg, mattresses are bulky and unwieldy, bed frames need disassembling and everything has to go through stairwells, around corners and into a vehicle that actually fits it all. With Ungdomskraft's bed transport you skip all of that. We come to your address with a van and a team of strong young workers, disassemble the bed if needed, carry it out through stairwells, protect with furniture blankets, load and drive to the delivery address. There we carry in and place the bed where you want it — or reassemble it if you like. Whether you bought a bed on Blocket, ordered a new one from a store, are moving a bed to a holiday home or sending it to a new owner, we handle the entire transport. No need to rent a car, make multiple trips or ask friends for help.",
        preparationHeading: "Prepare for bed transport",
        preparationText:
          "Remove all bedding, pillows and covers. If the bed has a headboard — unscrew it if you can, otherwise we will do it. Disassemble bed frames (screws in a labelled bag) if you want to save time. Make sure there is clear passage in stairwells and lifts — measure doorways if you know the bed is large. Have entry codes and lift keys ready at both addresses. If it's a continental bed with separate parts — leave them in place, we carry out piece by piece.",
        additionalSections: [
          {
            heading: "Bed transport for Blocket and Facebook Marketplace purchases",
            content:
              "Found the perfect mattress on Blocket or a continental bed on Facebook Marketplace? We collect the bed from the seller, carry out, protect, load and drive it to your home. You don't need to rent a car or ask the seller to wait — we handle everything. Many of our bed transports are exactly these kinds of purchases, and we know coordinating with a seller can be stressful. That's why we're always on time and handle the carrying quickly and smoothly.",
          },
          {
            heading: "Continental beds — how we handle the heavy transport",
            content:
              "A continental bed is one of the heaviest and most unwieldy items to transport. Base mattress, top mattress, mattress pad, headboard and sometimes legs — everything needs to come out, down and into a van. We've moved hundreds of continental beds and know exactly how it's done. We carry out piece by piece, protect each part with furniture blankets so fabric isn't scratched in the stairwell, load smartly in the van so nothing slides and deliver everything to the right room at the delivery address. Need help setting up the bed? Describe it when booking and we'll take care of that too.",
          },
          {
            heading: "Bed transport from stores and e-commerce",
            content:
              "Ordered a new bed from IKEA, Mio, JYSK or an online retailer but the delivery doesn't suit you? We collect the packages from the store or receive them at your door and carry them to the bedroom. We also assemble bed frames, adjustable beds and headboards if you like. This works even if you bought a bed in a physical store and can't get it delivered — enter the store address as the pickup location when booking.",
          },
          {
            heading: "Why book bed transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and you notice it in every bed transport we carry out. When you book bed transport with us, you get a team of motivated, energetic young people who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that attitude permeates everything — from how they protect the mattress to how they navigate around corners in tight stairwells.\n\nSince 2014 we have carried out thousands of bed transports in the Stockholm and Gothenburg areas. Along the way we have given thousands of young people their first real work experience — with everything that entails: showing up on time, communicating professionally with customers, solving problems on site, working as a team and taking responsibility for the result. Our workers learn lifting technique, safe handling of heavy mattresses and unwieldy bed frames, how to manoeuvre in tight stairwells and always protect the customer's belongings, floors, walls and door frames. They are trained to leave everything in good condition.\n\nThis is not only good for the young people — it's good for you as a customer. You notice the difference in commitment and energy. Our teams double-check that everything is loaded correctly, protect every part of the bed with furniture blankets and always ask if there is anything else they can help with. We have built a culture where every bed transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we've received on Trustpilot and Google.\n\nBut it's not just about quality — it's about purpose. Every bed transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport or service industries — carrying with them the values and work ethic they learned with us. Choosing us means your bed transport becomes part of something bigger.\n\nWe also take sustainability seriously. Old beds that are no longer needed — we help donate them if they're in good condition, or drive them to the correct recycling station. Mattresses that cannot be reused are taken to proper disposal. We never dump anything in the wrong place.\n\nIt's smooth bed transport with purpose.\n\nBed transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does bed transport cost?",
          answer:
            "The price depends on the type of bed, distance and whether stair carrying is needed. A single mattress often costs SEK 595–995. A full continental bed with headboard can cost SEK 995–1,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on bed transport?",
          answer:
            "Bed transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Can you transport a continental bed?",
          answer:
            "Yes, we transport all types of continental beds — with base mattress, top mattress, mattress pad and headboard. We carry out piece by piece, protect with furniture blankets and load safely in the van.",
        },
        {
          question: "Is a van included in bed transport?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting — no need to rent a vehicle, drive or refuel.",
        },
        {
          question: "Can you pick up a bed I bought on Blocket?",
          answer:
            "Yes, we collect the bed from the seller, carry out, load and deliver to your home. We handle everything — just enter the seller's address when booking. We also pick up beds from Facebook Marketplace.",
        },
        {
          question: "Do you disassemble beds before transport?",
          answer:
            "Yes, we disassemble bed frames, headboards, bunk beds and cots that need to be taken apart. We also reassemble them at the delivery address if you like — describe it when booking.",
        },
        {
          question: "Do you protect the bed during transport?",
          answer:
            "Yes, we use furniture blankets, carrying straps and tie-downs. We protect mattresses, headboards and frames during carrying, loading and transport. Everything is insured — if anything is damaged we replace it.",
        },
        {
          question: "Can you carry the bed upstairs?",
          answer:
            "Yes, we carry beds in all types of stairwells — narrow, winding and without a lift. We know how to turn mattresses and bed frames around corners and through tight passages.",
        },
        {
          question: "In which municipalities do you offer bed transport?",
          answer:
            "We offer bed transport across the greater Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Bed transport in 3 easy steps",
        step2Text:
          "Our workers disassemble if needed, carry the bed out, protect it with blankets, load the van and drive to the delivery address. There we carry in and place the bed where you want it.",
        step3Title: "Bed in place",
        step3Desc:
          "Your bed is where you want it — protected, intact and delivered. Done.",
        ctaHeading: "Book bed transport today",
        ctaText:
          "Enter the pickup address and delivery address, describe the bed and pick a time. You see the price instantly.",
      },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Transport vitvaror
  // Vinkel: Flytta/transportera vitvaror från punkt A till punkt B.
  // Unik vinkel vs bortforsling-vitvaror: här SKA vitvarorna BEVARAS och levereras
  // till ny adress (Blocket-köp, butikshämtning, flytt mellan bostäder).
  // bortforsling-vitvaror = bli av med gamla vitvaror → återvinning.
  // ──────────────────────────────────────────────────────────────────
  {
    slug: "transport-vitvaror",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Vitvaror – Hämtning & Leverans av Vitvaror | Ungdomskraft",
      description:
        "Boka transport av vitvaror – kylskåp, tvättmaskin, spis och diskmaskin. Vi hämtar, bär och levererar. Försäkrat och med fast pris. Boka idag!",
      h1: "Transport vitvaror",
    },

    hero: {
      heading: "Transport av vitvaror",
      intro:
        "Köpt en tvättmaskin på Blocket? Ska kylskåpet med i flytten? Eller behöver du hämta en ny diskmaskin från butiken? Vi hämtar vitvaror var de än står, bär ut, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer på plats. Du slipper hyra bil, lyfta tungt eller oroa dig för trappor och trånga dörrar.",
      heroImage: "/hero-transport-vitvaror.png",
    },

    featureCards: {
      sinceText: "Transport av vitvaror sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft transporterat tusentals vitvaror i Stockholms- och Göteborgsområdet – tvättmaskiner, kylskåp, spisar och diskmaskiner. Våra ungdomar vet hur man hanterar tunga, skrymmande vitvaror i trappor, hissar och trånga kök.",
      bookYourText: "Boka transport av vitvaror",
      bookDescText:
        "Ange hämtadress och leveransadress, beskriv vilka vitvaror som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar anpassade för vitvarutransport",
      vehicleDescText:
        "Vi kommer med skåpbil i rätt storlek och utrustning – säckkärra, bärremmar och möbelfiltar. Oavsett om det är en enda tvättmaskin eller ett helt kök med vitvaror.",
    },

    itemsSection: {
      heading: "Vitvaror vi transporterar",
      items: [
        {
          name: "Tvättmaskin & torktumlare",
          description:
            "Vi hämtar och levererar tvättmaskiner och torktumlare – även i trånga tvättstugor och källare",
          image: "/washer.webp",
        },
        {
          name: "Kylskåp & frys",
          description:
            "Kyl, frys och kyl-frys-kombiskåp – vi bär säkert i trappor och runt hörn",
          image: "/fridge.webp",
        },
        {
          name: "Diskmaskin",
          description:
            "Fristående och inbyggda diskmaskiner – vi hanterar anslutningar och vinklar",
          image: "/dishwasher.webp",
        },
        {
          name: "Spis & ugn",
          description:
            "Gasspis, induktionshäll, ugn och fristående spis – tunga men vi fixar det",
          image: "/stove.webp",
        },
        {
          name: "Mikrovågsugn & småapparater",
          description:
            "Mikro, kaffemaskiner, brödrostar och andra köksmaskiner",
          image: "/microwave.webp",
        },
        {
          name: "Luftkonditionering",
          description:
            "Portabla AC-enheter och fönstermonterade luftkonditioneringar",
          image: "/air-conditioner.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport av vitvaror – vi hämtar, kör och bär in åt dig",
      mainText:
        "Vitvaror är tunga, skrymmande och svåra att flytta själv. En tvättmaskin väger 70–90 kilo, ett kylskåp ännu mer – och båda ska genom trånga dörröppningar, runt hörn och uppför trappor utan att skada golv eller väggar. Med Ungdomskrafts vitvarutransport slipper du allt det. Vi kommer med skåpbil, säckkärra och ett team av starka ungdomar som vet hur man hanterar vitvaror. Vi hämtar vitvarorna där de står – hemma hos en Blocket-säljare, på en butiks utlämning eller i din gamla lägenhet. Vi kopplar loss om det behövs, bär ut, skyddar med filtar och remmar, lastar i skåpbilen och kör till leveransadressen. Där bär vi in och ställer vitvarorna på plats – i köket, tvättstugan, källaren eller var du vill ha dem. Behöver du hjälp med inkoppling? Säg till vid bokning så fixar vi det också. Vi transporterar alla typer av vitvaror: tvättmaskiner, torktumlare, kylskåp, frysar, diskmaskiner, spisar, ugnar, mikrovågsugnar och luftkonditioneringar. Oavsett om du köpt begagnat på Blocket, beställt från Elgiganten eller ska flytta vitvarorna till din nya bostad – vi hämtar och levererar.",
      preparationHeading: "Förbered inför vitvarutransport",
      preparationText:
        "Töm kylskåp och frys minst 24 timmar innan transport så att de hinner tina av. Stäng av vatten och el till tvättmaskin och diskmaskin. Töm tvättmaskinens filter och slangen på vatten. Se till att det finns fri passage genom dörrar, trappor och korridorer – mät gärna dörrbredden om du vet att det är trångt. Ha adress och portkod redo för både hämtning och leverans. Om vitvarorna ska kopplas in på leveransadressen – se till att vatten- och elanslutningar finns på plats.",
      additionalSections: [
        {
          heading: "Hämta vitvaror från Blocket, Facebook och butiker",
          content:
            "Hittat en tvättmaskin på Blocket? Köpt ett kylskåp på Facebook Marketplace? Eller beställt en diskmaskin från Elgiganten som du behöver hämta? Vi kör och hämtar vitvarorna åt dig, oavsett var de står. Ange säljarens adress som hämtpunkt och din hemadress som leveransadress – vi sköter resten. Vi bär ut ur säljarens bostad, lastar, kör och bär in hos dig. Perfekt när du handlar begagnat och inte har tillgång till bil eller skåpbil, eller när du inte orkar bära 80 kilo i trappan.",
        },
        {
          heading: "Transport av vitvaror vid flytt",
          content:
            "Ska vitvarorna med i flytten? Vi hämtar tvättmaskin, kylskåp, frys, diskmaskin och spis från din gamla bostad, kopplar loss, bär ut, skyddar och transporterar till din nya adress. Där bär vi in och ställer allt på plats. Vitvaror kräver extra omsorg vid flytt – de är tunga, har känslig teknik och måste transporteras upprätt (speciellt kylskåp och frysar). Våra ungdomar är tränade i att hantera vitvaror säkert och vet hur man undviker skador på både produkten och fastigheten. Kombinera gärna med vår flytthjälp om du har fler saker som ska med.",
        },
        {
          heading: "Så hanterar vi tunga vitvaror säkert",
          listItems: [
            {
              label: "Säckkärra & bärremmar",
              text: "Vi har professionella säckkärror och bärremmar som fördelar vikten jämnt – säkert i trappor och hissar.",
            },
            {
              label: "Möbelfiltar & skydd",
              text: "Vitvarorna lindas i skyddsfiltar så att varken produkten eller dina väggar, golv och dörrkarmar skadas.",
            },
            {
              label: "Upprätt transport",
              text: "Kylskåp och frysar transporteras alltid stående för att skydda kompressorn och köldmediet.",
            },
            {
              label: "Lossning & inkoppling",
              text: "Vi kopplar loss vatten och el vid hämtning och kan koppla in vid leverans om du vill.",
            },
            {
              label: "Försäkring",
              text: "Alla transporter är försäkrade. Skulle något gå sönder ersätter vi det.",
            },
          ],
          afterContent:
            "Vi har transporterat tusentals vitvaror sedan 2014 – vi vet hur man gör det rätt.",
        },
        {
          heading: "Varför boka transport av vitvaror med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje vitvarutransport vi genomför. När du bokar transport av vitvaror hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de bär din tvättmaskin i trappan till hur de ställer kylskåpet exakt där du vill ha det i köket.\n\nSedan starten 2014 har vi transporterat tusentals vitvaror i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker hantering av tunga vitvaror, hur man navigerar trånga trapphus och hur man skyddar både produkten och fastigheten. De tränas i att använda säckkärra, bärremmar och skyddsfiltar korrekt – och i att alltid lämna allt i gott skick.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team tar sig tid att göra rätt, dubbelkollar att vitvarorna står stabilt och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje vitvarutransport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nVarje vitvarutransport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport, installation och serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din vitvarutransport blir en del av något större.\n\nVi tar också hållbarhet på allvar. Förpackningsmaterial återvinns, och om du samtidigt vill bli av med en gammal vitvara som ersätts kan vi köra den till godkänd återvinningsstation så att köldmedier och komponenter tas om hand korrekt. Ingenting slängs i onödan.\n\nDet är smidig vitvarutransport med mening.\n\nTransport av vitvaror ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport av vitvaror?",
        answer:
          "Priset beror på antal vitvaror, avstånd och om bärning i trappor krävs. En enstaka tvättmaskin eller diskmaskin landar ofta på 595–995 kr. Flera vitvaror – till exempel kylskåp, frys och tvättmaskin – kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport av vitvaror?",
        answer:
          "Transport av vitvaror ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni hämta vitvaror jag köpt på Blocket?",
        answer:
          "Ja, vi hämtar vitvaror från Blocket-säljare i hela Stockholms- och Göteborgsområdet. Ange säljarens adress som hämtpunkt och din hemadress som leveransadress. Vi bär ut, lastar, kör och bär in hos dig.",
      },
      {
        question: "Transporterar ni kylskåp stående?",
        answer:
          "Ja, kylskåp och frysar transporteras alltid upprätt för att skydda kompressorn och köldmediet. Vi säkrar dem med remmar i skåpbilen så att de inte kan välta under transporten.",
      },
      {
        question: "Kopplar ni loss och in vitvaror?",
        answer:
          "Ja, vi kopplar loss vatten- och elanslutningar vid hämtning. Vid leverans kan vi koppla in vitvarorna om anslutningar finns på plats. Säg till vid bokning om du vill ha hjälp med inkoppling.",
      },
      {
        question: "Kan ni bära vitvaror i trappor utan hiss?",
        answer:
          "Ja, vi bär vitvaror i alla typer av trapphus – med eller utan hiss. Vi har säckkärra, bärremmar och rätt teknik för att hantera tunga vitvaror säkert i trappor. Ange antal trappor vid bokning.",
      },
      {
        question: "Ingår skåpbil i transport av vitvaror?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån antal och typ av vitvaror – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni transportera vitvaror vid flytt?",
        answer:
          "Ja, vi hämtar tvättmaskin, kylskåp, frys, diskmaskin och spis från din gamla bostad och levererar till den nya. Vi kopplar loss, bär ut, transporterar och bär in. Kombinera gärna med vår flytthjälp om du har fler saker.",
      },
      {
        question: "Skyddar ni vitvarorna under transport?",
        answer:
          "Ja, vi lindar vitvarorna i skyddsfiltar och säkrar med remmar och spännband. Kylskåp och frysar transporteras stående. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Kan ni transportera en spis eller ugn?",
        answer:
          "Ja, vi transporterar alla typer av spisar och ugnar – gasspis, induktionshäll, inbyggnadsugn och fristående spis. Vi kopplar loss, bär ut och levererar till ny adress.",
      },
      {
        question: "Hur snabbt kan ni köra vitvaror?",
        answer:
          "Vi erbjuder ofta vitvarutransport med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni hämta vitvaror från butik eller utlämningsställe?",
        answer:
          "Ja, vi hämtar vitvaror från Elgiganten, Power, MediaMarkt, IKEA och alla andra butiker och utlämningsställen. Ange butikens adress vid bokning.",
      },
      {
        question: "Kan ni transportera vitvaror på helger?",
        answer:
          "Ja, vi erbjuder vitvarutransport på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Måste kylskåpet tinas av innan transport?",
        answer:
          "Ja, vi rekommenderar att du tinar av kylskåp och frys minst 24 timmar innan transport. Det förhindrar vattenläckage under transporten och skyddar kompressorn. Töm alla matvaror i förväg.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni transport av vitvaror?",
        answer:
          "Vi erbjuder transport av vitvaror i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transport av vitvaror i 3 enkla steg",
      step2Text:
        "Våra ungdomar kopplar loss, bär ut och lastar vitvarorna i skåpbilen. Vi kör till leveransadressen, bär in och ställer på plats.",
      step3Title: "Vitvarorna på plats",
      step3Desc:
        "Dina vitvaror står där du vill ha dem – säkert levererade och redo att användas. Klart.",
      ctaHeading: "Boka transport av vitvaror idag",
      ctaText:
        "Ange hämtadress och leveransadress, beskriv vilka vitvaror som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "appliance-transport",
      meta: {
        title:
          "Appliance Transport – Pickup & Delivery of Appliances | Ungdomskraft",
        description:
          "Book appliance transport – fridges, washing machines, cookers and dishwashers. We pick up, carry and deliver with care. Fixed price, insured. Book today!",
        h1: "Appliance transport",
      },
      hero: {
        heading: "Appliance transport",
        intro:
          "Bought a washing machine on Blocket? Taking the fridge with you to your new home? Or need to collect a new dishwasher from the store? We pick up appliances wherever they are, carry them out, load the van and drive to the delivery address. There we carry in and place everything where you want it. No need to rent a car, lift heavy objects or worry about stairs and narrow doorways.",
      },
      featureCards: {
        sinceText: "Appliance transport since 2014",
        bookYourText: "Book your appliance transport",
        bookDescText:
          "Enter the pickup address and delivery address, describe which appliances need transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans equipped for appliance transport",
        vehicleDescText:
          "We arrive with the right size van and equipment — hand trucks, carrying straps and furniture blankets. Whether it's a single washing machine or a full kitchen's worth of appliances.",
      },
      itemsSection: {
        heading: "Appliances we transport",
        items: [
          {
            name: "Washing machine & dryer",
            description:
              "We pick up and deliver washers and dryers — even in tight laundry rooms and basements",
            image: "/washer.webp",
          },
          {
            name: "Fridge & freezer",
            description:
              "Fridges, freezers and fridge-freezer combos — carried safely through stairs and round corners",
            image: "/fridge.webp",
          },
          {
            name: "Dishwasher",
            description:
              "Freestanding and integrated dishwashers — we handle connections and tight angles",
            image: "/dishwasher.webp",
          },
          {
            name: "Cooker & oven",
            description:
              "Gas cookers, induction hobs, built-in ovens and freestanding cookers — heavy but we handle it",
            image: "/stove.webp",
          },
          {
            name: "Microwave & small appliances",
            description:
              "Microwaves, coffee machines, toasters and other kitchen appliances",
            image: "/microwave.webp",
          },
          {
            name: "Air conditioning",
            description:
              "Portable AC units and window-mounted air conditioners",
            image: "/air-conditioner.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Appliance transport — we pick up, drive and carry in for you",
        mainText:
          "Appliances are heavy, bulky and difficult to move on your own. A washing machine weighs 70–90 kg, a fridge even more — and both need to go through narrow doorways, round corners and up stairs without damaging floors or walls. With Ungdomskraft's appliance transport you skip all of that. We arrive with a van, hand truck and a team of strong young workers who know how to handle appliances. We collect the appliances wherever they are — from a Blocket seller's home, a store collection point or your old apartment. We disconnect if needed, carry out, wrap in protective blankets and straps, load the van and drive to the delivery address. There we carry in and place the appliances where you want them — in the kitchen, laundry room, basement or wherever. Need help connecting them? Let us know when booking and we'll take care of that too. We transport all types of appliances: washing machines, dryers, fridges, freezers, dishwashers, cookers, ovens, microwaves and air conditioners. Whether you've bought second-hand on Blocket, ordered from a store or are moving appliances to your new home — we pick up and deliver.",
        preparationHeading: "Preparing for appliance transport",
        preparationText:
          "Empty and defrost fridges and freezers at least 24 hours before transport. Turn off water and electricity to washing machines and dishwashers. Empty the washing machine filter and drain the hose. Make sure there is clear access through doors, stairs and corridors — measure doorway widths if space is tight. Have the address and door code ready for both pickup and delivery. If appliances need connecting at the delivery address, make sure water and electrical connections are in place.",
        additionalSections: [
          {
            heading:
              "Collecting appliances from Blocket, Facebook and stores",
            content:
              "Found a washing machine on Blocket? Bought a fridge on Facebook Marketplace? Or ordered a dishwasher from Elgiganten that you need collecting? We drive and pick up the appliances for you, wherever they are. Enter the seller's address as the pickup point and your home address as the delivery address — we take care of the rest. We carry out of the seller's home, load, drive and carry in at yours. Perfect when you're buying second-hand and don't have access to a car or van, or when you'd rather not carry 80 kg up the stairs.",
          },
          {
            heading: "Appliance transport when moving",
            content:
              "Taking the appliances with you to your new home? We collect washing machines, fridges, freezers, dishwashers and cookers from your old home, disconnect, carry out, protect and transport to your new address. There we carry in and place everything. Appliances need extra care during a move — they're heavy, have sensitive components and must be transported upright (especially fridges and freezers). Our young workers are trained to handle appliances safely and know how to avoid damage to both the product and the property. Feel free to combine with our moving help if you have more items to move.",
          },
          {
            heading: "How we handle heavy appliances safely",
            listItems: [
              {
                label: "Hand truck & straps",
                text: "We use professional hand trucks and carrying straps that distribute weight evenly — safe on stairs and in lifts.",
              },
              {
                label: "Protective blankets",
                text: "Appliances are wrapped in protective blankets so neither the product nor your walls, floors and door frames get damaged.",
              },
              {
                label: "Upright transport",
                text: "Fridges and freezers are always transported standing up to protect the compressor and refrigerant.",
              },
              {
                label: "Disconnection & connection",
                text: "We disconnect water and electricity at pickup and can connect at delivery if you want.",
              },
              {
                label: "Insurance",
                text: "Every transport is insured. If anything gets damaged, we compensate for it.",
              },
            ],
            afterContent:
              "We have transported thousands of appliances since 2014 — we know how to do it right.",
          },
          {
            heading:
              "Why book appliance transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every appliance transport we carry out. When you book appliance transport with us, you get a team of motivated, energetic young workers who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that mindset runs through everything — from how they carry your washing machine down the stairs to how they place the fridge exactly where you want it in the kitchen.\n\nSince our founding in 2014, we have transported thousands of appliances across the Stockholm and Gothenburg regions. Along the way, we have given thousands of young people their first real work experience — with everything that entails: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn lifting techniques, safe handling of heavy appliances, how to navigate narrow stairwells and how to protect both the product and the property. They are trained to use hand trucks, carrying straps and protective blankets correctly — and to always leave everything in good condition.\n\nThis is not just good for the young people — it is good for you as a customer. You notice the difference in commitment and energy. Our teams take the time to do it right, double-check that appliances are stable and always ask if there is anything else they can help with. We have built a culture where every appliance transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nEvery appliance transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport, installation and service industries — carrying with them the values and work ethic they learned with us. Choosing us means your appliance transport becomes part of something bigger.\n\nWe also take sustainability seriously. Packaging materials are recycled, and if you also want to get rid of an old appliance being replaced, we can take it to an approved recycling station so that refrigerants and components are handled correctly. Nothing is dumped unnecessarily.\n\nIt is smooth appliance transport with purpose.\n\nAppliance transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does appliance transport cost?",
          answer:
            "The price depends on the number of appliances, distance and whether stair carrying is needed. A single washing machine or dishwasher often comes to SEK 595–995. Several appliances — for example a fridge, freezer and washing machine — can come to SEK 1,495–2,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on appliance transport?",
          answer:
            "Appliance transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question:
            "Can you collect appliances I've bought on Blocket?",
          answer:
            "Yes, we collect appliances from Blocket sellers across the Stockholm and Gothenburg regions. Enter the seller's address as the pickup point and your home address as the delivery address. We carry out, load, drive and carry in at yours.",
        },
        {
          question: "Do you transport fridges upright?",
          answer:
            "Yes, fridges and freezers are always transported standing up to protect the compressor and refrigerant. We secure them with straps in the van so they cannot topple during transport.",
        },
        {
          question:
            "Do you disconnect and connect appliances?",
          answer:
            "Yes, we disconnect water and electrical connections at pickup. At delivery we can connect appliances if the connections are in place. Let us know when booking if you want help with connection.",
        },
        {
          question:
            "Can you carry appliances up stairs without a lift?",
          answer:
            "Yes, we carry appliances in all types of stairwells — with or without a lift. We have hand trucks, carrying straps and the right technique to handle heavy appliances safely on stairs. State the number of floors when booking.",
        },
        {
          question: "Is a van included in appliance transport?",
          answer:
            "Yes, a van is always included. We choose the right size based on the number and type of appliances — you don't need to rent a car, drive or refuel.",
        },
        {
          question:
            "Can you transport appliances when I'm moving?",
          answer:
            "Yes, we collect washing machines, fridges, freezers, dishwashers and cookers from your old home and deliver to the new one. We disconnect, carry out, transport and carry in. Feel free to combine with our moving help if you have more items.",
        },
        {
          question:
            "Do you protect appliances during transport?",
          answer:
            "Yes, we wrap appliances in protective blankets and secure with straps and ratchets. Fridges and freezers are transported upright. Everything is insured — if anything gets damaged, we compensate for it.",
        },
        {
          question: "Can you transport a cooker or oven?",
          answer:
            "Yes, we transport all types of cookers and ovens — gas, induction, built-in and freestanding. We disconnect, carry out and deliver to the new address.",
        },
        {
          question: "How quickly can you transport appliances?",
          answer:
            "We often offer appliance transport at short notice — sometimes the same week. Book online and you'll see available times straight away.",
        },
        {
          question:
            "Can you collect appliances from a store or collection point?",
          answer:
            "Yes, we collect appliances from Elgiganten, Power, MediaMarkt, IKEA and all other stores and collection points. Enter the store address when booking.",
        },
        {
          question:
            "Can you transport appliances on weekends?",
          answer:
            "Yes, we offer appliance transport on weekends and some evenings. Book online and you'll see all available slots.",
        },
        {
          question:
            "Does the fridge need defrosting before transport?",
          answer:
            "Yes, we recommend defrosting fridges and freezers at least 24 hours before transport. This prevents water leakage during transport and protects the compressor. Empty all food in advance.",
        },
        {
          question:
            "Which areas do you offer appliance transport in?",
          answer:
            "We offer appliance transport throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Appliance transport in 3 easy steps",
        step2Text:
          "Our young workers disconnect, carry out and load the appliances into the van. We drive to the delivery address, carry in and place everything where you want it.",
        step3Title: "Appliances in place",
        step3Desc:
          "Your appliances are where you want them — safely delivered and ready to use. Done.",
        ctaHeading: "Book appliance transport today",
        ctaText:
          "Enter the pickup address and delivery address, describe which appliances need transporting and pick a time. You see the price instantly.",
      },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Transport diskmaskin
  // Vinkel: Nischad sida specifikt för transport av diskmaskin.
  // Unik vinkel vs transport-vitvaror: fokus på just diskmaskiner – köpta på
  // Blocket, nya från butik, flytt till ny bostad, byte av gammal diskmaskin.
  // Inkluderar lossning/inkoppling av vatten- och elanslutningar.
  // ──────────────────────────────────────────────────────────────────
  {
    slug: "transport-diskmaskin",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Diskmaskin – Hämtning & Leverans av Diskmaskin | Ungdomskraft",
      description:
        "Boka transport av diskmaskin. Vi hämtar, kopplar loss, bär ut och levererar din diskmaskin. Perfekt vid Blocket-köp, flytt eller butikshämtning. Fast pris.",
      h1: "Transport diskmaskin",
    },

    hero: {
      heading: "Transport av diskmaskin",
      intro:
        "Köpt en diskmaskin på Blocket? Ska den med i flytten? Eller behöver du hämta en ny från Elgiganten eller IKEA? Vi hämtar diskmaskinen var den än står, kopplar loss vatten och el, bär ut, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer på plats – och kopplar in om du vill. Du slipper hyra bil, lyfta 40–50 kilo själv eller krångla med vattenanslutningar.",
      heroImage: "/hero-transport-vitvaror.png",
    },

    featureCards: {
      sinceText: "Diskmaskinstransport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft transporterat tusentals diskmaskiner i Stockholms- och Göteborgsområdet. Våra ungdomar vet hur man hanterar diskmaskiner – lossning av vattenanslutningar, bärning genom trånga kök och inkoppling på leveransadressen.",
      bookYourText: "Boka transport av diskmaskin",
      bookDescText:
        "Ange hämtadress och leveransadress, beskriv vilken typ av diskmaskin och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbil anpassad för diskmaskinstransport",
      vehicleDescText:
        "Vi kommer med skåpbil och rätt utrustning – säckkärra, bärremmar och skyddsfiltar. Diskmaskinen säkras ordentligt under hela transporten.",
    },

    itemsSection: {
      heading: "Diskmaskiner vi transporterar",
      items: [
        {
          name: "Fristående diskmaskin",
          description:
            "Standard diskmaskin i fullstorlek (60 cm) – vi hanterar vatten- och elanslutningar",
          image: "/dishwasher.webp",
        },
        {
          name: "Inbyggd diskmaskin",
          description:
            "Integrerade diskmaskiner med dekorfront – vi hanterar infästningar och anslutningar",
          image: "/dishwasher.webp",
        },
        {
          name: "Smal diskmaskin (45 cm)",
          description:
            "Kompakta diskmaskiner för mindre kök – lättare att bära men kräver samma omsorg",
          image: "/dishwasher.webp",
        },
        {
          name: "Bänkdiskmaskin",
          description:
            "Liten diskmaskin som står på bänken – lätt att transportera men kräver varsam hantering",
          image: "/dishwasher.webp",
        },
        {
          name: "Industriell diskmaskin",
          description:
            "Större diskmaskiner för restaurang, café eller kontor – tyngre och kräver extra personal",
          image: "/dishwasher.webp",
        },
        {
          name: "Diskmaskin + andra vitvaror",
          description:
            "Kombinera diskmaskinstransport med kylskåp, tvättmaskin eller spis i samma körning",
          image: "/washer.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport av diskmaskin – vi hämtar, kör och bär in åt dig",
      mainText:
        "En diskmaskin väger 40–50 kilo, har känsliga vattenanslutningar och ska ofta manövreras genom trånga köksdörrar och runt hörn. Att flytta den själv innebär risk för vattenskador, ryggproblem och repade golv. Med Ungdomskrafts diskmaskinstransport slipper du allt det. Vi kommer med skåpbil och ett team av ungdomar som vet hur man hanterar diskmaskiner. Vi kopplar loss vatten och el på hämtadressen, bär ut diskmaskinen, skyddar den med filtar, lastar i skåpbilen och kör till leveransadressen. Där bär vi in och ställer diskmaskinen på plats i köket. Vill du ha hjälp med inkoppling? Säg till vid bokning – vi kopplar in vatten och el så att du kan starta första disken direkt. Vi hämtar diskmaskiner överallt: hemma hos en Blocket-säljare, från Elgiganten, Power, IKEA eller MediaMarkt, från din gamla lägenhet vid flytt, eller från ett lager. Oavsett om det är en fristående fullstor diskmaskin, en smal 45 cm-modell, en inbyggd diskmaskin med dekorfront eller en liten bänkdiskmaskin – vi transporterar den säkert.",
      preparationHeading: "Förbered inför diskmaskinstransport",
      preparationText:
        "Stäng av vattenkranen till diskmaskinen och dra ur elkontakten. Kör gärna ett tomt program dagen innan så att maskinen töms på vatten. Lossa avloppsslangen från diskbänksavloppet om du kan – annars fixar vi det. Se till att det finns fri passage genom köksdörren och ut till trappan eller hissen. Mät dörrbredden om du misstänker att det kan vara trångt – en standard diskmaskin är 60 cm bred. Ha portkod och adress redo för både hämtning och leverans. Om diskmaskinen ska kopplas in på leveransadressen – kontrollera att vatten, avlopp och el finns på plats.",
      additionalSections: [
        {
          heading: "Hämta diskmaskin från Blocket eller butik",
          content:
            "Hittat en bra diskmaskin på Blocket? Beställt en ny från Elgiganten som du behöver hämta? Vi kör till säljarens adress eller butikens utlämning, hämtar diskmaskinen och levererar hem till dig. Vi bär ut ur säljarens bostad, lastar, kör och bär in i ditt kök. Du behöver inte hyra bil, be en kompis om hjälp eller bära 50 kilo i trappan själv. Perfekt vid Blocket-köp, Facebook Marketplace-fynd eller butikshämtning från Elgiganten, Power, IKEA, Miele eller vilken butik som helst.",
        },
        {
          heading: "Byta diskmaskin – vi hämtar den nya och tar den gamla",
          content:
            "Ska du byta ut din gamla diskmaskin? Vi kan hämta den nya diskmaskinen från butik eller säljare, köra hem till dig, bära in i köket och koppla in. Samtidigt kopplar vi loss den gamla diskmaskinen och tar med den ut. Vill du bli av med den helt? Vi kan köra den till godkänd återvinningsstation så att allt tas om hand korrekt. Du slipper två separata bokningar – allt sker i samma körning.",
        },
        {
          heading: "Transport av diskmaskin vid flytt",
          content:
            "Ska diskmaskinen med i flytten? Vi kopplar loss vatten och el, lossa avloppsslangen, bär ut diskmaskinen ur ditt gamla kök, skyddar den med filtar och lastar i skåpbilen. På leveransadressen bär vi in och ställer diskmaskinen på plats – och kopplar in om du vill. Diskmaskiner kräver extra omsorg vid flytt: vattenanslutningar måste stängas ordentligt så att inget läcker, och maskinen ska transporteras stående för att undvika att vatten rinner ut i elektroniken. Våra ungdomar är tränade i att hantera diskmaskiner säkert. Kombinera gärna med vår flytthjälp om du har fler saker som ska med.",
        },
        {
          heading: "Varför boka transport av diskmaskin med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje diskmaskinstransport vi genomför. När du bokar transport av diskmaskin hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de kopplar loss vattenanslutningen till hur de bär diskmaskinen genom ditt trånga kök utan att repa golvet.\n\nSedan starten 2014 har vi transporterat tusentals diskmaskiner och andra vitvaror i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, hur vatten- och elanslutningar fungerar, hur man navigerar trånga kök och trapphus och hur man skyddar både produkten och fastigheten. De tränas i att använda säckkärra, bärremmar och skyddsfiltar korrekt – och i att alltid lämna allt i gott skick.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team tar sig tid att göra rätt, dubbelkollar att diskmaskinen står stabilt och att alla anslutningar sitter som de ska. Vi har byggt en kultur där varje transport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nVarje diskmaskinstransport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport, installation och serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din diskmaskinstransport blir en del av något större.\n\nVi tar också hållbarhet på allvar. Förpackningsmaterial återvinns, och om du vill bli av med din gamla diskmaskin kan vi köra den till godkänd återvinningsstation så att komponenter och material tas om hand korrekt. Ingenting slängs i onödan.\n\nDet är smidig diskmaskinstransport med mening.\n\nTransport av diskmaskin ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport av diskmaskin?",
        answer:
          "Priset beror på avstånd och om bärning i trappor krävs. En enstaka diskmaskin landar ofta på 595–995 kr. Ska den kombineras med andra vitvaror blir det mer prisvärt per enhet. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport av diskmaskin?",
        answer:
          "Transport av diskmaskin ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni hämta en diskmaskin jag köpt på Blocket?",
        answer:
          "Ja, vi hämtar diskmaskiner från Blocket-säljare i hela Stockholms- och Göteborgsområdet. Ange säljarens adress som hämtpunkt och din hemadress som leveransadress. Vi bär ut, lastar, kör och bär in i ditt kök.",
      },
      {
        question: "Kopplar ni loss och in diskmaskinen?",
        answer:
          "Ja, vi kopplar loss vatten, avlopp och el vid hämtning. Vid leverans kan vi koppla in diskmaskinen om anslutningar finns på plats. Säg till vid bokning om du vill ha hjälp med inkoppling.",
      },
      {
        question: "Kan ni bära en diskmaskin i trappor utan hiss?",
        answer:
          "Ja, vi bär diskmaskiner i alla typer av trapphus – med eller utan hiss. Vi har säckkärra, bärremmar och rätt teknik. En diskmaskin väger 40–50 kilo och kräver två personer i branta trappor. Ange antal trappor vid bokning.",
      },
      {
        question: "Kan ni hämta diskmaskin från Elgiganten eller IKEA?",
        answer:
          "Ja, vi hämtar diskmaskiner från Elgiganten, Power, IKEA, MediaMarkt, Miele och alla andra butiker och utlämningsställen. Ange butikens adress vid bokning.",
      },
      {
        question: "Kan ni ta med min gamla diskmaskin till återvinning?",
        answer:
          "Ja, om du byter diskmaskin kan vi ta med den gamla till godkänd återvinningsstation i samma körning. Ange det vid bokning så ordnar vi det.",
      },
      {
        question: "Ingår skåpbil i transport av diskmaskin?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån uppdraget – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni transportera en inbyggd diskmaskin?",
        answer:
          "Ja, vi transporterar både fristående och inbyggda diskmaskiner. Vi hanterar dekorfront, infästningar och anslutningar varsamt. Inbyggda diskmaskiner kräver ofta lite extra tid vid lossning.",
      },
      {
        question: "Skyddar ni diskmaskinen under transport?",
        answer:
          "Ja, vi lindar diskmaskinen i skyddsfiltar och säkrar med remmar i skåpbilen. Maskinen transporteras stående så att inget vatten rinner ut i elektroniken. Allt är försäkrat.",
      },
      {
        question: "Kan ni transportera diskmaskin vid flytt?",
        answer:
          "Ja, vi kopplar loss diskmaskinen i din gamla bostad, bär ut, transporterar och bär in i ditt nya kök. Vi kan koppla in på plats. Kombinera gärna med vår flytthjälp om du har fler saker.",
      },
      {
        question: "Hur snabbt kan ni köra en diskmaskin?",
        answer:
          "Vi erbjuder ofta diskmaskinstransport med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni transportera diskmaskin på helger?",
        answer:
          "Ja, vi erbjuder diskmaskinstransport på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Behöver jag tömma diskmaskinen innan transport?",
        answer:
          "Ja, ta ur alla tallrikar och bestick. Kör gärna ett tomt program dagen innan så att maskinen töms på vatten. Stäng vattenkranen och dra ur elkontakten. Vi sköter resten vid hämtning.",
      },
      {
        question: "I vilka kommuner erbjuder ni transport av diskmaskin?",
        answer:
          "Vi erbjuder transport av diskmaskin i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transport av diskmaskin i 3 enkla steg",
      step2Text:
        "Våra ungdomar kopplar loss, bär ut och lastar diskmaskinen i skåpbilen. Vi kör till leveransadressen, bär in i köket och ställer på plats.",
      step3Title: "Diskmaskinen på plats",
      step3Desc:
        "Din diskmaskin står i köket – redo att kopplas in och köras igång. Klart.",
      ctaHeading: "Boka transport av diskmaskin idag",
      ctaText:
        "Ange hämtadress och leveransadress, beskriv diskmaskinen och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "dishwasher-transport",
      meta: {
        title:
          "Dishwasher Transport – Pickup & Delivery of Dishwashers | Ungdomskraft",
        description:
          "Book dishwasher transport – we disconnect, carry out and deliver your dishwasher safely. Perfect for Blocket purchases, moving or store pickups. Fixed price.",
        h1: "Dishwasher transport",
      },
      hero: {
        heading: "Dishwasher transport",
        intro:
          "Bought a dishwasher on Blocket? Taking it with you when moving? Or need to collect a new one from Elgiganten or IKEA? We pick up your dishwasher wherever it is, disconnect water and electricity, carry out, load the van and drive to the delivery address. There we carry in and place it — and connect it if you like. No need to rent a car, lift 40–50 kg yourself or wrestle with water connections.",
      },
      featureCards: {
        sinceText: "Dishwasher transport since 2014",
        bookYourText: "Book your dishwasher transport",
        bookDescText:
          "Enter the pickup address and delivery address, describe the dishwasher and pick a time. You see the price instantly.",
        vehicleTitleText: "Van equipped for dishwasher transport",
        vehicleDescText:
          "We arrive with a van and the right equipment — hand truck, carrying straps and protective blankets. Your dishwasher is secured throughout the entire transport.",
      },
      itemsSection: {
        heading: "Dishwashers we transport",
        items: [
          {
            name: "Freestanding dishwasher",
            description:
              "Standard full-size dishwasher (60 cm) — we handle water and electrical connections",
            image: "/dishwasher.webp",
          },
          {
            name: "Integrated dishwasher",
            description:
              "Built-in dishwashers with decorative fronts — we handle fittings and connections",
            image: "/dishwasher.webp",
          },
          {
            name: "Slimline dishwasher (45 cm)",
            description:
              "Compact dishwashers for smaller kitchens — lighter but requiring the same care",
            image: "/dishwasher.webp",
          },
          {
            name: "Countertop dishwasher",
            description:
              "Small dishwasher that sits on the countertop — easy to transport but needs careful handling",
            image: "/dishwasher.webp",
          },
          {
            name: "Commercial dishwasher",
            description:
              "Larger dishwashers for restaurants, cafés or offices — heavier and requiring extra crew",
            image: "/dishwasher.webp",
          },
          {
            name: "Dishwasher + other appliances",
            description:
              "Combine dishwasher transport with fridge, washing machine or cooker in the same trip",
            image: "/washer.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Dishwasher transport — we pick up, drive and carry in for you",
        mainText:
          "A dishwasher weighs 40–50 kg, has sensitive water connections and often needs manoeuvring through narrow kitchen doorways and round corners. Moving it yourself risks water damage, back strain and scratched floors. With Ungdomskraft's dishwasher transport you skip all of that. We arrive with a van and a team of young workers who know how to handle dishwashers. We disconnect water and electricity at the pickup address, carry the dishwasher out, wrap it in protective blankets, load the van and drive to the delivery address. There we carry in and place the dishwasher in the kitchen. Want help connecting it? Let us know when booking — we connect water and electricity so you can run your first load straight away. We collect dishwashers everywhere: from a Blocket seller's home, from Elgiganten, Power, IKEA or MediaMarkt, from your old apartment when moving, or from a warehouse. Whether it's a freestanding full-size dishwasher, a slimline 45 cm model, an integrated dishwasher with decorative front or a small countertop unit — we transport it safely.",
        preparationHeading: "Preparing for dishwasher transport",
        preparationText:
          "Turn off the water tap to the dishwasher and unplug it. Ideally run an empty cycle the day before so the machine drains properly. Disconnect the drain hose from the sink trap if you can — otherwise we'll handle it. Make sure there is clear passage through the kitchen door and out to the stairs or lift. Measure the doorway width if you suspect it might be tight — a standard dishwasher is 60 cm wide. Have the door code and address ready for both pickup and delivery. If the dishwasher needs connecting at the delivery address, check that water, drainage and electricity are in place.",
        additionalSections: [
          {
            heading:
              "Collecting a dishwasher from Blocket or a store",
            content:
              "Found a great dishwasher on Blocket? Ordered a new one from Elgiganten that you need collecting? We drive to the seller's address or the store's collection point, pick up the dishwasher and deliver it to your home. We carry out of the seller's home, load, drive and carry into your kitchen. No need to rent a car, ask a friend for help or carry 50 kg up the stairs yourself. Perfect for Blocket purchases, Facebook Marketplace finds or store pickups from Elgiganten, Power, IKEA, Miele or any other retailer.",
          },
          {
            heading:
              "Replacing your dishwasher — we bring the new one and take the old",
            content:
              "Replacing your old dishwasher? We can collect the new one from the store or seller, drive to your home, carry it into the kitchen and connect it. At the same time we disconnect the old dishwasher and carry it out. Want to get rid of it completely? We can take it to an approved recycling station in the same trip. No need for two separate bookings — everything happens in one go.",
          },
          {
            heading: "Dishwasher transport when moving",
            content:
              "Taking the dishwasher with you to your new home? We disconnect water and electricity, detach the drain hose, carry the dishwasher out of your old kitchen, wrap it in protective blankets and load the van. At the delivery address we carry in and place the dishwasher — and connect it if you like. Dishwashers need extra care during a move: water connections must be properly shut off so nothing leaks, and the machine should be transported upright to stop water running into the electronics. Our young workers are trained to handle dishwashers safely. Feel free to combine with our moving help if you have more items to move.",
          },
          {
            heading:
              "Why book dishwasher transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and it shows in every dishwasher transport we carry out. When you book dishwasher transport with us, you get a team of motivated, energetic young workers who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that mindset runs through everything — from how they disconnect the water supply to how they carry the dishwasher through your narrow kitchen without scratching the floor.\n\nSince our founding in 2014, we have transported thousands of dishwashers and other appliances across the Stockholm and Gothenburg regions. Along the way, we have given thousands of young people their first real work experience — with everything that entails: turning up on time, communicating professionally with customers, solving problems on-site, collaborating as a team and taking responsibility for the result. Our workers learn lifting techniques, how water and electrical connections work, how to navigate tight kitchens and stairwells and how to protect both the product and the property. They are trained to use hand trucks, carrying straps and protective blankets correctly — and to always leave everything in good condition.\n\nThis is not just good for the young people — it is good for you as a customer. You notice the difference in commitment and energy. Our teams take the time to do it right, double-check that the dishwasher is stable and that all connections are properly secured. We have built a culture where every transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we have received on Trustpilot and Google.\n\nEvery dishwasher transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport, installation and service industries — carrying with them the values and work ethic they learned with us. Choosing us means your dishwasher transport becomes part of something bigger.\n\nWe also take sustainability seriously. Packaging materials are recycled, and if you want to get rid of an old dishwasher being replaced, we take it to an approved recycling station so that components and materials are handled correctly. Nothing is dumped unnecessarily.\n\nIt is smooth dishwasher transport with purpose.\n\nDishwasher transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does dishwasher transport cost?",
          answer:
            "The price depends on distance and whether stair carrying is needed. A single dishwasher often comes to SEK 595–995. If combined with other appliances it becomes more cost-effective per item. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on dishwasher transport?",
          answer:
            "Dishwasher transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question:
            "Can you collect a dishwasher I've bought on Blocket?",
          answer:
            "Yes, we collect dishwashers from Blocket sellers across the Stockholm and Gothenburg regions. Enter the seller's address as the pickup point and your home address as the delivery address. We carry out, load, drive and carry into your kitchen.",
        },
        {
          question:
            "Do you disconnect and connect the dishwasher?",
          answer:
            "Yes, we disconnect water, drainage and electricity at pickup. At delivery we can connect the dishwasher if the connections are in place. Let us know when booking if you want help with connection.",
        },
        {
          question:
            "Can you carry a dishwasher up stairs without a lift?",
          answer:
            "Yes, we carry dishwashers in all types of stairwells — with or without a lift. We have hand trucks, carrying straps and the right technique. A dishwasher weighs 40–50 kg and needs two people on steep stairs. State the number of floors when booking.",
        },
        {
          question:
            "Can you collect a dishwasher from Elgiganten or IKEA?",
          answer:
            "Yes, we collect dishwashers from Elgiganten, Power, IKEA, MediaMarkt, Miele and all other stores and collection points. Enter the store address when booking.",
        },
        {
          question:
            "Can you take my old dishwasher to recycling?",
          answer:
            "Yes, if you're replacing your dishwasher we can take the old one to an approved recycling station in the same trip. Just let us know when booking.",
        },
        {
          question:
            "Is a van included in dishwasher transport?",
          answer:
            "Yes, a van is always included. We choose the right size based on the job — you don't need to rent a car, drive or refuel.",
        },
        {
          question:
            "Can you transport an integrated dishwasher?",
          answer:
            "Yes, we transport both freestanding and integrated dishwashers. We handle decorative fronts, fittings and connections carefully. Integrated dishwashers often need a little extra time to disconnect.",
        },
        {
          question:
            "Do you protect the dishwasher during transport?",
          answer:
            "Yes, we wrap the dishwasher in protective blankets and secure it with straps in the van. The machine is transported upright so no water runs into the electronics. Everything is insured.",
        },
        {
          question:
            "Can you transport a dishwasher when I'm moving?",
          answer:
            "Yes, we disconnect the dishwasher in your old home, carry out, transport and carry into your new kitchen. We can connect it on-site. Feel free to combine with our moving help if you have more items.",
        },
        {
          question:
            "How quickly can you transport a dishwasher?",
          answer:
            "We often offer dishwasher transport at short notice — sometimes the same week. Book online and you'll see available times straight away.",
        },
        {
          question:
            "Can you transport a dishwasher on weekends?",
          answer:
            "Yes, we offer dishwasher transport on weekends and some evenings. Book online and you'll see all available slots.",
        },
        {
          question:
            "Do I need to empty the dishwasher before transport?",
          answer:
            "Yes, remove all dishes and cutlery. Ideally run an empty cycle the day before so the machine drains. Turn off the water tap and unplug it. We handle the rest at pickup.",
        },
        {
          question:
            "Which areas do you offer dishwasher transport in?",
          answer:
            "We offer dishwasher transport throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Dishwasher transport in 3 easy steps",
        step2Text:
          "Our young workers disconnect, carry out and load the dishwasher into the van. We drive to the delivery address, carry into the kitchen and place it where you want it.",
        step3Title: "Dishwasher in place",
        step3Desc:
          "Your dishwasher is in the kitchen — ready to be connected and start its first load. Done.",
        ctaHeading: "Book dishwasher transport today",
        ctaText:
          "Enter the pickup address and delivery address, describe the dishwasher and pick a time. You see the price instantly.",
      },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Transport Tvättmaskin & Torktumlare — config för /transport-tvattmaskin-torktumlare
  // Vinkel: Nischad sida specifikt för transport av tvättmaskiner och torktumlare.
  // Unik vinkel vs transport-vitvaror: fokus på just tvättmaskiner och torktumlare –
  // Blocket-köp, nya från butik, flytt mellan bostäder, kombimaskin, värmepumpstork etc.
  // transport-vitvaror = bredd (alla vitvaror), denna = djup (bara tvätt/tork).
  // ──────────────────────────────────────────────────────────────────
  {
    slug: "transport-tvattmaskin-torktumlare",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Tvättmaskin & Torktumlare – Hämtning & Leverans | Ungdomskraft",
      description:
        "Boka transport av tvättmaskin och torktumlare med skåpbil och personal. Vi hämtar, bär, kör och bär in – köpt på Blocket, från butik eller vid flytt. Prisvärt med unga som gör skillnad.",
      h1: "Transport tvättmaskin & torktumlare",
    },

    hero: {
      heading: "Transport av tvättmaskin & torktumlare",
      intro:
        "Köpt en tvättmaskin på Blocket? Beställt en ny torktumlare från Elgiganten? Ska tvättmaskinen med i flytten? Vi hämtar tvättmaskinen eller torktumlaren var den än står, bär ut genom källare, trappor och trånga tvättstugor, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer på plats. Du slipper hyra bil, lyfta 80 kilo eller oroa dig för trappor.",
      heroImage: "/hero-transport-vitvaror.png",
    },

    featureCards: {
      sinceText: "Transport av tvättmaskiner sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft transporterat tusentals tvättmaskiner och torktumlare i Stockholms- och Göteborgsområdet. Våra ungdomar vet hur man hanterar tunga maskiner i trånga källare, tvättstugor och trapphus.",
      bookYourText: "Boka transport av tvättmaskin",
      bookDescText:
        "Ange hämtadress och leveransadress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar för transport av tvättmaskin & torktumlare",
      vehicleDescText:
        "Vi kommer med skåpbil, säckkärra och bärremmar – perfekt för tunga tvättmaskiner och torktumlare. Du slipper hyra bil eller göra det själv.",
    },

    itemsSection: {
      heading: "Tvättmaskiner & torktumlare vi transporterar",
      items: [
        {
          name: "Tvättmaskin",
          description:
            "Frontmatade och toppmatade tvättmaskiner – vi hanterar vikten och bär säkert i trappor",
          image: "/washer.webp",
        },
        {
          name: "Torktumlare",
          description:
            "Kondenstork, värmepumpstork och frånluftstork – vi bär och transporterar alla typer",
          image: "/washer.webp",
        },
        {
          name: "Kombimaskin",
          description:
            "Kombinerade tvättmaskiner och torktumlare – extra tunga men vi fixar det",
          image: "/washer.webp",
        },
        {
          name: "Tvätt-torkpelare",
          description:
            "Tvättmaskin och torktumlare staplade på varandra – vi separerar, transporterar och staplar igen",
          image: "/washer.webp",
        },
        {
          name: "Industritvätt",
          description:
            "Större tvättmaskiner för tvättstugor och fastigheter – vi har rätt utrustning för vikten",
          image: "/washer.webp",
        },
        {
          name: "Torkskåp",
          description:
            "Fristående torkskåp – vi demonterar vid behov och transporterar säkert",
          image: "/washer.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport av tvättmaskin och torktumlare – vi hämtar, kör och bär in",
      mainText:
        "En tvättmaskin väger 70–90 kilo, en torktumlare ofta lika mycket. De ska lossas från vatten och el, bäras ut genom trånga tvättstugor och källargångar, uppför trappor och in i en bil som rymmer dem – och sedan samma sak i omvänd ordning på leveransadressen. Med Ungdomskrafts transport av tvättmaskin och torktumlare slipper du allt det. Vi kommer med skåpbil, säckkärra och ett team av starka ungdomar. Vi kopplar loss vatten och el, bär ut maskinen, skyddar med filtar och remmar, lastar i skåpbilen och kör till ny adress. Där bär vi in och ställer på plats – i tvättstugan, badrummet, källaren eller var du vill. Behöver du hjälp med inkoppling? Säg till vid bokning. Vi transporterar alla typer: frontmatade och toppmatade tvättmaskiner, kondenstork, värmepumpstork, frånluftstork, kombinerade tvätt-tork-maskiner, pelare och torkskåp. Oavsett om du köpt begagnat på Blocket, beställt från Elgiganten eller ska flytta maskinen till ny bostad – vi hämtar och levererar.",
      preparationHeading: "Förbered inför transport av tvättmaskin",
      preparationText:
        "Stäng av vatten och el till tvättmaskinen eller torktumlaren. Kör en tom tvätt med högsta centrifugering för att tömma trumman på kvarvarande vatten. Töm filtret och lossa avloppsslangen – ha en handduk redo för spillvatten. Rulla ihop sladden och tejpa fast den. Om du har transportsäkringarna som följde med tvättmaskinen – montera dem (de skyddar trumman under transport). Se till att det finns fri passage genom dörrar, korridorer och trapphus. Ha portkod och nycklar redo för båda adresserna.",
      additionalSections: [
        {
          heading: "Hämta tvättmaskin från Blocket och Facebook Marketplace",
          content:
            "Hittat en tvättmaskin på Blocket eller en torktumlare på Facebook Marketplace? Vi hämtar maskinen hos säljaren, kopplar loss om det behövs, bär ut, lastar och kör hem till dig. Där bär vi in och ställer på plats i tvättstugan. Du behöver inte hyra bil eller be säljaren om hjälp med bärningen – vi sköter allt. Många av våra transporter av tvättmaskiner är just Blocket-köp, och vi vet att det kan vara stressigt att koordinera tid med en säljare. Därför är vi alltid i tid och hanterar bärningen snabbt och professionellt.",
        },
        {
          heading: "Transport av tvättmaskin vid flytt",
          content:
            "Ska tvättmaskinen och torktumlaren med till din nya bostad? Vi kopplar loss vatten och el, bär ut genom källare eller tvättstuga, lastar i skåpbilen och kör till din nya adress. Där bär vi in och ställer på plats. Tvättmaskiner kräver extra omsorg vid transport – de är tunga, har känslig elektronik och trumman behöver säkras för att inte skadas under körning. Våra ungdomar är tränade i att hantera tvättmaskiner korrekt och vet hur man undviker skador. Kombinera gärna med vår flytthjälp om du har fler saker att flytta.",
        },
        {
          heading: "Så hanterar vi tunga tvättmaskiner säkert",
          listItems: [
            {
              label: "Säckkärra & bärremmar",
              text: "Vi har professionella säckkärror och bärremmar som fördelar vikten jämnt – säkert i trappor och trånga utrymmen.",
            },
            {
              label: "Skyddsfiltar",
              text: "Maskinen lindas i skyddsfiltar så att varken produkten eller dina väggar, golv och dörrkarmar skadas.",
            },
            {
              label: "Transportsäkring",
              text: "Om transportsäkringarna finns monterar vi dem för att låsa trumman under transport – det skyddar upphängningen.",
            },
            {
              label: "Lossning & inkoppling",
              text: "Vi kopplar loss vatten och el vid hämtning och kan koppla in vid leverans om anslutningar finns.",
            },
            {
              label: "Försäkring",
              text: "Alla transporter är försäkrade. Skulle något gå sönder ersätter vi det.",
            },
          ],
          afterContent:
            "Vi har transporterat tusentals tvättmaskiner och torktumlare sedan 2014 – vi vet hur det ska göras.",
        },
        {
          heading: "Varför boka transport av tvättmaskin med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje transport av tvättmaskin och torktumlare vi genomför. När du bokar hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de bär din tvättmaskin i källartrappan till hur de ställer torktumlaren exakt där du vill ha den.\n\nSedan starten 2014 har vi transporterat tusentals tvättmaskiner och torktumlare i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker hantering av tunga maskiner, hur man navigerar trånga källargångar och trapphus, och hur man kopplar loss och ansluter vatten och el korrekt. De tränas i att använda säckkärra och bärremmar professionellt – och i att alltid lämna allt i gott skick.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team tar sig tid att göra rätt, dubbelkollar att maskinen står stabilt och vågrätt, och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje transport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nVarje transport av tvättmaskin du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport, installation och serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din transport av tvättmaskin blir en del av något större.\n\nVi tar också hållbarhet på allvar. Förpackningsmaterial återvinns, och om du samtidigt vill bli av med en gammal maskin som ersätts kan vi köra den till godkänd återvinningsstation. Ingenting slängs i onödan.\n\nDet är smidig transport av tvättmaskin med mening.\n\nTransport av tvättmaskin ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport av tvättmaskin?",
        answer:
          "Priset beror på avstånd, antal trappor och om du ska transportera en eller flera maskiner. En enstaka tvättmaskin landar ofta på 595–995 kr. Tvättmaskin plus torktumlare kan landa på 995–1 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport av tvättmaskin?",
        answer:
          "Transport av tvättmaskin ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni hämta en tvättmaskin jag köpt på Blocket?",
        answer:
          "Ja, vi hämtar tvättmaskinen hos säljaren, kopplar loss om det behövs, bär ut, lastar och kör hem till dig. Vi bär in och ställer på plats. Ange säljarens adress vid bokning.",
      },
      {
        question: "Kopplar ni loss och in tvättmaskinen?",
        answer:
          "Ja, vi kopplar loss vatten och el vid hämtning. Vid leverans kan vi koppla in om anslutningar finns på plats. Säg till vid bokning om du vill ha hjälp med inkoppling.",
      },
      {
        question: "Kan ni bära tvättmaskinen i trappor utan hiss?",
        answer:
          "Ja, vi bär tvättmaskiner och torktumlare i alla typer av trapphus – med eller utan hiss. Vi har säckkärra, bärremmar och rätt teknik för att hantera 70–90 kilo säkert i trappor.",
      },
      {
        question: "Ingår skåpbil i transport av tvättmaskin?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni transportera tvättmaskin och torktumlare samtidigt?",
        answer:
          "Ja, vi transporterar tvättmaskin och torktumlare i samma körning. Även tvätt-tork-pelare och kombinerade maskiner. Beskriv vad som ska med vid bokning.",
      },
      {
        question: "Transporterar ni tvättmaskiner från Elgiganten och andra butiker?",
        answer:
          "Ja, vi hämtar tvättmaskiner och torktumlare från Elgiganten, Power, MediaMarkt, IKEA och alla andra butiker och utlämningsställen. Ange butikens adress vid bokning.",
      },
      {
        question: "Skyddar ni tvättmaskinen under transport?",
        answer:
          "Ja, vi lindar maskinen i skyddsfiltar och säkrar med remmar i skåpbilen. Om transportsäkringarna finns monterar vi dem för att låsa trumman. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Behöver jag montera transportsäkringarna innan transport?",
        answer:
          "Det är bra om du kan, men inte nödvändigt. Transportsäkringarna skyddar trumman under körning. Om du har dem – bra, vi monterar dem. Om inte transporterar vi maskinen ändå, men med extra omsorg.",
      },
      {
        question: "Kan ni transportera en torktumlare separat?",
        answer:
          "Ja, vi transporterar torktumlare separat – kondenstork, värmepumpstork och frånluftstork. Vi bär, lastar och levererar till din adress.",
      },
      {
        question: "Hur snabbt kan ni köra min tvättmaskin?",
        answer:
          "Vi erbjuder ofta transport av tvättmaskin med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni transportera tvättmaskin på helger?",
        answer:
          "Ja, vi erbjuder transport av tvättmaskiner och torktumlare på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Kan ni ta med den gamla tvättmaskinen till återvinning?",
        answer:
          "Ja, om du byter ut din gamla tvättmaskin kan vi ta med den och köra till godkänd återvinningsstation. Beskriv det vid bokning så ordnar vi det i samma körning.",
      },
      {
        question: "I vilka kommuner erbjuder ni transport av tvättmaskin och torktumlare?",
        answer:
          "Vi erbjuder transport av tvättmaskin och torktumlare i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transport av tvättmaskin i 3 enkla steg",
      step2Text:
        "Våra ungdomar kopplar loss, bär ut och lastar tvättmaskinen i skåpbilen. Vi kör till leveransadressen, bär in och ställer på plats.",
      step3Title: "Maskinen på plats",
      step3Desc:
        "Din tvättmaskin eller torktumlare står där du vill ha den – säkert levererad och redo att kopplas in. Klart.",
      ctaHeading: "Boka transport av tvättmaskin idag",
      ctaText:
        "Ange hämtadress och leveransadress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "washer-dryer-transport",
      meta: {
        title:
          "Washer & Dryer Transport – Pickup & Delivery | Ungdomskraft",
        description:
          "Book washing machine and dryer transport. We pick up, carry and deliver safely – from Blocket, stores or between homes. Fixed price, insured. Book today!",
        h1: "Washer & dryer transport",
      },
      hero: {
        heading: "Washing machine & dryer transport",
        intro:
          "Bought a washing machine on Blocket? Ordered a new dryer from Elgiganten? Taking the washer with you to your new home? We pick up the machine wherever it is, carry it out through basements, stairs and tight laundry rooms, load the van and drive to the delivery address. There we carry in and place it where you want it. No need to rent a car, lift 80 kg or worry about stairs.",
      },
      featureCards: {
        sinceText: "Washer transport since 2014",
        bookYourText: "Book your washer transport",
        bookDescText:
          "Enter the pickup address and delivery address, describe what needs transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans for washing machine & dryer transport",
        vehicleDescText:
          "We arrive with a van, hand truck and carrying straps — perfect for heavy washing machines and dryers. No need to rent a vehicle or do it yourself.",
      },
      itemsSection: {
        heading: "Washers & dryers we transport",
        items: [
          {
            name: "Washing machine",
            description:
              "Front-loading and top-loading washing machines — we handle the weight and carry safely through stairs",
            image: "/washer.webp",
          },
          {
            name: "Tumble dryer",
            description:
              "Condenser, heat pump and vented dryers — we carry and transport all types",
            image: "/washer.webp",
          },
          {
            name: "Washer-dryer combo",
            description:
              "Combined washing machine and dryer units — extra heavy but we handle it",
            image: "/washer.webp",
          },
          {
            name: "Stacked washer-dryer",
            description:
              "Washing machine and dryer stacked on top of each other — we separate, transport and restack",
            image: "/washer.webp",
          },
          {
            name: "Commercial washer",
            description:
              "Larger washing machines for shared laundry rooms and properties — we have the right equipment",
            image: "/washer.webp",
          },
          {
            name: "Drying cabinet",
            description:
              "Freestanding drying cabinets — we disassemble if needed and transport safely",
            image: "/washer.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Washing machine and dryer transport — we pick up, drive and deliver",
        mainText:
          "A washing machine weighs 70–90 kg, a dryer often just as much. They need disconnecting from water and electricity, carrying out through tight laundry rooms and basement corridors, up stairs and into a vehicle that fits them — then the same in reverse at the delivery address. With Ungdomskraft's washer and dryer transport you skip all of that. We arrive with a van, hand truck and a team of strong young workers. We disconnect water and electricity, carry the machine out, protect with blankets and straps, load the van and drive to the new address. There we carry in and place it where you want it. Need help connecting? Let us know when booking. We transport all types: front-loading and top-loading washers, condenser dryers, heat pump dryers, vented dryers, washer-dryer combos, stacked units and drying cabinets.",
        preparationHeading: "Prepare for washing machine transport",
        preparationText:
          "Turn off water and electricity to the machine. Run an empty wash on the highest spin cycle to drain remaining water from the drum. Clean the filter and disconnect the drain hose — have a towel ready for spillage. Roll up the power cable and tape it in place. If you have the transit bolts that came with the machine — fit them (they protect the drum during transport). Make sure there is clear passage through doors, corridors and stairwells. Have entry codes and keys ready for both addresses.",
        additionalSections: [
          {
            heading: "Picking up a washing machine from Blocket and Facebook Marketplace",
            content:
              "Found a washing machine on Blocket or a dryer on Facebook Marketplace? We collect the machine from the seller, disconnect if needed, carry out, load and drive it to your home. There we carry in and place it in the laundry room. No need to rent a car or ask the seller for help carrying — we handle everything. Many of our washer transports are exactly these kinds of purchases, and we know coordinating with a seller can be stressful. That's why we're always on time and handle the carrying quickly and professionally.",
          },
          {
            heading: "Washing machine transport when moving",
            content:
              "Taking the washing machine and dryer to your new home? We disconnect water and electricity, carry out through the basement or laundry room, load the van and drive to your new address. There we carry in and place everything where you want it. Washing machines need extra care during transport — they're heavy, have sensitive electronics and the drum needs securing to avoid damage during driving. Our young workers are trained to handle washing machines correctly. Combine with our moving help service if you have more items to move.",
          },
          {
            heading: "How we handle heavy washing machines safely",
            listItems: [
              {
                label: "Hand truck & straps",
                text: "We use professional hand trucks and carrying straps that distribute weight evenly — safe on stairs and in tight spaces.",
              },
              {
                label: "Protective blankets",
                text: "The machine is wrapped in protective blankets so that neither the product nor your walls, floors and door frames are damaged.",
              },
              {
                label: "Transit bolts",
                text: "If transit bolts are available we fit them to lock the drum during transport — this protects the suspension.",
              },
              {
                label: "Disconnection & connection",
                text: "We disconnect water and electricity at pickup and can connect at delivery if plumbing is in place.",
              },
              {
                label: "Insurance",
                text: "All transports are insured. If anything is damaged we replace it.",
              },
            ],
            afterContent:
              "We have transported thousands of washing machines and dryers since 2014 — we know how it's done.",
          },
          {
            heading: "Why book washing machine transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and you notice it in every washer and dryer transport we carry out. When you book with us you get a team of motivated, energetic young people who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that attitude permeates everything — from how they carry your washing machine down the basement stairs to how they place the dryer exactly where you want it.\n\nSince 2014 we have transported thousands of washing machines and dryers in the Stockholm and Gothenburg areas. Along the way we have given thousands of young people their first real work experience — with everything that entails: showing up on time, communicating professionally with customers, solving problems on site, working as a team and taking responsibility for the result. Our workers learn lifting technique, safe handling of heavy machines, how to navigate tight basement corridors and stairwells, and how to disconnect and connect water and electricity correctly. They are trained to use hand trucks and carrying straps professionally — and to always leave everything in good condition.\n\nThis is not only good for the young people — it's good for you as a customer. You notice the difference in commitment and energy. Our teams take the time to do things right, double-check that the machine is stable and level, and always ask if there is anything else they can help with. We have built a culture where every transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we've received on Trustpilot and Google.\n\nEvery washing machine transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport, installation and service industries — carrying with them the values and work ethic they learned with us. Choosing us means your washer transport becomes part of something bigger.\n\nWe also take sustainability seriously. Packaging is recycled, and if you want to get rid of an old machine being replaced we can drive it to an approved recycling station. Nothing is dumped carelessly.\n\nIt's smooth washing machine transport with purpose.\n\nWasher and dryer transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does washing machine transport cost?",
          answer:
            "The price depends on distance, number of floors and whether you're transporting one or more machines. A single washing machine often costs SEK 595–995. Washer plus dryer can cost SEK 995–1,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on washer and dryer transport?",
          answer:
            "Washer and dryer transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Can you pick up a washing machine I bought on Blocket?",
          answer:
            "Yes, we collect the washing machine from the seller, disconnect if needed, carry out, load and deliver to your home. Just enter the seller's address when booking.",
        },
        {
          question: "Do you disconnect and connect the washing machine?",
          answer:
            "Yes, we disconnect water and electricity at pickup. At delivery we can connect if plumbing and electrical outlets are in place. Let us know when booking if you want help connecting.",
        },
        {
          question: "Can you carry a washing machine up stairs without a lift?",
          answer:
            "Yes, we carry washing machines and dryers in all types of stairwells — with or without a lift. We have hand trucks, straps and the right technique for handling 70–90 kg safely on stairs.",
        },
        {
          question: "Can you transport washer and dryer together?",
          answer:
            "Yes, we transport washing machines and dryers in the same trip. Including stacked washer-dryer units and combined machines. Describe what needs moving when booking.",
        },
        {
          question: "Do you protect the washing machine during transport?",
          answer:
            "Yes, we wrap the machine in protective blankets and secure with straps in the van. If transit bolts are available we fit them to lock the drum. Everything is insured — if anything is damaged we replace it.",
        },
        {
          question: "Can you take the old washing machine to recycling?",
          answer:
            "Yes, if you're replacing your old machine we can take it to an approved recycling station. Describe it when booking and we'll handle it in the same trip.",
        },
        {
          question: "In which municipalities do you offer washing machine transport?",
          answer:
            "We offer washing machine and dryer transport across the greater Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Washing machine transport in 3 easy steps",
        step2Text:
          "Our young workers disconnect, carry out and load the washing machine into the van. We drive to the delivery address, carry in and place it where you want it.",
        step3Title: "Machine in place",
        step3Desc:
          "Your washing machine or dryer is where you want it — safely delivered and ready to connect. Done.",
        ctaHeading: "Book washing machine transport today",
        ctaText:
          "Enter the pickup address and delivery address, describe what needs transporting and pick a time. You see the price instantly.",
      },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Transport Elektronik — config för /transport-elektronik
  // Vinkel: Nischad sida för transport av elektronik och hemelektronik.
  // Unik vinkel vs transport-vitvaror: fokus på ömtålig elektronik –
  // TV-apparater, datorer, skärmar, spelkonsoler, ljudanläggningar etc.
  // Kräver varsam hantering, originalförpackning, antistatisk omsorg.
  // ──────────────────────────────────────────────────────────────────
  {
    slug: "transport-elektronik",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Elektronik – Smidig Transport av Elektronik | Ungdomskraft",
      description:
        "Boka transport av elektronik – TV, dator, skärm och spelkonsol. Vi hämtar, skyddar och levererar varsamt från A till B. Fast pris, försäkrat.",
      h1: "Transport elektronik",
    },

    hero: {
      heading: "Transport av elektronik",
      intro:
        "Köpt en ny TV på Blocket? Ska gamingdatorn med i flytten? Behöver du få hem en ljudanläggning från butiken? Vi hämtar elektroniken på upphämtningsadressen, skyddar varsamt, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer på plats. Du slipper hyra bil, riskera skador eller oroa dig för ömtåliga skärmar.",
      heroImage: "/hero-transport-elektronik.png",
    },

    featureCards: {
      sinceText: "Elektroniktransport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft transporterat tusentals elektronikprodukter i Stockholms- och Göteborgsområdet – TV-apparater, datorer, skärmar, ljudanläggningar och spelkonsoler. Våra ungdomar vet hur man hanterar ömtålig elektronik varsamt och säkert.",
      bookYourText: "Boka transport av elektronik",
      bookDescText:
        "Ange hämtadress och leveransadress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar anpassade för elektroniktransport",
      vehicleDescText:
        "Vi kommer med skåpbil i rätt storlek, möbelfiltar och spännband. Elektroniken säkras så att inget glider, vickar eller skadas under körningen.",
    },

    itemsSection: {
      heading: "Elektronik vi transporterar",
      items: [
        {
          name: "TV-apparater",
          description:
            "Platt-TV, OLED, QLED och storbilds-TV – vi transporterar alla storlekar varsamt och stående",
          image: "/tv.webp",
        },
        {
          name: "Datorer & skärmar",
          description:
            "Stationära datorer, gamingdatorer, bildskärmar och allt-i-ett-datorer",
          image: "/tv.webp",
        },
        {
          name: "Ljudanläggningar",
          description:
            "Soundbar, hemmabioanläggning, högtalare, förstärkare och subwoofer",
          image: "/tv.webp",
        },
        {
          name: "Spelkonsoler & tillbehör",
          description:
            "PlayStation, Xbox, Nintendo, VR-headset och tillhörande utrustning",
          image: "/tv.webp",
        },
        {
          name: "Projektorer & dukar",
          description:
            "Hemmabioprojektorer, kontorsprojektorer och projektionsdukar",
          image: "/tv.webp",
        },
        {
          name: "Nätverksutrustning",
          description:
            "Servrar, NAS, switchar, routrar och rack-utrustning för hem och kontor",
          image: "/tv.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport av elektronik – varsam hantering från upphämtning till leverans",
      mainText:
        "Elektronik är ömtåligt, dyrt och svårt att ersätta om det går sönder under en transport. En 65-tums TV, en gamingdator med vattenkylning eller en hemmabio med subwoofer och surroundhögtalare kräver varsam hantering, rätt förpackning och säker lastning – inte ett snabbt kast in i baksätet. Med Ungdomskrafts elektroniktransport får du ett team som vet hur elektronik ska hanteras. Vi hämtar på upphämtningsadressen, skyddar varje produkt med möbelfiltar och bubbelplast vid behov, säkrar i skåpbilen med spännband och remmar, och kör försiktigt till leveransadressen. Där bär vi in och ställer på plats. TV-apparater transporteras alltid stående för att skydda panelen. Datorer säkras så att komponenter inte lossnar. Högtalare och förstärkare separeras och skyddas individuellt. Oavsett om du köpt elektronik på Blocket, hämtar från butik, flyttar till ny bostad eller levererar kontorsteknik – vi tar hand om hela transporten varsamt och professionellt.",
      preparationHeading: "Förbered inför transport av elektronik",
      preparationText:
        "Har du originalförpackningen? Använd den – den är designad för att skydda produkten under transport. Annars lindar vi in i möbelfiltar. Koppla loss alla kablar och packa dem separat (märk gärna med tejp vilken kabel som hör till vad). Stäng av och ladda ur batterier i bärbara enheter. Ta bort externa hårddiskar och USB-minnen från datorer. Rensa bort lösa delar från TV-fästen. Se till att det finns fri passage genom dörrar och trapphus. Ha portkod redo för båda adresserna.",
      additionalSections: [
        {
          heading: "Transport av TV – stående, skyddad och försäkrad",
          content:
            "En TV-transport kräver extra omsorg. Moderna platta TV-apparater har känsliga paneler som inte tål tryck, böjning eller liggande transport. Vi transporterar alltid TV-apparater stående, skyddade med möbelfiltar runt hela skärmen och säkrade med spännband i skåpbilen. Vi hanterar alla storlekar – från 32-tums köksteven till 85-tums hemmabio-TV. Har du originalkartongen? Perfekt – då packar vi TV:n i den. Annars skyddar vi med dubbla lager filtmaterial. Vi hämtar TV-apparater från Blocket-säljare, butiker som Elgiganten och Power, eller från din gamla bostad vid flytt.",
        },
        {
          heading: "Transport av dator och gamingutrustning",
          content:
            "Stationära datorer, gamingdatorer med stora grafikkort och vattenkylning, ultrawide-skärmar och dubbla bildskärmsuppsättningar – vi vet att varje komponent är värdefull. Vi transporterar datorer stående med sidopanelen uppåt, säkrar grafikkort och kylarfästen om du vill, och ser till att skärmar ligger skyddade och separerade. Flytta hela gamingsetuppet till en ny lägenhet, hämta en dator du köpt online eller leverera kontorsdatorer till ett nytt kontor – vi anpassar transporten efter utrustningen.",
        },
        {
          heading: "Hämta elektronik från Blocket, butik och e-handel",
          content:
            "Köpt en TV på Blocket? Hämtar en ljudanläggning från Elgiganten? Beställt en dator online som ska hämtas vid ett utlämningsställe? Vi kör och hämtar elektroniken åt dig, oavsett var den finns. Ange upphämtningsadressen vid bokning – vi sköter resten. Vi bär ut, skyddar, lastar och levererar hem till dig. Perfekt när du inte har bil, inte vill riskera att köra med en stor TV i personbilen eller helt enkelt vill slippa besväret.",
        },
        {
          heading: "Varför boka transport av elektronik med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje elektroniktransport vi genomför. När du bokar transport av elektronik hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de skyddar din TV-skärm till hur de säkrar datorn i skåpbilen.\n\nSedan starten 2014 har vi transporterat tusentals elektronikprodukter i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig varsam hantering av ömtålig elektronik, hur man skyddar skärmar och paneler, säker lastning och fastspänning, och hur man navigerar trånga trapphus utan att stöta i väggar eller dörrkontakter.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team tar sig tid att skydda varje produkt ordentligt, dubbelkollar att allt sitter säkert i skåpbilen och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje elektroniktransport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nVarje elektroniktransport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport och teknikbranschen – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din elektroniktransport blir en del av något större.\n\nVi tar också hållbarhet på allvar. Förpackningsmaterial som frigolit och kartong återvinns. Elektronik som inte längre fungerar hjälper vi att köra till godkänd elektronikåtervinning. Ingenting slängs i onödan.\n\nDet är varsam elektroniktransport med mening.\n\nTransport av elektronik ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport av elektronik?",
        answer:
          "Priset beror på typ av elektronik, antal produkter och avstånd. En enstaka TV eller dator landar ofta på 595–995 kr. Större mängder – till exempel hemmabioanläggning med flera högtalare – kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport av elektronik?",
        answer:
          "Transport av elektronik ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Kan ni transportera en stor TV?",
        answer:
          "Ja, vi transporterar TV-apparater i alla storlekar – från 32 tum till 85 tum och större. Vi transporterar alltid TV:n stående, skyddad med möbelfiltar och säkrad med spännband i skåpbilen.",
      },
      {
        question: "Transporterar ni TV:n stående?",
        answer:
          "Ja, alltid. Platta TV-apparater ska transporteras stående för att skydda panelen. Vi säkrar TV:n vertikalt i skåpbilen med filtar och spännband så att den inte kan falla eller vicka.",
      },
      {
        question: "Kan ni hämta elektronik jag köpt på Blocket?",
        answer:
          "Ja, vi hämtar elektronik från Blocket-säljare i hela Stockholms- och Göteborgsområdet. Ange säljarens adress vid bokning – vi bär ut, skyddar, lastar och levererar hem till dig.",
      },
      {
        question: "Skyddar ni elektroniken under transport?",
        answer:
          "Ja, vi skyddar med möbelfiltar, bubbelplast vid behov och spännband. Har du originalförpackningen använder vi den. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Kan ni transportera en stationär dator?",
        answer:
          "Ja, vi transporterar stationära datorer, gamingdatorer och servrar. Vi transporterar datorn stående, säkrar den så att interna komponenter inte lossnar och skyddar med filtar.",
      },
      {
        question: "Ingår skåpbil i transport av elektronik?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni transportera elektronik vid flytt?",
        answer:
          "Ja, vi transporterar all elektronik vid flytt – TV, datorer, skärmar, spelkonsoler, ljudanläggningar och nätverksutrustning. Kombinera gärna med vår flytthjälp om du har fler saker.",
      },
      {
        question: "Kan ni hämta elektronik från Elgiganten eller annan butik?",
        answer:
          "Ja, vi hämtar elektronik från Elgiganten, Power, MediaMarkt, NetOnNet, Webhallen och alla andra butiker och utlämningsställen. Ange butikens adress vid bokning.",
      },
      {
        question: "Kan ni transportera en hemmabioanläggning?",
        answer:
          "Ja, vi transporterar kompletta hemmabioanläggningar – projektor, duk, förstärkare, subwoofer och surroundhögtalare. Vi skyddar och transporterar varje komponent separat.",
      },
      {
        question: "Hur snabbt kan ni köra min elektronik?",
        answer:
          "Vi erbjuder ofta elektroniktransport med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni transportera elektronik på helger?",
        answer:
          "Ja, vi erbjuder elektroniktransport på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Behöver jag packa elektroniken själv?",
        answer:
          "Nej, du behöver inte packa – vi skyddar med möbelfiltar och spännband. Men har du originalförpackningen är det alltid bäst att använda den. Koppla gärna loss kablar i förväg och packa dem separat.",
      },
      {
        question: "I vilka kommuner erbjuder ni transport av elektronik?",
        answer:
          "Vi erbjuder transport av elektronik i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transport av elektronik i 3 enkla steg",
      step2Text:
        "Våra ungdomar skyddar elektroniken med möbelfiltar, lastar varsamt i skåpbilen och kör till leveransadressen. Där bär vi in och ställer på plats.",
      step3Title: "Elektroniken på plats",
      step3Desc:
        "Din elektronik står där du vill ha den – skyddad, hel och redo att kopplas in. Klart.",
      ctaHeading: "Boka transport av elektronik idag",
      ctaText:
        "Ange hämtadress och leveransadress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "electronics-transport",
      meta: {
        title:
          "Electronics Transport – Safe Transport of Electronics | Ungdomskraft",
        description:
          "Book electronics transport – TVs, computers, screens and game consoles. We pick up, protect and deliver with care. Fixed price, insured. Book today!",
        h1: "Electronics transport",
      },
      hero: {
        heading: "Electronics transport",
        intro:
          "Bought a new TV on Blocket? Taking the gaming PC with you when moving? Need to pick up a sound system from the store? We collect the electronics at the pickup address, protect carefully, load the van and drive to the delivery address. There we carry in and place everything where you want it. No need to rent a car, risk damage or worry about fragile screens.",
      },
      featureCards: {
        sinceText: "Electronics transport since 2014",
        bookYourText: "Book your electronics transport",
        bookDescText:
          "Enter the pickup address and delivery address, describe what needs transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans equipped for electronics transport",
        vehicleDescText:
          "We arrive with the right size van, furniture blankets and tie-downs. Electronics are secured so nothing slides, wobbles or gets damaged during the drive.",
      },
      itemsSection: {
        heading: "Electronics we transport",
        items: [
          {
            name: "TVs",
            description:
              "Flat screens, OLED, QLED and large-format TVs — we transport all sizes carefully, always upright",
            image: "/tv.webp",
          },
          {
            name: "Computers & monitors",
            description:
              "Desktop PCs, gaming computers, monitors and all-in-one computers",
            image: "/tv.webp",
          },
          {
            name: "Audio systems",
            description:
              "Soundbars, home cinema systems, speakers, amplifiers and subwoofers",
            image: "/tv.webp",
          },
          {
            name: "Game consoles & accessories",
            description:
              "PlayStation, Xbox, Nintendo, VR headsets and associated equipment",
            image: "/tv.webp",
          },
          {
            name: "Projectors & screens",
            description:
              "Home cinema projectors, office projectors and projection screens",
            image: "/tv.webp",
          },
          {
            name: "Network equipment",
            description:
              "Servers, NAS units, switches, routers and rack equipment for home and office",
            image: "/tv.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Electronics transport — careful handling from pickup to delivery",
        mainText:
          "Electronics are fragile, expensive and hard to replace if damaged during transport. A 65-inch TV, a gaming PC with water cooling or a home cinema with subwoofer and surround speakers requires careful handling, proper packaging and secure loading — not a quick toss into the back seat. With Ungdomskraft's electronics transport you get a team that knows how to handle electronics. We collect at the pickup address, protect each product with furniture blankets and bubble wrap if needed, secure in the van with tie-downs and straps, and drive carefully to the delivery address. There we carry in and place everything. TVs are always transported upright to protect the panel. Computers are secured so components don't come loose. Speakers and amplifiers are separated and individually protected.",
        preparationHeading: "Prepare for electronics transport",
        preparationText:
          "Have the original packaging? Use it — it's designed to protect the product during transport. Otherwise we wrap in furniture blankets. Disconnect all cables and pack them separately (label with tape which cable goes where). Turn off and discharge batteries in portable devices. Remove external hard drives and USB sticks from computers. Clear loose parts from TV mounts. Make sure there is clear passage through doors and stairwells. Have entry codes ready for both addresses.",
        additionalSections: [
          {
            heading: "TV transport — upright, protected and insured",
            content:
              "A TV transport requires extra care. Modern flat-screen TVs have sensitive panels that cannot tolerate pressure, bending or lying flat. We always transport TVs upright, protected with furniture blankets around the entire screen and secured with tie-downs in the van. We handle all sizes — from 32-inch kitchen TVs to 85-inch home cinema displays. Have the original box? Perfect — we'll pack the TV in it. Otherwise we protect with double layers of blanket material. We collect TVs from Blocket sellers, stores like Elgiganten and Power, or from your old home when moving.",
          },
          {
            heading: "Computer and gaming equipment transport",
            content:
              "Desktop PCs, gaming computers with large graphics cards and water cooling, ultrawide monitors and dual-screen setups — we know every component is valuable. We transport computers upright with the side panel facing up, secure graphics cards and cooler mounts if you like, and make sure monitors are protected and separated. Moving your entire gaming setup to a new flat, picking up a computer you bought online or delivering office PCs to a new office — we adapt the transport to the equipment.",
          },
          {
            heading: "Picking up electronics from Blocket, stores and e-commerce",
            content:
              "Bought a TV on Blocket? Collecting a sound system from Elgiganten? Ordered a computer online for store pickup? We drive and collect the electronics for you, wherever they are. Enter the pickup address when booking — we handle the rest. We carry out, protect, load and deliver to your home. Perfect when you don't have a car, don't want to risk driving with a large TV in a saloon car or simply want to skip the hassle.",
          },
          {
            heading: "Why book electronics transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and you notice it in every electronics transport we carry out. When you book electronics transport with us you get a team of motivated, energetic young people who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that attitude permeates everything — from how they protect your TV screen to how they secure the computer in the van.\n\nSince 2014 we have transported thousands of electronics products in the Stockholm and Gothenburg areas. Along the way we have given thousands of young people their first real work experience — with everything that entails: showing up on time, communicating professionally with customers, solving problems on site, working as a team and taking responsibility for the result. Our workers learn careful handling of fragile electronics, how to protect screens and panels, safe loading and securing, and how to navigate tight stairwells without bumping walls or door frames.\n\nThis is not only good for the young people — it's good for you as a customer. You notice the difference in commitment and energy. Our teams take the time to protect every product properly, double-check that everything is secure in the van and always ask if there is anything else they can help with. We have built a culture where every electronics transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we've received on Trustpilot and Google.\n\nEvery electronics transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport and the tech industry — carrying with them the values and work ethic they learned with us. Choosing us means your electronics transport becomes part of something bigger.\n\nWe also take sustainability seriously. Packaging materials like polystyrene and cardboard are recycled. Electronics that no longer work — we help drive them to approved electronics recycling. Nothing is dumped carelessly.\n\nIt's careful electronics transport with purpose.\n\nElectronics transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does electronics transport cost?",
          answer:
            "The price depends on the type of electronics, number of items and distance. A single TV or computer often costs SEK 595–995. Larger amounts — for example a home cinema system with multiple speakers — can cost SEK 1,495–2,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on electronics transport?",
          answer:
            "Electronics transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Can you transport a large TV?",
          answer:
            "Yes, we transport TVs of all sizes — from 32 inches to 85 inches and larger. We always transport the TV upright, protected with furniture blankets and secured with tie-downs in the van.",
        },
        {
          question: "Do you transport TVs upright?",
          answer:
            "Yes, always. Flat-screen TVs must be transported upright to protect the panel. We secure the TV vertically in the van with blankets and tie-downs so it cannot fall or wobble.",
        },
        {
          question: "Can you pick up electronics I bought on Blocket?",
          answer:
            "Yes, we collect electronics from Blocket sellers across the greater Stockholm and Gothenburg areas. Enter the seller's address when booking — we carry out, protect, load and deliver to your home.",
        },
        {
          question: "Do you protect electronics during transport?",
          answer:
            "Yes, we protect with furniture blankets, bubble wrap if needed and tie-downs. If you have the original packaging we use that. Everything is insured — if anything is damaged we replace it.",
        },
        {
          question: "Can you transport a desktop computer?",
          answer:
            "Yes, we transport desktop PCs, gaming computers and servers. We carry the computer upright, secure it so internal components don't come loose and protect with blankets.",
        },
        {
          question: "Can you pick up electronics from Elgiganten or other stores?",
          answer:
            "Yes, we collect electronics from Elgiganten, Power, MediaMarkt, NetOnNet, Webhallen and all other stores and collection points. Enter the store address when booking.",
        },
        {
          question: "In which municipalities do you offer electronics transport?",
          answer:
            "We offer electronics transport across the greater Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Electronics transport in 3 easy steps",
        step2Text:
          "Our young workers protect the electronics with furniture blankets, load carefully into the van and drive to the delivery address. There we carry in and place everything where you want it.",
        step3Title: "Electronics in place",
        step3Desc:
          "Your electronics are where you want them — protected, intact and ready to plug in. Done.",
        ctaHeading: "Book electronics transport today",
        ctaText:
          "Enter the pickup address and delivery address, describe what needs transporting and pick a time. You see the price instantly.",
      },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Transport TV — config för /transport-tv
  // Vinkel: Nischad sida specifikt för transport av TV-apparater.
  // Unik vinkel vs transport-elektronik: fokus enbart på TV – stående transport,
  // Blocket-köp, stora OLED/QLED-skärmar, väggfästen, originalförpackning etc.
  // transport-elektronik = bredd (all elektronik), denna = djup (bara TV).
  // ──────────────────────────────────────────────────────────────────
  {
    slug: "transport-tv",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport TV – Smidig TV-Transport med Skåpbil | Ungdomskraft",
      description:
        "Boka transport av TV med skåpbil och personal. Vi hämtar, skyddar och levererar din TV stående och säkert – köpt på Blocket, från butik eller vid flytt. Prisvärt med unga som gör skillnad.",
      h1: "Transport TV",
    },

    hero: {
      heading: "Transport av TV",
      intro:
        "Köpt en ny 65-tums OLED på Blocket? Ska storbilds-TV:n med i flytten? Behöver du hämta en TV från Elgiganten som inte fick plats i bilen? Vi hämtar TV:n på upphämtningsadressen, skyddar skärmen med möbelfiltar, transporterar alltid stående i skåpbilen och kör till leveransadressen. Där bär vi in och ställer på plats. Du slipper hyra bil, riskera en trasig skärm eller försöka klämma in en 75-tummare i personbilen.",
      heroImage: "/hero-transport-elektronik.png",
    },

    featureCards: {
      sinceText: "TV-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft transporterat tusentals TV-apparater i Stockholms- och Göteborgsområdet – från 32-tums kökstevar till 85-tums hemmabio-TV. Våra ungdomar vet att TV:n alltid ska stå, aldrig ligga, och hur man skyddar panelen i trapphus och skåpbil.",
      bookYourText: "Boka din TV-transport",
      bookDescText:
        "Ange hämtadress och leveransadress, beskriv TV:ns storlek och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för TV-transport",
      vehicleDescText:
        "Vi kommer med skåpbil som rymmer även de största TV-apparaterna. Möbelfiltar och spännband ingår – TV:n transporteras stående och säkrad hela vägen.",
    },

    itemsSection: {
      heading: "TV-apparater vi transporterar",
      items: [
        {
          name: "OLED-TV",
          description:
            "LG, Sony, Samsung och Philips OLED – extra känslig panel som kräver varsam hantering och stående transport",
          image: "/tv.webp",
        },
        {
          name: "QLED & LED-TV",
          description:
            "Samsung QLED, LG NanoCell och andra LED-TV – alla storlekar från 32 till 85 tum",
          image: "/tv.webp",
        },
        {
          name: "Storbilds-TV (75–85+)",
          description:
            "Extra stora TV-apparater som kräver skåpbil – vi bär och manövrerar genom trånga trapphus",
          image: "/tv.webp",
        },
        {
          name: "Hemmabio-TV & projektor",
          description:
            "Kompletta hemmabiouppsättningar med TV eller projektor, soundbar och surroundhögtalare",
          image: "/tv.webp",
        },
        {
          name: "TV med väggfäste",
          description:
            "Vi monterar loss TV:n från väggfästet, transporterar och kan montera på nytt fäste vid leverans",
          image: "/tv.webp",
        },
        {
          name: "Gaming-TV & monitor",
          description:
            "Gamingskärmar med hög uppdateringsfrekvens och stora curved monitorer",
          image: "/tv.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport av TV – alltid stående, skyddad och försäkrad",
      mainText:
        "Att transportera en TV är inte som att flytta en möbel. Moderna platta TV-apparater har känsliga paneler som inte tål tryck, böjning eller liggande transport. En OLED-panel kan skadas permanent om den böjs bara några millimeter. Och en 65-tums TV får helt enkelt inte plats i en vanlig personbil. Med Ungdomskrafts TV-transport får du ett team som vet exakt hur en TV ska hanteras. Vi hämtar TV:n på upphämtningsadressen, skyddar hela skärmen med möbelfiltar (eller originalförpackningen om du har den), transporterar alltid stående i skåpbilen och säkrar med spännband så att TV:n inte kan falla, glida eller vicka under körningen. På leveransadressen bär vi in och ställer TV:n där du vill ha den. Vi transporterar alla typer och storlekar – OLED, QLED, LED, plasma och curved-TV från 32 tum till 85+ tum. Oavsett om du köpt en TV på Blocket, hämtar från butik, flyttar till ny bostad eller skickar TV:n till stugan – vi tar hand om hela transporten.",
      preparationHeading: "Förbered inför TV-transport",
      preparationText:
        "Har du originalkartongen? Använd den – den ger bäst skydd. Annars skyddar vi med möbelfiltar. Koppla loss alla kablar (HDMI, ström, antenn) och packa dem separat. Om TV:n sitter på väggfäste – vi kan montera loss den, men det sparar tid om du gör det i förväg. Ta bort eventuella externa enheter (Apple TV, Chromecast, soundbar) som sitter fast. Torka av skärmen så att eventuella skador syns innan transport. Se till att det finns fri passage genom dörrar och trapphus. Ha portkod redo för båda adresserna.",
      additionalSections: [
        {
          heading: "Transport av TV köpt på Blocket eller Facebook Marketplace",
          content:
            "Hittat drömteven på Blocket eller en budgetskärm på Facebook Marketplace? Vi hämtar TV:n hos säljaren, skyddar skärmen, lastar stående i skåpbilen och kör hem till dig. Du behöver inte hyra bil, försöka klämma in en stor TV i baksätet eller oroa dig för att skärmen ska spricka på vägen. Många av våra TV-transporter är just Blocket-köp – vi vet hur det funkar och vi är alltid i tid hos säljaren.",
        },
        {
          heading: "Varför TV:n alltid ska transporteras stående",
          content:
            "En platt TV ska aldrig ligga ner under transport. Skärmpanelen – särskilt OLED och QLED – är designad för att bära sin vikt vertikalt. Läggs TV:n ner utsätts panelen för tryck och vibration som kan orsaka permanenta skador: döda pixlar, sprickor i panelen eller deformerade kanter. Vi transporterar alltid TV:n stående i skåpbilen, skyddad med möbelfiltar runt hela skärmen och säkrad med spännband mot skåpbilens vägg. Det är så tillverkarna rekommenderar det – och så vi alltid gör det.",
        },
        {
          heading: "TV-transport vid flytt och från butik",
          content:
            "Ska TV:n med i flytten? Vi skyddar, bär ut och transporterar den stående till din nya bostad. Kombinera gärna med vår flytthjälp om du har fler saker. Köpt en ny TV från Elgiganten, Power, NetOnNet eller IKEA men fick inte med den i bilen? Vi hämtar i butiken och levererar hem till dig. Behöver du hjälp att montera TV:n på väggfäste? Beskriv det vid bokning – vi har verktyg och kan sätta upp de flesta fästen.",
        },
        {
          heading: "Varför boka TV-transport med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje TV-transport vi genomför. När du bokar TV-transport hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de skyddar din OLED-panel till hur de navigerar försiktigt genom trapphuset.\n\nSedan starten 2014 har vi transporterat tusentals TV-apparater i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig varsam hantering av ömtåliga skärmar, varför TV:n alltid ska stå upprätt, hur man säkrar med spännband och filtar, och hur man manövrerar en 75-tummare genom en smal dörröppning utan att skada vare sig TV:n eller dörrkarmen.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team tar sig tid att skydda skärmen ordentligt, dubbelkollar att TV:n står säkert i skåpbilen och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje TV-transport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nVarje TV-transport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport och teknikbranschen – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din TV-transport blir en del av något större.\n\nVi tar också hållbarhet på allvar. Förpackningsmaterial som frigolit och kartong återvinns. Gamla TV-apparater som inte längre fungerar hjälper vi att köra till godkänd elektronikåtervinning. Ingenting slängs i onödan.\n\nDet är varsam TV-transport med mening.\n\nTV-transport ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport av TV?",
        answer:
          "Priset beror på TV:ns storlek, avstånd och om bärning i trappor krävs. En TV upp till 55 tum landar ofta på 595–995 kr. Större TV-apparater (65–85 tum) kan landa på 995–1 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på TV-transport?",
        answer:
          "TV-transport ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Transporterar ni TV:n stående?",
        answer:
          "Ja, alltid. Vi transporterar alla TV-apparater stående i skåpbilen – det är det enda säkra sättet. TV:n skyddas med möbelfiltar och säkras med spännband så att den inte kan falla eller vicka.",
      },
      {
        question: "Kan ni transportera en 85-tums TV?",
        answer:
          "Ja, vi transporterar TV-apparater i alla storlekar – från 32 till 85+ tum. Vi har skåpbilar som rymmer även de största TV-apparaterna och vet hur man bär dem genom trånga dörröppningar.",
      },
      {
        question: "Kan ni hämta en TV jag köpt på Blocket?",
        answer:
          "Ja, vi hämtar TV:n hos säljaren, skyddar skärmen, lastar stående i skåpbilen och kör hem till dig. Ange säljarens adress vid bokning. Vi hämtar även TV-apparater från Facebook Marketplace.",
      },
      {
        question: "Skyddar ni TV-skärmen under transport?",
        answer:
          "Ja, vi skyddar skärmen med möbelfiltar runt hela TV:n och säkrar med spännband i skåpbilen. Har du originalkartongen använder vi den – det ger bäst skydd. Allt är försäkrat.",
      },
      {
        question: "Kan ni montera loss TV:n från väggfäste?",
        answer:
          "Ja, vi monterar loss TV:n från väggfästet innan transport. Vi kan även montera TV:n på ett nytt väggfäste på leveransadressen – beskriv det vid bokning.",
      },
      {
        question: "Ingår skåpbil i TV-transport?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån TV:ns mått – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni hämta en TV från Elgiganten eller annan butik?",
        answer:
          "Ja, vi hämtar TV-apparater från Elgiganten, Power, NetOnNet, MediaMarkt, IKEA och alla andra butiker och utlämningsställen. Ange butikens adress vid bokning.",
      },
      {
        question: "Kan en OLED-TV transporteras säkert?",
        answer:
          "Ja, vi har lång erfarenhet av att transportera OLED-TV-apparater. OLED-paneler är extra känsliga för tryck och böjning – därför transporterar vi dem alltid stående, med extra skydd runt skärmen. Har du originalkartongen är det optimalt.",
      },
      {
        question: "Kan ni transportera TV vid flytt?",
        answer:
          "Ja, vi transporterar din TV stående och skyddad till din nya bostad. Kombinera gärna med vår flytthjälp om du har fler saker att flytta.",
      },
      {
        question: "Hur snabbt kan ni köra min TV?",
        answer:
          "Vi erbjuder ofta TV-transport med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni transportera TV på helger?",
        answer:
          "Ja, vi erbjuder TV-transport på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Kan ni ta med den gamla TV:n till återvinning?",
        answer:
          "Ja, om du byter ut din gamla TV kan vi ta med den och köra till godkänd elektronikåtervinning. Beskriv det vid bokning så ordnar vi det i samma körning.",
      },
      {
        question: "I vilka kommuner erbjuder ni transport av TV?",
        answer:
          "Vi erbjuder transport av TV i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "TV-transport i 3 enkla steg",
      step2Text:
        "Våra ungdomar skyddar TV:n med möbelfiltar, bär ut varsamt, lastar stående i skåpbilen och kör till leveransadressen. Där bär vi in och ställer TV:n på plats.",
      step3Title: "TV:n på plats",
      step3Desc:
        "Din TV står där du vill ha den – stående transporterad, skyddad och hel. Klart.",
      ctaHeading: "Boka TV-transport idag",
      ctaText:
        "Ange hämtadress och leveransadress, beskriv TV:ns storlek och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "tv-transport",
      meta: {
        title:
          "TV Transport – Safe TV Delivery with Van & Crew | Ungdomskraft",
        description:
          "Book TV transport – we pick up, protect and deliver your TV upright and safely. From Blocket, store or when moving. Fixed price, insured. Book today!",
        h1: "TV transport",
      },
      hero: {
        heading: "TV transport",
        intro:
          "Bought a new 65-inch OLED on Blocket? Taking the big-screen TV with you when moving? Need to pick up a TV from Elgiganten that didn't fit in the car? We collect the TV at the pickup address, protect the screen with furniture blankets, always transport upright in the van and drive to the delivery address. There we carry in and place the TV where you want it. No need to rent a car, risk a cracked screen or try to squeeze a 75-incher into your saloon car.",
      },
      featureCards: {
        sinceText: "TV transport since 2014",
        bookYourText: "Book your TV transport",
        bookDescText:
          "Enter the pickup address and delivery address, describe the TV size and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for TV transport",
        vehicleDescText:
          "We arrive with a van that fits even the largest TVs. Furniture blankets and tie-downs included — the TV is transported upright and secured the entire way.",
      },
      itemsSection: {
        heading: "TVs we transport",
        items: [
          {
            name: "OLED TVs",
            description:
              "LG, Sony, Samsung and Philips OLED — extra sensitive panels requiring careful handling and upright transport",
            image: "/tv.webp",
          },
          {
            name: "QLED & LED TVs",
            description:
              "Samsung QLED, LG NanoCell and other LED TVs — all sizes from 32 to 85 inches",
            image: "/tv.webp",
          },
          {
            name: "Large-format TVs (75–85+)",
            description:
              "Extra large TVs that require a van — we carry and manoeuvre through tight stairwells",
            image: "/tv.webp",
          },
          {
            name: "Home cinema TV & projector",
            description:
              "Complete home cinema setups with TV or projector, soundbar and surround speakers",
            image: "/tv.webp",
          },
          {
            name: "Wall-mounted TVs",
            description:
              "We unmount the TV from the wall bracket, transport and can remount at the delivery address",
            image: "/tv.webp",
          },
          {
            name: "Gaming TVs & monitors",
            description:
              "High refresh rate gaming screens and large curved monitors",
            image: "/tv.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "TV transport — always upright, protected and insured",
        mainText:
          "Transporting a TV is not like moving a piece of furniture. Modern flat-screen TVs have sensitive panels that cannot tolerate pressure, bending or lying flat. An OLED panel can be permanently damaged if bent just a few millimetres. And a 65-inch TV simply does not fit in a regular car. With Ungdomskraft's TV transport you get a team that knows exactly how a TV should be handled. We collect the TV at the pickup address, protect the entire screen with furniture blankets (or the original box if you have it), always transport upright in the van and secure with tie-downs so the TV cannot fall, slide or wobble during the drive. At the delivery address we carry in and place the TV where you want it.",
        preparationHeading: "Prepare for TV transport",
        preparationText:
          "Have the original box? Use it — it provides the best protection. Otherwise we protect with furniture blankets. Disconnect all cables (HDMI, power, aerial) and pack them separately. If the TV is wall-mounted — we can unmount it, but it saves time if you do it in advance. Remove any external devices (Apple TV, Chromecast, soundbar) that are attached. Wipe the screen so any existing damage is visible before transport. Make sure there is clear passage through doors and stairwells. Have entry codes ready for both addresses.",
        additionalSections: [
          {
            heading: "TV transport for Blocket and Facebook Marketplace purchases",
            content:
              "Found the perfect TV on Blocket or a budget screen on Facebook Marketplace? We collect the TV from the seller, protect the screen, load upright in the van and drive it to your home. No need to rent a car, try to squeeze a large TV into the back seat or worry about the screen cracking on the way. Many of our TV transports are exactly these kinds of purchases — we know how it works and we're always on time at the seller's address.",
          },
          {
            heading: "Why TVs must always be transported upright",
            content:
              "A flat-screen TV should never lie down during transport. The screen panel — especially OLED and QLED — is designed to bear its weight vertically. If the TV lies flat, the panel is exposed to pressure and vibration that can cause permanent damage: dead pixels, panel cracks or warped edges. We always transport the TV upright in the van, protected with furniture blankets around the entire screen and secured with tie-downs against the van wall. That's how the manufacturers recommend it — and how we always do it.",
          },
          {
            heading: "TV transport when moving and from stores",
            content:
              "Taking the TV with you when moving? We protect, carry out and transport it upright to your new home. Combine with our moving help if you have more items. Bought a new TV from Elgiganten, Power, NetOnNet or IKEA but couldn't fit it in the car? We collect from the store and deliver to your home. Need help mounting the TV on a wall bracket? Describe it when booking — we have tools and can fit most brackets.",
          },
          {
            heading: "Why book TV transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and you notice it in every TV transport we carry out. When you book TV transport with us you get a team of motivated, energetic young people who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that attitude permeates everything — from how they protect your OLED panel to how they carefully navigate through the stairwell.\n\nSince 2014 we have transported thousands of TVs in the Stockholm and Gothenburg areas. Along the way we have given thousands of young people their first real work experience — with everything that entails: showing up on time, communicating professionally with customers, solving problems on site, working as a team and taking responsibility for the result. Our workers learn careful handling of fragile screens, why the TV must always stand upright, how to secure with tie-downs and blankets, and how to manoeuvre a 75-incher through a narrow doorway without damaging the TV or the door frame.\n\nThis is not only good for the young people — it's good for you as a customer. You notice the difference in commitment and energy. Our teams take the time to protect the screen properly, double-check that the TV is secure in the van and always ask if there is anything else they can help with. We have built a culture where every TV transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we've received on Trustpilot and Google.\n\nEvery TV transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport and the tech industry — carrying with them the values and work ethic they learned with us. Choosing us means your TV transport becomes part of something bigger.\n\nWe also take sustainability seriously. Packaging materials like polystyrene and cardboard are recycled. Old TVs that no longer work — we help drive them to approved electronics recycling. Nothing is dumped carelessly.\n\nIt's careful TV transport with purpose.\n\nTV transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does TV transport cost?",
          answer:
            "The price depends on the TV size, distance and whether stair carrying is needed. A TV up to 55 inches often costs SEK 595–995. Larger TVs (65–85 inches) can cost SEK 995–1,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on TV transport?",
          answer:
            "TV transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Do you transport the TV upright?",
          answer:
            "Yes, always. We transport all TVs upright in the van — it's the only safe way. The TV is protected with furniture blankets and secured with tie-downs so it cannot fall or wobble.",
        },
        {
          question: "Can you transport an 85-inch TV?",
          answer:
            "Yes, we transport TVs of all sizes — from 32 to 85+ inches. We have vans that fit even the largest TVs and know how to carry them through tight doorways.",
        },
        {
          question: "Can you pick up a TV I bought on Blocket?",
          answer:
            "Yes, we collect the TV from the seller, protect the screen, load upright in the van and deliver to your home. Enter the seller's address when booking. We also pick up TVs from Facebook Marketplace.",
        },
        {
          question: "Do you protect the TV screen during transport?",
          answer:
            "Yes, we protect the screen with furniture blankets around the entire TV and secure with tie-downs in the van. If you have the original box we use that — it provides the best protection. Everything is insured.",
        },
        {
          question: "Can you unmount a TV from a wall bracket?",
          answer:
            "Yes, we unmount the TV from the wall bracket before transport. We can also mount it on a new bracket at the delivery address — describe it when booking.",
        },
        {
          question: "Can you pick up a TV from Elgiganten or other stores?",
          answer:
            "Yes, we collect TVs from Elgiganten, Power, NetOnNet, MediaMarkt, IKEA and all other stores and collection points. Enter the store address when booking.",
        },
        {
          question: "In which municipalities do you offer TV transport?",
          answer:
            "We offer TV transport across the greater Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "TV transport in 3 easy steps",
        step2Text:
          "Our young workers protect the TV with furniture blankets, carefully carry it out, load upright in the van and drive to the delivery address. There we carry in and place the TV where you want it.",
        step3Title: "TV in place",
        step3Desc:
          "Your TV is where you want it — transported upright, protected and intact. Done.",
        ctaHeading: "Book TV transport today",
        ctaText:
          "Enter the pickup address and delivery address, describe the TV size and pick a time. You see the price instantly.",
      },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Transporthjälp — config för /transporthjalp
  // Vinkel: Bred generell sida som rankar för "transporthjälp" och "hjälp transport".
  // Täcker ALLA typer av transporter vi erbjuder – möbler, vitvaror, elektronik,
  // Blocket-köp, flytt, förråd etc. Fungerar som en hub/landningssida.
  // ──────────────────────────────────────────────────────────────────
  {
    slug: "transporthjalp",
    serviceCategory: "transport",

    meta: {
      title:
        "Transporthjälp – Skåpbil & Personal för Alla Transporter | Ungdomskraft",
      description:
        "Boka transporthjälp i Stockholm och Göteborg. Skåpbil och personal som hämtar, bär och levererar möbler, vitvaror och allt annat. Fast pris, försäkrat.",
      h1: "Transporthjälp",
    },

    hero: {
      heading: "Transporthjälp",
      intro:
        "Behöver du hjälp att transportera något? Oavsett om det är en soffa från Blocket, en tvättmaskin till nya lägenheten, kartonger till förrådet eller en TV från butiken – vi fixar det. Vi kommer med skåpbil och ett team av starka ungdomar, hämtar på upphämtningsadressen, bär ut, lastar och kör till leveransadressen. Där bär vi in och ställer på plats. Du slipper hyra bil, bära själv eller göra flera vändor.",
      heroImage: "/hero-transporthjalp.png",
    },

    featureCards: {
      headingText: "Vi hjälper dig med transporten",
      sinceText: "Transporthjälp sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder med alla typer av transporter i Stockholms- och Göteborgsområdet. Möbler, vitvaror, elektronik, kartonger, Blocket-köp, butikshämtningar och flytt – vi tar hand om allt.",
      bookYourText: "Boka din transporthjälp",
      bookDescText:
        "Ange hämtadress och leveransadress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Rätt skåpbil för varje transport",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska köra en enda sak eller fylla hela lastutrymmet. Möbelfiltar, bärremmar och spännband ingår alltid.",
    },

    itemsSection: {
      heading: "Saker vi hjälper dig att transportera",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, sängar, bord, garderober, hyllor och allt annat – köpt på Blocket, från butik eller vid flytt",
          image: "/sofa.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Tvättmaskin, kylskåp, frys, diskmaskin och spis – vi hanterar tunga vitvaror säkert",
          image: "/washer.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, skärmar, spelkonsoler och ljudanläggningar – varsam hantering",
          image: "/tv.webp",
        },
        {
          name: "Kartonger & lådor",
          description:
            "Flyttkartonger, paket, lådor och säckar – till och från förråd, vid flytt eller leverans",
          image: "/box.webp",
        },
        {
          name: "Trädgård & utemöbler",
          description:
            "Trädgårdsmöbler, grill, krukor, cyklar och säsongsprylar",
          image: "/patio-furniture.webp",
        },
        {
          name: "Övrigt",
          description:
            "Konst, byggmaterial, kontorsutrustning, träningsutrustning – finns det, kör vi det",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transporthjälp – skåpbil och personal som kör allt från A till B",
      mainText:
        "Ibland behöver du bara hjälp att flytta något från en plats till en annan. En möbel du köpt på Blocket, en tvättmaskin som ska till nya lägenheten, kartonger som ska till förrådet eller en TV som inte fick plats i bilen. Det är precis det vi gör. Ungdomskrafts transporthjälp ger dig en skåpbil och ett team av starka, motiverade ungdomar som hämtar, bär, lastar, kör och levererar. Vi hämtar på vilken adress som helst – hemma hos en Blocket-säljare, en butik, ett utlämningsställe, ett förråd eller din gamla bostad. Vi bär ut, skyddar med möbelfiltar, lastar säkert i skåpbilen och kör till leveransadressen. Där bär vi in och ställer allt på plats. Du behöver inte hyra bil, fråga kompisar om hjälp, göra flera turer eller slita ryggen. Vår transporthjälp täcker allt: möbler, vitvaror, elektronik, kartonger, trädgårdsmöbler, byggmaterial, kontorsutrustning och allt annat som behöver komma från punkt A till punkt B.",
      preparationHeading: "Förbered inför din transport",
      preparationText:
        "Beskriv så detaljerat du kan vad som ska transporteras – typ av föremål, ungefärlig storlek och vikt. Ju mer vi vet desto bättre kan vi planera fordon och team. Demontera möbler som inte går genom dörrar hela. Stäng av vatten och el till vitvaror. Koppla loss kablar från elektronik. Ha allt redo vid dörren eller i det rum det ska hämtas. Se till att det finns fri passage genom trapphus och att skåpbilen kan parkera nära porten. Ha portkod och nycklar redo för båda adresserna.",
      additionalSections: [
        {
          heading: "Transporthjälp för Blocket-köp och Marketplace",
          content:
            "Hittat något på Blocket eller Facebook Marketplace men har ingen bil? Vi hämtar ditt köp hos säljaren, bär ut, lastar och kör hem till dig. Soffor, bord, kylskåp, tvättmaskiner, TV-apparater – oavsett vad du köpt fixar vi transporten. Du slipper hyra bil, koordinera med kompisar eller betala för dyr hemleverans. Ange säljarens adress som hämtpunkt vid bokning – vi sköter resten.",
        },
        {
          heading: "Transporthjälp vid flytt",
          content:
            "Ska du flytta men behöver inte en hel flyttfirma? Vår transporthjälp är perfekt när du har packat själv men behöver hjälp att köra sakerna till nya bostaden. Vi hämtar möbler, kartonger, vitvaror och elektronik från din gamla adress, lastar allt i skåpbilen och levererar till den nya. Vi bär in och ställer allt på plats. Behöver du mer omfattande flytthjälp med packning och planering? Kolla vår flytthjälp-sida.",
        },
        {
          heading: "Transporthjälp från butik och e-handel",
          content:
            "Köpt möbler, vitvaror eller elektronik i butik men leveransen passar inte eller kostar för mycket? Vi hämtar i butiken och kör hem till dig. IKEA, Elgiganten, Mio, JYSK, Power, Bauhaus, Byggmax – oavsett butik hämtar vi och levererar. Beställt online och paketet väntar på utlämningsstället men det är för stort för bilen? Samma sak – vi hämtar och kör hem.",
        },
        {
          heading: "Varför boka transporthjälp med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje transport vi genomför. När du bokar transporthjälp hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de bär din soffa i trappan till hur de ställer tvättmaskinen på exakt rätt plats i tvättstugan.\n\nSedan starten 2014 har vi genomfört tusentals transporter i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, säker lastning, varsam hantering av ömtåliga föremål, hur man navigerar trånga trapphus och hur man alltid skyddar kundens ägodelar, golv, väggar och dörrkarmar.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team springer där andra går, dubbelkollar att allt är rätt lastat och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje transport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nMen det handlar inte bara om kvalitet – det handlar om mening. Varje transporthjälp du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport och serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din transport blir en del av något större.\n\nVi tar också hållbarhet på allvar. Saker som inte längre behövs hjälper vi att skänka vidare eller köra till rätt återvinningsstation. Förpackningsmaterial sorteras och återvinns. Ingenting slängs i onödan.\n\nDet är smidig transporthjälp med mening.\n\nTransporthjälp ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transporthjälp?",
        answer:
          "Priset beror på vad som ska transporteras, avstånd och om bärning i trappor krävs. En enstaka möbel eller vitvara landar ofta på 595–995 kr. Större mängder med flera tunga saker kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på transporthjälp?",
        answer:
          "Transporthjälp ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Vad kan ni hjälpa mig att transportera?",
        answer:
          "Vi transporterar allt: möbler, vitvaror, elektronik, kartonger, trädgårdsmöbler, byggmaterial, kontorsutrustning, konst, cyklar och mycket mer. Om det får plats i en skåpbil kör vi det.",
      },
      {
        question: "Ingår skåpbil i transporthjälpen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni hämta saker jag köpt på Blocket?",
        answer:
          "Ja, vi hämtar ditt Blocket-köp hos säljaren, bär ut, lastar och levererar hem till dig. Soffor, vitvaror, elektronik, möbler – oavsett vad. Ange säljarens adress vid bokning.",
      },
      {
        question: "Kan ni hämta saker från butik?",
        answer:
          "Ja, vi hämtar från IKEA, Elgiganten, Mio, JYSK, Power, Bauhaus, Byggmax och alla andra butiker. Ange butikens adress vid bokning.",
      },
      {
        question: "Bär ni sakerna i trappor?",
        answer:
          "Ja, vi bär i alla typer av trapphus – smala, svängda och utan hiss. Vi har säckkärra, bärremmar och rätt teknik för tunga och skrymmande föremål.",
      },
      {
        question: "Skyddar ni sakerna under transport?",
        answer:
          "Ja, vi skyddar med möbelfiltar, bärremmar och spännband. TV-apparater transporteras stående. Vitvaror säkras med remmar. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Kan ni hjälpa med transport vid flytt?",
        answer:
          "Ja, vår transporthjälp är perfekt när du packat själv men behöver hjälp att köra sakerna. Vi hämtar, lastar och levererar till din nya bostad. Kombinera med vår flytthjälp för en komplett lösning.",
      },
      {
        question: "Kan ni köra saker till förråd?",
        answer:
          "Ja, vi kör möbler, kartonger och annat till och från förråd. Vi bär in och ställer organiserat i förrådet. Vi kör till alla förrådsanläggningar i Stockholm och Göteborg.",
      },
      {
        question: "Hur snabbt kan ni hjälpa med transport?",
        answer:
          "Vi erbjuder ofta transporthjälp med kort varsel – ibland redan samma vecka. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni transportera tunga saker?",
        answer:
          "Ja, vi transporterar tunga saker – kylskåp, tvättmaskiner, kontinentalsängar, kassaskåp och byggmaterial. Vi har säckkärra, bärremmar och team som är vana vid tungt arbete.",
      },
      {
        question: "Kan jag boka transporthjälp på helger?",
        answer:
          "Ja, vi erbjuder transporthjälp på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Demonterar ni möbler innan transport?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär. Vi monterar även ihop dem på leveransadressen om du vill.",
      },
      {
        question: "I vilka kommuner erbjuder ni transporthjälp?",
        answer:
          "Vi erbjuder transporthjälp i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transporthjälp i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar sakerna, bär ut, skyddar med möbelfiltar, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer allt på plats.",
      step3Title: "Levererat",
      step3Desc:
        "Dina saker står där du vill ha dem – säkert transporterade och snyggt inställda. Klart.",
      ctaHeading: "Boka transporthjälp idag",
      ctaText:
        "Ange hämtadress och leveransadress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "transport-help",
      meta: {
        title:
          "Transport Help – Van & Crew for All Transports | Ungdomskraft",
        description:
          "Book transport help in Stockholm & Gothenburg. Van and crew to pick up, carry and deliver furniture, appliances and more. Fixed price, insured. Book today!",
        h1: "Transport help",
      },
      hero: {
        heading: "Transport help",
        intro:
          "Need help transporting something? Whether it's a sofa from Blocket, a washing machine to your new flat, boxes to storage or a TV from the shop — we've got it. We arrive with a van and a team of strong young workers, pick up at the collection address, carry out, load and drive to the delivery address. There we carry in and place everything where you want it. No need to rent a car, carry things yourself or make multiple trips.",
      },
      featureCards: {
        headingText: "We help you with the transport",
        sinceText: "Transport help since 2014",
        bookYourText: "Book your transport help",
        bookDescText:
          "Enter the pickup address and delivery address, describe what needs transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "The right van for every transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're transporting a single item or filling the entire cargo area. Furniture blankets, straps and tie-downs always included.",
      },
      itemsSection: {
        heading: "Things we help you transport",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, beds, tables, wardrobes, shelves and everything else — bought on Blocket, from a store or when moving",
            image: "/sofa.webp",
          },
          {
            name: "Appliances",
            description:
              "Washing machines, fridges, freezers, dishwashers and cookers — we handle heavy appliances safely",
            image: "/washer.webp",
          },
          {
            name: "Electronics",
            description:
              "TVs, computers, monitors, game consoles and audio systems — careful handling",
            image: "/tv.webp",
          },
          {
            name: "Boxes & packages",
            description:
              "Moving boxes, parcels, crates and bags — to and from storage, when moving or for delivery",
            image: "/box.webp",
          },
          {
            name: "Garden & outdoor",
            description:
              "Garden furniture, barbecues, pots, bicycles and seasonal items",
            image: "/patio-furniture.webp",
          },
          {
            name: "Everything else",
            description:
              "Artwork, building materials, office equipment, gym equipment — if it exists, we transport it",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Transport help — van and crew that move anything from A to B",
        mainText:
          "Sometimes you just need help moving something from one place to another. A piece of furniture you bought on Blocket, a washing machine going to the new flat, boxes heading to storage or a TV that wouldn't fit in the car. That's exactly what we do. Ungdomskraft's transport help gives you a van and a team of strong, motivated young workers who pick up, carry, load, drive and deliver. We collect from any address — a Blocket seller's home, a store, a collection point, a storage facility or your old home. We carry out, protect with furniture blankets, load safely in the van and drive to the delivery address. There we carry in and place everything where you want it. No need to rent a car, ask friends for help, make multiple trips or strain your back.",
        preparationHeading: "Prepare for your transport",
        preparationText:
          "Describe as thoroughly as you can what needs transporting — type of item, approximate size and weight. The more we know the better we can plan the right vehicle and team. Disassemble furniture that won't fit through doors whole. Turn off water and electricity to appliances. Disconnect cables from electronics. Have everything ready by the door or in the room where it needs collecting. Make sure there is clear passage through stairwells and the van can park near the entrance. Have entry codes and keys ready for both addresses.",
        additionalSections: [
          {
            heading: "Transport help for Blocket and Marketplace purchases",
            content:
              "Found something on Blocket or Facebook Marketplace but don't have a car? We collect your purchase from the seller, carry out, load and deliver to your home. Sofas, appliances, electronics, furniture — whatever you've bought, we handle the transport. No need to rent a car, coordinate with friends or pay for expensive home delivery. Enter the seller's address as the pickup point when booking — we take care of the rest.",
          },
          {
            heading: "Transport help when moving",
            content:
              "Moving but don't need a full removal company? Our transport help is perfect when you've packed yourself but need help getting everything to the new home. We collect furniture, boxes, appliances and electronics from your old address, load everything into the van and deliver to the new one. We carry in and place everything where you want it. Need more comprehensive moving help with packing and planning? Check out our moving help page.",
          },
          {
            heading: "Transport help from stores and e-commerce",
            content:
              "Bought furniture, appliances or electronics in a store but the delivery doesn't suit you or costs too much? We collect from the store and deliver to your home. IKEA, Elgiganten, Mio, JYSK, Power, Bauhaus, Byggmax — whatever the store, we pick up and deliver. Ordered online and the package is waiting at a collection point but it's too big for the car? Same thing — we collect and drive it home.",
          },
          {
            heading: "Why book transport help with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and you notice it in every transport we carry out. When you book transport help with us you get a team of motivated, energetic young people who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that attitude permeates everything — from how they carry your sofa down the stairs to how they place the washing machine in exactly the right spot.\n\nSince 2014 we have completed thousands of transports in the Stockholm and Gothenburg areas. Along the way we have given thousands of young people their first real work experience — with everything that entails: showing up on time, communicating professionally with customers, solving problems on site, working as a team and taking responsibility for the result. Our workers learn lifting technique, safe loading, careful handling of fragile items, how to navigate tight stairwells and how to always protect the customer's belongings, floors, walls and door frames.\n\nThis is not only good for the young people — it's good for you as a customer. You notice the difference in commitment and energy. Our teams go the extra mile, double-check that everything is loaded correctly and always ask if there is anything else they can help with. We have built a culture where every transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we've received on Trustpilot and Google.\n\nBut it's not just about quality — it's about purpose. Every transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport and service industries — carrying with them the values and work ethic they learned with us. Choosing us means your transport becomes part of something bigger.\n\nWe also take sustainability seriously. Items that are no longer needed — we help donate them or drive them to the correct recycling station. Packaging materials are sorted and recycled. Nothing is dumped carelessly.\n\nIt's smooth transport help with purpose.\n\nTransport help does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does transport help cost?",
          answer:
            "The price depends on what needs transporting, distance and whether stair carrying is needed. A single piece of furniture or appliance often costs SEK 595–995. Multiple heavy items can cost SEK 1,495–2,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on transport help?",
          answer:
            "Transport help does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "What can you help me transport?",
          answer:
            "We transport everything: furniture, appliances, electronics, boxes, garden furniture, building materials, office equipment, artwork, bicycles and much more. If it fits in a van, we drive it.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting — no need to rent a vehicle, drive or refuel.",
        },
        {
          question: "Can you pick up things I bought on Blocket?",
          answer:
            "Yes, we collect your Blocket purchase from the seller, carry out, load and deliver to your home. Sofas, appliances, electronics, furniture — whatever it is. Enter the seller's address when booking.",
        },
        {
          question: "Can you collect from stores?",
          answer:
            "Yes, we collect from IKEA, Elgiganten, Mio, JYSK, Power, Bauhaus, Byggmax and all other stores. Enter the store address when booking.",
        },
        {
          question: "Do you carry items up stairs?",
          answer:
            "Yes, we carry in all types of stairwells — narrow, winding and without a lift. We have hand trucks, straps and the right technique for heavy and bulky items.",
        },
        {
          question: "Do you protect items during transport?",
          answer:
            "Yes, we protect with furniture blankets, carrying straps and tie-downs. TVs are transported upright. Appliances are secured with straps. Everything is insured — if anything is damaged we replace it.",
        },
        {
          question: "In which municipalities do you offer transport help?",
          answer:
            "We offer transport help across the greater Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Transport help in 3 easy steps",
        step2Text:
          "Our young workers collect the items, carry out, protect with furniture blankets, load the van and drive to the delivery address. There we carry in and place everything where you want it.",
        step3Title: "Delivered",
        step3Desc:
          "Your items are where you want them — safely transported and neatly placed. Done.",
        ctaHeading: "Book transport help today",
        ctaText:
          "Enter the pickup address and delivery address, describe what needs transporting and pick a time. You see the price instantly.",
      },
    },
  },

  {
    // Hjälp med transport av TV — config för /hjalp-med-transport-av-tv
    // Vinkel: Sökordsvariant som fångar "hjälp med transport av TV" / "help TV transport".
    // Unik vinkel vs transport-tv: hjälp-perspektivet — du behöver hjälp att flytta/hämta din TV.
    slug: "hjalp-med-transport-av-tv",
    serviceCategory: "transport",

    meta: {
      title:
        "Hjälp Med Transport Av TV – Skåpbil & Bärhjälp | Ungdomskraft",
      description:
        "Behöver du hjälp med transport av TV? Vi hämtar, skyddar och levererar din TV stående och säkert – köpt på Blocket, från butik eller vid flytt. Boka hjälp med TV-transport idag.",
      h1: "Hjälp med transport av TV",
    },

    hero: {
      heading: "Hjälp med transport av TV",
      intro:
        "Behöver du hjälp att transportera en TV? Oavsett om du köpt en ny 75-tums OLED på Elgiganten, hittat ett fynd på Blocket eller ska flytta hemmabio-TV:n till nya lägenheten – vi hjälper dig. Ungdomskraft hämtar din TV, skyddar panelen med möbelfiltar, transporterar alltid stående i skåpbilen och levererar hem till dig. Vi bär in genom trapphus och dörrar och ställer på plats. Säker TV-transport utan risk för trasig skärm.",
      heroImage: "/hero-transport-elektronik.png",
    },

    featureCards: {
      sinceText: "TV-transporthjälp sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft transporterat tusentals TV-apparater i Stockholms- och Göteborgsområdet. Våra ungdomar vet att TV:n alltid ska stå, aldrig ligga, och hur man skyddar panelen i trapphus och skåpbil. Du får professionell hjälp utan att lyfta ett finger.",
      bookYourText: "Boka hjälp med TV-transport",
      bookDescText:
        "Ange hämtadress och leveransadress, beskriv TV:ns storlek och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för TV-transport",
      vehicleDescText:
        "Vi kommer med skåpbil som rymmer även de största TV-apparaterna. Möbelfiltar och spännband ingår – TV:n transporteras stående och säkrad hela vägen.",
    },

    itemsSection: {
      heading: "TV-apparater vi hjälper dig transportera",
      items: [
        {
          name: "OLED-TV",
          description:
            "LG, Sony, Samsung och Philips OLED – extra känslig panel som kräver varsam hantering och stående transport",
          image: "/tv.webp",
        },
        {
          name: "QLED & LED-TV",
          description:
            "Samsung QLED, LG NanoCell och andra LED-TV – alla storlekar från 32 till 85 tum",
          image: "/tv.webp",
        },
        {
          name: "Storbilds-TV (75–85+)",
          description:
            "Extra stora TV-apparater som kräver skåpbil – vi bär och manövrerar genom trånga trapphus",
          image: "/tv.webp",
        },
        {
          name: "Hemmabio & projektor",
          description:
            "Projektorer, projektordukar och surroundsystem – varsam hantering av ömtålig utrustning",
          image: "/painting.webp",
        },
        {
          name: "Gaming-skärmar",
          description:
            "Ultrawide-skärmar, curved monitors och stora gaming-skärmar som inte får plats i bilen",
          image: "/patio-furniture.webp",
        },
        {
          name: "Soundbars & ljud",
          description:
            "Soundbars, subwoofers och surroundhögtalare – ofta skrymmande och ömtåliga",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hjälp med transport av TV – vi hämtar, skyddar och levererar",
      mainText:
        "Att transportera en TV själv är riskabelt. Moderna TV-apparater har tunna, ömtåliga paneler som lätt skadas om de lutas, läggs ner eller stöts emot. En 65-tums TV i originalförpackning är cirka 160 cm bred och väger 25–40 kg – den ryms inte i de flesta personbilar, och att balansera den på baksätet riskerar att spräcka skärmen. Lägger du en OLED-TV platt kan panelen böjas och ge permanenta pixelfel. Det är därför professionell hjälp med TV-transport är den tryggaste lösningen. Ungdomskraft erbjuder hjälp med transport av TV i hela Stockholm och Göteborg. Vi hämtar din TV på upphämtningsadressen – hemma hos en Blocket-säljare, i en Elgiganten-butik, i din gamla lägenhet eller var den än befinner sig. Vi skyddar skärmen med möbelfiltar, spänner fast TV:n stående i skåpbilen (aldrig liggande) och kör till leveransadressen. Där bär vi in genom trapphus och dörrar och ställer TV:n på plats – på TV-bänken, väggen eller var du vill ha den. Vi transporterar alla typer av TV-apparater: OLED, QLED, LED, Mini LED och plasma. Alla storlekar från 32 tum till 85 tum och större. Vi hanterar Samsung, LG, Sony, Philips, TCL, Hisense och alla andra märken med samma omsorg. Våra ungdomar är utbildade i hur varje paneltyp ska hanteras – OLED-skärmar kräver extra försiktighet eftersom panelen är tunn och flexibel, medan LED-TV-apparater tål något mer men fortfarande behöver stående transport och stötskydd. Varje vecka hjälper vi hundratals kunder med TV-transport. De vanligaste situationerna: Blocket-köp där säljaren bor långt bort, butiksköp på Elgiganten eller Power som inte fick plats i bilen, flytt till ny lägenhet där TV:n är för stor för att bära själv, eller returnering av en defekt TV. Oavsett anledning – vi ger dig den hjälp du behöver.",
      preparationHeading: "Förbered inför din TV-transport",
      preparationText:
        "Packa gärna TV:n i originalförpackningen om du har kvar den – det ger bäst skydd under transporten. Har du inte originalkartongen? Inga problem – vi skyddar med möbelfiltar och bärremmar. Koppla loss alla kablar (HDMI, ström, antenner) och ta bort eventuella TV-fötter om det gör hanteringen enklare. Ha hämtadressen och leveransadressen redo, inklusive portkoder och våningsplan. Meddela om det finns trånga trapphus, smala dörröppningar eller saknad hiss – det hjälper oss att planera rätt.",
      additionalSections: [
        {
          heading: "Hjälp med TV-transport vid Blocket-köp",
          content:
            "Blocket är Sveriges största marknadsplats för begagnade TV-apparater, och många av de bästa fynden är TV-apparater som inte går att hämta med personbil. Du hittar en 65-tums Samsung OLED för halva priset – men säljaren bor tre våningar upp utan hiss i Sundbyberg, och du bor i Nacka. Med Ungdomskrafts TV-transporthjälp hämtar vi TV:n hos säljaren, bär ner genom trapphuset, skyddar och lastar stående i skåpbilen och kör till dig. Vi bär in och ställer på plats. Du slipper riskera skärmen i en personbil och behöver inte be kompisar om hjälp. Vi ansvarar enbart för transporten – kontrollera TV:ns skick hos säljaren innan vi hämtar.",
        },
        {
          heading: "Hjälp med TV-transport från butik",
          content:
            "Köpt en ny TV på Elgiganten, Power eller MediaMarkt som inte fick plats i bilen? Butikernas egna leveranser kan ta dagar att boka och kostar ofta extra för inbärning uppför trappor. Med vår TV-transporthjälp hämtar vi din TV samma dag eller nästa dag. Vi kör till butiken, hämtar din betalda TV, lastar stående i skåpbilen och levererar hem till dig med inbärning på plats. Perfekt även för hämta-i-butik-beställningar där paketet är för stort att ta hem själv.",
        },
        {
          heading: "Hjälp med TV-transport vid flytt",
          content:
            "TV:n är ofta den mest nervösa delen av flytten – en stöt i trapphuset eller ett felaktigt lyft kan kosta tusentals kronor i skada. Med vår hjälp slipper du oroa dig. Vi kopplar loss TV:n (om du vill), skyddar med filtar och transporterar stående i skåpbilen till nya lägenheten. Där bär vi in och ställer på plats. Behöver du hjälp att montera väggfästet? Beskriv det vid bokning. Vi hanterar ofta TV-transport som en del av en större flytt – du kan kombinera TV:n med andra möbler och prylar i samma tur.",
        },
        {
          heading: "Så fungerar hjälp med TV-transport",
          listItems: [
            {
              label: "Boka online",
              text: "Ange hämtadress och leveransadress. Beskriv TV:ns storlek (tum) och om originalförpackning finns. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar",
              text: "Vårt team kommer med skåpbil. Vi skyddar TV:n med möbelfiltar, bär ut och lastar stående i skåpbilen.",
            },
            {
              label: "Vi levererar",
              text: "Vi kör till leveransadressen, bär in genom trapphus och dörrar och ställer TV:n på plats.",
            },
            {
              label: "Klart",
              text: "Din TV står säkert på plats hemma hos dig. Utan en repa, utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–2 timmar. Du kan följa statusen i realtid.",
        },
        {
          heading: "Varför anlita hjälp med TV-transport?",
          content:
            "En modern TV kostar 5 000–40 000 kr. Att riskera den i en personbil, balkanserat mot baksätet utan ordentligt skydd, kan sluta med en trasig skärm och tusentals kronor i förlust. Professionell TV-transporthjälp kostar från 595 kr – en bråkdel av kostnaden att ersätta en skadad TV. Med Ungdomskraft får du skåpbil, möbelfiltar, spännband, bärhjälp och inbärning – allt inkluderat i priset. Allt är dessutom försäkrat. Om något mot förmodan skulle skadas under transporten ersätter vi det. Det ger dig trygghet som ingen personbilstransport kan matcha.",
        },
        {
          heading: "Varför boka TV-transporthjälp med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hjälp med TV-transport hos oss bidrar du också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till varsam hantering av dyr elektronik. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Allt är försäkrat, vi har egna skåpbilar och du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar.\n\nTV-transporthjälp ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hjälp med transport av TV?",
        answer:
          "Priset beror på TV:ns storlek och avståndet. En vanlig TV-transport kostar från 595 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på TV-transporthjälp?",
        answer:
          "TV-transporthjälp ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i TV-transporten?",
        answer:
          "Ja, skåpbil ingår alltid. Vi kommer med rätt storlek – du behöver inte hyra bil eller släp.",
      },
      {
        question: "Transporterar ni TV:n stående?",
        answer:
          "Ja, alltid. Vi transporterar alla TV-apparater stående i skåpbilen – aldrig liggande. Det gäller oavsett storlek och paneltyp. Att lägga en TV platt kan skada panelen permanent.",
      },
      {
        question: "Skyddar ni TV:n under transporten?",
        answer:
          "Ja, vi skyddar med möbelfiltar och spännband. TV:n säkras stående i skåpbilen. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Kan ni hjälpa till med TV-transport från Blocket?",
        answer:
          "Ja, vi hämtar TV-apparater köpta på Blocket, Facebook Marketplace och Tradera. Vi bär ut hos säljaren och levererar hem till dig. Vi ansvarar enbart för transporten – kontrollera TV:n innan vi hämtar.",
      },
      {
        question: "Kan ni hämta min TV på Elgiganten eller Power?",
        answer:
          "Ja, vi hämtar TV-apparater från alla elektronikbutiker – Elgiganten, Power, MediaMarkt och andra. Se till att köpet är betalt och redo att hämtas.",
      },
      {
        question: "Kan ni bära en stor TV i trappor?",
        answer:
          "Ja, vi bär TV-apparater till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid att manövrera stora TV-apparater genom trånga trapphus och smala dörröppningar.",
      },
      {
        question: "Behöver jag originalförpackningen?",
        answer:
          "Nej, det är en fördel men inte ett krav. Har du inte kvar kartongen skyddar vi TV:n med möbelfiltar och bärremmar istället.",
      },
      {
        question: "Hur snabbt kan ni hjälpa med TV-transport?",
        answer:
          "Vi erbjuder ofta TV-transport med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Erbjuder ni TV-transporthjälp på helger?",
        answer:
          "Ja, vi erbjuder TV-transport på helger och vissa kvällar. Perfekt om du köper en TV på Blocket och behöver hämta på helgen.",
      },
      {
        question: "Kan ni montera väggfäste för TV:n?",
        answer:
          "Vi fokuserar på transport och inbärning, men kan ofta hjälpa med enklare montering. Beskriv vad du behöver vid bokning så ser vi vad vi kan göra.",
      },
      {
        question: "Kan ni transportera min TV som del av en flytt?",
        answer:
          "Ja, vi kombinerar ofta TV-transport med flytt av möbler och andra saker. Beskriv allt som ska transporteras vid bokning – vi gör det i en och samma tur.",
      },
      {
        question: "Är TV-transporten försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om din TV skadas under bärning, lastning eller transport ersätter vi den. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hjälp med TV-transport?",
        answer:
          "Vi erbjuder TV-transporthjälp i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "TV-transporthjälp i 3 enkla steg",
      step2Text:
        "Våra ungdomar skyddar TV:n med möbelfiltar, bär ut och lastar stående i skåpbilen. Sedan kör vi till leveransadressen.",
      step3Title: "TV:n på plats",
      step3Desc:
        "Din TV står säkert hemma hos dig – inburen och uppställd. Klart.",
      ctaHeading: "Boka hjälp med TV-transport idag",
      ctaText:
        "Ange hämtadress och leveransadress, beskriv TV:ns storlek och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "help-tv-transport",
      meta: {
        title:
          "Help TV Transport – Van & Carrying Help | Ungdomskraft",
        description:
          "Need help transporting a TV? We pick up, protect and deliver your TV standing and safe — bought on Blocket, from a store or when moving. Book TV transport help today.",
        h1: "Help TV transport",
      },
      hero: {
        heading: "Help with TV transport",
        intro:
          "Need help transporting a TV? Whether you bought a new 75-inch OLED at Elgiganten, found a bargain on Blocket or are moving your home cinema TV to a new apartment — we've got you. Ungdomskraft picks up your TV, protects the panel with furniture blankets, always transports standing in the van and delivers to your home. We carry inside through stairwells and doorways and place it where you want it. Safe TV transport without risking a cracked screen.",
      },
      featureCards: {
        sinceText: "TV transport help since 2014",
        bookYourText: "Book TV transport help",
        bookDescText:
          "Enter the pickup address and delivery address, describe the TV size and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for TV transport",
        vehicleDescText:
          "We arrive with a van that fits even the largest TVs. Furniture blankets and straps included — the TV is transported standing and secured the entire way.",
      },
      itemsSection: {
        heading: "TVs we help you transport",
        items: [
          {
            name: "OLED TVs",
            description:
              "LG, Sony, Samsung and Philips OLED — extra sensitive panels requiring careful handling and upright transport",
            image: "/tv.webp",
          },
          {
            name: "QLED & LED TVs",
            description:
              "Samsung QLED, LG NanoCell and other LED TVs — all sizes from 32 to 85 inches",
            image: "/tv.webp",
          },
          {
            name: "Large TVs (75–85+)",
            description:
              "Extra large TVs that require a van — we carry and manoeuvre through narrow stairwells",
            image: "/tv.webp",
          },
          {
            name: "Home cinema & projectors",
            description:
              "Projectors, projection screens and surround systems — careful handling of fragile equipment",
            image: "/painting.webp",
          },
          {
            name: "Gaming monitors",
            description:
              "Ultrawide monitors, curved screens and large gaming displays that won't fit in a car",
            image: "/patio-furniture.webp",
          },
          {
            name: "Soundbars & audio",
            description:
              "Soundbars, subwoofers and surround speakers — often bulky and fragile",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Help with TV transport — we pick up, protect and deliver",
        mainText:
          "Transporting a TV yourself is risky. Modern TVs have thin, fragile panels that are easily damaged if tilted, laid flat or bumped. A 65-inch TV in its original packaging is approximately 160 cm wide and weighs 25–40 kg — it won't fit in most cars, and balancing it on the back seat risks cracking the screen. Laying an OLED TV flat can bend the panel and cause permanent pixel defects. That's why professional TV transport help is the safest solution. Ungdomskraft offers help with TV transport across Stockholm and Gothenburg. We pick up your TV at the collection address — at a Blocket seller's home, at an Elgiganten store, at your old apartment or wherever it is. We protect the screen with furniture blankets, secure the TV standing in the van (never flat) and drive to the delivery address. There we carry inside through stairwells and doorways and place the TV where you want it — on the TV stand, wall or wherever you prefer. We transport all types of TVs: OLED, QLED, LED, Mini LED and plasma. All sizes from 32 inches to 85 inches and larger. We handle Samsung, LG, Sony, Philips, TCL, Hisense and all other brands with the same care. Our crew are trained in how each panel type should be handled — OLED screens require extra caution because the panel is thin and flexible, while LED TVs tolerate slightly more but still need upright transport and impact protection. Every week we help hundreds of customers with TV transport. The most common situations: Blocket purchases where the seller lives far away, store purchases at Elgiganten or Power that didn't fit in the car, moving to a new apartment where the TV is too large to carry yourself, or returning a defective TV. Whatever the reason — we give you the help you need.",
        preparationHeading: "Prepare for your TV transport",
        preparationText:
          "Pack the TV in its original packaging if you still have it — that provides the best protection during transport. Don't have the original box? No problem — we protect with furniture blankets and carrying straps. Disconnect all cables (HDMI, power, aerials) and remove any TV feet if it makes handling easier. Have the pickup address and delivery address ready, including door codes and floor numbers. Let us know if there are narrow stairwells, tight doorways or no lift — it helps us plan properly.",
        additionalSections: [
          {
            heading: "Help with TV transport for Blocket purchases",
            content:
              "Blocket is Sweden's largest marketplace for second-hand TVs, and many of the best deals are TVs that can't be collected by car. You find a 65-inch Samsung OLED for half price — but the seller lives three floors up without a lift in Sundbyberg, and you live in Nacka. With Ungdomskraft's TV transport help we pick up the TV at the seller's home, carry down through the stairwell, protect and load standing in the van and drive to your home. We carry inside and place it where you want it. No risking the screen in a car and no need to ask friends for help. We are only responsible for the transport — check the TV's condition at the seller's place before we collect.",
          },
          {
            heading: "Help with TV transport from stores",
            content:
              "Bought a new TV at Elgiganten, Power or MediaMarkt that didn't fit in your car? Stores' own deliveries can take days to book and often charge extra for carrying upstairs. With our TV transport help we pick up your TV the same day or next day. We drive to the store, collect your paid-for TV, load standing in the van and deliver to your home with carrying and placement included. Perfect also for click-and-collect orders where the package is too large to take home yourself.",
          },
          {
            heading: "Help with TV transport when moving",
            content:
              "The TV is often the most nerve-wracking part of a move — a bump in the stairwell or an incorrect lift can cost thousands in damage. With our help you don't have to worry. We disconnect the TV (if you want), protect with blankets and transport standing in the van to the new apartment. There we carry inside and place it where you want it. Need help mounting the wall bracket? Describe it when booking. We often handle TV transport as part of a larger move — you can combine the TV with other furniture and belongings in the same trip.",
          },
          {
            heading: "How TV transport help works",
            listItems: [
              {
                label: "Book online",
                text: "Enter the pickup address and delivery address. Describe the TV size (inches) and whether original packaging is available. You see the price instantly.",
              },
              {
                label: "We collect",
                text: "Our team arrive with a van. We protect the TV with furniture blankets, carry out and load standing in the van.",
              },
              {
                label: "We deliver",
                text: "We drive to the delivery address, carry inside through stairwells and doorways and place the TV where you want it.",
              },
              {
                label: "Done",
                text: "Your TV is safely in place at your home. Without a scratch, without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–2 hours. You can follow the status in real time.",
          },
          {
            heading: "Why get professional help with TV transport?",
            content:
              "A modern TV costs 5 000–40 000 SEK. Risking it in a car, balanced against the back seat without proper protection, can end with a cracked screen and thousands in losses. Professional TV transport help costs from 595 SEK — a fraction of the cost of replacing a damaged TV. With Ungdomskraft you get a van, furniture blankets, straps, carrying help and placement — all included in the price. Everything is also insured. If anything were to be damaged during transport we replace it. That gives you peace of mind that no car transport can match.",
          },
          {
            heading: "Why book TV transport help with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book TV transport help with us, you also contribute to getting young people into the workforce and building their future. Every job is staffed by motivated young workers who learn everything from customer service and logistics to careful handling of expensive electronics. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Everything is insured, we have our own vans and you always see the price before you book. No hidden fees, no surprises.\n\nTV transport help does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does TV transport help cost?",
          answer:
            "The price depends on the TV size and distance. A standard TV transport starts at 595 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on TV transport help?",
          answer:
            "TV transport help does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We arrive with the right size — you don't need to rent a vehicle or trailer.",
        },
        {
          question: "Do you transport the TV standing up?",
          answer:
            "Yes, always. We transport all TVs standing upright in the van — never flat. This applies regardless of size and panel type. Laying a TV flat can permanently damage the panel.",
        },
        {
          question: "Do you protect the TV during transport?",
          answer:
            "Yes, we protect with furniture blankets and straps. The TV is secured standing in the van. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "Can you help with TV transport from Blocket?",
          answer:
            "Yes, we pick up TVs bought on Blocket, Facebook Marketplace and Tradera. We carry out at the seller's home and deliver to yours. We are only responsible for the transport — check the TV before we collect.",
        },
        {
          question: "Can you pick up my TV at Elgiganten or Power?",
          answer:
            "Yes, we pick up TVs from all electronics stores — Elgiganten, Power, MediaMarkt and others. Make sure the purchase is paid for and ready to collect.",
        },
        {
          question: "Can you carry a large TV up stairs?",
          answer:
            "Yes, we carry TVs to any floor — with or without a lift. Our crew are used to manoeuvring large TVs through narrow stairwells and tight doorways.",
        },
        {
          question: "Do I need the original packaging?",
          answer:
            "No, it's an advantage but not a requirement. If you don't have the box, we protect the TV with furniture blankets and carrying straps instead.",
        },
        {
          question: "How quickly can you help with TV transport?",
          answer:
            "We often offer TV transport at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Is TV transport help available on weekends?",
          answer:
            "Yes, we offer TV transport on weekends and some evenings. Perfect if you're buying a TV on Blocket and need to pick up at the weekend.",
        },
        {
          question: "Can you mount a TV wall bracket?",
          answer:
            "We focus on transport and carrying, but can often help with simple mounting. Describe what you need when booking and we'll see what we can do.",
        },
        {
          question: "Can you transport my TV as part of a move?",
          answer:
            "Yes, we often combine TV transport with moving furniture and other items. Describe everything that needs transporting when booking — we do it all in one trip.",
        },
        {
          question: "Is the TV transport insured?",
          answer:
            "Yes, all our transports are fully insured. If your TV is damaged during carrying, loading or transport we replace it. You can book with confidence.",
        },
        {
          question: "In which areas do you offer TV transport help?",
          answer:
            "We offer TV transport help across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "TV transport help in 3 easy steps",
        step2Text:
          "Our crew protect the TV with furniture blankets, carry out and load standing in the van. Then we drive to the delivery address.",
        step3Title: "TV in place",
        step3Desc:
          "Your TV is safely at home — carried in and placed. Done.",
        ctaHeading: "Book TV transport help today",
        ctaText:
          "Enter the pickup address and delivery address, describe the TV size and choose a time. You see the price instantly.",
      },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Transport Kyl & Frys — config för /transport-kyl-frys
  // Vinkel: Nischad sida specifikt för transport av kylskåp och frysar.
  // Unik vinkel vs transport-vitvaror: fokus enbart på kyl/frys –
  // stående transport, avtining, Blocket-köp, kyl-frys-kombiskåp, side-by-side etc.
  // transport-vitvaror = bredd (alla vitvaror), denna = djup (bara kyl & frys).
  // ──────────────────────────────────────────────────────────────────
  {
    slug: "transport-kyl-frys",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Kyl & Frys – Smidig Kylskåpstransport med Skåpbil | Ungdomskraft",
      description:
        "Boka transport av kylskåp och frys med skåpbil och personal. Vi hämtar, bär, transporterar stående och levererar – köpt på Blocket, från butik eller vid flytt. Prisvärt med unga som gör skillnad.",
      h1: "Transport kyl & frys",
    },

    hero: {
      heading: "Kyl & frys transport",
      intro:
        "Köpt ett kylskåp på Blocket? Ska frysen med i flytten? Beställt en ny kyl-frys från Elgiganten? Vi hämtar kylskåpet eller frysen var den än står, bär ut genom trappor och trånga passager, transporterar alltid stående i skåpbilen och kör till leveransadressen. Där bär vi in och ställer på plats i köket. Du slipper hyra bil, lyfta 100 kilo eller oroa dig för att kompressorn skadas.",
      heroImage: "/hero-transport-vitvaror.png",
    },

    featureCards: {
      sinceText: "Kylskåpstransport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft transporterat tusentals kylskåp och frysar i Stockholms- och Göteborgsområdet. Våra ungdomar vet att kylskåp alltid ska stå upprätt, hur man manövrerar tunga side-by-side-skåp i trappor och varför avtining är viktigt.",
      bookYourText: "Boka transport av kylskåp",
      bookDescText:
        "Ange hämtadress och leveransadress, beskriv kylskåpet och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar för transport av kyl & frys",
      vehicleDescText:
        "Vi kommer med skåpbil, säckkärra och bärremmar – perfekt för tunga kylskåp och frysar. Kylen transporteras alltid stående och säkrad med spännband.",
    },

    itemsSection: {
      heading: "Kylskåp & frysar vi transporterar",
      items: [
        {
          name: "Kylskåp",
          description:
            "Fristående kylskåp i alla storlekar – vi bär, transporterar stående och ställer på plats i köket",
          image: "/fridge.webp",
        },
        {
          name: "Kyl-frys-kombiskåp",
          description:
            "Kombinerade kylskåp och frysar – de vanligaste och tyngsta, ofta 70–100 kg",
          image: "/fridge.webp",
        },
        {
          name: "Side-by-side",
          description:
            "Amerikanska side-by-side-kylskåp med is- och vattenmaskin – extra breda och tunga",
          image: "/fridge.webp",
        },
        {
          name: "Frys",
          description:
            "Fristående frysskåp och fryslådor – vi transporterar stående för att skydda kompressorn",
          image: "/fridge.webp",
        },
        {
          name: "Inbyggnadskyl & -frys",
          description:
            "Inbyggda kylskåp och frysar – vi kopplar loss, lyfter ur och transporterar säkert",
          image: "/fridge.webp",
        },
        {
          name: "Vinkyl",
          description:
            "Fristående och inbyggda vinkylar – känslig teknik som kräver varsam och stående transport",
          image: "/fridge.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport av kylskåp och frys – alltid stående, säkert och försäkrat",
      mainText:
        "Ett kylskåp väger 60–120 kilo beroende på modell. Ett side-by-side-skåp kan väga ännu mer. De är ohanterliga, breda, höga och måste alltid transporteras stående för att kompressorn och köldmediet ska fungera korrekt efteråt. Att försöka klämma in ett kylskåp i en personbil – liggande – är ett recept för skador, läckage och dyra reparationer. Med Ungdomskrafts transport av kyl och frys slipper du allt det. Vi kommer med skåpbil, säckkärra och ett team av starka ungdomar. Vi kopplar loss, bär ut genom kök, korridorer och trappor, skyddar med möbelfiltar, lastar stående i skåpbilen och säkrar med spännband. På leveransadressen bär vi in och ställer kylskåpet på plats i köket. Vi transporterar alla typer: fristående kylskåp, kyl-frys-kombiskåp, side-by-side med is- och vattenmaskin, fristående frysskåp, fryslådor, inbyggnadskyl, inbyggnadsfrys och vinkylar. Oavsett om du köpt begagnat på Blocket, hämtar från Elgiganten eller flyttar till ny bostad – vi hämtar och levererar stående.",
      preparationHeading: "Förbered inför transport av kylskåp",
      preparationText:
        "Tina av kylskåpet och frysen minst 24 timmar innan transport – det förhindrar vattenläckage och skyddar kompressorn. Töm alla matvaror, hyllor och lådor (lådorna kan ofta sitta kvar men hyllor i glas bör tas ut och packas separat). Stäng av elen och dra ut kontakten. Torka ur eventuellt smältvatten. Tejpa fast dörren så den inte öppnar sig under transport. Rulla ihop sladden och tejpa fast. Se till att det finns fri passage genom dörrar, korridorer och trapphus – mät dörrbredden om kylskåpet är extra brett. Ha portkod och nycklar redo för båda adresserna.",
      additionalSections: [
        {
          heading: "Hämta kylskåp från Blocket och Facebook Marketplace",
          content:
            "Hittat ett kylskåp på Blocket eller en frys på Facebook Marketplace? Vi hämtar hos säljaren, kopplar loss om det behövs, bär ut, lastar stående i skåpbilen och kör hem till dig. Där bär vi in och ställer på plats i köket. Du behöver inte hyra bil, försöka lägga ett kylskåp i baksätet eller be säljaren om hjälp. Vi sköter allt – snabbt, professionellt och med kylskåpet stående hela vägen.",
        },
        {
          heading: "Varför kylskåpet alltid ska transporteras stående",
          content:
            "Ett kylskåp eller en frys ska alltid transporteras stående. Anledningen är kompressorn och köldmediet. När kylskåpet ligger ner kan oljan i kompressorn rinna ut i kylsystemet, vilket kan orsaka dyrbar skada. Köldmediet kan också hamna fel i systemet och göra att kylen inte kyler ordentligt efter transport. Vi transporterar alltid kylskåp och frysar stående i skåpbilen, säkrade med spännband mot väggen så att de inte kan vicka eller välta. Efter leverans rekommenderar vi att du låter kylskåpet stå i minst 4 timmar innan du slår på det – det ger oljan tid att rinna tillbaka till kompressorn.",
        },
        {
          heading: "Transport av kylskåp vid flytt och från butik",
          content:
            "Ska kylskåpet med i flytten? Vi kopplar loss, bär ut ur gamla köket, transporterar stående och levererar till din nya bostad. Kombinera gärna med vår flytthjälp om du har fler saker. Köpt ett nytt kylskåp från Elgiganten, Power, MediaMarkt eller IKEA men leveransen passar inte? Vi hämtar i butiken och kör hem till dig. Vi bär in och ställer på plats – du behöver bara koppla in. Vill du att vi tar med det gamla kylskåpet till återvinning? Beskriv det vid bokning så ordnar vi det i samma körning.",
        },
        {
          heading: "Varför boka transport av kylskåp med Ungdomskraft?",
          content:
            "Ungdomskraft är inte en vanlig transportfirma. Vi är ett socialt företag som sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet – och det märks i varje kylskåpstransport vi genomför. När du bokar transport av kylskåp hos oss får du ett team av motiverade, energiska unga människor som verkligen vill leverera ett bra jobb. Varje uppdrag är för dem en chans att visa vad de går för, och den inställningen genomsyrar allt – från hur de manövrerar ett tungt side-by-side-skåp genom en smal dörröppning till hur de ställer kylen på exakt rätt plats i köket.\n\nSedan starten 2014 har vi transporterat tusentals kylskåp och frysar i Stockholms- och Göteborgsområdet. Under den resan har vi gett tusentals ungdomar deras första riktiga arbetslivserfarenhet – med allt vad det innebär: att komma i tid, kommunicera professionellt med kunder, lösa problem på plats, samarbeta i team och ta ansvar för resultatet. Våra medarbetare lär sig lyftteknik, varför kylskåp alltid ska stå upprätt, hur man använder säckkärra och bärremmar i trappor, och hur man skyddar golv, väggar och dörrkarmar vid bärning av tunga vitvaror.\n\nDet här är inte bara bra för ungdomarna – det är bra för dig som kund. Du märker skillnaden i engagemang och energi. Våra team tar sig tid att göra rätt, dubbelkollar att kylskåpet står stabilt i skåpbilen och frågar alltid om det finns något mer de kan hjälpa till med. Vi har byggt en kultur där varje transport ses som en möjlighet att överträffa förväntningarna, och det syns i de hundratals femstjärniga recensioner vi fått på Trustpilot och Google.\n\nVarje kylskåpstransport du bokar genom Ungdomskraft bidrar direkt till att en ung person får arbetslivserfarenhet, lär sig ett yrke och bygger sitt cv. Många av våra medarbetare går vidare till fasta anställningar inom logistik, transport och serviceyrken – och de bär med sig de värderingar och den arbetsmoral de lärt sig hos oss. Att välja oss innebär att din kylskåpstransport blir en del av något större.\n\nVi tar också hållbarhet på allvar. Gamla kylskåp och frysar som ska bytas ut kör vi till godkänd återvinningsstation så att köldmedier och komponenter tas om hand korrekt. Förpackningsmaterial återvinns. Ingenting slängs i onödan.\n\nDet är smidig kylskåpstransport med mening.\n\nTransport av kylskåp och frys ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transport av kylskåp?",
        answer:
          "Priset beror på typ av kylskåp, avstånd och antal trappor. Ett vanligt kyl-frys-kombiskåp landar ofta på 595–995 kr. Ett stort side-by-side-skåp kan landa på 995–1 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport av kylskåp och frys?",
        answer:
          "Transport av kylskåp och frys ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Transporterar ni kylskåpet stående?",
        answer:
          "Ja, alltid. Vi transporterar alla kylskåp och frysar stående i skåpbilen, säkrade med spännband. Det skyddar kompressorn och köldmediet.",
      },
      {
        question: "Kan ni hämta ett kylskåp jag köpt på Blocket?",
        answer:
          "Ja, vi hämtar kylskåpet hos säljaren, bär ut, lastar stående och kör hem till dig. Vi bär in och ställer på plats i köket. Ange säljarens adress vid bokning.",
      },
      {
        question: "Måste kylskåpet tinas av innan transport?",
        answer:
          "Ja, vi rekommenderar att du tinar av kylskåp och frys minst 24 timmar innan transport. Det förhindrar vattenläckage och skyddar kompressorn. Töm alla matvaror i förväg.",
      },
      {
        question: "Kan ni bära kylskåpet i trappor utan hiss?",
        answer:
          "Ja, vi bär kylskåp och frysar i alla typer av trapphus – med eller utan hiss. Vi har säckkärra, bärremmar och rätt teknik för att hantera 60–120 kilo säkert i trappor.",
      },
      {
        question: "Ingår skåpbil i transport av kylskåp?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kopplar ni loss och in kylskåpet?",
        answer:
          "Vi kopplar loss elen vid hämtning. Vid leverans ställer vi kylskåpet på plats – men vi rekommenderar att du väntar minst 4 timmar innan du slår på det så att oljan hinner rinna tillbaka till kompressorn.",
      },
      {
        question: "Kan ni transportera ett side-by-side-kylskåp?",
        answer:
          "Ja, vi transporterar alla typer av side-by-side-skåp – med och utan is- och vattenmaskin. De är extra breda och tunga men vi har rätt utrustning och erfarenhet.",
      },
      {
        question: "Kan ni hämta kylskåp från Elgiganten eller annan butik?",
        answer:
          "Ja, vi hämtar kylskåp och frysar från Elgiganten, Power, MediaMarkt, IKEA och alla andra butiker. Ange butikens adress vid bokning.",
      },
      {
        question: "Skyddar ni kylskåpet under transport?",
        answer:
          "Ja, vi lindar kylskåpet i skyddsfiltar och säkrar med spännband i skåpbilen. Kylen transporteras alltid stående. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur länge ska kylskåpet stå innan jag slår på det?",
        answer:
          "Vi rekommenderar att du väntar minst 4 timmar efter transport innan du slår på kylskåpet. Det ger oljan i kompressorn tid att rinna tillbaka till rätt position. Om kylskåpet stått stående hela transporten kan du vänta kortare.",
      },
      {
        question: "Kan ni ta med det gamla kylskåpet till återvinning?",
        answer:
          "Ja, om du byter ut ditt gamla kylskåp kan vi ta med det och köra till godkänd återvinningsstation så att köldmedier tas om hand korrekt. Beskriv det vid bokning.",
      },
      {
        question: "Kan ni transportera kylskåp på helger?",
        answer:
          "Ja, vi erbjuder transport av kylskåp och frys på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "I vilka kommuner erbjuder ni transport av kylskåp och frys?",
        answer:
          "Vi erbjuder transport av kylskåp och frys i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transport av kylskåp i 3 enkla steg",
      step2Text:
        "Våra ungdomar kopplar loss, bär ut kylskåpet med säckkärra, lastar stående i skåpbilen och kör till leveransadressen. Där bär vi in och ställer på plats i köket.",
      step3Title: "Kylen på plats",
      step3Desc:
        "Ditt kylskåp står där du vill ha det – stående transporterat och redo att kopplas in. Klart.",
      ctaHeading: "Boka transport av kylskåp idag",
      ctaText:
        "Ange hämtadress och leveransadress, beskriv kylskåpet och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "fridge-freezer-transport",
      meta: {
        title:
          "Fridge & Freezer Transport – Safe Upright Delivery | Ungdomskraft",
        description:
          "Book fridge and freezer transport. We pick up, carry upright and deliver safely – from Blocket, stores or when moving. Fixed price, insured. Book today!",
        h1: "Fridge & freezer transport",
      },
      hero: {
        heading: "Fridge & freezer transport",
        intro:
          "Bought a fridge on Blocket? Taking the freezer with you when moving? Ordered a new fridge-freezer from Elgiganten? We pick up the fridge or freezer wherever it is, carry out through stairs and tight passages, always transport upright in the van and drive to the delivery address. There we carry in and place it in the kitchen. No need to rent a car, lift 100 kg or worry about the compressor being damaged.",
      },
      featureCards: {
        sinceText: "Fridge transport since 2014",
        bookYourText: "Book your fridge transport",
        bookDescText:
          "Enter the pickup address and delivery address, describe the fridge and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans for fridge & freezer transport",
        vehicleDescText:
          "We arrive with a van, hand truck and carrying straps — perfect for heavy fridges and freezers. The fridge is always transported upright and secured with tie-downs.",
      },
      itemsSection: {
        heading: "Fridges & freezers we transport",
        items: [
          {
            name: "Fridge",
            description:
              "Freestanding fridges in all sizes — we carry, transport upright and place in the kitchen",
            image: "/fridge.webp",
          },
          {
            name: "Fridge-freezer combo",
            description:
              "Combined fridge-freezers — the most common and heaviest, often 70–100 kg",
            image: "/fridge.webp",
          },
          {
            name: "Side-by-side",
            description:
              "American-style side-by-side fridges with ice and water dispenser — extra wide and heavy",
            image: "/fridge.webp",
          },
          {
            name: "Freezer",
            description:
              "Freestanding upright freezers and chest freezers — transported upright to protect the compressor",
            image: "/fridge.webp",
          },
          {
            name: "Built-in fridge & freezer",
            description:
              "Integrated fridges and freezers — we disconnect, lift out and transport safely",
            image: "/fridge.webp",
          },
          {
            name: "Wine cooler",
            description:
              "Freestanding and built-in wine coolers — sensitive technology requiring careful upright transport",
            image: "/fridge.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Fridge and freezer transport — always upright, safe and insured",
        mainText:
          "A fridge weighs 60–120 kg depending on model. A side-by-side can weigh even more. They are unwieldy, wide, tall and must always be transported upright so the compressor and refrigerant work correctly afterwards. Trying to squeeze a fridge into a saloon car on its side is a recipe for damage, leaks and expensive repairs. With Ungdomskraft's fridge and freezer transport you skip all of that. We arrive with a van, hand truck and a team of strong young workers. We disconnect, carry out through the kitchen, corridors and stairs, protect with furniture blankets, load upright in the van and secure with tie-downs. At the delivery address we carry in and place the fridge in the kitchen.",
        preparationHeading: "Prepare for fridge transport",
        preparationText:
          "Defrost the fridge and freezer at least 24 hours before transport — this prevents water leakage and protects the compressor. Empty all food, shelves and drawers (drawers can usually stay but glass shelves should be removed and packed separately). Turn off the power and unplug. Wipe up any meltwater. Tape the door shut so it doesn't open during transport. Roll up the cable and tape it in place. Make sure there is clear passage through doors, corridors and stairwells — measure the doorway width if the fridge is extra wide. Have entry codes and keys ready for both addresses.",
        additionalSections: [
          {
            heading: "Picking up a fridge from Blocket and Facebook Marketplace",
            content:
              "Found a fridge on Blocket or a freezer on Facebook Marketplace? We collect from the seller, disconnect if needed, carry out, load upright in the van and drive it to your home. There we carry in and place it in the kitchen. No need to rent a car, try to lay a fridge in the back seat or ask the seller for help. We handle everything — quickly, professionally and with the fridge upright the entire way.",
          },
          {
            heading: "Why the fridge must always be transported upright",
            content:
              "A fridge or freezer must always be transported upright. The reason is the compressor and refrigerant. When the fridge lies on its side, oil from the compressor can leak into the cooling system, potentially causing expensive damage. The refrigerant can also end up in the wrong part of the system, meaning the fridge won't cool properly after transport. We always transport fridges and freezers upright in the van, secured with tie-downs against the wall so they cannot tilt or fall. After delivery we recommend letting the fridge stand for at least 4 hours before turning it on — this gives the oil time to settle back into the compressor.",
          },
          {
            heading: "Fridge transport when moving and from stores",
            content:
              "Taking the fridge with you when moving? We disconnect, carry out of the old kitchen, transport upright and deliver to your new home. Combine with our moving help if you have more items. Bought a new fridge from Elgiganten, Power, MediaMarkt or IKEA but the delivery doesn't suit you? We collect from the store and deliver to your home. We carry in and place it — you just need to plug it in. Want us to take the old fridge to recycling? Describe it when booking and we'll handle it in the same trip.",
          },
          {
            heading: "Why book fridge transport with Ungdomskraft?",
            content:
              "Ungdomskraft is not an ordinary transport company. We are a social enterprise that employs young people and gives them real work experience — and you notice it in every fridge transport we carry out. When you book fridge transport with us you get a team of motivated, energetic young people who genuinely want to deliver a great job. Every assignment is a chance for them to prove themselves, and that attitude permeates everything — from how they manoeuvre a heavy side-by-side fridge through a narrow doorway to how they place it in exactly the right spot in the kitchen.\n\nSince 2014 we have transported thousands of fridges and freezers in the Stockholm and Gothenburg areas. Along the way we have given thousands of young people their first real work experience — with everything that entails: showing up on time, communicating professionally with customers, solving problems on site, working as a team and taking responsibility for the result. Our workers learn lifting technique, why fridges must always stand upright, how to use hand trucks and carrying straps on stairs, and how to protect floors, walls and door frames when carrying heavy appliances.\n\nThis is not only good for the young people — it's good for you as a customer. You notice the difference in commitment and energy. Our teams take the time to do things right, double-check that the fridge is stable in the van and always ask if there is anything else they can help with. We have built a culture where every transport is seen as an opportunity to exceed expectations, and it shows in the hundreds of five-star reviews we've received on Trustpilot and Google.\n\nEvery fridge transport you book through Ungdomskraft directly contributes to a young person gaining work experience, learning a trade and building their CV. Many of our workers go on to permanent positions in logistics, transport and service industries — carrying with them the values and work ethic they learned with us. Choosing us means your fridge transport becomes part of something bigger.\n\nWe also take sustainability seriously. Old fridges and freezers being replaced — we drive them to approved recycling stations so that refrigerants and components are handled correctly. Packaging is recycled. Nothing is dumped carelessly.\n\nIt's smooth fridge transport with purpose.\n\nFridge and freezer transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does fridge transport cost?",
          answer:
            "The price depends on the type of fridge, distance and number of floors. A standard fridge-freezer combo often costs SEK 595–995. A large side-by-side can cost SEK 995–1,495. You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on fridge and freezer transport?",
          answer:
            "Fridge and freezer transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Do you transport the fridge upright?",
          answer:
            "Yes, always. We transport all fridges and freezers upright in the van, secured with tie-downs. This protects the compressor and refrigerant.",
        },
        {
          question: "Can you pick up a fridge I bought on Blocket?",
          answer:
            "Yes, we collect the fridge from the seller, carry out, load upright and deliver to your home. We carry in and place it in the kitchen. Enter the seller's address when booking.",
        },
        {
          question: "Does the fridge need to be defrosted before transport?",
          answer:
            "Yes, we recommend defrosting fridge and freezer at least 24 hours before transport. This prevents water leakage and protects the compressor. Empty all food in advance.",
        },
        {
          question: "Can you carry a fridge up stairs without a lift?",
          answer:
            "Yes, we carry fridges and freezers in all types of stairwells — with or without a lift. We have hand trucks, carrying straps and the right technique for handling 60–120 kg safely on stairs.",
        },
        {
          question: "Is a van included in fridge transport?",
          answer:
            "Yes, a van is always included. We choose the right size based on the fridge — no need to rent a vehicle, drive or refuel.",
        },
        {
          question: "How long should the fridge stand before turning it on?",
          answer:
            "We recommend waiting at least 4 hours after transport before turning on the fridge. This gives the oil in the compressor time to settle back into the correct position. If the fridge was upright the entire transport you can wait a shorter time.",
        },
        {
          question: "Can you transport a side-by-side fridge?",
          answer:
            "Yes, we transport all types of side-by-side fridges — with and without ice and water dispensers. They are extra wide and heavy but we have the right equipment and experience.",
        },
        {
          question: "Can you pick up a fridge from Elgiganten or other stores?",
          answer:
            "Yes, we collect fridges and freezers from Elgiganten, Power, MediaMarkt, IKEA and all other stores. Enter the store address when booking.",
        },
        {
          question: "Do you protect the fridge during transport?",
          answer:
            "Yes, we wrap the fridge in protective blankets and secure with tie-downs in the van. The fridge is always transported upright. Everything is insured — if anything is damaged we replace it.",
        },
        {
          question: "Can you transport a fridge when moving?",
          answer:
            "Yes, we disconnect, carry out of the old kitchen, transport upright and deliver to your new home. Combine with our moving help if you have more items to move.",
        },
        {
          question: "Can you take the old fridge to recycling?",
          answer:
            "Yes, if you're replacing your old fridge we can take it to an approved recycling station so that refrigerants are handled correctly. Describe it when booking and we'll handle it in the same trip.",
        },
        {
          question: "Can you transport a fridge on weekends?",
          answer:
            "Yes, we offer fridge and freezer transport on weekends and some evenings. Book online and see all available times.",
        },
        {
          question: "In which municipalities do you offer fridge transport?",
          answer:
            "We offer fridge and freezer transport across the greater Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Fridge transport in 3 easy steps",
        step2Text:
          "Our young workers disconnect, carry the fridge out with a hand truck, load upright in the van and drive to the delivery address. There we carry in and place it in the kitchen.",
        step3Title: "Fridge in place",
        step3Desc:
          "Your fridge is where you want it — transported upright and ready to plug in. Done.",
        ctaHeading: "Book fridge transport today",
        ctaText:
          "Enter the pickup address and delivery address, describe the fridge and pick a time. You see the price instantly.",
      },
    },
  },
  {
    // Transport Återbruk — config för /transport-aterbruk
    // Vinkel: Hämtning och lämning av saker till second hand-butiker och återbruk.
    // Unik vinkel: fokus på att skänka, sälja eller hämta från återbruksbutiker — inte bortforsling eller sopor.
    slug: "transport-aterbruk",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Återbruk & Second Hand – Hämtning & Lämning | Ungdomskraft",
      description:
        "Boka transport till och från återbruk och second hand-butiker. Vi hämtar hemma hos dig, lastar skåpbilen och kör till återbruket. Prisvärt och med unga som gör skillnad.",
      h1: "Transport till återbruk & second hand",
    },

    hero: {
      heading: "Transport till återbruk och second hand",
      intro:
        "Vill du skänka möbler, kläder eller prylar till en second hand-butik eller återbrukscentral – men saknar bil eller ork att bära? Vi hämtar hemma hos dig, bär ut, lastar skåpbilen och kör till återbruket. Eller tvärtom – har du köpt något på second hand som inte får plats i bilen? Vi hämtar och levererar hem. Du slipper hyra bil, bära tungt och göra flera turer.",
      heroImage: "/hero-transport-aterbruk.png",
    },

    featureCards: {
      sinceText: "Second hand-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att skänka och transportera saker till återbruk och second hand-butiker i Stockholms- och Göteborgsområdet. Våra ungdomar vet vilka butiker som tar emot vad och hanterar allt från tunga möbler till fyllda plastkassar.",
      bookYourText: "Boka din transport till återbruk",
      bookDescText:
        "Ange din adress och återbruksbutikens adress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för second hand-transport",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska skänka en soffa eller tömma hela garderoben. Du slipper hyra bil, göra flera turer eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi kör till och från återbruk",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, bord, stolar, bokhyllor och garderober till second hand eller återbruk",
          image: "/sofa.webp",
        },
        {
          name: "Kläder & textilier",
          description:
            "Kassar och kartonger med kläder, skor, gardiner och sängkläder till second hand",
          image: "/box.webp",
        },
        {
          name: "Husgeråd",
          description:
            "Porslin, bestick, köksredskap, lampor och annat hushållsgods till återbruk",
          image: "/patio-furniture.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, högtalare och annan fungerande elektronik till second hand",
          image: "/tv.webp",
        },
        {
          name: "Böcker & media",
          description:
            "Böcker, vinylskivor, spel och filmer – perfekt att skänka till second hand",
          image: "/painting.webp",
        },
        {
          name: "Barnprylar",
          description:
            "Barnvagnar, barnsäng, leksaker och barnkläder som får nytt liv via återbruk",
          image: "/washer.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport till återbruk och second hand – vi hämtar, kör och lämnar åt dig",
      mainText:
        "Att skänka till second hand är bra för plånboken, miljön och någon annan – men transporten brukar vara det som stoppar. Möblerna är för tunga, bilen för liten och butiken ligger för långt bort. Med Ungdomskrafts transport till återbruk slipper du allt det. Vi kommer hem till dig med skåpbil och ett team av starka ungdomar, bär ut allt du vill skänka, lastar och kör till den second hand-butik eller återbrukscentral du väljer. Där lastar vi av och lämnar in åt dig. Har du i stället hittat en möbel eller pryl på en second hand-butik som inte får plats i bilen? Vi hämtar den och levererar hem till dig. Vi kör till alla typer av återbruk och second hand: Emmaus, Erikshjälpen, Myrorna, Röda Korset, Stadsmissionen, Retuna, Blocket-hämtningar, lokala loppisar och kommunala återbrukscentraler. Oavsett om du ska skänka en enda soffa eller tömma ett helt rum med prylar anpassar vi fordon och team efter mängden. Varje år slängs tusentals möbler och prylar som fortfarande är fullt fungerande – bara för att ägaren inte har möjlighet att transportera dem till en second hand-butik. Genom att boka en återbrukstransport med Ungdomskraft ser du till att dina saker får ett nytt liv hos någon som behöver dem, i stället för att hamna på tippen. Vi samarbetar med butiker som tar emot allt från kläder, husgeråd och elektronik till stora möbler som soffor, sängar och matbord. Våra ungdomar hanterar även tunga och skrymmande föremål – vi bär från vilken våning som helst, genom trånga trapphus och ut till skåpbilen. Behöver du lämna på flera ställen under samma tur? Inga problem – vi planerar rutten så att allt kommer fram effektivt. Många av våra kunder kombinerar återbrukstransport med en utrensning hemma: du sorterar vad som ska till second hand, vad som ska till återvinningscentralen och vad som ska bli kvar. Vi kör sedan allt till rätt ställe åt dig. Det är det enklaste sättet att rensa hemmet, göra en insats för miljön och samtidigt stötta second hand-verksamheter som gör skillnad i samhället.",
      preparationHeading: "Förbered inför transport till återbruk",
      preparationText:
        "Sortera det du vill skänka – tänk på att second hand-butiker oftast bara tar emot saker i gott skick. Packa kläder och småsaker i kassar eller kartonger. Kolla med butiken att de tar emot det du vill skänka – vissa butiker tar inte emot vitvaror, madrasser eller trasiga möbler. Ha allt redo vid dörren eller i hallen så går det snabbt. Om du hämtar från en second hand-butik – se till att du vet exakt vilken vara det gäller och att den är betald eller reserverad.",
      additionalSections: [
        {
          heading: "Skänka till second hand – varför transport gör skillnaden",
          content:
            "Många har saker hemma som de gärna vill skänka till second hand, men som aldrig lämnar hemmet för att transporten är krånglig. En soffa ryms inte i personbilen, 15 kassar med kläder kräver flera turer, och den närmaste second hand-butiken ligger kanske mil bort. Med vår transport till återbruk tar vi bort det hindret. Du sorterar, vi hämtar och kör. Resultatet: dina gamla prylar får ett nytt hem i stället för att hamna i soporna.",
        },
        {
          heading: "Hämta möbler och prylar från second hand",
          content:
            "Hittat en fin byrå på Blocket, ett matbord på Emmaus eller en bokhylla på Myrorna – men den får inte plats i bilen? Vi hämtar din second hand-fynd och levererar hem till dig. Vi bär in och ställer på plats. Perfekt för tunga möbler, ömtåliga glasskåp eller stora tavlor som inte går att transportera själv.",
        },
        {
          heading: "Vi kör till alla second hand-butiker och återbruk",
          listItems: [
            {
              label: "Emmaus",
              text: "Vi kör till alla Emmaus-butiker i Stockholm och Göteborg.",
            },
            {
              label: "Erikshjälpen",
              text: "Vi lämnar möbler, kläder och husgeråd hos Erikshjälpen.",
            },
            {
              label: "Myrorna",
              text: "Vi hämtar och levererar till Myrornas butiker i hela regionen.",
            },
            {
              label: "Röda Korset",
              text: "Vi kör till Röda Korsets second hand-butiker.",
            },
            {
              label: "Stadsmissionen",
              text: "Vi levererar till Stadsmissionens butiker och insamlingspunkter.",
            },
            {
              label: "Retuna Återbruksgalleria",
              text: "Vi kör till Retuna i Eskilstuna – världens första återbruksgalleria.",
            },
            {
              label: "Kommunala återbrukscentraler",
              text: "Vi kör till alla kommunala återbrukscentraler i Stockholm och Göteborg.",
            },
          ],
          afterContent:
            "Vi kör även till lokala loppisar, vintage-butiker och privata insamlingar. Ange bara adressen vid bokning.\n\nOm sakerna transporteras till återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Avdraget hanteras direkt på fakturan – du behöver aldrig ligga ute med pengarna.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar transport till återbruk och second hand?",
        answer:
          "Priset beror på mängden och avståndet. En liten lämning med några kassar kostar från 595 kr, medan en större transport med möbler och kartonger kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport till återbruk?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Transport som inte går till återanvändning ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Ingår skåpbil i transporten till second hand?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Vilka second hand-butiker kör ni till?",
        answer:
          "Vi kör till alla second hand-butiker och återbrukscentraler: Emmaus, Erikshjälpen, Myrorna, Röda Korset, Stadsmissionen, Retuna, kommunala återbrukscentraler, vintage-butiker och lokala loppisar. Ange bara adressen vid bokning.",
      },
      {
        question: "Kan ni hämta möbler jag köpt på second hand?",
        answer:
          "Ja, vi hämtar möbler och prylar du köpt på second hand-butiker, Blocket, Facebook Marketplace eller loppisar. Vi kör till butiken, hämtar varan, lastar och levererar hem till dig.",
      },
      {
        question: "Tar second hand-butiker emot allt?",
        answer:
          "Nej, de flesta second hand-butiker tar bara emot saker i gott skick. Trasiga möbler, smutsiga textilier och madrasser brukar inte accepteras. Kolla med butiken innan du bokar. Vi kan även köra till återvinningscentralen om något inte kan skänkas.",
      },
      {
        question: "Bär ni ut sakerna från min lägenhet?",
        answer:
          "Ja, vi bär ut allt från lägenheten – oavsett våning, trånga trappor eller tunga pjäser. Vi lastar skåpbilen och kör till återbruket. Du behöver inte lyfta ett finger.",
      },
      {
        question: "Kan ni köra till flera second hand-butiker på samma tur?",
        answer:
          "Ja, vi kan lämna på flera ställen under samma körning om sakerna ska till olika butiker. Beskriv vad som ska vart när du bokar.",
      },
      {
        question:
          "Kan ni köra till second hand och återbruk på helger?",
        answer:
          "Ja, vi erbjuder transport till second hand och återbruk på helger. Kontrollera att butiken har öppet den tid du bokar – vi kan inte lämna om butiken är stängd.",
      },
      {
        question:
          "Vad är skillnaden mellan återbruk och bortforsling?",
        answer:
          "Återbruk innebär att vi kör dina saker till en second hand-butik eller återbrukscentral där de säljs eller skänks vidare. Bortforsling innebär att vi kör bort saker som ska slängas – till återvinningscentralen eller tippen. Har du saker i bra skick? Välj återbrukstransport. Är sakerna trasiga eller uttjänta? Välj bortforsling.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni transport till second hand och återbruk?",
        answer:
          "Vi erbjuder transport till second hand och återbruk i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transport till återbruk i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut dina saker, lastar skåpbilen och kör till second hand-butiken eller återbrukscentralen. Där lastar vi av och lämnar in.",
      step3Title: "Allt inlämnat",
      step3Desc:
        "Dina saker har fått ett nytt hem via second hand – och du slipper bära, köra och parkera. Klart.",
      ctaHeading: "Boka transport till återbruk idag",
      ctaText:
        "Ange din adress och återbruksbutikens adress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "second-hand-transport",
      meta: {
        title:
          "Second Hand Transport – Pickup & Delivery to Thrift Stores | Ungdomskraft",
        description:
          "Book transport to and from second hand stores and reuse centres. We pick up at your home, load the van and deliver. Fixed price, insured.",
        h1: "Second hand transport",
      },
      hero: {
        heading: "Transport to second hand stores and reuse centres",
        intro:
          "Want to donate furniture, clothes or household items to a second hand shop — but you don't have a car or the energy to carry heavy things? We come to your home with a van and crew, carry everything out, load up and drive to the store of your choice. Or the other way — bought something from a thrift shop that won't fit in your car? We pick it up and deliver to your door.",
      },
      featureCards: {
        sinceText: "Second hand transport since 2014",
        bookYourText: "Book your second hand transport",
        bookDescText:
          "Enter your address and the store address, describe what needs transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for second hand transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're donating a single sofa or clearing out an entire wardrobe. No need to rent a vehicle, make multiple trips or strain your back.",
      },
      itemsSection: {
        heading: "Items we transport to and from second hand stores",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, tables, chairs, bookshelves and wardrobes to thrift stores",
            image: "/sofa.webp",
          },
          {
            name: "Clothes & textiles",
            description:
              "Bags and boxes of clothes, shoes, curtains and bedding to second hand",
            image: "/box.webp",
          },
          {
            name: "Household goods",
            description:
              "Porcelain, cutlery, kitchen tools, lamps and other household items",
            image: "/patio-furniture.webp",
          },
          {
            name: "Electronics",
            description:
              "TVs, computers, speakers and other working electronics to second hand",
            image: "/tv.webp",
          },
          {
            name: "Books & media",
            description:
              "Books, vinyl records, games and films — perfect to donate",
            image: "/painting.webp",
          },
          {
            name: "Kids' stuff",
            description:
              "Strollers, cribs, toys and children's clothes that get a new life through reuse",
            image: "/washer.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Transport to second hand and reuse centres — we pick up, drive and drop off for you",
        mainText:
          "Donating to second hand is good for your wallet, the environment and someone else — but transport is usually the thing that stops people. The furniture is too heavy, the car too small and the shop is too far away. With Ungdomskraft's second hand transport we remove that obstacle. We come to your home with a van and a team of strong young people, carry out everything you want to donate, load up and drive to the second hand shop or reuse centre of your choice. We also deliver the other way — bought a table on Facebook Marketplace or a wardrobe at a thrift store? We pick it up and deliver to your door.",
        preparationHeading: "Prepare for second hand transport",
        preparationText:
          "Sort what you want to donate — remember that most second hand shops only accept items in good condition. Pack clothes and small items in bags or boxes. Check with the store that they accept what you want to donate — some stores don't take appliances, mattresses or broken furniture. Have everything ready by the door. If we're picking up from a store, make sure the item is paid for or reserved.",
        additionalSections: [
          {
            heading: "Donating made easy with transport",
            content:
              "Many people have items at home they'd love to donate, but they never leave because transport is too complicated. A sofa doesn't fit in the car, 15 bags of clothes require multiple trips, and the nearest thrift store might be miles away. Our second hand transport removes that barrier. You sort, we pick up and drive. The result: your old things find a new home instead of ending up in landfill.",
          },
          {
            heading: "Pick up furniture bought second hand",
            content:
              "Found a beautiful dresser on Marketplace, a dining table at a charity shop or a bookshelf at a thrift store — but it won't fit in your car? We pick up your second hand find and deliver it to your door. We carry it inside and place it where you want it.\n\nIf the items are transported for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. The deduction is handled directly on the invoice – you never have to pay the full amount upfront.",
          },
        ],
      },
      faq: [
        {
          question: "How much does second hand transport cost?",
          answer:
            "The price depends on the amount and distance. A small drop-off with a few bags starts at 595 SEK, while a larger transport with furniture and boxes costs 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on second hand transport?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Transport that does not go to reuse does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs to be transported — you don't need to rent a vehicle.",
        },
        {
          question: "Which second hand stores do you deliver to?",
          answer:
            "We deliver to all second hand shops and reuse centres: Emmaus, Erikshjälpen, Myrorna, Red Cross, Stadsmissionen, municipal reuse centres, vintage shops and local flea markets. Just enter the address when booking.",
        },
        {
          question: "Can you pick up furniture I bought second hand?",
          answer:
            "Yes, we pick up furniture and items you've bought from thrift stores, Marketplace, or flea markets. We drive to the store, pick up the item and deliver it to your home.",
        },
        {
          question: "In which areas do you offer second hand transport?",
          answer:
            "We offer second hand transport across the Stockholm region and the Gothenburg region, covering all major municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Second hand transport in 3 easy steps",
        step2Text:
          "Our young crew carry your items out, load the van and drive to the second hand store or reuse centre. We unload and drop everything off.",
        step3Title: "All delivered",
        step3Desc:
          "Your items have found a new home through second hand — and you didn't have to carry, drive or park. Done.",
        ctaHeading: "Book second hand transport today",
        ctaText:
          "Enter your address and the store address, describe what needs transporting and pick a time. You see the price instantly.",
      },
    },
  },
  {
    // Transport Blocket — config för /transport-blocket
    // Vinkel: Hämta varor köpta på Blocket och få dem transporterade hem.
    // Unik vinkel vs transport-möbler: här handlar det specifikt om Blocket-köp — hämta hos säljare och leverera hem till köpare.
    slug: "transport-blocket",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Blocket – Hämta Ditt Blocket-Köp Med Skåpbil | Ungdomskraft",
      description:
        "Köpt något på Blocket som inte får plats i bilen? Vi hämtar hos säljaren, lastar skåpbilen och levererar hem till dig. Boka Blocket-transport med personal idag.",
      h1: "Transport Blocket",
    },

    hero: {
      heading: "Vi kör hem ditt Blocket-köp",
      intro:
        "Hittat en soffa, ett matbord eller en tvättmaskin på Blocket – men den får inte plats i bilen? Vi åker till säljaren, bär ut, lastar skåpbilen och levererar hem till dig. Vi bär in och ställer på plats. Du slipper hyra släp, be kompisar om hjälp eller göra flera turer. Funkar lika bra för köp på Facebook Marketplace, Tradera och andra köp-och-sälj-sajter.",
      heroImage: "/hero-transport-blocket.png",
    },

    featureCards: {
      sinceText: "Blocket-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals Blocket-köpare att hämta möbler, vitvaror och andra fynd. Våra ungdomar vet hur man bär tunga pjäser genom trånga trapphus och lastar skåpbilar effektivt – du får ditt köp hem utan att lyfta ett finger.",
      bookYourText: "Boka din Blocket-transport",
      bookDescText:
        "Ange säljarens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Blocket-hämtningar",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en bokhylla eller ett helt vardagsrum. Du slipper hyra släp, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Vanliga Blocket-köp vi hämtar och kör hem",
      items: [
        {
          name: "Soffor & fåtöljer",
          description:
            "Soffor, hörnsoffor, fåtöljer och sittmöbler köpta på Blocket",
          image: "/sofa.webp",
        },
        {
          name: "Bord & stolar",
          description:
            "Matbord, skrivbord, soffbord och stolar från Blocket-säljare",
          image: "/patio-furniture.webp",
        },
        {
          name: "Sängar & madrasser",
          description:
            "Sängramar, resårmadrasser och huvudgavlar hämtade via Blocket",
          image: "/bed.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Tvättmaskiner, kylskåp, frysar och diskmaskiner köpta begagnat",
          image: "/washer.webp",
        },
        {
          name: "Garderober & förvaring",
          description:
            "Garderober, byråer, bokhyllor och förvaringsmöbler från Blocket",
          image: "/painting.webp",
        },
        {
          name: "Elektronik & övrigt",
          description:
            "TV-apparater, träningsredskap, kassaskåp och andra tunga Blocket-fynd",
          image: "/tv.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Blocket-transport – vi hämtar hos säljaren och levererar hem till dig",
      mainText:
        "Du har hittat det perfekta köpet på Blocket – men säljaren bor tre mil bort, möbeln väger 80 kilo och du har ingen skåpbil. Det är här Ungdomskraft kommer in. Vi åker till säljarens adress med skåpbil och ett team av starka ungdomar, bär ut varan, skyddar den med möbelfiltar och spännband, lastar och kör hem till dig. Där bär vi in och ställer på plats – i vardagsrummet, sovrummet eller köket. Du behöver inte ens vara med hos säljaren om ni redan kommit överens. Blocket-transport fungerar för alla typer av köp: möbler, vitvaror, elektronik, byggmaterial, trädgårdsmöbler, träningsutrustning och allt annat som är för tungt, för stort eller för krångligt att ta hem själv. Vi hämtar även från Facebook Marketplace, Tradera, Shpock och andra köp-och-sälj-plattformar – ange bara säljarens adress vid bokning. Varje vecka kör vi hundratals Blocket-hämtningar i Stockholm och Göteborg. Våra ungdomar är vana vid att bära genom smala trapphus, runt trånga hörn och upp flera våningar utan hiss. Vi vet hur man skyddar ömtåliga ytor, demonterar möbler som inte går genom dörren och lastar skåpbilen så att inget skadas under transporten. Oavsett om du köpt en enda stol eller ett helt bohag anpassar vi fordon och team efter mängden – du ser priset direkt när du bokar. Att köpa begagnat på Blocket är ett av de smartaste sätten att möblera hemmet – du sparar pengar, hittar unika pjäser och gör en insats för miljön genom att ge möbler ett nytt liv i stället för att köpa nytt. Men transporten är nästan alltid det som krånglar till det. Många Blocket-affärer faller just för att köparen inte har möjlighet att hämta varan. Med Ungdomskrafts Blocket-transport tar vi bort det hindret helt. Du fokuserar på att hitta rätt köp – vi tar hand om hela transporten från dörr till dörr.",
      preparationHeading: "Förbered inför din Blocket-hämtning",
      preparationText:
        "Bekräfta med säljaren exakt tid och adress – och att varan är redo att hämtas när vi kommer. Se till att portkod och våningsplan stämmer. Om möbeln behöver demonteras – fråga säljaren om den är isärtagen eller om vi ska göra det på plats. Ha din hemadress och eventuell portkod redo. Kolla att det finns fri passage i trapphus och att skåpbilen kan parkera nära porten både hos säljaren och hemma hos dig. Mät gärna dörröppningar och trapphus om möbeln är stor – en garderob eller hörnsoffa kan behöva demonteras för att komma ut. Ju mer information du anger vid bokning, desto smidigare går hämtningen.",
      additionalSections: [
        {
          heading: "Hämta möbler från Blocket utan egen bil",
          content:
            "Det vanligaste hindret för Blocket-köp är transporten. Möbeln ser perfekt ut på bilderna, priset är rätt – men sedan inser du att den inte ryms i bilen, att säljaren bor på femte våningen utan hiss, eller att du helt enkelt inte orkar bära en 90-kilos soffa. Med Ungdomskrafts Blocket-transport försvinner det hindret. Du köper, vi hämtar. Vi bär från vilken våning som helst, lastar i skåpbilen och levererar hela vägen in i ditt hem. Många av våra kunder säger att de vågar köpa helt andra saker på Blocket nu – tunga matbord, hörnsoffor, garderober och vitvaror som de aldrig hade kunnat ta hem själva. Vår personal hanterar allt från små fåtöljer till stora sektionssoffor och IKEA-garderober som måste demonteras på plats. Vi tar med oss verktyg, möbelfiltar och bärremmar – du behöver inte förbereda något extra. Om möbeln inte går genom dörren demonterar vi den hos säljaren och monterar ihop den igen hemma hos dig. Vi skyddar alla ytor – golv, väggar och dörrkarm – under bärningen så att inget skadas varken hos säljaren eller hos dig.",
        },
        {
          heading: "Transport av vitvaror köpta på Blocket",
          content:
            "Köpt en begagnad tvättmaskin, kylskåp eller diskmaskin på Blocket? Vitvaror är tunga, otympliga och kräver rätt hantering. En tvättmaskin väger 70–90 kg och ett kylskåp ännu mer – och bägge måste transporteras stående för att inte skada kompressorn. Vi hämtar vitvaror hos säljaren, kopplar loss om det behövs, bär ut och lastar säkert med bärremmar och möbelfiltar. Vi använder pirra och säckärra när det behövs för att komma genom trånga utrymmen. Hemma hos dig bär vi in och ställer på plats i köket, tvättstugan eller där du vill ha vitvarun. Behöver du hjälp med inkoppling? Meddela vid bokning så ordnar vi det. Vi transporterar alla typer av vitvaror: tvättmaskiner, torktumlare, kombinerade tvätt-tork, kylskåp, frysar, kyl-fryskombiskap, diskmaskiner, spisar, ugnar och mikrovågsugnar. Att köpa begagnade vitvaror på Blocket är ofta betydligt billigare än att köpa nytt – och med vår transport får du dem levererade hela vägen hem utan att lyfta ett finger.",
        },
        {
          heading: "Så fungerar Blocket-transport med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange säljarens adress, din hemadress och beskriv vad som ska hämtas. Ladda gärna upp en bild från Blocket-annonsen. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar",
              text: "Vårt team åker till säljaren med skåpbil och rätt utrustning. Vi bär ut varan, demonterar om det behövs, skyddar med filtar och spännband och lastar skåpbilen.",
            },
            {
              label: "Vi levererar",
              text: "Vi kör till din adress, bär in genom trapphus och dörrar och ställer på exakt den plats du vill ha. Möbler monteras ihop igen om vi demonterat dem.",
            },
            {
              label: "Klart",
              text: "Ditt Blocket-köp står hemma hos dig – snabbt, säkert och utan att du behövde hyra bil, be kompisar om hjälp eller slita ryggen.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och vad som ska transporteras. Du kan följa statusen i realtid och behöver bara vara hemma när vi levererar.",
        },
        {
          heading: "Facebook Marketplace, Tradera och andra plattformar",
          content:
            "Vår Blocket-transport fungerar lika bra för köp på Facebook Marketplace, Tradera, Shpock och lokala köp-och-sälj-grupper. Ange bara säljarens adress som hämtpunkt och din hemadress som leveransadress – vi sköter resten. Många köpare använder oss regelbundet för att hämta fynd från flera plattformar. Facebook Marketplace har vuxit enormt de senaste åren och vi ser allt fler hämtningar därifrån. Processen är identisk: du skickar oss adressen, vi hämtar och levererar. Vi hanterar även köp från auktionssajter som Tradera där du vunnit en budgivning och behöver hämta varan hos säljaren. Oavsett plattform är vår tjänst densamma – professionell transport med skåpbil och personal från dörr till dörr.",
        },
        {
          heading: "Varför boka Blocket-transport med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara professionell hjälp med din Blocket-hämtning – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och ansvarsfullt miljötänk. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra kunder kommer tillbaka gång efter gång – inte bara för att tjänsten fungerar, utan för att de vet att deras pengar gör skillnad. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Ingen dolda avgifter, inga överraskningar.\n\nBlocket-transport ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar det att hämta ett Blocket-köp?",
        answer:
          "Priset beror på varans storlek, vikt och avståndet mellan säljare och köpare. En mindre möbel eller vitvaror kostar från 595 kr, medan större hämtningar med tunga möbler kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på Blocket-transport?",
        answer:
          "Blocket-transport ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil vid Blocket-hämtning?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska hämtas – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Behöver jag vara med hos säljaren?",
        answer:
          "Nej, du behöver inte vara med hos säljaren om ni redan kommit överens om köpet. Se till att säljaren vet att vi kommer och att varan är redo att hämtas. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Kan ni bära tunga möbler från Blocket i trappor?",
        answer:
          "Ja, vi bär från vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft i trånga trapphus. Vi hanterar soffor, garderober, sängar, vitvaror och allt annat tungt.",
      },
      {
        question: "Hämtar ni även från Facebook Marketplace?",
        answer:
          "Ja, vi hämtar köp från Blocket, Facebook Marketplace, Tradera, Shpock och alla andra köp-och-sälj-plattformar. Ange bara säljarens adress vid bokning.",
      },
      {
        question: "Kan ni demontera möbler vid hämtning?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att transporteras. Vi monterar ihop dem igen hemma hos dig om du vill.",
      },
      {
        question: "Skyddar ni möblerna under transporten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar alla möbler och ömtåliga föremål under bärning, lastning och transport. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni hämta mitt Blocket-köp?",
        answer:
          "Vi erbjuder ofta Blocket-hämtning med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni hämta flera Blocket-köp på samma tur?",
        answer:
          "Ja, vi kan hämta från flera säljare under samma körning om adresserna ligger i rimligt avstånd. Beskriv alla hämtpunkter när du bokar.",
      },
      {
        question: "Kan ni transportera vitvaror köpta på Blocket?",
        answer:
          "Ja, vi transporterar alla typer av vitvaror – tvättmaskiner, torktumlare, kylskåp, frysar, diskmaskiner, spisar och ugnar. Vi kopplar loss hos säljaren om det behövs, bär ut, transporterar stående och bär in hemma hos dig. Vi kan även hjälpa till med inkoppling.",
      },
      {
        question: "Erbjuder ni Blocket-transport på helger?",
        answer:
          "Ja, vi erbjuder Blocket-transport på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Ansvarar ni för varans skick?",
        answer:
          "Nej, vi ansvarar enbart för transporten – inte för varans skick eller om den matchar annonsen. Köpet är en överenskommelse mellan dig och säljaren. Vi rekommenderar att du själv kontrollerar varan innan vi lastar, eller att du är tydlig med säljaren om vad du förväntar dig.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni Blocket-transport?",
        answer:
          "Vi erbjuder Blocket-transport i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Är Blocket-transporten försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under bärning, lastning eller transport ersätter vi det. Du kan boka med trygghet – vi tar fullt ansvar för dina saker från hämtning till leverans.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Blocket-transport i 3 enkla steg",
      step2Text:
        "Våra ungdomar åker till säljaren, bär ut ditt köp, skyddar det med filtar och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Hemma hos dig",
      step3Desc:
        "Ditt Blocket-köp står på plats hemma hos dig – inburet, uppställt och klart. Utan att du lyfte ett finger.",
      ctaHeading: "Boka Blocket-transport idag",
      ctaText:
        "Ange säljarens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "blocket-transport",
      meta: {
        title:
          "Blocket Transport – Pick Up Your Blocket Purchase With Van | Ungdomskraft",
        description:
          "Bought something on Blocket that won't fit in your car? We pick up from the seller, load the van and deliver to your door. Book Blocket transport with crew today.",
        h1: "Blocket transport",
      },
      hero: {
        heading: "We deliver your Blocket-purchase",
        intro:
          "Found a sofa, dining table or washing machine on Blocket — but it won't fit in your car? We drive to the seller, carry out, load the van and deliver to your door. We carry inside and place it where you want it. No need to rent a trailer, ask friends for help or make multiple trips. Works just as well for purchases on Facebook Marketplace, Tradera and other buy-and-sell platforms.",
      },
      featureCards: {
        sinceText: "Blocket transport since 2014",
        bookYourText: "Book your Blocket transport",
        bookDescText:
          "Enter the seller's address and your home address, describe what needs picking up and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Blocket pickups",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a bookshelf or an entire living room set. No need to rent a trailer, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Common Blocket purchases we pick up and deliver",
        items: [
          {
            name: "Sofas & armchairs",
            description:
              "Sofas, corner sofas, armchairs and seating bought on Blocket",
            image: "/sofa.webp",
          },
          {
            name: "Tables & chairs",
            description:
              "Dining tables, desks, coffee tables and chairs from Blocket sellers",
            image: "/patio-furniture.webp",
          },
          {
            name: "Beds & mattresses",
            description:
              "Bed frames, mattresses and headboards picked up via Blocket",
            image: "/bed.webp",
          },
          {
            name: "Appliances",
            description:
              "Washing machines, fridges, freezers and dishwashers bought second hand",
            image: "/washer.webp",
          },
          {
            name: "Wardrobes & storage",
            description:
              "Wardrobes, dressers, bookshelves and storage furniture from Blocket",
            image: "/painting.webp",
          },
          {
            name: "Electronics & other",
            description:
              "TVs, gym equipment, safes and other heavy Blocket finds",
            image: "/tv.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Blocket transport — we pick up from the seller and deliver to your door",
        mainText:
          "You've found the perfect deal on Blocket — but the seller lives 30 km away, the item weighs 80 kilos and you don't have a van. That's where Ungdomskraft comes in. We drive to the seller's address with a van and a team of strong young workers, carry the item out, protect it with furniture blankets and straps, load up and drive to your home. There we carry it inside and place it exactly where you want it — in the living room, bedroom or kitchen. You don't even need to be at the seller's place if you've already agreed on the purchase. Blocket transport works for all types of purchases: furniture, appliances, electronics, building materials, garden furniture, gym equipment and anything else that's too heavy, too large or too awkward to take home yourself. We also pick up from Facebook Marketplace, Tradera, Shpock and other buy-and-sell platforms — just enter the seller's address when booking. Every week we make hundreds of Blocket pickups across Stockholm and Gothenburg. Our crew are used to carrying through narrow stairwells, around tight corners and up multiple flights without a lift. We know how to protect delicate surfaces, disassemble furniture that won't fit through doorways and load the van so nothing gets damaged during transport. Whether you've bought a single chair or an entire household, we match the vehicle and crew to the job — and you see the price instantly when you book. Buying second hand on Blocket is one of the smartest ways to furnish your home — you save money, find unique pieces and do something good for the environment by giving furniture a new life instead of buying new. But transport is almost always the sticking point. Many Blocket deals fall through simply because the buyer has no way to collect the item. With Ungdomskraft's Blocket transport we remove that barrier entirely. You focus on finding the right deal — we take care of the entire journey from door to door.",
        preparationHeading: "Prepare for your Blocket pickup",
        preparationText:
          "Confirm the exact time and address with the seller — and that the item is ready for pickup when we arrive. Make sure the door code and floor number are correct. If the furniture needs disassembling, ask the seller whether it's already taken apart or if we should do it on site. Have your home address and any door codes ready. Check that there is clear access through doorways and stairwells at both ends. Measure doorways and stairwells if the piece is large — a wardrobe or corner sofa may need disassembling to get out. The more information you provide when booking, the smoother the pickup will be.",
        additionalSections: [
          {
            heading: "Pick up furniture from Blocket without your own car",
            content:
              "The most common barrier to Blocket purchases is transport. The furniture looks perfect in the photos, the price is right — but then you realise it won't fit in your car, the seller lives on the fifth floor with no lift, or you simply can't carry a 90-kilo sofa. With Ungdomskraft's Blocket transport that barrier disappears. You buy, we pick up. We carry from any floor, load the van and deliver all the way into your home. Many of our customers say they now dare to buy completely different things on Blocket — heavy dining tables, corner sofas, wardrobes and appliances they could never have collected on their own. Our crew handle everything from small armchairs to large sectional sofas and IKEA wardrobes that need disassembling on site. We bring tools, furniture blankets and carrying straps — you don't need to prepare anything extra. If a piece won't fit through the door, we disassemble it at the seller's and reassemble it at your place. We protect all surfaces — floors, walls and door frames — during carrying so nothing gets damaged at either end.",
          },
          {
            heading: "Appliance transport from Blocket",
            content:
              "Bought a second-hand washing machine, fridge or dishwasher on Blocket? Appliances are heavy, bulky and need proper handling. A washing machine weighs 70–90 kg and a fridge even more — and both must be transported upright to avoid damaging the compressor. We collect from the seller, disconnect if needed, carry out and load securely with straps and blankets. We use hand trucks and sack barrows when needed to navigate tight spaces. At your home we carry in and place the appliance in the kitchen, laundry room or wherever you want it. Need help connecting? Let us know when booking and we'll take care of it. We transport all types of appliances: washing machines, dryers, washer-dryer combos, fridges, freezers, fridge-freezer combos, dishwashers, cookers, ovens and microwaves. Buying second-hand appliances on Blocket is often significantly cheaper than buying new — and with our transport you get them delivered all the way home without lifting a finger.",
          },
          {
            heading: "How Blocket transport works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the seller's address, your home address and describe what needs picking up. Upload a photo from the Blocket ad if you like. You see the price instantly.",
              },
              {
                label: "We pick up",
                text: "Our team drive to the seller with a van and the right equipment. We carry the item out, disassemble if needed, protect with blankets and straps and load the van.",
              },
              {
                label: "We deliver",
                text: "We drive to your address, carry through stairwells and doorways and place the item exactly where you want it. Furniture is reassembled if we took it apart.",
              },
              {
                label: "Done",
                text: "Your Blocket purchase is at home — quickly, safely and without you having to rent a car, ask friends for help or strain your back.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and what needs transporting. You can follow the status in real time and only need to be home when we deliver.",
          },
          {
            heading: "Also works for Facebook Marketplace and Tradera",
            content:
              "Our Blocket transport works just as well for purchases on Facebook Marketplace, Tradera, Shpock and local buy-and-sell groups. Just enter the seller's address as the pickup point and your home address as the delivery address — we handle the rest. Facebook Marketplace has grown enormously in recent years and we see more and more pickups from there. The process is identical: you send us the address, we pick up and deliver. We also handle purchases from auction sites like Tradera where you've won a bid and need to collect the item from the seller. Regardless of the platform, our service is the same — professional door-to-door transport with a van and crew.",
          },
          {
            heading: "Why book Blocket transport with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get professional help with your Blocket pickup — you also contribute to getting young people into the workforce and building their future. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and responsible environmental practices. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Our customers come back time and again — not just because the service works, but because they know their money makes a difference. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nBlocket transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Blocket transport cost?",
          answer:
            "The price depends on the item's size, weight and the distance between seller and buyer. A smaller piece of furniture or appliance starts at 595 SEK, while larger pickups with heavy furniture cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Blocket transport?",
          answer:
            "Blocket transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs picking up — you don't need to rent a vehicle.",
        },
        {
          question: "Do I need to be at the seller's address?",
          answer:
            "No, you don't need to be at the seller's address if you've already agreed on the purchase. Just make sure the seller knows we're coming. You only need to be at your home address to receive the delivery.",
        },
        {
          question: "Can you carry heavy Blocket purchases up stairs?",
          answer:
            "Yes, we carry from any floor — with or without a lift. Our crew are used to heavy items in narrow stairwells. We handle sofas, wardrobes, beds, appliances and anything else that's heavy.",
        },
        {
          question: "Do you also pick up from Facebook Marketplace?",
          answer:
            "Yes, we pick up purchases from Blocket, Facebook Marketplace, Tradera, Shpock and all other buy-and-sell platforms. Just enter the seller's address when booking.",
        },
        {
          question: "Can you disassemble furniture when picking up from Blocket?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart for transport. We bring the right tools and reassemble everything at your home if you want.",
        },
        {
          question: "Do you protect the items during transport?",
          answer:
            "Yes, we use furniture blankets, carrying straps and ratchet straps. We protect all furniture and fragile items during carrying, loading and transport. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you pick up my Blocket purchase?",
          answer:
            "We often offer Blocket pickups at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Can you pick up multiple Blocket purchases in one trip?",
          answer:
            "Yes, we can pick up from several sellers during the same run if the addresses are within a reasonable distance. Describe all pickup points when you book.",
        },
        {
          question: "Can you transport appliances bought on Blocket?",
          answer:
            "Yes, we transport all types of appliances — washing machines, dryers, fridges, freezers, dishwashers, cookers and ovens. We disconnect at the seller's if needed, carry out, transport upright and carry in at your home. We can also help with reconnection.",
        },
        {
          question: "Is Blocket transport available on weekends?",
          answer:
            "Yes, we offer Blocket transport on weekends and some evenings. Book online and see all available time slots.",
        },
        {
          question: "Are you responsible for the item's condition?",
          answer:
            "No, we are only responsible for the transport — not for the item's condition or whether it matches the listing. The purchase is an agreement between you and the seller. We recommend that you inspect the item yourself before we load, or that you are clear with the seller about your expectations.",
        },
        {
          question: "In which areas do you offer Blocket transport?",
          answer:
            "We offer Blocket transport across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Blocket transport in 3 easy steps",
        step2Text:
          "Our crew drive to the seller, carry out your purchase, protect it with blankets and load the van. Then we drive to your home.",
        step3Title: "Home with you",
        step3Desc:
          "Your Blocket purchase is in place at your home — carried in, set up and done. Without lifting a finger.",
        ctaHeading: "Book Blocket transport today",
        ctaText:
          "Enter the seller's address and your home address, describe what needs picking up and choose a time. You see the price instantly.",
      },
    },
  },
  {
    // Transport Facebook Marketplace — config för /transport-facebook-marketplace
    // Vinkel: Hämta varor köpta på Facebook Marketplace och få dem transporterade hem.
    // Unik vinkel vs transport-blocket: här handlar det specifikt om Marketplace-köp — många söker explicit på "Facebook Marketplace transport".
    slug: "transport-facebook-marketplace",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Facebook Marketplace – Hämta Ditt Köp Med Skåpbil | Ungdomskraft",
      description:
        "Köpt något på Facebook Marketplace som inte får plats i bilen? Vi hämtar hos säljaren, lastar skåpbilen och levererar hem till dig. Boka Marketplace-transport med personal idag.",
      h1: "Transport Facebook Marketplace",
    },

    hero: {
      heading: "Vi hämtar ditt Marketplace-köp",
      intro:
        "Hittat en soffa, ett skåp eller en tvättmaskin på Facebook Marketplace – men den får inte plats i bilen? Vi åker till säljaren, bär ut, lastar skåpbilen och levererar hem till dig. Vi bär in och ställer på plats. Du slipper hyra släp, be kompisar om hjälp eller göra flera turer. Funkar lika bra för köp på Blocket, Tradera och andra köp-och-sälj-plattformar.",
      heroImage: "/hero-transport-blocket.png",
    },

    featureCards: {
      sinceText: "Marketplace-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals köpare att hämta möbler, vitvaror och andra fynd från Facebook Marketplace. Våra ungdomar vet hur man bär tunga pjäser genom trånga trapphus och lastar skåpbilar effektivt – du får ditt köp hem utan att lyfta ett finger.",
      bookYourText: "Boka din Marketplace-transport",
      bookDescText:
        "Ange säljarens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Marketplace-hämtningar",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en bokhylla eller ett helt vardagsrum. Du slipper hyra släp, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Vanliga Marketplace-köp vi hämtar och kör hem",
      items: [
        {
          name: "Soffor & fåtöljer",
          description:
            "Soffor, hörnsoffor, fåtöljer och sittmöbler köpta på Facebook Marketplace",
          image: "/sofa.webp",
        },
        {
          name: "Bord & stolar",
          description:
            "Matbord, skrivbord, soffbord och stolar från Marketplace-säljare",
          image: "/patio-furniture.webp",
        },
        {
          name: "Sängar & madrasser",
          description:
            "Sängramar, resårmadrasser och huvudgavlar hämtade via Marketplace",
          image: "/bed.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Tvättmaskiner, kylskåp, frysar och diskmaskiner köpta begagnat på Marketplace",
          image: "/washer.webp",
        },
        {
          name: "Garderober & förvaring",
          description:
            "Garderober, byråer, bokhyllor och förvaringsmöbler från Facebook Marketplace",
          image: "/painting.webp",
        },
        {
          name: "Elektronik & övrigt",
          description:
            "TV-apparater, träningsredskap, kassaskåp och andra tunga Marketplace-fynd",
          image: "/tv.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Facebook Marketplace-transport – vi hämtar hos säljaren och levererar hem till dig",
      mainText:
        "Facebook Marketplace har blivit en av Sveriges största plattformar för att köpa och sälja begagnade möbler, vitvaror och prylar. Utbudet är enormt, priserna är bra och det finns alltid någon i närheten som säljer precis det du letar efter. Men det finns ett problem som de flesta Marketplace-köpare känner igen: transporten. Möbeln väger 80 kilo, säljaren bor tre mil bort och du har ingen skåpbil. Det är här Ungdomskraft kommer in. Vi åker till säljarens adress med skåpbil och ett team av starka ungdomar, bär ut varan, skyddar den med möbelfiltar och spännband, lastar och kör hem till dig. Där bär vi in och ställer på plats – i vardagsrummet, sovrummet eller köket. Du behöver inte ens vara med hos säljaren om ni redan kommit överens om köpet. Facebook Marketplace-transport fungerar för alla typer av köp: möbler, vitvaror, elektronik, byggmaterial, trädgårdsmöbler, träningsutrustning och allt annat som är för tungt, för stort eller för krångligt att ta hem själv. Vi hämtar även från Blocket, Tradera, Shpock och andra köp-och-sälj-plattformar – ange bara säljarens adress vid bokning. Varje vecka kör vi hundratals hämtningar från Facebook Marketplace i Stockholm och Göteborg. Våra ungdomar är vana vid att bära genom smala trapphus, runt trånga hörn och upp flera våningar utan hiss. Vi vet hur man skyddar ömtåliga ytor, demonterar möbler som inte går genom dörren och lastar skåpbilen så att inget skadas under transporten. Att köpa begagnat på Facebook Marketplace är smart – du sparar pengar, hittar unika pjäser och ger möbler ett nytt liv i stället för att köpa nytt. Men utan transport fastnar affären. Många Marketplace-annonser innehåller texten 'köparen hämtar själv' – och det är ofta det som gör att köpet aldrig blir av. Med Ungdomskrafts Marketplace-transport tar vi bort det hindret. Du fokuserar på att hitta rätt köp – vi tar hand om hela resan från dörr till dörr.",
      preparationHeading: "Förbered inför din Marketplace-hämtning",
      preparationText:
        "Bekräfta med säljaren exakt tid och adress – och att varan är redo att hämtas när vi kommer. Se till att portkod och våningsplan stämmer. Om möbeln behöver demonteras – fråga säljaren om den är isärtagen eller om vi ska göra det på plats. Ha din hemadress och eventuell portkod redo. Kolla att det finns fri passage i trapphus och att skåpbilen kan parkera nära porten både hos säljaren och hemma hos dig. Mät gärna dörröppningar och trapphus om möbeln är stor – en garderob eller hörnsoffa kan behöva demonteras för att komma ut. Skicka gärna en skärmdump av Marketplace-annonsen när du bokar så att vi vet exakt vad som ska hämtas. Ju mer information du anger vid bokning, desto smidigare går hämtningen.",
      additionalSections: [
        {
          heading: "Hämta möbler från Facebook Marketplace utan egen bil",
          content:
            "Det vanligaste hindret för Marketplace-köp är transporten. Möbeln ser perfekt ut på bilderna, priset är rätt – men sedan inser du att den inte ryms i bilen, att säljaren bor på femte våningen utan hiss, eller att du helt enkelt inte orkar bära en 90-kilos soffa. Med Ungdomskrafts Marketplace-transport försvinner det hindret. Du köper, vi hämtar. Vi bär från vilken våning som helst, lastar i skåpbilen och levererar hela vägen in i ditt hem. Många av våra kunder säger att de vågar köpa helt andra saker på Marketplace nu – tunga matbord, hörnsoffor, garderober och vitvaror som de aldrig hade kunnat ta hem själva. Vår personal hanterar allt från små fåtöljer till stora sektionssoffor och IKEA-garderober som måste demonteras på plats. Vi tar med oss verktyg, möbelfiltar och bärremmar – du behöver inte förbereda något extra. Om möbeln inte går genom dörren demonterar vi den hos säljaren och monterar ihop den igen hemma hos dig. Vi skyddar alla ytor – golv, väggar och dörrkarm – under bärningen så att inget skadas varken hos säljaren eller hos dig.",
        },
        {
          heading: "Transport av vitvaror köpta på Facebook Marketplace",
          content:
            "Köpt en begagnad tvättmaskin, kylskåp eller diskmaskin på Facebook Marketplace? Vitvaror är tunga, otympliga och kräver rätt hantering. En tvättmaskin väger 70–90 kg och ett kylskåp ännu mer – och bägge måste transporteras stående för att inte skada kompressorn. Vi hämtar vitvaror hos säljaren, kopplar loss om det behövs, bär ut och lastar säkert med bärremmar och möbelfiltar. Vi använder pirra och säckärra när det behövs för att komma genom trånga utrymmen. Hemma hos dig bär vi in och ställer på plats i köket, tvättstugan eller där du vill ha vitvarun. Behöver du hjälp med inkoppling? Meddela vid bokning så ordnar vi det. Vi transporterar alla typer av vitvaror: tvättmaskiner, torktumlare, kombinerade tvätt-tork, kylskåp, frysar, kyl-fryskombiskåp, diskmaskiner, spisar, ugnar och mikrovågsugnar. Att köpa begagnade vitvaror på Marketplace är ofta betydligt billigare än att köpa nytt – och med vår transport får du dem levererade hela vägen hem utan att lyfta ett finger.",
        },
        {
          heading: "Så fungerar Facebook Marketplace-transport med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange säljarens adress, din hemadress och beskriv vad som ska hämtas. Skicka gärna en skärmdump av Marketplace-annonsen. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar",
              text: "Vårt team åker till säljaren med skåpbil och rätt utrustning. Vi bär ut varan, demonterar om det behövs, skyddar med filtar och spännband och lastar skåpbilen.",
            },
            {
              label: "Vi levererar",
              text: "Vi kör till din adress, bär in genom trapphus och dörrar och ställer på exakt den plats du vill ha. Möbler monteras ihop igen om vi demonterat dem.",
            },
            {
              label: "Klart",
              text: "Ditt Marketplace-köp står hemma hos dig – snabbt, säkert och utan att du behövde hyra bil, be kompisar om hjälp eller slita ryggen.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och vad som ska transporteras. Du kan följa statusen i realtid och behöver bara vara hemma när vi levererar.",
        },
        {
          heading: "Blocket, Tradera och andra köp-och-sälj-plattformar",
          content:
            "Vår Marketplace-transport fungerar lika bra för köp på Blocket, Tradera, Shpock och lokala köp-och-sälj-grupper. Ange bara säljarens adress som hämtpunkt och din hemadress som leveransadress – vi sköter resten. Blocket är fortfarande en av de största plattformarna i Sverige och vi hämtar därifrån dagligen. Vi hanterar även köp från auktionssajter som Tradera där du vunnit en budgivning och behöver hämta varan hos säljaren. Oavsett plattform är vår tjänst densamma – professionell transport med skåpbil och personal från dörr till dörr.",
        },
        {
          heading: "Varför boka Marketplace-transport med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara professionell hjälp med din Marketplace-hämtning – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och ansvarsfullt miljötänk. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra kunder kommer tillbaka gång efter gång – inte bara för att tjänsten fungerar, utan för att de vet att deras pengar gör skillnad. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Ingen dolda avgifter, inga överraskningar.\n\nMarketplace-transport ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar transport från Facebook Marketplace?",
        answer:
          "Priset beror på varans storlek, vikt och avståndet mellan säljare och köpare. En mindre möbel eller vitvaror kostar från 595 kr, medan större hämtningar med tunga möbler kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på Marketplace-transport?",
        answer:
          "Marketplace-transport ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil vid Marketplace-hämtning?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska hämtas – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Behöver jag vara med hos säljaren?",
        answer:
          "Nej, du behöver inte vara med hos säljaren om ni redan kommit överens om köpet via Messenger. Se till att säljaren vet att vi kommer och att varan är redo att hämtas. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Kan ni bära tunga möbler från Marketplace i trappor?",
        answer:
          "Ja, vi bär från vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft i trånga trapphus. Vi hanterar soffor, garderober, sängar, vitvaror och allt annat tungt.",
      },
      {
        question: "Hämtar ni även från Blocket och Tradera?",
        answer:
          "Ja, vi hämtar köp från Facebook Marketplace, Blocket, Tradera, Shpock och alla andra köp-och-sälj-plattformar. Ange bara säljarens adress vid bokning.",
      },
      {
        question: "Kan ni demontera möbler vid hämtning?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att transporteras. Vi monterar ihop dem igen hemma hos dig om du vill.",
      },
      {
        question: "Skyddar ni möblerna under transporten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar alla möbler och ömtåliga föremål under bärning, lastning och transport. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni hämta mitt Marketplace-köp?",
        answer:
          "Vi erbjuder ofta Marketplace-hämtning med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni hämta flera Marketplace-köp på samma tur?",
        answer:
          "Ja, vi kan hämta från flera säljare under samma körning om adresserna ligger i rimligt avstånd. Beskriv alla hämtpunkter när du bokar.",
      },
      {
        question: "Kan ni transportera vitvaror köpta på Marketplace?",
        answer:
          "Ja, vi transporterar alla typer av vitvaror – tvättmaskiner, torktumlare, kylskåp, frysar, diskmaskiner, spisar och ugnar. Vi kopplar loss hos säljaren om det behövs, bär ut, transporterar stående och bär in hemma hos dig. Vi kan även hjälpa till med inkoppling.",
      },
      {
        question: "Erbjuder ni Marketplace-transport på helger?",
        answer:
          "Ja, vi erbjuder transport på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Ansvarar ni för varans skick?",
        answer:
          "Nej, vi ansvarar enbart för transporten – inte för varans skick eller om den matchar annonsen. Köpet är en överenskommelse mellan dig och säljaren. Vi rekommenderar att du själv kontrollerar varan innan vi lastar, eller att du är tydlig med säljaren om vad du förväntar dig.",
      },
      {
        question: "Är Marketplace-transporten försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under bärning, lastning eller transport ersätter vi det. Du kan boka med trygghet – vi tar fullt ansvar för dina saker från hämtning till leverans.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni Facebook Marketplace-transport?",
        answer:
          "Vi erbjuder Marketplace-transport i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Marketplace-transport i 3 enkla steg",
      step2Text:
        "Våra ungdomar åker till säljaren, bär ut ditt köp, skyddar det med filtar och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Hemma hos dig",
      step3Desc:
        "Ditt Marketplace-köp står på plats hemma hos dig – inburet, uppställt och klart. Utan att du lyfte ett finger.",
      ctaHeading: "Boka Marketplace-transport idag",
      ctaText:
        "Ange säljarens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "facebook-marketplace-transport",
      meta: {
        title:
          "Facebook Marketplace Transport – Pick Up Your Purchase With Van | Ungdomskraft",
        description:
          "Bought something on Facebook Marketplace that won't fit in your car? We pick up from the seller, load the van and deliver to your door. Book Marketplace transport with crew today.",
        h1: "Facebook Marketplace transport",
      },
      hero: {
        heading: "We deliver your Marketplace-purchase",
        intro:
          "Found a sofa, a cabinet or a washing machine on Facebook Marketplace — but it won't fit in your car? We drive to the seller, carry out, load the van and deliver to your door. We carry inside and place it where you want it. No need to rent a trailer, ask friends for help or make multiple trips. Works just as well for purchases on Blocket, Tradera and other buy-and-sell platforms.",
      },
      featureCards: {
        sinceText: "Marketplace transport since 2014",
        bookYourText: "Book your Marketplace transport",
        bookDescText:
          "Enter the seller's address and your home address, describe what needs picking up and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Marketplace pickups",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a bookshelf or an entire living room set. No need to rent a trailer, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Common Marketplace purchases we pick up and deliver",
        items: [
          {
            name: "Sofas & armchairs",
            description:
              "Sofas, corner sofas, armchairs and seating bought on Facebook Marketplace",
            image: "/sofa.webp",
          },
          {
            name: "Tables & chairs",
            description:
              "Dining tables, desks, coffee tables and chairs from Marketplace sellers",
            image: "/patio-furniture.webp",
          },
          {
            name: "Beds & mattresses",
            description:
              "Bed frames, mattresses and headboards picked up via Marketplace",
            image: "/bed.webp",
          },
          {
            name: "Appliances",
            description:
              "Washing machines, fridges, freezers and dishwashers bought second hand on Marketplace",
            image: "/washer.webp",
          },
          {
            name: "Wardrobes & storage",
            description:
              "Wardrobes, dressers, bookshelves and storage furniture from Facebook Marketplace",
            image: "/painting.webp",
          },
          {
            name: "Electronics & other",
            description:
              "TVs, gym equipment, safes and other heavy Marketplace finds",
            image: "/tv.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Facebook Marketplace transport — we pick up from the seller and deliver to your door",
        mainText:
          "Facebook Marketplace has become one of Sweden's largest platforms for buying and selling second-hand furniture, appliances and household goods. The selection is enormous, prices are great and there's always someone nearby selling exactly what you're looking for. But there's one problem most Marketplace buyers recognise: transport. The item weighs 80 kilos, the seller lives 30 km away and you don't have a van. That's where Ungdomskraft comes in. We drive to the seller's address with a van and a team of strong young workers, carry the item out, protect it with furniture blankets and straps, load up and drive to your home. There we carry it inside and place it exactly where you want it — in the living room, bedroom or kitchen. You don't even need to be at the seller's place if you've already agreed on the purchase via Messenger. Facebook Marketplace transport works for all types of purchases: furniture, appliances, electronics, building materials, garden furniture, gym equipment and anything else that's too heavy, too large or too awkward to take home yourself. We also pick up from Blocket, Tradera, Shpock and other buy-and-sell platforms — just enter the seller's address when booking. Every week we make hundreds of Marketplace pickups across Stockholm and Gothenburg. Our crew are used to carrying through narrow stairwells, around tight corners and up multiple flights without a lift. We know how to protect delicate surfaces, disassemble furniture that won't fit through doorways and load the van so nothing gets damaged during transport. Buying second hand on Facebook Marketplace is one of the smartest ways to furnish your home — you save money, find unique pieces and do something good for the environment by giving furniture a new life instead of buying new. But without transport, the deal often falls through. Many Marketplace listings say 'buyer collects' — and that's often what kills the deal. With Ungdomskraft's Marketplace transport we remove that barrier entirely. You focus on finding the right deal — we take care of the entire journey from door to door.",
        preparationHeading: "Prepare for your Marketplace pickup",
        preparationText:
          "Confirm the exact time and address with the seller via Messenger — and that the item is ready for pickup when we arrive. Make sure the door code and floor number are correct. If the furniture needs disassembling, ask the seller whether it's already taken apart or if we should do it on site. Have your home address and any door codes ready. Check that there is clear access through doorways and stairwells at both ends. Measure doorways and stairwells if the piece is large — a wardrobe or corner sofa may need disassembling to get out. Send us a screenshot of the Marketplace listing when you book so we know exactly what to pick up. The more information you provide, the smoother the pickup will be.",
        additionalSections: [
          {
            heading: "Pick up furniture from Facebook Marketplace without your own car",
            content:
              "The most common barrier to Marketplace purchases is transport. The furniture looks perfect in the photos, the price is right — but then you realise it won't fit in your car, the seller lives on the fifth floor with no lift, or you simply can't carry a 90-kilo sofa. With Ungdomskraft's Marketplace transport that barrier disappears. You buy, we pick up. We carry from any floor, load the van and deliver all the way into your home. Many of our customers say they now dare to buy completely different things on Marketplace — heavy dining tables, corner sofas, wardrobes and appliances they could never have collected on their own. Our crew handle everything from small armchairs to large sectional sofas and IKEA wardrobes that need disassembling on site. We bring tools, furniture blankets and carrying straps — you don't need to prepare anything extra. If a piece won't fit through the door, we disassemble it at the seller's and reassemble it at your place. We protect all surfaces — floors, walls and door frames — during carrying so nothing gets damaged at either end.",
          },
          {
            heading: "Appliance transport from Facebook Marketplace",
            content:
              "Bought a second-hand washing machine, fridge or dishwasher on Facebook Marketplace? Appliances are heavy, bulky and need proper handling. A washing machine weighs 70–90 kg and a fridge even more — and both must be transported upright to avoid damaging the compressor. We collect from the seller, disconnect if needed, carry out and load securely with straps and blankets. We use hand trucks and sack barrows when needed to navigate tight spaces. At your home we carry in and place the appliance in the kitchen, laundry room or wherever you want it. Need help connecting? Let us know when booking and we'll take care of it. We transport all types of appliances: washing machines, dryers, washer-dryer combos, fridges, freezers, fridge-freezer combos, dishwashers, cookers, ovens and microwaves. Buying second-hand appliances on Marketplace is often significantly cheaper than buying new — and with our transport you get them delivered all the way home without lifting a finger.",
          },
          {
            heading: "How Facebook Marketplace transport works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the seller's address, your home address and describe what needs picking up. Send a screenshot of the Marketplace listing if you like. You see the price instantly.",
              },
              {
                label: "We pick up",
                text: "Our team drive to the seller with a van and the right equipment. We carry the item out, disassemble if needed, protect with blankets and straps and load the van.",
              },
              {
                label: "We deliver",
                text: "We drive to your address, carry through stairwells and doorways and place the item exactly where you want it. Furniture is reassembled if we took it apart.",
              },
              {
                label: "Done",
                text: "Your Marketplace purchase is at home — quickly, safely and without you having to rent a car, ask friends for help or strain your back.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and what needs transporting. You can follow the status in real time and only need to be home when we deliver.",
          },
          {
            heading: "Also works for Blocket and Tradera",
            content:
              "Our Marketplace transport works just as well for purchases on Blocket, Tradera, Shpock and local buy-and-sell groups. Just enter the seller's address as the pickup point and your home address as the delivery address — we handle the rest. Blocket is still one of the largest platforms in Sweden and we pick up from there daily. We also handle purchases from auction sites like Tradera where you've won a bid and need to collect the item from the seller. Regardless of the platform, our service is the same — professional door-to-door transport with a van and crew.",
          },
          {
            heading: "Why book Marketplace transport with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get professional help with your Marketplace pickup — you also contribute to getting young people into the workforce and building their future. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and responsible environmental practices. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Our customers come back time and again — not just because the service works, but because they know their money makes a difference. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nFacebook Marketplace transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Facebook Marketplace transport cost?",
          answer:
            "The price depends on the item's size, weight and the distance between seller and buyer. A smaller piece of furniture or appliance starts at 595 SEK, while larger pickups with heavy furniture cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Facebook Marketplace transport?",
          answer:
            "Facebook Marketplace transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs picking up — you don't need to rent a vehicle.",
        },
        {
          question: "Do I need to be at the seller's address?",
          answer:
            "No, you don't need to be at the seller's address if you've already agreed on the purchase via Messenger. Just make sure the seller knows we're coming. You only need to be at your home address to receive the delivery.",
        },
        {
          question: "Can you carry heavy Marketplace purchases up stairs?",
          answer:
            "Yes, we carry from any floor — with or without a lift. Our crew are used to heavy items in narrow stairwells. We handle sofas, wardrobes, beds, appliances and anything else that's heavy.",
        },
        {
          question: "Do you also pick up from Blocket and Tradera?",
          answer:
            "Yes, we pick up purchases from Facebook Marketplace, Blocket, Tradera, Shpock and all other buy-and-sell platforms. Just enter the seller's address when booking.",
        },
        {
          question: "Can you disassemble furniture when picking up?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart for transport. We bring the right tools and reassemble everything at your home if you want.",
        },
        {
          question: "Do you protect the items during transport?",
          answer:
            "Yes, we use furniture blankets, carrying straps and ratchet straps. We protect all furniture and fragile items during carrying, loading and transport. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you pick up my Marketplace purchase?",
          answer:
            "We often offer Marketplace pickups at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Can you pick up from multiple sellers in one trip?",
          answer:
            "Yes, we can pick up from several sellers during the same run if the addresses are within a reasonable distance. Describe all pickup points when you book.",
        },
        {
          question: "Can you transport appliances bought on Marketplace?",
          answer:
            "Yes, we transport all types of appliances — washing machines, dryers, fridges, freezers, dishwashers, cookers and ovens. We disconnect at the seller's if needed, carry out, transport upright and carry in at your home. We can also help with reconnection.",
        },
        {
          question: "Is Marketplace transport available on weekends?",
          answer:
            "Yes, we offer Marketplace transport on weekends and some evenings. Book online and see all available time slots.",
        },
        {
          question: "Are you responsible for the item's condition?",
          answer:
            "No, we are only responsible for the transport — not for the item's condition or whether it matches the listing. The purchase is an agreement between you and the seller. We recommend that you inspect the item yourself before we load, or that you are clear with the seller about your expectations.",
        },
        {
          question: "Is the transport insured?",
          answer:
            "Yes, all our transports are fully insured. If anything is damaged during carrying, loading or transport we replace it. You can book with confidence — we take full responsibility for your items from pickup to delivery.",
        },
        {
          question: "In which areas do you offer Facebook Marketplace transport?",
          answer:
            "We offer Marketplace transport across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Marketplace transport in 3 easy steps",
        step2Text:
          "Our crew drive to the seller, carry out your purchase, protect it with blankets and load the van. Then we drive to your home.",
        step3Title: "Home with you",
        step3Desc:
          "Your Marketplace purchase is in place at your home — carried in, set up and done. Without lifting a finger.",
        ctaHeading: "Book Marketplace transport today",
        ctaText:
          "Enter the seller's address and your home address, describe what needs picking up and choose a time. You see the price instantly.",
      },
    },
  },
  {
    // Transport Tradera — config för /transport-tradera
    // Vinkel: Hämta varor vunna/köpta på Tradera-auktioner och få dem transporterade hem.
    // Unik vinkel vs Blocket/Marketplace: auktionsköp med budgivning, ofta unika/antika föremål, tidsbegränsad hämtning.
    slug: "transport-tradera",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Tradera – Hämta Ditt Auktionsköp Med Skåpbil | Ungdomskraft",
      description:
        "Vunnit en auktion på Tradera men kan inte hämta varan? Vi hämtar hos säljaren, lastar skåpbilen och levererar hem till dig. Boka Tradera-transport med personal idag.",
      h1: "Transport Tradera",
    },

    hero: {
      heading: "Vi hämtar ditt Tradera-köp",
      intro:
        "Vunnit en auktion på Tradera – en byrå, en vintagelampa eller en tvättmaskin – men den får inte plats i bilen? Vi åker till säljaren, bär ut, lastar skåpbilen och levererar hem till dig. Vi bär in och ställer på plats. Du slipper hyra släp, be kompisar om hjälp eller riskera att missa hämtningsfristen. Funkar lika bra för köp på Blocket, Facebook Marketplace och andra köp-och-sälj-plattformar.",
      heroImage: "/hero-transport-blocket.png",
    },

    featureCards: {
      sinceText: "Tradera-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals auktionsköpare att hämta möbler, antikviteter, vitvaror och andra fynd från Tradera. Våra ungdomar vet hur man hanterar ömtåliga och värdefulla föremål – du får ditt auktionsköp hem säkert och i tid.",
      bookYourText: "Boka din Tradera-transport",
      bookDescText:
        "Ange säljarens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Tradera-hämtningar",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en liten antik pryl eller en hel möbelgrupp. Du slipper hyra släp, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Vanliga Tradera-köp vi hämtar och kör hem",
      items: [
        {
          name: "Möbler & antikt",
          description:
            "Byråer, skåp, bord, stolar och antika möbler vunna på Tradera-auktioner",
          image: "/sofa.webp",
        },
        {
          name: "Soffor & fåtöljer",
          description:
            "Soffor, hörnsoffor, fåtöljer och designklassiker köpta på Tradera",
          image: "/patio-furniture.webp",
        },
        {
          name: "Sängar & madrasser",
          description:
            "Sängramar, madrasser och huvudgavlar hämtade via Tradera",
          image: "/bed.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Tvättmaskiner, kylskåp, frysar och diskmaskiner köpta på Tradera",
          image: "/washer.webp",
        },
        {
          name: "Konst & ömtåligt",
          description:
            "Tavlor, speglar, porslin, glaskonst och andra ömtåliga auktionsfynd",
          image: "/painting.webp",
        },
        {
          name: "Elektronik & övrigt",
          description:
            "TV-apparater, musikutrustning, träningsredskap och andra tunga Tradera-köp",
          image: "/tv.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Tradera-transport – vi hämtar ditt auktionsköp och levererar hem till dig",
      mainText:
        "Tradera är Sveriges största auktionsplattform med miljontals annonser – från vintagemöbler och designklassiker till begagnade vitvaror och elektronik. Att vinna en auktion är spännande, men sedan kommer den praktiska biten: hur får du hem varan? Säljaren bor kanske i en annan del av staden, möbeln väger 70 kilo och du har ingen skåpbil. Det är här Ungdomskraft kommer in. Vi åker till säljarens adress med skåpbil och ett team av starka ungdomar, bär ut varan, skyddar den med möbelfiltar och spännband, lastar och kör hem till dig. Där bär vi in och ställer på plats – i vardagsrummet, sovrummet eller köket. Du behöver inte ens vara med hos säljaren om ni redan kommit överens. Tradera-transport fungerar för alla typer av auktionsköp: möbler, antikt, vitvaror, konst, elektronik, musikinstrument, trädgårdsmöbler och allt annat som är för tungt, för stort eller för ömtåligt att ta hem själv. Vi hämtar även från Blocket, Facebook Marketplace, Shpock och andra köp-och-sälj-plattformar – ange bara säljarens adress vid bokning. Varje vecka kör vi hundratals hämtningar i Stockholm och Göteborg. Våra ungdomar är vana vid att bära genom smala trapphus, runt trånga hörn och upp flera våningar utan hiss. Vi vet hur man skyddar ömtåliga ytor, demonterar möbler som inte går genom dörren och lastar skåpbilen så att inget skadas under transporten. En viktig skillnad med Tradera-köp jämfört med andra plattformar är att du ofta har en tidsfrist för att hämta varan. Missar du fristen riskerar du att förlora både köpet och pengarna. Med Ungdomskraft bokar du transporten direkt efter att du vunnit auktionen – vi hämtar inom dagar och du slipper stressa över att hitta bil och tid. Du fokuserar på att vinna auktionen – vi tar hand om hela resan från dörr till dörr.",
      preparationHeading: "Förbered inför din Tradera-hämtning",
      preparationText:
        "Bekräfta med säljaren exakt tid och adress – och att varan är redo att hämtas när vi kommer. Se till att portkod och våningsplan stämmer. Om möbeln behöver demonteras – fråga säljaren om den är isärtagen eller om vi ska göra det på plats. Ha din hemadress och eventuell portkod redo. Kolla att det finns fri passage i trapphus och att skåpbilen kan parkera nära porten både hos säljaren och hemma hos dig. Mät gärna dörröppningar och trapphus om möbeln är stor – en garderob eller hörnsoffa kan behöva demonteras för att komma ut. Skicka gärna en länk till Tradera-annonsen när du bokar så att vi vet exakt vad som ska hämtas. Ju mer information du anger vid bokning, desto smidigare går hämtningen.",
      additionalSections: [
        {
          heading: "Hämta auktionsfynd från Tradera utan egen bil",
          content:
            "Det vanligaste hindret för Tradera-köp är transporten. Du vinner auktionen, betalningen går igenom – men sedan inser du att möbeln inte ryms i bilen, att säljaren bor på fjärde våningen utan hiss, eller att du helt enkelt inte orkar bära en tung antik byrå. Med Ungdomskrafts Tradera-transport försvinner det hindret. Du vinner, vi hämtar. Vi bär från vilken våning som helst, lastar i skåpbilen och levererar hela vägen in i ditt hem. Många av våra kunder säger att de nu vågar buda på helt andra saker på Tradera – tunga möbler, skrymmande designklassiker, garderober och vitvaror som de aldrig hade kunnat ta hem själva. Vår personal hanterar allt från små antikviteter till stora möbelgrupper som måste demonteras på plats. Vi tar med oss verktyg, möbelfiltar och bärremmar – du behöver inte förbereda något extra. Om möbeln inte går genom dörren demonterar vi den hos säljaren och monterar ihop den igen hemma hos dig. Vi skyddar alla ytor – golv, väggar och dörrkarmar – under bärningen så att inget skadas varken hos säljaren eller hos dig.",
        },
        {
          heading: "Transport av ömtåliga och värdefulla Tradera-köp",
          content:
            "Tradera är känt för unika fynd – antika möbler, konst, glassamlingar, porslin, vintagelampor och designföremål som ofta har högt värde. Dessa föremål kräver extra omsorg under transport. Vi skyddar ömtåliga ytor med tjocka möbelfiltar och polstermaterial, spänner fast med remmar och transporterar stående eller liggande beroende på vad som är säkrast. Tavlor och speglar transporteras alltid stående med hörnskydd. Porslin och glas packas i bubbelplast om säljaren inte redan gjort det. Vi har lång erfarenhet av att transportera värdefulla föremål och vet att en repa på en antik byrå eller en spricka i en glasvas inte går att reparera. Därför tar vi inga genvägar – varje föremål behandlas som om det vore ovärderligt. Allt är dessutom helförsäkrat under hela transporten.",
        },
        {
          heading: "Så fungerar Tradera-transport med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange säljarens adress, din hemadress och beskriv vad som ska hämtas. Skicka gärna en länk till Tradera-annonsen. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar",
              text: "Vårt team åker till säljaren med skåpbil och rätt utrustning. Vi bär ut varan, demonterar om det behövs, skyddar med filtar och spännband och lastar skåpbilen.",
            },
            {
              label: "Vi levererar",
              text: "Vi kör till din adress, bär in genom trapphus och dörrar och ställer på exakt den plats du vill ha. Möbler monteras ihop igen om vi demonterat dem.",
            },
            {
              label: "Klart",
              text: "Ditt Tradera-köp står hemma hos dig – säkert, oskadat och utan att du behövde hyra bil eller stressa över hämtningsfristen.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och vad som ska transporteras. Du kan följa statusen i realtid och behöver bara vara hemma när vi levererar.",
        },
        {
          heading: "Blocket, Facebook Marketplace och andra plattformar",
          content:
            "Vår Tradera-transport fungerar lika bra för köp på Blocket, Facebook Marketplace, Shpock och lokala köp-och-sälj-grupper. Ange bara säljarens adress som hämtpunkt och din hemadress som leveransadress – vi sköter resten. Blocket och Facebook Marketplace är de andra stora plattformarna i Sverige och vi hämtar därifrån dagligen. Oavsett plattform är vår tjänst densamma – professionell transport med skåpbil och personal från dörr till dörr. Många av våra kunder köper regelbundet på flera plattformar och använder oss som sin fasta transportpartner.",
        },
        {
          heading: "Varför boka Tradera-transport med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara professionell hjälp med din Tradera-hämtning – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och ansvarsfullt miljötänk. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra kunder kommer tillbaka gång efter gång – inte bara för att tjänsten fungerar, utan för att de vet att deras pengar gör skillnad. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar.\n\nTradera-transport ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar transport av Tradera-köp?",
        answer:
          "Priset beror på varans storlek, vikt och avståndet mellan säljare och köpare. En mindre möbel eller pryl kostar från 595 kr, medan större hämtningar med tunga möbler kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på Tradera-transport?",
        answer:
          "Tradera-transport ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil vid Tradera-hämtning?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska hämtas – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Behöver jag vara med hos säljaren?",
        answer:
          "Nej, du behöver inte vara med hos säljaren om ni redan kommit överens om hämtningen. Se till att säljaren vet att vi kommer och att varan är redo. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Kan ni bära tunga Tradera-köp i trappor?",
        answer:
          "Ja, vi bär från vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft i trånga trapphus. Vi hanterar soffor, garderober, sängar, vitvaror och allt annat tungt.",
      },
      {
        question: "Hämtar ni även från Blocket och Facebook Marketplace?",
        answer:
          "Ja, vi hämtar köp från Tradera, Blocket, Facebook Marketplace, Shpock och alla andra köp-och-sälj-plattformar. Ange bara säljarens adress vid bokning.",
      },
      {
        question: "Kan ni hantera ömtåliga och antika föremål?",
        answer:
          "Ja, vi har stor erfarenhet av att transportera antika möbler, konst, glas, porslin och andra ömtåliga föremål. Vi använder tjocka möbelfiltar, polstermaterial och hörnskydd. Allt är helförsäkrat under hela transporten.",
      },
      {
        question: "Kan ni demontera möbler vid hämtning?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att transporteras. Vi monterar ihop dem igen hemma hos dig om du vill.",
      },
      {
        question: "Skyddar ni föremålen under transporten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar, spännband och polstermaterial. Vi skyddar alla möbler och ömtåliga föremål under bärning, lastning och transport. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni hämta mitt Tradera-köp?",
        answer:
          "Vi erbjuder ofta Tradera-hämtning med kort varsel – ofta redan samma dag – inom några timmar. Det är extra viktigt vid auktionsköp där du kan ha en tidsfrist för hämtning. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni hämta flera Tradera-köp på samma tur?",
        answer:
          "Ja, vi kan hämta från flera säljare under samma körning om adresserna ligger i rimligt avstånd. Beskriv alla hämtpunkter när du bokar.",
      },
      {
        question: "Kan ni transportera vitvaror köpta på Tradera?",
        answer:
          "Ja, vi transporterar alla typer av vitvaror – tvättmaskiner, torktumlare, kylskåp, frysar, diskmaskiner, spisar och ugnar. Vi kopplar loss hos säljaren om det behövs, bär ut, transporterar stående och bär in hemma hos dig.",
      },
      {
        question: "Erbjuder ni Tradera-transport på helger?",
        answer:
          "Ja, vi erbjuder Tradera-transport på helger och vissa kvällar. Boka online och se alla tillgängliga tider.",
      },
      {
        question: "Ansvarar ni för varans skick?",
        answer:
          "Nej, vi ansvarar enbart för transporten – inte för varans skick eller om den matchar annonsen. Köpet är en överenskommelse mellan dig och säljaren. Vi rekommenderar att du själv kontrollerar varan innan vi lastar, eller att du är tydlig med säljaren om vad du förväntar dig.",
      },
      {
        question: "Är Tradera-transporten försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under bärning, lastning eller transport ersätter vi det. Du kan boka med trygghet – vi tar fullt ansvar för dina saker från hämtning till leverans.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni Tradera-transport?",
        answer:
          "Vi erbjuder Tradera-transport i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Tradera-transport i 3 enkla steg",
      step2Text:
        "Våra ungdomar åker till säljaren, bär ut ditt auktionsköp, skyddar det med filtar och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Hemma hos dig",
      step3Desc:
        "Ditt Tradera-köp står på plats hemma hos dig – inburet, uppställt och oskadat. Utan att du lyfte ett finger.",
      ctaHeading: "Boka Tradera-transport idag",
      ctaText:
        "Ange säljarens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "tradera-transport",
      meta: {
        title:
          "Tradera Transport – Pick Up Your Auction Purchase With Van | Ungdomskraft",
        description:
          "Won an auction on Tradera but can't collect the item? We pick up from the seller, load the van and deliver to your door. Book Tradera transport with crew today.",
        h1: "Tradera transport",
      },
      hero: {
        heading: "We deliver your Tradera-purchase",
        intro:
          "Won an auction on Tradera — a dresser, a vintage lamp or a washing machine — but it won't fit in your car? We drive to the seller, carry out, load the van and deliver to your door. We carry inside and place it where you want it. No need to rent a trailer, ask friends for help or risk missing the collection deadline. Works just as well for purchases on Blocket, Facebook Marketplace and other buy-and-sell platforms.",
      },
      featureCards: {
        sinceText: "Tradera transport since 2014",
        bookYourText: "Book your Tradera transport",
        bookDescText:
          "Enter the seller's address and your home address, describe what needs picking up and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Tradera pickups",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a small antique or an entire furniture set. No need to rent a trailer, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Common Tradera purchases we pick up and deliver",
        items: [
          {
            name: "Furniture & antiques",
            description:
              "Dressers, cabinets, tables, chairs and antique furniture won at Tradera auctions",
            image: "/sofa.webp",
          },
          {
            name: "Sofas & armchairs",
            description:
              "Sofas, corner sofas, armchairs and design classics bought on Tradera",
            image: "/patio-furniture.webp",
          },
          {
            name: "Beds & mattresses",
            description:
              "Bed frames, mattresses and headboards picked up via Tradera",
            image: "/bed.webp",
          },
          {
            name: "Appliances",
            description:
              "Washing machines, fridges, freezers and dishwashers bought on Tradera",
            image: "/washer.webp",
          },
          {
            name: "Art & fragile items",
            description:
              "Paintings, mirrors, porcelain, glassware and other delicate auction finds",
            image: "/painting.webp",
          },
          {
            name: "Electronics & other",
            description:
              "TVs, music equipment, gym gear and other heavy Tradera purchases",
            image: "/tv.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Tradera transport — we pick up your auction purchase and deliver to your door",
        mainText:
          "Tradera is Sweden's largest auction platform with millions of listings — from vintage furniture and design classics to second-hand appliances and electronics. Winning an auction is exciting, but then comes the practical part: how do you get the item home? The seller might live in another part of the city, the piece of furniture weighs 70 kilos and you don't have a van. That's where Ungdomskraft comes in. We drive to the seller's address with a van and a team of strong young workers, carry the item out, protect it with furniture blankets and straps, load up and drive to your home. There we carry it inside and place it exactly where you want it — in the living room, bedroom or kitchen. You don't even need to be at the seller's place if you've already agreed on the pickup. Tradera transport works for all types of auction purchases: furniture, antiques, appliances, art, electronics, musical instruments, garden furniture and anything else that's too heavy, too large or too fragile to take home yourself. We also pick up from Blocket, Facebook Marketplace, Shpock and other buy-and-sell platforms — just enter the seller's address when booking. Every week we make hundreds of pickups across Stockholm and Gothenburg. Our crew are used to carrying through narrow stairwells, around tight corners and up multiple flights without a lift. We know how to protect delicate surfaces, disassemble furniture that won't fit through doorways and load the van so nothing gets damaged during transport. An important difference with Tradera purchases compared to other platforms is that you often have a deadline for collecting the item. If you miss it, you risk losing both the purchase and your money. With Ungdomskraft you book the transport right after winning the auction — we collect within days and you don't have to stress about finding a vehicle and time. You focus on winning auctions — we take care of the entire journey from door to door.",
        preparationHeading: "Prepare for your Tradera pickup",
        preparationText:
          "Confirm the exact time and address with the seller — and that the item is ready for pickup when we arrive. Make sure the door code and floor number are correct. If the furniture needs disassembling, ask the seller whether it's already taken apart or if we should do it on site. Have your home address and any door codes ready. Check that there is clear access through doorways and stairwells at both ends. Measure doorways and stairwells if the piece is large — a wardrobe or dresser may need disassembling to get out. Send us a link to the Tradera listing when you book so we know exactly what to pick up. The more information you provide, the smoother the pickup will be.",
        additionalSections: [
          {
            heading: "Pick up auction finds from Tradera without your own car",
            content:
              "The most common barrier to Tradera purchases is transport. You win the auction, payment goes through — but then you realise the furniture won't fit in your car, the seller lives on the fourth floor with no lift, or you simply can't carry a heavy antique dresser. With Ungdomskraft's Tradera transport that barrier disappears. You win, we pick up. We carry from any floor, load the van and deliver all the way into your home. Many of our customers say they now dare to bid on completely different things on Tradera — heavy furniture, bulky design classics, wardrobes and appliances they could never have collected on their own. Our crew handle everything from small antiques to large furniture sets that need disassembling on site. We bring tools, furniture blankets and carrying straps — you don't need to prepare anything extra. If a piece won't fit through the door, we disassemble it at the seller's and reassemble it at your place. We protect all surfaces — floors, walls and door frames — during carrying so nothing gets damaged at either end.",
          },
          {
            heading: "Transport of fragile and valuable Tradera purchases",
            content:
              "Tradera is known for unique finds — antique furniture, art, glass collections, porcelain, vintage lamps and designer pieces that often have high value. These items require extra care during transport. We protect delicate surfaces with thick furniture blankets and padding material, secure with straps and transport upright or lying down depending on what's safest. Paintings and mirrors are always transported upright with corner protectors. Porcelain and glass are wrapped in bubble wrap if the seller hasn't already done so. We have extensive experience transporting valuable items and know that a scratch on an antique dresser or a crack in a glass vase can't be repaired. That's why we take no shortcuts — every item is treated as if it were priceless. Everything is also fully insured throughout the entire transport.",
          },
          {
            heading: "How Tradera transport works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the seller's address, your home address and describe what needs picking up. Send a link to the Tradera listing if you like. You see the price instantly.",
              },
              {
                label: "We pick up",
                text: "Our team drive to the seller with a van and the right equipment. We carry the item out, disassemble if needed, protect with blankets and straps and load the van.",
              },
              {
                label: "We deliver",
                text: "We drive to your address, carry through stairwells and doorways and place the item exactly where you want it. Furniture is reassembled if we took it apart.",
              },
              {
                label: "Done",
                text: "Your Tradera purchase is at home — safely, undamaged and without you having to rent a car or stress about the collection deadline.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and what needs transporting. You can follow the status in real time and only need to be home when we deliver.",
          },
          {
            heading: "Also works for Blocket and Facebook Marketplace",
            content:
              "Our Tradera transport works just as well for purchases on Blocket, Facebook Marketplace, Shpock and local buy-and-sell groups. Just enter the seller's address as the pickup point and your home address as the delivery address — we handle the rest. Blocket and Facebook Marketplace are the other major platforms in Sweden and we pick up from them daily. Regardless of the platform, our service is the same — professional door-to-door transport with a van and crew. Many of our customers buy regularly across multiple platforms and use us as their go-to transport partner.",
          },
          {
            heading: "Why book Tradera transport with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get professional help with your Tradera pickup — you also contribute to getting young people into the workforce and building their future. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and responsible environmental practices. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Our customers come back time and again — not just because the service works, but because they know their money makes a difference. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nTradera transport does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Tradera transport cost?",
          answer:
            "The price depends on the item's size, weight and the distance between seller and buyer. A smaller piece of furniture or item starts at 595 SEK, while larger pickups with heavy furniture cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Tradera transport?",
          answer:
            "Tradera transport does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs picking up — you don't need to rent a vehicle.",
        },
        {
          question: "Do I need to be at the seller's address?",
          answer:
            "No, you don't need to be at the seller's address if you've already agreed on the pickup. Just make sure the seller knows we're coming. You only need to be at your home address to receive the delivery.",
        },
        {
          question: "Can you carry heavy Tradera purchases up stairs?",
          answer:
            "Yes, we carry from any floor — with or without a lift. Our crew are used to heavy items in narrow stairwells. We handle sofas, wardrobes, beds, appliances, antique cabinets and anything else that's heavy.",
        },
        {
          question: "Do you also pick up from Blocket and Facebook Marketplace?",
          answer:
            "Yes, we pick up purchases from Tradera, Blocket, Facebook Marketplace, Shpock and all other buy-and-sell platforms. Just enter the seller's address when booking.",
        },
        {
          question: "Can you handle fragile and valuable items?",
          answer:
            "Yes, we have extensive experience transporting antiques, art, glass, porcelain and other fragile items. We use thick furniture blankets, padding and corner protectors. Everything is fully insured throughout the transport.",
        },
        {
          question: "Can you disassemble furniture when picking up?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart for transport. We bring the right tools and reassemble everything at your home if you want.",
        },
        {
          question: "Do you protect the items during transport?",
          answer:
            "Yes, we use furniture blankets, carrying straps, ratchet straps and padding material. We protect all items during carrying, loading and transport. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you pick up my Tradera purchase?",
          answer:
            "We often offer Tradera pickups at short notice — often the same day — within a few hours. This is especially important for auction purchases where you may have a collection deadline. Book online and see available times instantly.",
        },
        {
          question: "Can you pick up from multiple sellers in one trip?",
          answer:
            "Yes, we can pick up from several sellers during the same run if the addresses are within a reasonable distance. Describe all pickup points when you book.",
        },
        {
          question: "Can you transport appliances bought on Tradera?",
          answer:
            "Yes, we transport all types of appliances — washing machines, dryers, fridges, freezers, dishwashers, cookers and ovens. We disconnect at the seller's if needed, carry out, transport upright and carry in at your home.",
        },
        {
          question: "Is Tradera transport available on weekends?",
          answer:
            "Yes, we offer Tradera transport on weekends and some evenings. Book online and see all available time slots.",
        },
        {
          question: "Are you responsible for the item's condition?",
          answer:
            "No, we are only responsible for the transport — not for the item's condition or whether it matches the listing. The purchase is an agreement between you and the seller. We recommend that you inspect the item yourself before we load, or that you are clear with the seller about your expectations.",
        },
        {
          question: "In which areas do you offer Tradera transport?",
          answer:
            "We offer Tradera transport across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Tradera transport in 3 easy steps",
        step2Text:
          "Our crew drive to the seller, carry out your auction purchase, protect it with blankets and load the van. Then we drive to your home.",
        step3Title: "Home with you",
        step3Desc:
          "Your Tradera purchase is in place at your home — carried in, set up and undamaged. Without lifting a finger.",
        ctaHeading: "Book Tradera transport today",
        ctaText:
          "Enter the seller's address and your home address, describe what needs picking up and choose a time. You see the price instantly.",
      },
    },
  },
  {
    // Transport Myrorna — config för /transport-myrorna
    // Vinkel: Hämta köp från Myrorna ELLER skänka/lämna saker till Myrorna.
    // Unik vinkel: dubbelriktad — både lämna och hämta, specifikt Myrorna som varumärke.
    slug: "transport-myrorna",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Myrorna – Hämta Eller Skänk Med Skåpbil | Ungdomskraft",
      description:
        "Köpt något på Myrorna som inte får plats i bilen? Eller vill du skänka möbler och kläder? Vi hämtar och kör – åt båda hållen. Boka Myrorna-transport med personal idag.",
      h1: "Transport Myrorna",
    },

    hero: {
      heading: "Vi kör till och från Myrorna-butiker",
      intro:
        "Hittat en fantastisk soffa eller byrå på Myrorna – men den får inte plats i bilen? Eller har du möbler, kläder och prylar hemma som du vill skänka till Myrorna men saknar transport? Vi löser båda. Vi hämtar ditt köp i butiken och levererar hem, eller hämtar hemma hos dig och kör till närmaste Myrorna-butik. Du slipper hyra släp, be kompisar om hjälp eller göra flera turer.",
      heroImage: "/hero-transport-aterbruk.png",
    },

    featureCards: {
      sinceText: "Myrorna-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att hämta köp från Myrorna och skänka saker dit. Våra ungdomar vet hur man bär tunga möbler genom trånga trapphus och lastar skåpbilar effektivt – du får ditt köp hem eller dina saker lämnade utan att lyfta ett finger.",
      bookYourText: "Boka din Myrorna-transport",
      bookDescText:
        "Ange din adress och Myrorna-butikens adress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Myrorna-transporter",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en bokhylla eller skänker ett helt rum med prylar. Du slipper hyra släp, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi kör till och från Myrorna",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, bord, stolar, bokhyllor och garderober – hämta köp eller skänka till Myrorna",
          image: "/sofa.webp",
        },
        {
          name: "Kläder & textilier",
          description:
            "Kassar och kartonger med kläder, skor, gardiner och sängkläder till Myrorna",
          image: "/box.webp",
        },
        {
          name: "Husgeråd",
          description:
            "Porslin, bestick, köksredskap, lampor och annat hushållsgods",
          image: "/patio-furniture.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, högtalare och annan fungerande elektronik",
          image: "/tv.webp",
        },
        {
          name: "Böcker & media",
          description:
            "Böcker, vinylskivor, spel och filmer – perfekt att skänka eller hämta",
          image: "/painting.webp",
        },
        {
          name: "Barnprylar",
          description:
            "Barnvagnar, barnsängar, leksaker och barnkläder till eller från Myrorna",
          image: "/washer.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport till och från Myrorna – vi hämtar, kör och lämnar åt dig",
      mainText:
        "Myrorna är en av Sveriges mest älskade second hand-kedjor med butiker runt om i landet. Här hittar du allt från vintagemöbler och designkläder till husgeråd, böcker och leksaker – och allt du köper går till Frälsningsarméns sociala arbete. Men det finns ett praktiskt problem som många Myrorna-kunder känner igen: transporten. Du hittar en perfekt soffa, ett matbord eller en garderob i butiken – men den får inte plats i bilen. Eller så har du hemma ett helt rum med kläder, möbler och prylar som du vill skänka till Myrorna, men du har ingen skåpbil och orkar inte bära allt själv. Det är här Ungdomskraft kommer in. Vi erbjuder transport åt båda hållen. Har du köpt något på Myrorna? Vi åker till butiken, bär ut ditt köp, lastar skåpbilen och levererar hem till dig. Vi bär in och ställer på plats. Vill du skänka till Myrorna? Vi kommer hem till dig, bär ut allt du vill skänka, lastar och kör till närmaste Myrorna-butik. Där lastar vi av och lämnar in åt dig. Vi kör till alla Myrorna-butiker i Stockholm och Göteborg – Hornsgatan, Götgatan, Kungsholmen, Södermalm, Vasastan, Hisingen, Linnéstaden och alla andra. Varje vecka kör vi hundratals transporter till och från second hand-butiker. Våra ungdomar är vana vid att bära genom smala trapphus, runt trånga hörn och upp flera våningar utan hiss. Vi vet hur man skyddar ömtåliga ytor, demonterar möbler som inte går genom dörren och lastar skåpbilen så att inget skadas. Att handla och skänka på Myrorna är bra för plånboken, miljön och samhället. Med Ungdomskrafts transport tar vi bort det sista hindret – du fokuserar på att hitta rätt prylar eller rensa hemma, vi tar hand om hela transporten från dörr till dörr.",
      preparationHeading: "Förbered inför din Myrorna-transport",
      preparationText:
        "Om du hämtar ett köp: kontrollera butikens öppettider och att varan är redo att hämtas. Ha din hemadress och eventuell portkod redo. Om du skänker till Myrorna: sortera det du vill skänka och se till att allt är i gott skick – Myrorna tar bara emot saker som kan säljas vidare. Packa kläder i kassar eller kartonger. Kolla att det finns fri passage i trapphus och att skåpbilen kan parkera nära porten både hemma och vid butiken. Mät gärna dörröppningar om möblerna är stora. Ju mer information du anger vid bokning, desto smidigare går transporten.",
      additionalSections: [
        {
          heading: "Hämta möbler och prylar köpta på Myrorna",
          content:
            "Myrorna har ofta fantastiska fynd – vintagemöbler, retrostolar, massiva matbord och unika garderober som du inte hittar någon annanstans. Problemet är att de sällan får plats i en vanlig personbil. Med Ungdomskrafts transport hämtar vi ditt köp direkt i butiken. Vi bär ut möbeln, skyddar den med filtar och spännband, lastar skåpbilen och kör hem till dig. Där bär vi in genom trapphus och dörrar och ställer på plats. Många av våra kunder berättar att de nu vågar köpa helt andra saker på Myrorna – tunga möbler, skrymmande hyllor och ömtåliga glasskåp som de aldrig hade kunnat ta hem själva. Vår personal hanterar allt från små fåtöljer till stora möbelgrupper. Vi tar med oss verktyg, möbelfiltar och bärremmar – du behöver inte förbereda något extra. Om möbeln inte går genom dörren demonterar vi den och monterar ihop den igen hemma hos dig. Vi skyddar alla ytor under bärningen så att inget skadas varken i butiken eller hos dig.",
        },
        {
          heading: "Skänka möbler och kläder till Myrorna",
          content:
            "Har du möbler, kläder eller husgeråd hemma som förtjänar ett nytt liv? Att skänka till Myrorna är ett av de enklaste sätten att göra skillnad – dina saker säljs vidare och pengarna går till Frälsningsarméns sociala arbete. Men transporten brukar vara det som stoppar. En soffa ryms inte i personbilen, 15 kassar med kläder kräver flera turer, och närmaste Myrorna-butik kanske ligger mil bort. Med vår transport hämtar vi allt hemma hos dig. Vi bär ut från vilken våning som helst, lastar skåpbilen och kör till Myrorna-butiken du väljer. Där lastar vi av och lämnar in. Du behöver bara sortera – vi sköter resten. Tänk på att Myrorna bara tar emot saker i gott skick som kan säljas vidare. Trasiga möbler, smutsiga textilier och madrasser brukar inte accepteras. Är du osäker – ring butiken innan du bokar.",
        },
        {
          heading: "Så fungerar Myrorna-transport med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange din adress och Myrorna-butikens adress. Beskriv om du hämtar ett köp eller skänker saker. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar",
              text: "Vårt team kommer med skåpbil och rätt utrustning. Vi bär ut, skyddar med filtar och spännband och lastar allt.",
            },
            {
              label: "Vi kör och lämnar",
              text: "Vi kör till Myrorna och lämnar in dina skänkta saker – eller kör hem ditt köp och bär in på plats.",
            },
            {
              label: "Klart",
              text: "Dina saker har hittat ett nytt hem via Myrorna – eller ditt köp står på plats hemma hos dig. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör till alla Myrorna-butiker",
          content:
            "Vi kör till alla Myrorna-butiker i Stockholm och Göteborg. I Stockholm: Hornsgatan, Götgatan, St Eriksgatan, Folkungagatan, Kungsholmen och alla övriga. I Göteborg: Linnégatan, Hisingen, Nordstan-området och övriga butiker. Ange bara butikens adress vid bokning så sköter vi resten. Vi kör även till andra second hand-butiker som Erikshjälpen, Emmaus, Röda Korset och Stadsmissionen om du vill skänka till flera ställen under samma tur.",
        },
        {
          heading: "Varför boka Myrorna-transport med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara professionell hjälp med din Myrorna-transport – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och ansvarsfullt miljötänk. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra kunder kommer tillbaka gång efter gång – inte bara för att tjänsten fungerar, utan för att de vet att deras pengar gör skillnad. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar.\n\nOm sakerna transporteras till återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Avdraget hanteras direkt på fakturan – du behöver aldrig ligga ute med pengarna.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar transport till och från Myrorna?",
        answer:
          "Priset beror på mängden och avståndet. En mindre hämtning eller lämning kostar från 595 kr, medan större transporter med möbler kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport till Myrorna?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Transport som inte går till återanvändning ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Ingår skåpbil i transporten?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni både hämta köp och lämna skänkta saker?",
        answer:
          "Ja, vi kör åt båda hållen. Vi hämtar köp i Myrorna-butiken och levererar hem till dig, eller hämtar hemma hos dig och kör till Myrorna för att lämna in.",
      },
      {
        question: "Vilka Myrorna-butiker kör ni till?",
        answer:
          "Vi kör till alla Myrorna-butiker i Stockholm och Göteborg. Ange bara butikens adress vid bokning. Vi kör även till andra second hand-butiker som Erikshjälpen, Emmaus, Röda Korset och Stadsmissionen.",
      },
      {
        question: "Kan ni bära tunga möbler i trappor?",
        answer:
          "Ja, vi bär från vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft i trånga trapphus. Vi hanterar soffor, garderober, sängar och allt annat tungt.",
      },
      {
        question: "Kan ni demontera möbler?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att transporteras. Vi monterar ihop dem igen hemma hos dig om du hämtar ett köp.",
      },
      {
        question: "Skyddar ni sakerna under transporten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar alla möbler och ömtåliga föremål under bärning, lastning och transport. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Tar Myrorna emot allt jag vill skänka?",
        answer:
          "Nej, Myrorna tar bara emot saker i gott skick som kan säljas vidare. Trasiga möbler, smutsiga textilier och madrasser brukar inte accepteras. Kolla med butiken innan du bokar. Vi ansvarar enbart för transporten – inte för vad butiken tar emot.",
      },
      {
        question: "Hur snabbt kan ni köra till Myrorna?",
        answer:
          "Vi erbjuder ofta transport med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni skänka till flera butiker på samma tur?",
        answer:
          "Ja, vi kan lämna på flera ställen under samma körning om butikerna ligger i rimligt avstånd. Beskriv alla lämningspunkter när du bokar.",
      },
      {
        question: "Erbjuder ni Myrorna-transport på helger?",
        answer:
          "Ja, vi erbjuder transport på helger och vissa kvällar. Kontrollera att butiken har öppet den tid du bokar – vi kan inte lämna om butiken är stängd.",
      },
      {
        question: "Behöver jag vara med i butiken?",
        answer:
          "Om du hämtar ett köp bör du ha betalat och reserverat varan i förväg så att vi bara behöver hämta den. Om du skänker behöver du inte vara med i butiken – vi lastar av och lämnar in åt dig.",
      },
      {
        question: "Är transporten försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under bärning, lastning eller transport ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni Myrorna-transport?",
        answer:
          "Vi erbjuder Myrorna-transport i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Myrorna-transport i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut dina saker eller ditt köp, skyddar med filtar och lastar skåpbilen. Sedan kör vi till Myrorna – eller hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Dina skänkta saker är inlämnade på Myrorna – eller ditt köp står hemma hos dig. Klart.",
      ctaHeading: "Boka Myrorna-transport idag",
      ctaText:
        "Ange din adress och Myrorna-butikens adress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "myrorna-transport",
      meta: {
        title:
          "Myrorna Transport – Pick Up Or Donate With Van | Ungdomskraft",
        description:
          "Bought something at Myrorna that won't fit in your car? Or want to donate furniture and clothes? We pick up and deliver — both ways. Book Myrorna transport today.",
        h1: "Myrorna transport",
      },
      hero: {
        heading: "We drive to and from Myrorna-stores",
        intro:
          "Found an amazing sofa or dresser at Myrorna — but it won't fit in your car? Or do you have furniture, clothes and household items at home that you want to donate to Myrorna but lack transport? We handle both. We pick up your purchase at the store and deliver home, or collect from your home and drive to the nearest Myrorna store. No need to rent a trailer, ask friends for help or make multiple trips.",
      },
      featureCards: {
        sinceText: "Myrorna transport since 2014",
        bookYourText: "Book your Myrorna transport",
        bookDescText:
          "Enter your address and the Myrorna store address, describe what needs transporting and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Myrorna transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a bookshelf or donating an entire room of belongings. No need to rent a trailer, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Items we transport to and from Myrorna",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, tables, chairs, bookshelves and wardrobes — pick up purchases or donate to Myrorna",
            image: "/sofa.webp",
          },
          {
            name: "Clothes & textiles",
            description:
              "Bags and boxes of clothes, shoes, curtains and bedding to Myrorna",
            image: "/box.webp",
          },
          {
            name: "Household goods",
            description:
              "Porcelain, cutlery, kitchen tools, lamps and other household items",
            image: "/patio-furniture.webp",
          },
          {
            name: "Electronics",
            description:
              "TVs, computers, speakers and other working electronics",
            image: "/tv.webp",
          },
          {
            name: "Books & media",
            description:
              "Books, vinyl records, games and films — perfect to donate or pick up",
            image: "/painting.webp",
          },
          {
            name: "Kids' stuff",
            description:
              "Strollers, cribs, toys and children's clothes to or from Myrorna",
            image: "/washer.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Transport to and from Myrorna — we pick up, drive and drop off for you",
        mainText:
          "Myrorna is one of Sweden's most beloved second hand chains with stores across the country. Here you'll find everything from vintage furniture and designer clothes to household goods, books and toys — and everything you buy supports the Salvation Army's social work. But there's a practical problem many Myrorna customers recognise: transport. You find a perfect sofa, dining table or wardrobe in the store — but it won't fit in your car. Or you have an entire room at home full of clothes, furniture and belongings you want to donate to Myrorna, but you don't have a van and can't carry everything yourself. That's where Ungdomskraft comes in. We offer transport in both directions. Bought something at Myrorna? We drive to the store, carry out your purchase, load the van and deliver to your home. We carry inside and place it where you want it. Want to donate to Myrorna? We come to your home, carry out everything you want to donate, load up and drive to the nearest Myrorna store. There we unload and drop off for you. We drive to all Myrorna stores in Stockholm and Gothenburg — Hornsgatan, Götgatan, Kungsholmen, Södermalm, Vasastan, Hisingen, Linnéstaden and all others. Every week we make hundreds of transports to and from second hand stores. Our crew are used to carrying through narrow stairwells, around tight corners and up multiple flights without a lift. We know how to protect delicate surfaces, disassemble furniture that won't fit through doorways and load the van safely. Shopping and donating at Myrorna is good for your wallet, the environment and society. With Ungdomskraft's transport we remove the last barrier — you focus on finding the right items or clearing out at home, we take care of the entire journey from door to door.",
        preparationHeading: "Prepare for your Myrorna transport",
        preparationText:
          "If you're picking up a purchase: check the store's opening hours and make sure the item is ready to collect. Have your home address and any door codes ready. If you're donating to Myrorna: sort what you want to donate and make sure everything is in good condition — Myrorna only accepts items that can be resold. Pack clothes in bags or boxes. Check that there is clear access through stairwells and that the van can park near the entrance both at your home and at the store. Measure doorways if furniture is large. The more information you provide when booking, the smoother the transport will be.",
        additionalSections: [
          {
            heading: "Pick up furniture and finds bought at Myrorna",
            content:
              "Myrorna often has fantastic finds — vintage furniture, retro chairs, solid dining tables and unique wardrobes you won't find anywhere else. The problem is they rarely fit in an ordinary car. With Ungdomskraft's transport we pick up your purchase directly from the store. We carry the furniture out, protect it with blankets and straps, load the van and drive to your home. There we carry it through stairwells and doorways and place it where you want it. Many of our customers tell us they now dare to buy completely different things at Myrorna — heavy furniture, bulky shelving units and delicate glass cabinets they could never have taken home on their own. Our crew handle everything from small armchairs to large furniture sets. We bring tools, furniture blankets and carrying straps — you don't need to prepare anything extra. If a piece won't fit through the door, we disassemble it and reassemble at your place. We protect all surfaces during carrying so nothing gets damaged.",
          },
          {
            heading: "Donate furniture and clothes to Myrorna",
            content:
              "Have furniture, clothes or household goods at home that deserve a new life? Donating to Myrorna is one of the easiest ways to make a difference — your items are resold and the money goes to the Salvation Army's social work. But transport is usually what holds people back. A sofa won't fit in the car, 15 bags of clothes require multiple trips, and the nearest Myrorna store might be miles away. With our transport we collect everything from your home. We carry out from any floor, load the van and drive to the Myrorna store of your choice. There we unload and drop off. You just need to sort — we handle the rest. Remember that Myrorna only accepts items in good condition that can be resold. Broken furniture, dirty textiles and mattresses are usually not accepted. If in doubt, call the store before booking. We are only responsible for the transport — not for what the store accepts.",
          },
          {
            heading: "How Myrorna transport works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter your address and the Myrorna store address. Describe whether you're picking up a purchase or donating items. You see the price instantly.",
              },
              {
                label: "We collect",
                text: "Our team arrive with a van and the right equipment. We carry out, protect with blankets and straps and load everything.",
              },
              {
                label: "We drive and deliver",
                text: "We drive to Myrorna and drop off your donated items — or drive your purchase home and carry it inside.",
              },
              {
                label: "Done",
                text: "Your items have found a new home via Myrorna — or your purchase is in place at your home. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We drive to all Myrorna stores",
            content:
              "We drive to all Myrorna stores in Stockholm and Gothenburg. In Stockholm: Hornsgatan, Götgatan, St Eriksgatan, Folkungagatan, Kungsholmen and all others. In Gothenburg: Linnégatan, Hisingen, the Nordstan area and all other locations. Just enter the store address when booking and we handle the rest. We also drive to other second hand stores like Erikshjälpen, Emmaus, Red Cross and Stadsmissionen if you want to donate to multiple places in the same trip.",
          },
          {
            heading: "Why book Myrorna transport with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get professional help with your Myrorna transport — you also contribute to getting young people into the workforce and building their future. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and responsible environmental practices. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Our customers come back time and again — not just because the service works, but because they know their money makes a difference. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nIf the items are transported for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. The deduction is handled directly on the invoice – you never have to pay the full amount upfront.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Myrorna transport cost?",
          answer:
            "The price depends on the amount and distance. A smaller pickup or drop-off starts at 595 SEK, while larger transports with furniture cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Myrorna transport?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Transport that does not go to reuse does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting — you don't need to rent a vehicle.",
        },
        {
          question: "Can you both pick up purchases and deliver donations?",
          answer:
            "Yes, we drive in both directions. We pick up purchases from Myrorna stores and deliver to your home, or collect from your home and drive to Myrorna for drop-off.",
        },
        {
          question: "Which Myrorna stores do you drive to?",
          answer:
            "We drive to all Myrorna stores in Stockholm and Gothenburg. Just enter the store address when booking. We also drive to other second hand stores like Erikshjälpen, Emmaus, Red Cross and Stadsmissionen.",
        },
        {
          question: "Can you carry heavy furniture up stairs?",
          answer:
            "Yes, we carry from any floor — with or without a lift. Our crew are used to heavy items in narrow stairwells. We handle sofas, wardrobes, beds and anything else that's heavy.",
        },
        {
          question: "Can you disassemble furniture?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart for transport. We reassemble at your home if you're picking up a purchase.",
        },
        {
          question: "Do you protect items during transport?",
          answer:
            "Yes, we use furniture blankets, carrying straps and ratchet straps. We protect all furniture and fragile items during carrying, loading and transport. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "Does Myrorna accept everything I want to donate?",
          answer:
            "No, Myrorna only accepts items in good condition that can be resold. Broken furniture, dirty textiles and mattresses are usually not accepted. Check with the store before booking. We are only responsible for the transport — not for what the store accepts.",
        },
        {
          question: "How quickly can you drive to Myrorna?",
          answer:
            "We often offer transport at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Can you donate to multiple stores in one trip?",
          answer:
            "Yes, we can drop off at several stores during the same run if they're within a reasonable distance. Describe all drop-off points when you book.",
        },
        {
          question: "Is Myrorna transport available on weekends?",
          answer:
            "Yes, we offer transport on weekends and some evenings. Check that the store is open at your chosen time — we can't drop off if the store is closed.",
        },
        {
          question: "Do I need to be at the store?",
          answer:
            "If you're picking up a purchase, you should have paid for and reserved the item in advance so we only need to collect it. If you're donating, you don't need to be at the store — we unload and drop off for you.",
        },
        {
          question: "Is the transport insured?",
          answer:
            "Yes, all our transports are fully insured. If anything is damaged during carrying, loading or transport we replace it. You can book with confidence.",
        },
        {
          question: "In which areas do you offer Myrorna transport?",
          answer:
            "We offer Myrorna transport across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Myrorna transport in 3 easy steps",
        step2Text:
          "Our crew carry out your items or your purchase, protect with blankets and load the van. Then we drive to Myrorna — or to your home.",
        step3Title: "All in place",
        step3Desc:
          "Your donated items are dropped off at Myrorna — or your purchase is in place at your home. Done.",
        ctaHeading: "Book Myrorna transport today",
        ctaText:
          "Enter your address and the Myrorna store address, describe what needs transporting and choose a time. You see the price instantly.",
      },
    },
  },
  {
    // Transport Erikshjälpen — config för /transport-erikshjalpen
    // Vinkel: Hämta köp från Erikshjälpen ELLER skänka/lämna saker till Erikshjälpen.
    // Unik vinkel: dubbelriktad — Erikshjälpens biståndsarbete, intäkterna finansierar barn i utsatta länder.
    slug: "transport-erikshjalpen",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Erikshjälpen – Hämta Eller Skänk Med Skåpbil | Ungdomskraft",
      description:
        "Köpt något på Erikshjälpen som inte får plats i bilen? Eller vill du skänka möbler och kläder? Vi hämtar och kör – åt båda hållen. Boka Erikshjälpen-transport med personal idag.",
      h1: "Transport Erikshjälpen",
    },

    hero: {
      heading: "Vi kör till och från Erikshjälpen-butiker",
      intro:
        "Hittat en fantastisk möbel eller fynd på Erikshjälpen – men den får inte plats i bilen? Eller har du möbler, kläder och prylar hemma som du vill skänka till Erikshjälpen men saknar transport? Vi löser båda. Vi hämtar ditt köp i butiken och levererar hem, eller hämtar hemma hos dig och kör till närmaste Erikshjälpen-butik. Du slipper hyra släp, be kompisar om hjälp eller göra flera turer.",
      heroImage: "/hero-transport-aterbruk.png",
    },

    featureCards: {
      sinceText: "Erikshjälpen-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att hämta köp från Erikshjälpen och skänka saker dit. Våra ungdomar vet hur man bär tunga möbler genom trånga trapphus och lastar skåpbilar effektivt – du får ditt köp hem eller dina saker lämnade utan att lyfta ett finger.",
      bookYourText: "Boka din Erikshjälpen-transport",
      bookDescText:
        "Ange din adress och Erikshjälpen-butikens adress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Erikshjälpen-transporter",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en bokhylla eller skänker ett helt rum med prylar. Du slipper hyra släp, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi kör till och från Erikshjälpen",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, bord, stolar, bokhyllor och garderober – hämta köp eller skänka till Erikshjälpen",
          image: "/sofa.webp",
        },
        {
          name: "Kläder & textilier",
          description:
            "Kassar och kartonger med kläder, skor, gardiner och sängkläder till Erikshjälpen",
          image: "/box.webp",
        },
        {
          name: "Husgeråd",
          description:
            "Porslin, bestick, köksredskap, lampor och annat hushållsgods",
          image: "/patio-furniture.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, högtalare och annan fungerande elektronik",
          image: "/tv.webp",
        },
        {
          name: "Böcker & media",
          description:
            "Böcker, vinylskivor, spel och filmer – perfekt att skänka eller hämta",
          image: "/painting.webp",
        },
        {
          name: "Barnprylar",
          description:
            "Barnvagnar, barnsängar, leksaker och barnkläder till eller från Erikshjälpen",
          image: "/washer.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport till och från Erikshjälpen – vi hämtar, kör och lämnar åt dig",
      mainText:
        "Erikshjälpen är en av Sveriges mest kända second hand-kedjor med butiker över hela landet. Här hittar du allt från möbler och kläder till husgeråd, böcker och leksaker – och allt du köper eller skänker bidrar till Erikshjälpens arbete för barn i utsatta situationer runt om i världen. Men det finns ett praktiskt problem som många kunder känner igen: transporten. Du hittar en perfekt soffa, ett matbord eller en garderob i butiken – men den får inte plats i bilen. Eller så har du hemma ett helt rum med kläder, möbler och prylar som du vill skänka till Erikshjälpen, men du har ingen skåpbil och orkar inte bära allt själv. Det är här Ungdomskraft kommer in. Vi erbjuder transport åt båda hållen. Har du köpt något på Erikshjälpen? Vi åker till butiken, bär ut ditt köp, lastar skåpbilen och levererar hem till dig. Vi bär in och ställer på plats. Vill du skänka till Erikshjälpen? Vi kommer hem till dig, bär ut allt du vill skänka, lastar och kör till närmaste Erikshjälpen-butik. Där lastar vi av och lämnar in åt dig. Vi kör till alla Erikshjälpen-butiker i Stockholm och Göteborg samt till deras insamlingscentraler och mottagningspunkter. Varje vecka kör vi hundratals transporter till och från second hand-butiker. Våra ungdomar är vana vid att bära genom smala trapphus, runt trånga hörn och upp flera våningar utan hiss. Vi vet hur man skyddar ömtåliga ytor, demonterar möbler som inte går genom dörren och lastar skåpbilen så att inget skadas. Att handla och skänka på Erikshjälpen är bra för plånboken, miljön och barn som behöver hjälp. Med Ungdomskrafts transport tar vi bort det sista hindret – du fokuserar på att hitta rätt prylar eller rensa hemma, vi tar hand om hela transporten från dörr till dörr.",
      preparationHeading: "Förbered inför din Erikshjälpen-transport",
      preparationText:
        "Om du hämtar ett köp: kontrollera butikens öppettider och att varan är redo att hämtas. Ha din hemadress och eventuell portkod redo. Om du skänker till Erikshjälpen: sortera det du vill skänka och se till att allt är i gott skick – Erikshjälpen tar bara emot saker som kan säljas vidare. Packa kläder i kassar eller kartonger. Kolla att det finns fri passage i trapphus och att skåpbilen kan parkera nära porten både hemma och vid butiken. Mät gärna dörröppningar om möblerna är stora. Ju mer information du anger vid bokning, desto smidigare går transporten.",
      additionalSections: [
        {
          heading: "Hämta möbler och prylar köpta på Erikshjälpen",
          content:
            "Erikshjälpen har ofta fantastiska fynd – möbler, retrostolar, massiva matbord och unika garderober som du inte hittar någon annanstans. Problemet är att de sällan får plats i en vanlig personbil. Med Ungdomskrafts transport hämtar vi ditt köp direkt i butiken. Vi bär ut möbeln, skyddar den med filtar och spännband, lastar skåpbilen och kör hem till dig. Där bär vi in genom trapphus och dörrar och ställer på plats. Många av våra kunder berättar att de nu vågar köpa helt andra saker på Erikshjälpen – tunga möbler, skrymmande hyllor och ömtåliga glasskåp som de aldrig hade kunnat ta hem själva. Vår personal hanterar allt från små fåtöljer till stora möbelgrupper. Vi tar med oss verktyg, möbelfiltar och bärremmar – du behöver inte förbereda något extra. Om möbeln inte går genom dörren demonterar vi den och monterar ihop den igen hemma hos dig. Vi skyddar alla ytor under bärningen så att inget skadas varken i butiken eller hos dig.",
        },
        {
          heading: "Skänka möbler och kläder till Erikshjälpen",
          content:
            "Har du möbler, kläder eller husgeråd hemma som förtjänar ett nytt liv? Att skänka till Erikshjälpen är ett av de enklaste sätten att göra skillnad – dina saker säljs vidare och intäkterna går till Erikshjälpens arbete för barn i utsatta situationer världen över. Men transporten brukar vara det som stoppar. En soffa ryms inte i personbilen, 15 kassar med kläder kräver flera turer, och närmaste Erikshjälpen-butik kanske ligger långt bort. Med vår transport hämtar vi allt hemma hos dig. Vi bär ut från vilken våning som helst, lastar skåpbilen och kör till Erikshjälpen-butiken du väljer. Där lastar vi av och lämnar in. Du behöver bara sortera – vi sköter resten. Tänk på att Erikshjälpen bara tar emot saker i gott skick som kan säljas vidare. Trasiga möbler, smutsiga textilier och madrasser brukar inte accepteras. Är du osäker – ring butiken innan du bokar.",
        },
        {
          heading: "Så fungerar Erikshjälpen-transport med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange din adress och Erikshjälpen-butikens adress. Beskriv om du hämtar ett köp eller skänker saker. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar",
              text: "Vårt team kommer med skåpbil och rätt utrustning. Vi bär ut, skyddar med filtar och spännband och lastar allt.",
            },
            {
              label: "Vi kör och lämnar",
              text: "Vi kör till Erikshjälpen och lämnar in dina skänkta saker – eller kör hem ditt köp och bär in på plats.",
            },
            {
              label: "Klart",
              text: "Dina saker bidrar till Erikshjälpens arbete för barn – eller ditt köp står på plats hemma hos dig. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör till alla Erikshjälpen-butiker",
          content:
            "Vi kör till alla Erikshjälpen-butiker i Stockholm och Göteborg samt till deras insamlingscentraler och mottagningspunkter i hela regionen. Ange bara butikens adress vid bokning så sköter vi resten. Vi kör även till andra second hand-butiker som Myrorna, Emmaus, Röda Korset och Stadsmissionen om du vill skänka till flera ställen under samma tur.",
        },
        {
          heading: "Varför boka Erikshjälpen-transport med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara professionell hjälp med din Erikshjälpen-transport – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och ansvarsfullt miljötänk. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra kunder kommer tillbaka gång efter gång – inte bara för att tjänsten fungerar, utan för att de vet att deras pengar gör skillnad. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar.\n\nOm sakerna transporteras till återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Avdraget hanteras direkt på fakturan – du behöver aldrig ligga ute med pengarna.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar transport till och från Erikshjälpen?",
        answer:
          "Priset beror på mängden och avståndet. En mindre hämtning eller lämning kostar från 595 kr, medan större transporter med möbler kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport till Erikshjälpen?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Transport som inte går till återanvändning ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Ingår skåpbil i transporten?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni både hämta köp och lämna skänkta saker?",
        answer:
          "Ja, vi kör åt båda hållen. Vi hämtar köp i Erikshjälpen-butiken och levererar hem till dig, eller hämtar hemma hos dig och kör till Erikshjälpen för att lämna in.",
      },
      {
        question: "Vilka Erikshjälpen-butiker kör ni till?",
        answer:
          "Vi kör till alla Erikshjälpen-butiker i Stockholm och Göteborg samt till deras insamlingscentraler. Ange bara butikens adress vid bokning. Vi kör även till andra second hand-butiker som Myrorna, Emmaus, Röda Korset och Stadsmissionen.",
      },
      {
        question: "Kan ni bära tunga möbler i trappor?",
        answer:
          "Ja, vi bär från vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft i trånga trapphus. Vi hanterar soffor, garderober, sängar och allt annat tungt.",
      },
      {
        question: "Kan ni demontera möbler?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att transporteras. Vi monterar ihop dem igen hemma hos dig om du hämtar ett köp.",
      },
      {
        question: "Skyddar ni sakerna under transporten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar alla möbler och ömtåliga föremål under bärning, lastning och transport. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Tar Erikshjälpen emot allt jag vill skänka?",
        answer:
          "Nej, Erikshjälpen tar bara emot saker i gott skick som kan säljas vidare. Trasiga möbler, smutsiga textilier och madrasser brukar inte accepteras. Kolla med butiken innan du bokar. Vi ansvarar enbart för transporten – inte för vad butiken tar emot.",
      },
      {
        question: "Hur snabbt kan ni köra till Erikshjälpen?",
        answer:
          "Vi erbjuder ofta transport med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni skänka till flera butiker på samma tur?",
        answer:
          "Ja, vi kan lämna på flera ställen under samma körning om butikerna ligger i rimligt avstånd. Beskriv alla lämningspunkter när du bokar.",
      },
      {
        question: "Erbjuder ni Erikshjälpen-transport på helger?",
        answer:
          "Ja, vi erbjuder transport på helger och vissa kvällar. Kontrollera att butiken har öppet den tid du bokar – vi kan inte lämna om butiken är stängd.",
      },
      {
        question: "Behöver jag vara med i butiken?",
        answer:
          "Om du hämtar ett köp bör du ha betalat och reserverat varan i förväg så att vi bara behöver hämta den. Om du skänker behöver du inte vara med i butiken – vi lastar av och lämnar in åt dig.",
      },
      {
        question: "Är transporten försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under bärning, lastning eller transport ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni Erikshjälpen-transport?",
        answer:
          "Vi erbjuder Erikshjälpen-transport i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Erikshjälpen-transport i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut dina saker eller ditt köp, skyddar med filtar och lastar skåpbilen. Sedan kör vi till Erikshjälpen – eller hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Dina skänkta saker är inlämnade på Erikshjälpen – eller ditt köp står hemma hos dig. Klart.",
      ctaHeading: "Boka Erikshjälpen-transport idag",
      ctaText:
        "Ange din adress och Erikshjälpen-butikens adress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "erikshjalpen-transport",
      meta: {
        title:
          "Erikshjälpen Transport – Pick Up Or Donate With Van | Ungdomskraft",
        description:
          "Bought something at Erikshjälpen that won't fit in your car? Or want to donate furniture and clothes? We pick up and deliver — both ways. Book Erikshjälpen transport today.",
        h1: "Erikshjälpen transport",
      },
      hero: {
        heading: "We drive to and from Erikshjälpen-stores",
        intro:
          "Found an amazing piece of furniture at Erikshjälpen — but it won't fit in your car? Or do you have furniture, clothes and household items at home that you want to donate to Erikshjälpen but lack transport? We handle both. We pick up your purchase at the store and deliver home, or collect from your home and drive to the nearest Erikshjälpen store. No need to rent a trailer, ask friends for help or make multiple trips.",
      },
      featureCards: {
        sinceText: "Erikshjälpen transport since 2014",
        bookYourText: "Book your Erikshjälpen transport",
        bookDescText:
          "Enter your address and the Erikshjälpen store address, describe what needs transporting and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Erikshjälpen transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a bookshelf or donating an entire room of belongings. No need to rent a trailer, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Items we transport to and from Erikshjälpen",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, tables, chairs, bookshelves and wardrobes — pick up purchases or donate to Erikshjälpen",
            image: "/sofa.webp",
          },
          {
            name: "Clothes & textiles",
            description:
              "Bags and boxes of clothes, shoes, curtains and bedding to Erikshjälpen",
            image: "/box.webp",
          },
          {
            name: "Household goods",
            description:
              "Porcelain, cutlery, kitchen tools, lamps and other household items",
            image: "/patio-furniture.webp",
          },
          {
            name: "Electronics",
            description:
              "TVs, computers, speakers and other working electronics",
            image: "/tv.webp",
          },
          {
            name: "Books & media",
            description:
              "Books, vinyl records, games and films — perfect to donate or pick up",
            image: "/painting.webp",
          },
          {
            name: "Kids' stuff",
            description:
              "Strollers, cribs, toys and children's clothes to or from Erikshjälpen",
            image: "/washer.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Transport to and from Erikshjälpen — we pick up, drive and drop off for you",
        mainText:
          "Erikshjälpen is one of Sweden's most well-known second hand chains with stores across the country. Here you'll find everything from furniture and clothes to household goods, books and toys — and everything you buy or donate contributes to Erikshjälpen's work for children in vulnerable situations around the world. But there's a practical problem many customers recognise: transport. You find a perfect sofa, dining table or wardrobe in the store — but it won't fit in your car. Or you have an entire room at home full of clothes, furniture and belongings you want to donate to Erikshjälpen, but you don't have a van and can't carry everything yourself. That's where Ungdomskraft comes in. We offer transport in both directions. Bought something at Erikshjälpen? We drive to the store, carry out your purchase, load the van and deliver to your home. We carry inside and place it where you want it. Want to donate to Erikshjälpen? We come to your home, carry out everything you want to donate, load up and drive to the nearest Erikshjälpen store. There we unload and drop off for you. We drive to all Erikshjälpen stores in Stockholm and Gothenburg as well as their collection centres and receiving points. Every week we make hundreds of transports to and from second hand stores. Our crew are used to carrying through narrow stairwells, around tight corners and up multiple flights without a lift. We know how to protect delicate surfaces, disassemble furniture that won't fit through doorways and load the van safely. Shopping and donating at Erikshjälpen is good for your wallet, the environment and children who need help. With Ungdomskraft's transport we remove the last barrier — you focus on finding the right items or clearing out at home, we take care of the entire journey from door to door.",
        preparationHeading: "Prepare for your Erikshjälpen transport",
        preparationText:
          "If you're picking up a purchase: check the store's opening hours and make sure the item is ready to collect. Have your home address and any door codes ready. If you're donating to Erikshjälpen: sort what you want to donate and make sure everything is in good condition — Erikshjälpen only accepts items that can be resold. Pack clothes in bags or boxes. Check that there is clear access through stairwells and that the van can park near the entrance both at your home and at the store. Measure doorways if furniture is large. The more information you provide when booking, the smoother the transport will be.",
        additionalSections: [
          {
            heading: "Pick up furniture and finds bought at Erikshjälpen",
            content:
              "Erikshjälpen often has fantastic finds — furniture, retro chairs, solid dining tables and unique wardrobes you won't find anywhere else. The problem is they rarely fit in an ordinary car. With Ungdomskraft's transport we pick up your purchase directly from the store. We carry the furniture out, protect it with blankets and straps, load the van and drive to your home. There we carry it through stairwells and doorways and place it where you want it. Many of our customers tell us they now dare to buy completely different things at Erikshjälpen — heavy furniture, bulky shelving units and delicate glass cabinets they could never have taken home on their own. Our crew handle everything from small armchairs to large furniture sets. We bring tools, furniture blankets and carrying straps — you don't need to prepare anything extra. If a piece won't fit through the door, we disassemble it and reassemble at your place. We protect all surfaces during carrying so nothing gets damaged.",
          },
          {
            heading: "Donate furniture and clothes to Erikshjälpen",
            content:
              "Have furniture, clothes or household goods at home that deserve a new life? Donating to Erikshjälpen is one of the easiest ways to make a difference — your items are resold and the revenue goes to Erikshjälpen's work for children in vulnerable situations worldwide. But transport is usually what holds people back. A sofa won't fit in the car, 15 bags of clothes require multiple trips, and the nearest Erikshjälpen store might be far away. With our transport we collect everything from your home. We carry out from any floor, load the van and drive to the Erikshjälpen store of your choice. There we unload and drop off. You just need to sort — we handle the rest. Remember that Erikshjälpen only accepts items in good condition that can be resold. Broken furniture, dirty textiles and mattresses are usually not accepted. If in doubt, call the store before booking. We are only responsible for the transport — not for what the store accepts.",
          },
          {
            heading: "How Erikshjälpen transport works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter your address and the Erikshjälpen store address. Describe whether you're picking up a purchase or donating items. You see the price instantly.",
              },
              {
                label: "We collect",
                text: "Our team arrive with a van and the right equipment. We carry out, protect with blankets and straps and load everything.",
              },
              {
                label: "We drive and deliver",
                text: "We drive to Erikshjälpen and drop off your donated items — or drive your purchase home and carry it inside.",
              },
              {
                label: "Done",
                text: "Your items are contributing to Erikshjälpen's work for children — or your purchase is in place at your home. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We drive to all Erikshjälpen stores",
            content:
              "We drive to all Erikshjälpen stores in Stockholm and Gothenburg as well as their collection centres and receiving points across the region. Just enter the store address when booking and we handle the rest. We also drive to other second hand stores like Myrorna, Emmaus, Red Cross and Stadsmissionen if you want to donate to multiple places in the same trip.",
          },
          {
            heading: "Why book Erikshjälpen transport with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get professional help with your Erikshjälpen transport — you also contribute to getting young people into the workforce and building their future. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and responsible environmental practices. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Our customers come back time and again — not just because the service works, but because they know their money makes a difference. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nIf the items are transported for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. The deduction is handled directly on the invoice – you never have to pay the full amount upfront.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Erikshjälpen transport cost?",
          answer:
            "The price depends on the amount and distance. A smaller pickup or drop-off starts at 595 SEK, while larger transports with furniture cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Erikshjälpen transport?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Transport that does not go to reuse does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting — you don't need to rent a vehicle.",
        },
        {
          question: "Can you both pick up purchases and deliver donations?",
          answer:
            "Yes, we drive in both directions. We pick up purchases from Erikshjälpen stores and deliver to your home, or collect from your home and drive to Erikshjälpen for drop-off.",
        },
        {
          question: "Which Erikshjälpen stores do you drive to?",
          answer:
            "We drive to all Erikshjälpen stores in Stockholm and Gothenburg as well as their collection centres. Just enter the store address when booking. We also drive to other second hand stores like Myrorna, Emmaus, Red Cross and Stadsmissionen.",
        },
        {
          question: "Can you carry heavy furniture up stairs?",
          answer:
            "Yes, we carry from any floor — with or without a lift. Our crew are used to heavy items in narrow stairwells. We handle sofas, wardrobes, beds and anything else that's heavy.",
        },
        {
          question: "Can you disassemble furniture?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart for transport. We reassemble at your home if you're picking up a purchase.",
        },
        {
          question: "Do you protect items during transport?",
          answer:
            "Yes, we use furniture blankets, carrying straps and ratchet straps. We protect all furniture and fragile items during carrying, loading and transport. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "Does Erikshjälpen accept everything I want to donate?",
          answer:
            "No, Erikshjälpen only accepts items in good condition that can be resold. Broken furniture, dirty textiles and mattresses are usually not accepted. Check with the store before booking. We are only responsible for the transport — not for what the store accepts.",
        },
        {
          question: "How quickly can you drive to Erikshjälpen?",
          answer:
            "We often offer transport at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Can you donate to multiple stores in one trip?",
          answer:
            "Yes, we can drop off at several stores during the same run if they're within a reasonable distance. Describe all drop-off points when you book.",
        },
        {
          question: "Is Erikshjälpen transport available on weekends?",
          answer:
            "Yes, we offer transport on weekends and some evenings. Check that the store is open at your chosen time — we can't drop off if the store is closed.",
        },
        {
          question: "Do I need to be at the store?",
          answer:
            "If you're picking up a purchase, you should have paid for and reserved the item in advance so we only need to collect it. If you're donating, you don't need to be at the store — we unload and drop off for you.",
        },
        {
          question: "Is the transport insured?",
          answer:
            "Yes, all our transports are fully insured. If anything is damaged during carrying, loading or transport we replace it. You can book with confidence.",
        },
        {
          question: "In which areas do you offer Erikshjälpen transport?",
          answer:
            "We offer Erikshjälpen transport across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Erikshjälpen transport in 3 easy steps",
        step2Text:
          "Our crew carry out your items or your purchase, protect with blankets and load the van. Then we drive to Erikshjälpen — or to your home.",
        step3Title: "All in place",
        step3Desc:
          "Your donated items are dropped off at Erikshjälpen — or your purchase is in place at your home. Done.",
        ctaHeading: "Book Erikshjälpen transport today",
        ctaText:
          "Enter your address and the Erikshjälpen store address, describe what needs transporting and choose a time. You see the price instantly.",
      },
    },
  },
  {
    // Transport Röda Korset — config för /transport-roda-korset
    // Vinkel: Hämta köp från Röda Korsets second hand ELLER skänka/lämna saker dit.
    // Unik vinkel: Röda Korsets humanitära arbete — intäkterna finansierar katastrofinsatser och socialt arbete.
    slug: "transport-roda-korset",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Röda Korset – Hämta Eller Skänk Med Skåpbil | Ungdomskraft",
      description:
        "Köpt något på Röda Korsets second hand som inte får plats i bilen? Eller vill du skänka möbler och kläder? Vi hämtar och kör – åt båda hållen. Boka transport idag.",
      h1: "Transport Röda Korset",
    },

    hero: {
      heading: "Vi kör till och från Röda-Korset",
      intro:
        "Hittat en fantastisk möbel eller fynd på Röda Korsets second hand – men den får inte plats i bilen? Eller har du möbler, kläder och prylar hemma som du vill skänka till Röda Korset men saknar transport? Vi löser båda. Vi hämtar ditt köp i butiken och levererar hem, eller hämtar hemma hos dig och kör till närmaste Röda Korset-butik. Du slipper hyra släp, be kompisar om hjälp eller göra flera turer.",
      heroImage: "/hero-transport-aterbruk.png",
    },

    featureCards: {
      sinceText: "Röda Korset-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att hämta köp från Röda Korsets second hand och skänka saker dit. Våra ungdomar vet hur man bär tunga möbler genom trånga trapphus och lastar skåpbilar effektivt – du får ditt köp hem eller dina saker lämnade utan att lyfta ett finger.",
      bookYourText: "Boka din Röda Korset-transport",
      bookDescText:
        "Ange din adress och Röda Korset-butikens adress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Röda Korset-transporter",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en bokhylla eller skänker ett helt rum med prylar. Du slipper hyra släp, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi kör till och från Röda Korset",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, bord, stolar, bokhyllor och garderober – hämta köp eller skänka till Röda Korset",
          image: "/sofa.webp",
        },
        {
          name: "Kläder & textilier",
          description:
            "Kassar och kartonger med kläder, skor, gardiner och sängkläder till Röda Korset",
          image: "/box.webp",
        },
        {
          name: "Husgeråd",
          description:
            "Porslin, bestick, köksredskap, lampor och annat hushållsgods",
          image: "/patio-furniture.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, högtalare och annan fungerande elektronik",
          image: "/tv.webp",
        },
        {
          name: "Böcker & media",
          description:
            "Böcker, vinylskivor, spel och filmer – perfekt att skänka eller hämta",
          image: "/painting.webp",
        },
        {
          name: "Barnprylar",
          description:
            "Barnvagnar, barnsängar, leksaker och barnkläder till eller från Röda Korset",
          image: "/washer.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport till och från Röda Korset – vi hämtar, kör och lämnar åt dig",
      mainText:
        "Röda Korset driver second hand-butiker runt om i Sverige där intäkterna går direkt till humanitärt arbete – katastrofinsatser, socialt stöd och hjälp till människor i utsatta situationer både i Sverige och internationellt. I butikerna hittar du allt från möbler och kläder till husgeråd, böcker och leksaker till riktigt bra priser. Men det finns ett praktiskt problem som många kunder känner igen: transporten. Du hittar en perfekt soffa, ett matbord eller en garderob i butiken – men den får inte plats i bilen. Eller så har du hemma ett helt rum med kläder, möbler och prylar som du vill skänka till Röda Korset, men du har ingen skåpbil och orkar inte bära allt själv. Det är här Ungdomskraft kommer in. Vi erbjuder transport åt båda hållen. Har du köpt något på Röda Korsets second hand? Vi åker till butiken, bär ut ditt köp, lastar skåpbilen och levererar hem till dig. Vi bär in och ställer på plats. Vill du skänka till Röda Korset? Vi kommer hem till dig, bär ut allt du vill skänka, lastar och kör till närmaste Röda Korset-butik. Där lastar vi av och lämnar in åt dig. Vi kör till alla Röda Korsets second hand-butiker och insamlingspunkter i Stockholm och Göteborg. Varje vecka kör vi hundratals transporter till och från second hand-butiker. Våra ungdomar är vana vid att bära genom smala trapphus, runt trånga hörn och upp flera våningar utan hiss. Vi vet hur man skyddar ömtåliga ytor, demonterar möbler som inte går genom dörren och lastar skåpbilen så att inget skadas. Att handla och skänka på Röda Korset är bra för plånboken, miljön och människor som behöver hjälp. Med Ungdomskrafts transport tar vi bort det sista hindret – du fokuserar på att hitta rätt prylar eller rensa hemma, vi tar hand om hela transporten från dörr till dörr.",
      preparationHeading: "Förbered inför din Röda Korset-transport",
      preparationText:
        "Om du hämtar ett köp: kontrollera butikens öppettider och att varan är redo att hämtas. Ha din hemadress och eventuell portkod redo. Om du skänker till Röda Korset: sortera det du vill skänka och se till att allt är i gott skick – Röda Korset tar bara emot saker som kan säljas vidare. Packa kläder i kassar eller kartonger. Kolla att det finns fri passage i trapphus och att skåpbilen kan parkera nära porten både hemma och vid butiken. Mät gärna dörröppningar om möblerna är stora. Ju mer information du anger vid bokning, desto smidigare går transporten.",
      additionalSections: [
        {
          heading: "Hämta möbler och prylar köpta på Röda Korset",
          content:
            "Röda Korsets second hand-butiker har ofta fantastiska fynd – möbler, retrostolar, massiva matbord och unika garderober som du inte hittar någon annanstans. Problemet är att de sällan får plats i en vanlig personbil. Med Ungdomskrafts transport hämtar vi ditt köp direkt i butiken. Vi bär ut möbeln, skyddar den med filtar och spännband, lastar skåpbilen och kör hem till dig. Där bär vi in genom trapphus och dörrar och ställer på plats. Många av våra kunder berättar att de nu vågar köpa helt andra saker på Röda Korset – tunga möbler, skrymmande hyllor och ömtåliga glasskåp som de aldrig hade kunnat ta hem själva. Vår personal hanterar allt från små fåtöljer till stora möbelgrupper. Vi tar med oss verktyg, möbelfiltar och bärremmar – du behöver inte förbereda något extra. Om möbeln inte går genom dörren demonterar vi den och monterar ihop den igen hemma hos dig. Vi skyddar alla ytor under bärningen så att inget skadas varken i butiken eller hos dig.",
        },
        {
          heading: "Skänka möbler och kläder till Röda Korset",
          content:
            "Har du möbler, kläder eller husgeråd hemma som förtjänar ett nytt liv? Att skänka till Röda Korset är ett av de enklaste sätten att göra skillnad – dina saker säljs vidare och intäkterna går direkt till Röda Korsets humanitära arbete i Sverige och internationellt. Pengarna finansierar katastrofinsatser, socialt stöd för utsatta och hjälp till flyktingar. Men transporten brukar vara det som stoppar. En soffa ryms inte i personbilen, 15 kassar med kläder kräver flera turer, och närmaste Röda Korset-butik kanske ligger långt bort. Med vår transport hämtar vi allt hemma hos dig. Vi bär ut från vilken våning som helst, lastar skåpbilen och kör till Röda Korset-butiken du väljer. Där lastar vi av och lämnar in. Du behöver bara sortera – vi sköter resten. Tänk på att Röda Korset bara tar emot saker i gott skick som kan säljas vidare. Trasiga möbler, smutsiga textilier och madrasser brukar inte accepteras. Är du osäker – ring butiken innan du bokar.",
        },
        {
          heading: "Så fungerar Röda Korset-transport med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange din adress och Röda Korset-butikens adress. Beskriv om du hämtar ett köp eller skänker saker. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar",
              text: "Vårt team kommer med skåpbil och rätt utrustning. Vi bär ut, skyddar med filtar och spännband och lastar allt.",
            },
            {
              label: "Vi kör och lämnar",
              text: "Vi kör till Röda Korset och lämnar in dina skänkta saker – eller kör hem ditt köp och bär in på plats.",
            },
            {
              label: "Klart",
              text: "Dina saker bidrar till Röda Korsets humanitära arbete – eller ditt köp står på plats hemma hos dig. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör till alla Röda Korsets second hand-butiker",
          content:
            "Vi kör till alla Röda Korsets second hand-butiker och insamlingspunkter i Stockholm och Göteborg. Ange bara butikens adress vid bokning så sköter vi resten. Vi kör även till andra second hand-butiker som Myrorna, Erikshjälpen, Emmaus och Stadsmissionen om du vill skänka till flera ställen under samma tur.",
        },
        {
          heading: "Varför boka Röda Korset-transport med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara professionell hjälp med din Röda Korset-transport – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och ansvarsfullt miljötänk. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra kunder kommer tillbaka gång efter gång – inte bara för att tjänsten fungerar, utan för att de vet att deras pengar gör skillnad. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar.\n\nOm sakerna transporteras till återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Avdraget hanteras direkt på fakturan – du behöver aldrig ligga ute med pengarna.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar transport till och från Röda Korset?",
        answer:
          "Priset beror på mängden och avståndet. En mindre hämtning eller lämning kostar från 595 kr, medan större transporter med möbler kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport till Röda Korset?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Transport som inte går till återanvändning ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Ingår skåpbil i transporten?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni både hämta köp och lämna skänkta saker?",
        answer:
          "Ja, vi kör åt båda hållen. Vi hämtar köp i Röda Korset-butiken och levererar hem till dig, eller hämtar hemma hos dig och kör till Röda Korset för att lämna in.",
      },
      {
        question: "Vilka Röda Korset-butiker kör ni till?",
        answer:
          "Vi kör till alla Röda Korsets second hand-butiker och insamlingspunkter i Stockholm och Göteborg. Ange bara butikens adress vid bokning. Vi kör även till andra second hand-butiker som Myrorna, Erikshjälpen, Emmaus och Stadsmissionen.",
      },
      {
        question: "Kan ni bära tunga möbler i trappor?",
        answer:
          "Ja, vi bär från vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft i trånga trapphus. Vi hanterar soffor, garderober, sängar och allt annat tungt.",
      },
      {
        question: "Kan ni demontera möbler?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att transporteras. Vi monterar ihop dem igen hemma hos dig om du hämtar ett köp.",
      },
      {
        question: "Skyddar ni sakerna under transporten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar alla möbler och ömtåliga föremål under bärning, lastning och transport. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Tar Röda Korset emot allt jag vill skänka?",
        answer:
          "Nej, Röda Korset tar bara emot saker i gott skick som kan säljas vidare. Trasiga möbler, smutsiga textilier och madrasser brukar inte accepteras. Kolla med butiken innan du bokar. Vi ansvarar enbart för transporten – inte för vad butiken tar emot.",
      },
      {
        question: "Hur snabbt kan ni köra till Röda Korset?",
        answer:
          "Vi erbjuder ofta transport med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni skänka till flera butiker på samma tur?",
        answer:
          "Ja, vi kan lämna på flera ställen under samma körning om butikerna ligger i rimligt avstånd. Beskriv alla lämningspunkter när du bokar.",
      },
      {
        question: "Erbjuder ni Röda Korset-transport på helger?",
        answer:
          "Ja, vi erbjuder transport på helger och vissa kvällar. Kontrollera att butiken har öppet den tid du bokar – vi kan inte lämna om butiken är stängd.",
      },
      {
        question: "Behöver jag vara med i butiken?",
        answer:
          "Om du hämtar ett köp bör du ha betalat och reserverat varan i förväg så att vi bara behöver hämta den. Om du skänker behöver du inte vara med i butiken – vi lastar av och lämnar in åt dig.",
      },
      {
        question: "Är transporten försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under bärning, lastning eller transport ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni Röda Korset-transport?",
        answer:
          "Vi erbjuder Röda Korset-transport i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Röda Korset-transport i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut dina saker eller ditt köp, skyddar med filtar och lastar skåpbilen. Sedan kör vi till Röda Korset – eller hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Dina skänkta saker är inlämnade på Röda Korset – eller ditt köp står hemma hos dig. Klart.",
      ctaHeading: "Boka Röda Korset-transport idag",
      ctaText:
        "Ange din adress och Röda Korset-butikens adress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "red-cross-transport",
      meta: {
        title:
          "Red Cross Transport – Pick Up Or Donate With Van | Ungdomskraft",
        description:
          "Bought something at a Red Cross second hand store that won't fit in your car? Or want to donate furniture and clothes? We pick up and deliver — both ways. Book today.",
        h1: "Red Cross transport",
      },
      hero: {
        heading: "We drive to and from Red-Cross-stores",
        intro:
          "Found an amazing piece of furniture at a Red Cross second hand store — but it won't fit in your car? Or do you have furniture, clothes and household items at home that you want to donate to the Red Cross but lack transport? We handle both. We pick up your purchase at the store and deliver home, or collect from your home and drive to the nearest Red Cross store. No need to rent a trailer, ask friends for help or make multiple trips.",
      },
      featureCards: {
        sinceText: "Red Cross transport since 2014",
        bookYourText: "Book your Red Cross transport",
        bookDescText:
          "Enter your address and the Red Cross store address, describe what needs transporting and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Red Cross transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a bookshelf or donating an entire room of belongings. No need to rent a trailer, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Items we transport to and from the Red Cross",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, tables, chairs, bookshelves and wardrobes — pick up purchases or donate to the Red Cross",
            image: "/sofa.webp",
          },
          {
            name: "Clothes & textiles",
            description:
              "Bags and boxes of clothes, shoes, curtains and bedding to the Red Cross",
            image: "/box.webp",
          },
          {
            name: "Household goods",
            description:
              "Porcelain, cutlery, kitchen tools, lamps and other household items",
            image: "/patio-furniture.webp",
          },
          {
            name: "Electronics",
            description:
              "TVs, computers, speakers and other working electronics",
            image: "/tv.webp",
          },
          {
            name: "Books & media",
            description:
              "Books, vinyl records, games and films — perfect to donate or pick up",
            image: "/painting.webp",
          },
          {
            name: "Kids' stuff",
            description:
              "Strollers, cribs, toys and children's clothes to or from the Red Cross",
            image: "/washer.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Transport to and from the Red Cross — we pick up, drive and drop off for you",
        mainText:
          "The Red Cross operates second hand stores across Sweden where revenue goes directly to humanitarian work — disaster relief, social support and help for people in vulnerable situations both in Sweden and internationally. In the stores you'll find everything from furniture and clothes to household goods, books and toys at great prices. But there's a practical problem many customers recognise: transport. You find a perfect sofa, dining table or wardrobe in the store — but it won't fit in your car. Or you have an entire room at home full of clothes, furniture and belongings you want to donate to the Red Cross, but you don't have a van and can't carry everything yourself. That's where Ungdomskraft comes in. We offer transport in both directions. Bought something at a Red Cross second hand store? We drive to the store, carry out your purchase, load the van and deliver to your home. We carry inside and place it where you want it. Want to donate to the Red Cross? We come to your home, carry out everything you want to donate, load up and drive to the nearest Red Cross store. There we unload and drop off for you. We drive to all Red Cross second hand stores and collection points in Stockholm and Gothenburg. Every week we make hundreds of transports to and from second hand stores. Our crew are used to carrying through narrow stairwells, around tight corners and up multiple flights without a lift. We know how to protect delicate surfaces, disassemble furniture that won't fit through doorways and load the van safely. Shopping and donating at the Red Cross is good for your wallet, the environment and people who need help. With Ungdomskraft's transport we remove the last barrier — you focus on finding the right items or clearing out at home, we take care of the entire journey from door to door.",
        preparationHeading: "Prepare for your Red Cross transport",
        preparationText:
          "If you're picking up a purchase: check the store's opening hours and make sure the item is ready to collect. Have your home address and any door codes ready. If you're donating to the Red Cross: sort what you want to donate and make sure everything is in good condition — the Red Cross only accepts items that can be resold. Pack clothes in bags or boxes. Check that there is clear access through stairwells and that the van can park near the entrance both at your home and at the store. Measure doorways if furniture is large. The more information you provide when booking, the smoother the transport will be.",
        additionalSections: [
          {
            heading: "Pick up furniture and finds bought at the Red Cross",
            content:
              "Red Cross second hand stores often have fantastic finds — furniture, retro chairs, solid dining tables and unique wardrobes you won't find anywhere else. The problem is they rarely fit in an ordinary car. With Ungdomskraft's transport we pick up your purchase directly from the store. We carry the furniture out, protect it with blankets and straps, load the van and drive to your home. There we carry it through stairwells and doorways and place it where you want it. Many of our customers tell us they now dare to buy completely different things at the Red Cross — heavy furniture, bulky shelving units and delicate glass cabinets they could never have taken home on their own. Our crew handle everything from small armchairs to large furniture sets. We bring tools, furniture blankets and carrying straps — you don't need to prepare anything extra. If a piece won't fit through the door, we disassemble it and reassemble at your place. We protect all surfaces during carrying so nothing gets damaged.",
          },
          {
            heading: "Donate furniture and clothes to the Red Cross",
            content:
              "Have furniture, clothes or household goods at home that deserve a new life? Donating to the Red Cross is one of the easiest ways to make a difference — your items are resold and the revenue goes directly to the Red Cross's humanitarian work in Sweden and internationally, funding disaster relief, social support for vulnerable people and aid for refugees. But transport is usually what holds people back. A sofa won't fit in the car, 15 bags of clothes require multiple trips, and the nearest Red Cross store might be far away. With our transport we collect everything from your home. We carry out from any floor, load the van and drive to the Red Cross store of your choice. There we unload and drop off. You just need to sort — we handle the rest. Remember that the Red Cross only accepts items in good condition that can be resold. Broken furniture, dirty textiles and mattresses are usually not accepted. If in doubt, call the store before booking.",
          },
          {
            heading: "How Red Cross transport works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter your address and the Red Cross store address. Describe whether you're picking up a purchase or donating items. You see the price instantly.",
              },
              {
                label: "We collect",
                text: "Our team arrive with a van and the right equipment. We carry out, protect with blankets and straps and load everything.",
              },
              {
                label: "We drive and deliver",
                text: "We drive to the Red Cross and drop off your donated items — or drive your purchase home and carry it inside.",
              },
              {
                label: "Done",
                text: "Your items are contributing to the Red Cross's humanitarian work — or your purchase is in place at your home. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We drive to all Red Cross second hand stores",
            content:
              "We drive to all Red Cross second hand stores and collection points in Stockholm and Gothenburg. Just enter the store address when booking and we handle the rest. We also drive to other second hand stores like Myrorna, Erikshjälpen, Emmaus and Stadsmissionen if you want to donate to multiple places in the same trip.",
          },
          {
            heading: "Why book Red Cross transport with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get professional help with your Red Cross transport — you also contribute to getting young people into the workforce and building their future. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and responsible environmental practices. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Our customers come back time and again — not just because the service works, but because they know their money makes a difference. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nIf the items are transported for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. The deduction is handled directly on the invoice – you never have to pay the full amount upfront.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Red Cross transport cost?",
          answer:
            "The price depends on the amount and distance. A smaller pickup or drop-off starts at 595 SEK, while larger transports with furniture cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Red Cross transport?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Transport that does not go to reuse does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting — you don't need to rent a vehicle.",
        },
        {
          question: "Can you both pick up purchases and deliver donations?",
          answer:
            "Yes, we drive in both directions. We pick up purchases from Red Cross stores and deliver to your home, or collect from your home and drive to the Red Cross for drop-off.",
        },
        {
          question: "Which Red Cross stores do you drive to?",
          answer:
            "We drive to all Red Cross second hand stores and collection points in Stockholm and Gothenburg. Just enter the store address when booking. We also drive to other second hand stores like Myrorna, Erikshjälpen, Emmaus and Stadsmissionen.",
        },
        {
          question: "Can you carry heavy furniture up stairs?",
          answer:
            "Yes, we carry from any floor — with or without a lift. Our crew are used to heavy items in narrow stairwells. We handle sofas, wardrobes, beds and anything else that's heavy.",
        },
        {
          question: "Can you disassemble furniture?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart for transport. We reassemble at your home if you're picking up a purchase.",
        },
        {
          question: "Do you protect items during transport?",
          answer:
            "Yes, we use furniture blankets, carrying straps and ratchet straps. We protect all furniture and fragile items during carrying, loading and transport. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "Does the Red Cross accept everything I want to donate?",
          answer:
            "No, the Red Cross only accepts items in good condition that can be resold. Broken furniture, dirty textiles and mattresses are usually not accepted. Check with the store before booking. We are only responsible for the transport — not for what the store accepts.",
        },
        {
          question: "How quickly can you drive to the Red Cross?",
          answer:
            "We often offer transport at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Can you donate to multiple stores in one trip?",
          answer:
            "Yes, we can drop off at several stores during the same run if they're within a reasonable distance. Describe all drop-off points when you book.",
        },
        {
          question: "Is Red Cross transport available on weekends?",
          answer:
            "Yes, we offer transport on weekends and some evenings. Check that the store is open at your chosen time — we can't drop off if the store is closed.",
        },
        {
          question: "Do I need to be at the store?",
          answer:
            "If you're picking up a purchase, you should have paid for and reserved the item in advance so we only need to collect it. If you're donating, you don't need to be at the store — we unload and drop off for you.",
        },
        {
          question: "Is the transport insured?",
          answer:
            "Yes, all our transports are fully insured. If anything is damaged during carrying, loading or transport we replace it. You can book with confidence.",
        },
        {
          question: "In which areas do you offer Red Cross transport?",
          answer:
            "We offer Red Cross transport across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Red Cross transport in 3 easy steps",
        step2Text:
          "Our crew carry out your items or your purchase, protect with blankets and load the van. Then we drive to the Red Cross — or to your home.",
        step3Title: "All in place",
        step3Desc:
          "Your donated items are dropped off at the Red Cross — or your purchase is in place at your home. Done.",
        ctaHeading: "Book Red Cross transport today",
        ctaText:
          "Enter your address and the Red Cross store address, describe what needs transporting and choose a time. You see the price instantly.",
      },
    },
  },
  {
    // Transport Stadsmissionen — config för /transport-stadsmissionen
    // Vinkel: Hämta köp från Stadsmissionens second hand ELLER skänka/lämna saker dit.
    // Unik vinkel: Stadsmissionens sociala arbete — intäkterna finansierar stöd till hemlösa och utsatta.
    slug: "transport-stadsmissionen",
    serviceCategory: "transport",

    meta: {
      title:
        "Transport Stadsmissionen – Hämta Eller Skänk Med Skåpbil | Ungdomskraft",
      description:
        "Köpt något på Stadsmissionens second hand som inte får plats i bilen? Eller vill du skänka möbler och kläder? Vi hämtar och kör – åt båda hållen. Boka transport idag.",
      h1: "Transport Stadsmissionen",
    },

    hero: {
      heading: "Vi kör till och från Stadsmissionen",
      intro:
        "Hittat en drömmöbel eller ett unikt fynd på Stadsmissionens second hand – men den får inte plats i bilen? Eller har du möbler, kläder och prylar hemma som du vill skänka till Stadsmissionen men saknar transport? Vi löser båda. Vi hämtar ditt köp i butiken och levererar hem, eller hämtar hemma hos dig och kör till närmaste Stadsmissionen-butik. Du slipper hyra släp, be kompisar om hjälp eller göra flera turer.",
      heroImage: "/hero-transport-aterbruk.png",
    },

    featureCards: {
      sinceText: "Stadsmissionen-transport sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att hämta köp från Stadsmissionens second hand och skänka saker dit. Våra ungdomar vet hur man bär tunga möbler genom trånga trapphus och lastar skåpbilar effektivt – du får ditt köp hem eller dina saker lämnade utan att lyfta ett finger.",
      bookYourText: "Boka din Stadsmissionen-transport",
      bookDescText:
        "Ange din adress och Stadsmissionen-butikens adress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Stadsmissionen-transporter",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en bokhylla eller skänker ett helt rum med prylar. Du slipper hyra släp, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi kör till och från Stadsmissionen",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, bord, stolar, bokhyllor och garderober – hämta köp eller skänka till Stadsmissionen",
          image: "/sofa.webp",
        },
        {
          name: "Kläder & textilier",
          description:
            "Kassar och kartonger med kläder, skor, gardiner och sängkläder till Stadsmissionen",
          image: "/box.webp",
        },
        {
          name: "Husgeråd",
          description:
            "Porslin, bestick, köksredskap, lampor och annat hushållsgods",
          image: "/patio-furniture.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, högtalare och annan fungerande elektronik",
          image: "/tv.webp",
        },
        {
          name: "Böcker & media",
          description:
            "Böcker, vinylskivor, spel och filmer – perfekt att skänka eller hämta",
          image: "/painting.webp",
        },
        {
          name: "Barnprylar",
          description:
            "Barnvagnar, barnsängar, leksaker och barnkläder till eller från Stadsmissionen",
          image: "/washer.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Transport till och från Stadsmissionen – vi hämtar, kör och lämnar åt dig",
      mainText:
        "Stadsmissionen driver second hand-butiker i Stockholm och Göteborg där intäkterna går direkt till socialt arbete – stöd till hemlösa, utsatta barn och ungdomar, äldre i ensamhet och människor som lever i utsatthet. Stockholms Stadsmission och Göteborgs Stadsmission är två av Sveriges mest välkända sociala organisationer, och deras second hand-butiker har blivit populära shopping-destinationer där du hittar allt från kvalitetsmöbler och designkläder till husgeråd, böcker och leksaker till bra priser. Men det finns ett praktiskt problem som många kunder känner igen: transporten. Du hittar en perfekt soffa, ett retrobord eller en unik bokhylla i butiken – men den får inte plats i personbilen. Eller så har du hemma ett helt rum med kläder, möbler och prylar som du vill skänka till Stadsmissionen, men du har ingen skåpbil och orkar inte bära allt själv. Det är här Ungdomskraft kommer in. Vi erbjuder transport åt båda hållen. Har du köpt något på Stadsmissionens second hand? Vi åker till butiken, bär ut ditt köp, lastar skåpbilen och levererar hem till dig. Vi bär in och ställer på plats. Vill du skänka till Stadsmissionen? Vi kommer hem till dig, bär ut allt du vill skänka, lastar och kör till närmaste Stadsmissionen-butik. Där lastar vi av och lämnar in åt dig. Vi kör till alla Stadsmissionens second hand-butiker och insamlingspunkter i Stockholm och Göteborg. Varje vecka kör vi hundratals transporter till och från second hand-butiker. Våra ungdomar är vana vid att bära genom smala trapphus, runt trånga hörn och upp flera våningar utan hiss. Vi vet hur man skyddar ömtåliga ytor, demonterar möbler som inte går genom dörren och lastar skåpbilen så att inget skadas. Att handla och skänka på Stadsmissionen är bra för plånboken, miljön och människor som behöver stöd. Med Ungdomskrafts transport tar vi bort det sista hindret – du fokuserar på att hitta rätt prylar eller rensa hemma, vi tar hand om hela transporten från dörr till dörr.",
      preparationHeading: "Förbered inför din Stadsmissionen-transport",
      preparationText:
        "Om du hämtar ett köp: kontrollera butikens öppettider och att varan är redo att hämtas. Ha din hemadress och eventuell portkod redo. Om du skänker till Stadsmissionen: sortera det du vill skänka och se till att allt är i gott skick – Stadsmissionen tar bara emot saker som kan säljas vidare. Packa kläder i kassar eller kartonger. Kolla att det finns fri passage i trapphus och att skåpbilen kan parkera nära porten både hemma och vid butiken. Mät gärna dörröppningar om möblerna är stora. Ju mer information du anger vid bokning, desto smidigare går transporten.",
      additionalSections: [
        {
          heading: "Hämta möbler och prylar köpta på Stadsmissionen",
          content:
            "Stadsmissionens second hand-butiker är kända för sin kuraterade inredning – du hittar designmöbler, retroklassiker, kvalitetssoffor och unika hyllor som du inte stöter på någon annanstans. Butikerna på Hornsgatan, i Vasastan och på Södermalm i Stockholm samt i centrala Göteborg lockar inredningsintresserade från hela regionen. Problemet är att de bästa fynden sällan får plats i en vanlig personbil. Med Ungdomskrafts transport hämtar vi ditt köp direkt i butiken. Vi bär ut möbeln, skyddar den med filtar och spännband, lastar skåpbilen och kör hem till dig. Där bär vi in genom trapphus och dörrar och ställer på plats. Många av våra kunder berättar att de nu vågar köpa helt andra saker på Stadsmissionen – tunga möbler, skrymmande hyllor och ömtåliga glasskåp som de aldrig hade kunnat ta hem själva. Vår personal hanterar allt från små fåtöljer till stora möbelgrupper. Vi tar med oss verktyg, möbelfiltar och bärremmar – du behöver inte förbereda något extra. Om möbeln inte går genom dörren demonterar vi den och monterar ihop den igen hemma hos dig. Vi skyddar alla ytor under bärningen så att inget skadas varken i butiken eller hos dig.",
        },
        {
          heading: "Skänka möbler och kläder till Stadsmissionen",
          content:
            "Har du möbler, kläder eller husgeråd hemma som förtjänar ett nytt liv? Att skänka till Stadsmissionen är ett av de enklaste sätten att göra skillnad – dina saker säljs vidare och intäkterna går direkt till Stadsmissionens sociala arbete. Pengarna finansierar härbärgen för hemlösa, stödverksamhet för utsatta barn och ungdomar, jobbträning och social integration. Varje möbel och varje kasse med kläder du skänker bidrar till att någon får tak över huvudet, en varm måltid eller en väg tillbaka till samhället. Men transporten brukar vara det som stoppar. En soffa ryms inte i personbilen, 15 kassar med kläder kräver flera turer, och närmaste Stadsmissionen-butik kanske ligger långt bort. Med vår transport hämtar vi allt hemma hos dig. Vi bär ut från vilken våning som helst, lastar skåpbilen och kör till Stadsmissionen-butiken du väljer. Där lastar vi av och lämnar in. Du behöver bara sortera – vi sköter resten. Tänk på att Stadsmissionen bara tar emot saker i gott skick som kan säljas vidare. Trasiga möbler, smutsiga textilier och madrasser brukar inte accepteras. Är du osäker – ring butiken innan du bokar.",
        },
        {
          heading: "Så fungerar Stadsmissionen-transport med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange din adress och Stadsmissionen-butikens adress. Beskriv om du hämtar ett köp eller skänker saker. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar",
              text: "Vårt team kommer med skåpbil och rätt utrustning. Vi bär ut, skyddar med filtar och spännband och lastar allt.",
            },
            {
              label: "Vi kör och lämnar",
              text: "Vi kör till Stadsmissionen och lämnar in dina skänkta saker – eller kör hem ditt köp och bär in på plats.",
            },
            {
              label: "Klart",
              text: "Dina saker bidrar till Stadsmissionens sociala arbete – eller ditt köp står på plats hemma hos dig. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör till alla Stadsmissionens second hand-butiker",
          content:
            "Vi kör till alla Stadsmissionens second hand-butiker och insamlingspunkter i Stockholm och Göteborg. I Stockholm kör vi till butikerna på Hornsgatan, i Vasastan, på Södermalm och alla övriga. I Göteborg når vi butikerna i centrum, på Hisingen och i övriga stadsdelar. Ange bara butikens adress vid bokning så sköter vi resten. Vi kör även till andra second hand-butiker som Myrorna, Erikshjälpen, Emmaus och Röda Korset om du vill skänka till flera ställen under samma tur.",
        },
        {
          heading: "Varför boka Stadsmissionen-transport med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara professionell hjälp med din Stadsmissionen-transport – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och ansvarsfullt miljötänk. Det blir en dubbel insats: dina skänkta saker stödjer Stadsmissionens arbete med hemlösa och utsatta, samtidigt som transporten ger ungdomar jobb och erfarenhet. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra kunder kommer tillbaka gång efter gång – inte bara för att tjänsten fungerar, utan för att de vet att deras pengar gör skillnad. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar.\n\nOm sakerna transporteras till återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Avdraget hanteras direkt på fakturan – du behöver aldrig ligga ute med pengarna.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar transport till och från Stadsmissionen?",
        answer:
          "Priset beror på mängden och avståndet. En mindre hämtning eller lämning kostar från 595 kr, medan större transporter med möbler kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på transport till Stadsmissionen?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Transport som inte går till återanvändning ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Ingår skåpbil i transporten?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni både hämta köp och lämna skänkta saker?",
        answer:
          "Ja, vi kör åt båda hållen. Vi hämtar köp i Stadsmissionen-butiken och levererar hem till dig, eller hämtar hemma hos dig och kör till Stadsmissionen för att lämna in.",
      },
      {
        question: "Vilka Stadsmissionen-butiker kör ni till?",
        answer:
          "Vi kör till alla Stadsmissionens second hand-butiker och insamlingspunkter i Stockholm och Göteborg. Ange bara butikens adress vid bokning. Vi kör även till andra second hand-butiker som Myrorna, Erikshjälpen, Emmaus och Röda Korset.",
      },
      {
        question: "Kan ni bära tunga möbler i trappor?",
        answer:
          "Ja, vi bär från vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft i trånga trapphus. Vi hanterar soffor, garderober, sängar och allt annat tungt.",
      },
      {
        question: "Kan ni demontera möbler?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär för att transporteras. Vi monterar ihop dem igen hemma hos dig om du hämtar ett köp.",
      },
      {
        question: "Skyddar ni sakerna under transporten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar alla möbler och ömtåliga föremål under bärning, lastning och transport. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Tar Stadsmissionen emot allt jag vill skänka?",
        answer:
          "Nej, Stadsmissionen tar bara emot saker i gott skick som kan säljas vidare. Trasiga möbler, smutsiga textilier och madrasser brukar inte accepteras. Kolla med butiken innan du bokar. Vi ansvarar enbart för transporten – inte för vad butiken tar emot.",
      },
      {
        question: "Hur snabbt kan ni köra till Stadsmissionen?",
        answer:
          "Vi erbjuder ofta transport med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni skänka till flera butiker på samma tur?",
        answer:
          "Ja, vi kan lämna på flera ställen under samma körning om butikerna ligger i rimligt avstånd. Beskriv alla lämningspunkter när du bokar.",
      },
      {
        question: "Erbjuder ni Stadsmissionen-transport på helger?",
        answer:
          "Ja, vi erbjuder transport på helger och vissa kvällar. Kontrollera att butiken har öppet den tid du bokar – vi kan inte lämna om butiken är stängd.",
      },
      {
        question: "Behöver jag vara med i butiken?",
        answer:
          "Om du hämtar ett köp bör du ha betalat och reserverat varan i förväg så att vi bara behöver hämta den. Om du skänker behöver du inte vara med i butiken – vi lastar av och lämnar in åt dig.",
      },
      {
        question: "Är transporten försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under bärning, lastning eller transport ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni Stadsmissionen-transport?",
        answer:
          "Vi erbjuder Stadsmissionen-transport i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Vart går pengarna när jag skänker till Stadsmissionen?",
        answer:
          "Intäkterna från Stadsmissionens second hand-butiker går direkt till socialt arbete – härbärgen för hemlösa, stöd till utsatta barn och ungdomar, jobbträningsprogram och social integration. Dina skänkta saker gör konkret skillnad för människor i utsatthet.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Stadsmissionen-transport i 3 enkla steg",
      step2Text:
        "Våra ungdomar bär ut dina saker eller ditt köp, skyddar med filtar och lastar skåpbilen. Sedan kör vi till Stadsmissionen – eller hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Dina skänkta saker är inlämnade på Stadsmissionen – eller ditt köp står hemma hos dig. Klart.",
      ctaHeading: "Boka Stadsmissionen-transport idag",
      ctaText:
        "Ange din adress och Stadsmissionen-butikens adress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "stadsmissionen-transport",
      meta: {
        title:
          "Stadsmissionen Transport – Pick Up Or Donate With Van | Ungdomskraft",
        description:
          "Bought something at a Stadsmissionen second hand store that won't fit in your car? Or want to donate furniture and clothes? We pick up and deliver — both ways. Book today.",
        h1: "Stadsmissionen transport",
      },
      hero: {
        heading: "We drive to and from Stadsmissionen",
        intro:
          "Found an amazing piece of furniture at a Stadsmissionen second hand store — but it won't fit in your car? Or do you have furniture, clothes and household items at home that you want to donate to Stadsmissionen but lack transport? We handle both. We pick up your purchase at the store and deliver home, or collect from your home and drive to the nearest Stadsmissionen store. No need to rent a trailer, ask friends for help or make multiple trips.",
      },
      featureCards: {
        sinceText: "Stadsmissionen transport since 2014",
        bookYourText: "Book your Stadsmissionen transport",
        bookDescText:
          "Enter your address and the Stadsmissionen store address, describe what needs transporting and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Stadsmissionen transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a bookshelf or donating an entire room of belongings. No need to rent a trailer, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Items we transport to and from Stadsmissionen",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, tables, chairs, bookshelves and wardrobes — pick up purchases or donate to Stadsmissionen",
            image: "/sofa.webp",
          },
          {
            name: "Clothes & textiles",
            description:
              "Bags and boxes of clothes, shoes, curtains and bedding to Stadsmissionen",
            image: "/box.webp",
          },
          {
            name: "Household goods",
            description:
              "Porcelain, cutlery, kitchen tools, lamps and other household items",
            image: "/patio-furniture.webp",
          },
          {
            name: "Electronics",
            description:
              "TVs, computers, speakers and other working electronics",
            image: "/tv.webp",
          },
          {
            name: "Books & media",
            description:
              "Books, vinyl records, games and films — perfect to donate or pick up",
            image: "/painting.webp",
          },
          {
            name: "Kids' stuff",
            description:
              "Strollers, cribs, toys and children's clothes to or from Stadsmissionen",
            image: "/washer.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Transport to and from Stadsmissionen — we pick up, drive and drop off for you",
        mainText:
          "Stadsmissionen operates second hand stores in Stockholm and Gothenburg where revenue goes directly to social work — shelters for the homeless, support for vulnerable children and young people, job training programmes and social integration. Stockholm Stadsmission and Gothenburg Stadsmission are two of Sweden's most well-known social organisations, and their second hand stores have become popular shopping destinations where you'll find everything from quality furniture and designer clothing to household goods, books and toys at great prices. But there's a practical problem many customers recognise: transport. You find a perfect sofa, retro table or unique bookshelf in the store — but it won't fit in your car. Or you have an entire room at home full of clothes, furniture and belongings you want to donate to Stadsmissionen, but you don't have a van and can't carry everything yourself. That's where Ungdomskraft comes in. We offer transport in both directions. Bought something at a Stadsmissionen second hand store? We drive to the store, carry out your purchase, load the van and deliver to your home. We carry inside and place it where you want it. Want to donate to Stadsmissionen? We come to your home, carry out everything you want to donate, load up and drive to the nearest Stadsmissionen store. There we unload and drop off for you. We drive to all Stadsmissionen second hand stores and collection points in Stockholm and Gothenburg. Every week we make hundreds of transports to and from second hand stores. Our crew are used to carrying through narrow stairwells, around tight corners and up multiple flights without a lift. We know how to protect delicate surfaces, disassemble furniture that won't fit through doorways and load the van safely. Shopping and donating at Stadsmissionen is good for your wallet, the environment and people who need support. With Ungdomskraft's transport we remove the last barrier — you focus on finding the right items or clearing out at home, we take care of the entire journey from door to door.",
        preparationHeading: "Prepare for your Stadsmissionen transport",
        preparationText:
          "If you're picking up a purchase: check the store's opening hours and make sure the item is ready to collect. Have your home address and any door codes ready. If you're donating to Stadsmissionen: sort what you want to donate and make sure everything is in good condition — Stadsmissionen only accepts items that can be resold. Pack clothes in bags or boxes. Check that there is clear access through stairwells and that the van can park near the entrance both at your home and at the store. Measure doorways if furniture is large. The more information you provide when booking, the smoother the transport will be.",
        additionalSections: [
          {
            heading: "Pick up furniture and finds bought at Stadsmissionen",
            content:
              "Stadsmissionen second hand stores are known for their curated interiors — you'll find designer furniture, retro classics, quality sofas and unique shelving you won't encounter anywhere else. The stores on Hornsgatan, in Vasastan and on Södermalm in Stockholm as well as in central Gothenburg attract design enthusiasts from across the region. The problem is that the best finds rarely fit in an ordinary car. With Ungdomskraft's transport we pick up your purchase directly from the store. We carry the furniture out, protect it with blankets and straps, load the van and drive to your home. There we carry it through stairwells and doorways and place it where you want it. Many of our customers tell us they now dare to buy completely different things at Stadsmissionen — heavy furniture, bulky shelving units and delicate glass cabinets they could never have taken home on their own. Our crew handle everything from small armchairs to large furniture sets. We bring tools, furniture blankets and carrying straps — you don't need to prepare anything extra. If a piece won't fit through the door, we disassemble it and reassemble at your place. We protect all surfaces during carrying so nothing gets damaged.",
          },
          {
            heading: "Donate furniture and clothes to Stadsmissionen",
            content:
              "Have furniture, clothes or household goods at home that deserve a new life? Donating to Stadsmissionen is one of the easiest ways to make a difference — your items are resold and the revenue goes directly to Stadsmissionen's social work: shelters for the homeless, support programmes for vulnerable children and young people, job training and social integration. Every piece of furniture and every bag of clothes you donate helps someone get a roof over their head, a warm meal or a path back into society. But transport is usually what holds people back. A sofa won't fit in the car, 15 bags of clothes require multiple trips, and the nearest Stadsmissionen store might be far away. With our transport we collect everything from your home. We carry out from any floor, load the van and drive to the Stadsmissionen store of your choice. There we unload and drop off. You just need to sort — we handle the rest. Remember that Stadsmissionen only accepts items in good condition that can be resold. Broken furniture, dirty textiles and mattresses are usually not accepted. If in doubt, call the store before booking.",
          },
          {
            heading: "How Stadsmissionen transport works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter your address and the Stadsmissionen store address. Describe whether you're picking up a purchase or donating items. You see the price instantly.",
              },
              {
                label: "We collect",
                text: "Our team arrive with a van and the right equipment. We carry out, protect with blankets and straps and load everything.",
              },
              {
                label: "We drive and deliver",
                text: "We drive to Stadsmissionen and drop off your donated items — or drive your purchase home and carry it inside.",
              },
              {
                label: "Done",
                text: "Your items are contributing to Stadsmissionen's social work — or your purchase is in place at your home. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We drive to all Stadsmissionen second hand stores",
            content:
              "We drive to all Stadsmissionen second hand stores and collection points in Stockholm and Gothenburg. In Stockholm we drive to the stores on Hornsgatan, in Vasastan, on Södermalm and all others. In Gothenburg we reach the stores in the city centre, on Hisingen and in other districts. Just enter the store address when booking and we handle the rest. We also drive to other second hand stores like Myrorna, Erikshjälpen, Emmaus and the Red Cross if you want to donate to multiple places in the same trip.",
          },
          {
            heading: "Why book Stadsmissionen transport with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get professional help with your Stadsmissionen transport — you also contribute to getting young people into the workforce and building their future. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and responsible environmental practices. It's a double impact: your donated items support Stadsmissionen's work with the homeless and vulnerable, while the transport gives young people jobs and experience. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Our customers come back time and again — not just because the service works, but because they know their money makes a difference. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nIf the items are transported for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. The deduction is handled directly on the invoice – you never have to pay the full amount upfront.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Stadsmissionen transport cost?",
          answer:
            "The price depends on the amount and distance. A smaller pickup or drop-off starts at 595 SEK, while larger transports with furniture cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Stadsmissionen transport?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Transport that does not go to reuse does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting — you don't need to rent a vehicle.",
        },
        {
          question: "Can you both pick up purchases and deliver donations?",
          answer:
            "Yes, we drive in both directions. We pick up purchases from Stadsmissionen stores and deliver to your home, or collect from your home and drive to Stadsmissionen for drop-off.",
        },
        {
          question: "Which Stadsmissionen stores do you drive to?",
          answer:
            "We drive to all Stadsmissionen second hand stores and collection points in Stockholm and Gothenburg. Just enter the store address when booking. We also drive to other second hand stores like Myrorna, Erikshjälpen, Emmaus and the Red Cross.",
        },
        {
          question: "Can you carry heavy furniture up stairs?",
          answer:
            "Yes, we carry from any floor — with or without a lift. Our crew are used to heavy items in narrow stairwells. We handle sofas, wardrobes, beds and anything else that's heavy.",
        },
        {
          question: "Can you disassemble furniture?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart for transport. We reassemble at your home if you're picking up a purchase.",
        },
        {
          question: "Do you protect items during transport?",
          answer:
            "Yes, we use furniture blankets, carrying straps and ratchet straps. We protect all furniture and fragile items during carrying, loading and transport. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "Does Stadsmissionen accept everything I want to donate?",
          answer:
            "No, Stadsmissionen only accepts items in good condition that can be resold. Broken furniture, dirty textiles and mattresses are usually not accepted. Check with the store before booking. We are only responsible for the transport — not for what the store accepts.",
        },
        {
          question: "How quickly can you drive to Stadsmissionen?",
          answer:
            "We often offer transport at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Can you donate to multiple stores in one trip?",
          answer:
            "Yes, we can drop off at several stores during the same run if they're within a reasonable distance. Describe all drop-off points when you book.",
        },
        {
          question: "Is Stadsmissionen transport available on weekends?",
          answer:
            "Yes, we offer transport on weekends and some evenings. Check that the store is open at your chosen time — we can't drop off if the store is closed.",
        },
        {
          question: "Do I need to be at the store?",
          answer:
            "If you're picking up a purchase, you should have paid for and reserved the item in advance so we only need to collect it. If you're donating, you don't need to be at the store — we unload and drop off for you.",
        },
        {
          question: "Is the transport insured?",
          answer:
            "Yes, all our transports are fully insured. If anything is damaged during carrying, loading or transport we replace it. You can book with confidence.",
        },
        {
          question: "In which areas do you offer Stadsmissionen transport?",
          answer:
            "We offer Stadsmissionen transport across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Where does the money go when I donate to Stadsmissionen?",
          answer:
            "Revenue from Stadsmissionen's second hand stores goes directly to social work — shelters for the homeless, support for vulnerable children and young people, job training programmes and social integration. Your donated items make a real difference for people in need.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Stadsmissionen transport in 3 easy steps",
        step2Text:
          "Our crew carry out your items or your purchase, protect with blankets and load the van. Then we drive to Stadsmissionen — or to your home.",
        step3Title: "All in place",
        step3Desc:
          "Your donated items are dropped off at Stadsmissionen — or your purchase is in place at your home. Done.",
        ctaHeading: "Book Stadsmissionen transport today",
        ctaText:
          "Enter your address and the Stadsmissionen store address, describe what needs transporting and choose a time. You see the price instantly.",
      },
    },
  },
];
